import * as i0 from '@angular/core';
import { NgModule, Optional, SkipSelf, InjectionToken, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpContext, HttpParams } from '@angular/common/http';
class Configuration {
  constructor({
    accessToken,
    apiKeys,
    basePath,
    credentials,
    encodeParam,
    encoder,
    password,
    username,
    withCredentials
  } = {}) {
    if (apiKeys) {
      this.apiKeys = apiKeys;
    }
    if (username !== undefined) {
      this.username = username;
    }
    if (password !== undefined) {
      this.password = password;
    }
    if (accessToken !== undefined) {
      this.accessToken = accessToken;
    }
    if (basePath !== undefined) {
      this.basePath = basePath;
    }
    if (withCredentials !== undefined) {
      this.withCredentials = withCredentials;
    }
    if (encoder) {
      this.encoder = encoder;
    }
    this.encodeParam = encodeParam ?? (param => this.defaultEncodeParam(param));
    this.credentials = credentials ?? {};
    // init default APIKeyQuery credential
    if (!this.credentials['APIKeyQuery']) {
      this.credentials['APIKeyQuery'] = () => {
        if (this.apiKeys === null || this.apiKeys === undefined) {
          return undefined;
        } else {
          return this.apiKeys['APIKeyQuery'] || this.apiKeys['authentication_token'];
        }
      };
    }
    // init default OAuth2PasswordBearer credential
    if (!this.credentials['OAuth2PasswordBearer']) {
      this.credentials['OAuth2PasswordBearer'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application/json|[^;/ \t]+/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  addCredentialToHeaders(credentialKey, headerName, headers, prefix) {
    const value = this.lookupCredential(credentialKey);
    return value ? headers.set(headerName, (prefix ?? '') + value) : headers;
  }
  addCredentialToQuery(credentialKey, paramName, query) {
    const value = this.lookupCredential(credentialKey);
    return value ? query.set(paramName, value) : query;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}
let ApiModule = /*#__PURE__*/(() => {
  class ApiModule {
    static forRoot(configurationFactory) {
      return {
        ngModule: ApiModule,
        providers: [{
          provide: Configuration,
          useFactory: configurationFactory
        }]
      };
    }
    constructor(parentModule, http) {
      if (parentModule) {
        throw new Error('ApiModule is already loaded. Import in your base AppModule only.');
      }
      if (!http) {
        throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
      }
    }
    static {
      this.ɵfac = function ApiModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ApiModule)(i0.ɵɵinject(ApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: ApiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return ApiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
class BaseService {
  constructor(basePath, configuration) {
    this.basePath = 'https://api.oak-events.com/api';
    this.defaultHeaders = new HttpHeaders();
    this.configuration = configuration || new Configuration();
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  canConsumeForm(consumes) {
    return consumes.indexOf('multipart/form-data') !== -1;
  }
  addToHttpParams(httpParams, value, key, isDeep = false) {
    // If the value is an object (but not a Date), recursively add its keys.
    if (typeof value === 'object' && !(value instanceof Date)) {
      return this.addToHttpParamsRecursive(httpParams, value, isDeep ? key : undefined, isDeep);
    }
    return this.addToHttpParamsRecursive(httpParams, value, key);
  }
  addToHttpParamsRecursive(httpParams, value, key, isDeep = false) {
    if (value === null || value === undefined) {
      return httpParams;
    }
    if (typeof value === 'object') {
      // If JSON format is preferred, key must be provided.
      if (key != null) {
        return isDeep ? Object.keys(value).reduce((hp, k) => hp.append(`${key}[${k}]`, value[k]), httpParams) : httpParams.append(key, JSON.stringify(value));
      }
      // Otherwise, if it's an array, add each element.
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString());
        } else {
          throw Error('key may not be null if value is Date');
        }
      } else {
        Object.keys(value).forEach(k => {
          const paramKey = key ? `${key}.${k}` : k;
          httpParams = this.addToHttpParamsRecursive(httpParams, value[k], paramKey);
        });
      }
      return httpParams;
    } else if (key != null) {
      return httpParams.append(key, value);
    }
    throw Error('key may not be null if value is not object or array');
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  csv: ',',
  tsv: '   ',
  ssv: ' ',
  pipes: '|'
};

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AdministratorService = /*#__PURE__*/(() => {
  class AdministratorService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    registerAdministrator(registerAdministratorInput, observe = 'body', reportProgress = false, options) {
      if (registerAdministratorInput === null || registerAdministratorInput === undefined) {
        throw new Error('Required parameter registerAdministratorInput was null or undefined when calling registerAdministrator.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/administrators/register`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registerAdministratorInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AdministratorService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AdministratorService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AdministratorService,
        factory: AdministratorService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AdministratorService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AdministratorCRUDService = /*#__PURE__*/(() => {
  class AdministratorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAdministrator(administratorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (administratorCreateSchema === null || administratorCreateSchema === undefined) {
        throw new Error('Required parameter administratorCreateSchema was null or undefined when calling manageCreateAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministrator(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministrator(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCount(orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministrators(orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministrator(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministrators(orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministrator(itemId, administratorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministrator.');
      }
      if (administratorUpdateSchema === null || administratorUpdateSchema === undefined) {
        throw new Error('Required parameter administratorUpdateSchema was null or undefined when calling manageUpdateAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AdministratorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AdministratorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AdministratorCRUDService,
        factory: AdministratorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AdministratorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AdministratorCompanyCRUDService = /*#__PURE__*/(() => {
  class AdministratorCompanyCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAdministratorCompany(administratorCompanyCreateSchema, observe = 'body', reportProgress = false, options) {
      if (administratorCompanyCreateSchema === null || administratorCompanyCreateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyCreateSchema was null or undefined when calling manageCreateAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAdministratorCompanyByAdministrator(administratorId, administratorCompanyCreateSchema, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageCreateAdministratorCompanyByAdministrator.');
      }
      if (administratorCompanyCreateSchema === null || administratorCompanyCreateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyCreateSchema was null or undefined when calling manageCreateAdministratorCompanyByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAdministratorCompanyByAdministratorAndCompany(administratorId, companyId, administratorCompanyCreateSchema, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageCreateAdministratorCompanyByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorCompanyCreateSchema === null || administratorCompanyCreateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyCreateSchema was null or undefined when calling manageCreateAdministratorCompanyByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAdministratorCompanyByCompany(companyId, administratorCompanyCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAdministratorCompanyByCompany.');
      }
      if (administratorCompanyCreateSchema === null || administratorCompanyCreateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyCreateSchema was null or undefined when calling manageCreateAdministratorCompanyByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorCompany(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorCompanyByAdministrator(itemId, administratorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorCompanyByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAdministratorCompanyByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorCompanyByAdministratorAndCompany(itemId, administratorId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAdministratorCompanyByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAdministratorCompanyByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorCompanyByCompany(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorCompanyByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAdministratorCompanyByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorCompaniesByAdministrator(administratorId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAllAdministratorCompaniesByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorCompaniesByAdministratorAndCompany(administratorId, companyId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorCompaniesByCompany(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllAdministratorCompaniesByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompany(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyByAdministrator(itemId, administratorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorCompanyByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorCompanyByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyByAdministratorAndCompany(itemId, administratorId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorCompanyByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorCompanyByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyByCompany(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorCompanyByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorCompanyByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyCount(orderBy, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyCountByAdministrator(administratorId, orderBy, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorCompanyCountByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyCountByAdministratorAndCompany(administratorId, companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorCompanyCountByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorCompanyCountByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorCompanyCountByCompany(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorCompanyCountByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorCompanies(orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorCompaniesByAdministrator(administratorId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAllAdministratorCompaniesByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorCompaniesByAdministratorAndCompany(administratorId, companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorCompaniesByCompany(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAdministratorCompaniesByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorCompany(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorCompanyByAdministrator(itemId, administratorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorCompanyByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAdministratorCompanyByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorCompanyByAdministratorAndCompany(itemId, administratorId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAdministratorCompanyByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAdministratorCompanyByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorCompanyByCompany(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorCompanyByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAdministratorCompanyByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorCompanies(orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorCompaniesByAdministrator(administratorId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAllAdministratorCompaniesByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorCompaniesByAdministratorAndCompany(administratorId, companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAdministratorCompaniesByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorCompaniesByCompany(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAdministratorCompaniesByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetAdministratorCompanies(administratorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageSetAdministratorCompanies.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetAdministratorCompanies.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetCompanyAdministrators(companyId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetCompanyAdministrators.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetCompanyAdministrators.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorCompany(itemId, administratorCompanyUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorCompany.');
      }
      if (administratorCompanyUpdateSchema === null || administratorCompanyUpdateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyUpdateSchema was null or undefined when calling manageUpdateAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorCompanyByAdministrator(itemId, administratorId, administratorCompanyUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorCompanyByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageUpdateAdministratorCompanyByAdministrator.');
      }
      if (administratorCompanyUpdateSchema === null || administratorCompanyUpdateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyUpdateSchema was null or undefined when calling manageUpdateAdministratorCompanyByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorCompanyByAdministratorAndCompany(itemId, administratorId, companyId, administratorCompanyUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageUpdateAdministratorCompanyByAdministratorAndCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAdministratorCompanyByAdministratorAndCompany.');
      }
      if (administratorCompanyUpdateSchema === null || administratorCompanyUpdateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyUpdateSchema was null or undefined when calling manageUpdateAdministratorCompanyByAdministratorAndCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorCompanyByCompany(itemId, companyId, administratorCompanyUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorCompanyByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAdministratorCompanyByCompany.');
      }
      if (administratorCompanyUpdateSchema === null || administratorCompanyUpdateSchema === undefined) {
        throw new Error('Required parameter administratorCompanyUpdateSchema was null or undefined when calling manageUpdateAdministratorCompanyByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorCompanyUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AdministratorCompanyCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AdministratorCompanyCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AdministratorCompanyCRUDService,
        factory: AdministratorCompanyCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AdministratorCompanyCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AdministratorEventCheckInCRUDService = /*#__PURE__*/(() => {
  class AdministratorEventCheckInCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAdministratorEventCheckIn(companyId, administratorEventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAdministratorEventCheckIn.');
      }
      if (administratorEventCheckInCreateSchema === null || administratorEventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInCreateSchema was null or undefined when calling manageCreateAdministratorEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAdministratorEventCheckInByAdministrator(companyId, administratorId, administratorEventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAdministratorEventCheckInByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageCreateAdministratorEventCheckInByAdministrator.');
      }
      if (administratorEventCheckInCreateSchema === null || administratorEventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInCreateSchema was null or undefined when calling manageCreateAdministratorEventCheckInByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAdministratorEventCheckInByEvent(companyId, eventId, administratorEventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAdministratorEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateAdministratorEventCheckInByEvent.');
      }
      if (administratorEventCheckInCreateSchema === null || administratorEventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInCreateSchema was null or undefined when calling manageCreateAdministratorEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorEventCheckIn(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAdministratorEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorEventCheckInByAdministrator(itemId, companyId, administratorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorEventCheckInByAdministrator.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAdministratorEventCheckInByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAdministratorEventCheckInByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAdministratorEventCheckInByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAdministratorEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAdministratorEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAdministratorEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorEventCheckIns(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllAdministratorEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorEventCheckInsByAdministrator(companyId, administratorId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllAdministratorEventCheckInsByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageDeleteAllAdministratorEventCheckInsByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllAdministratorEventCheckInsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllAdministratorEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllAdministratorEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckIn(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckInByAdministrator(itemId, companyId, administratorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorEventCheckInByAdministrator.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckInByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorEventCheckInByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckInByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAdministratorEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAdministratorEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckInCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckInCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckInCountByAdministrator(companyId, administratorId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckInCountByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAdministratorEventCheckInCountByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAdministratorEventCheckInCountByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAdministratorEventCheckInCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAdministratorEventCheckInCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorEventCheckIns(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAdministratorEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorEventCheckInsByAdministrator(companyId, administratorId, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAdministratorEventCheckInsByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageGetAllAdministratorEventCheckInsByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAdministratorEventCheckInsByEvent(companyId, eventId, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAdministratorEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllAdministratorEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorEventCheckIn(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAdministratorEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorEventCheckInByAdministrator(itemId, companyId, administratorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorEventCheckInByAdministrator.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAdministratorEventCheckInByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAdministratorEventCheckInByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAdministratorEventCheckInByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAdministratorEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAdministratorEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAdministratorEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorEventCheckIns(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAdministratorEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorEventCheckInsByAdministrator(companyId, administratorId, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAdministratorEventCheckInsByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageRenderAllAdministratorEventCheckInsByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAdministratorEventCheckInsByEvent(companyId, eventId, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAdministratorEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllAdministratorEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorEventCheckIn(itemId, companyId, administratorEventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAdministratorEventCheckIn.');
      }
      if (administratorEventCheckInUpdateSchema === null || administratorEventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInUpdateSchema was null or undefined when calling manageUpdateAdministratorEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorEventCheckInByAdministrator(itemId, companyId, administratorId, administratorEventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorEventCheckInByAdministrator.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAdministratorEventCheckInByAdministrator.');
      }
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling manageUpdateAdministratorEventCheckInByAdministrator.');
      }
      if (administratorEventCheckInUpdateSchema === null || administratorEventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInUpdateSchema was null or undefined when calling manageUpdateAdministratorEventCheckInByAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAdministratorEventCheckInByEvent(itemId, companyId, eventId, administratorEventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAdministratorEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAdministratorEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateAdministratorEventCheckInByEvent.');
      }
      if (administratorEventCheckInUpdateSchema === null || administratorEventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter administratorEventCheckInUpdateSchema was null or undefined when calling manageUpdateAdministratorEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/administrator_event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: administratorEventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AdministratorEventCheckInCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AdministratorEventCheckInCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AdministratorEventCheckInCRUDService,
        factory: AdministratorEventCheckInCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AdministratorEventCheckInCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaCRUDService = /*#__PURE__*/(() => {
  class AgendaCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgenda(companyId, agendaCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgenda.');
      }
      if (agendaCreateSchema === null || agendaCreateSchema === undefined) {
        throw new Error('Required parameter agendaCreateSchema was null or undefined when calling manageCreateAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAgendaByEvent(companyId, eventId, agendaCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateAgendaByEvent.');
      }
      if (agendaCreateSchema === null || agendaCreateSchema === undefined) {
        throw new Error('Required parameter agendaCreateSchema was null or undefined when calling manageCreateAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgenda(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaIcon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaIconByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaIconByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaIconByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaIconByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaMapImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaMapImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaMapImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaMapImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaMapImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaPrimaryImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaPrimaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaPrimaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaPrimaryImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaPrimaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaPrimaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaPrimaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSecondaryImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSecondaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSecondaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSecondaryImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSecondaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSecondaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAgendaSecondaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadAgendaDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaIcon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaIconByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaIconByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaIconByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadAgendaIconByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaMapImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaMapImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaMapImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaMapImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadAgendaMapImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaPrimaryImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaPrimaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaPrimaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaPrimaryImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaPrimaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaPrimaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadAgendaPrimaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaSecondaryImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaSecondaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaSecondaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaSecondaryImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadAgendaSecondaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaSecondaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadAgendaSecondaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgenda(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCategories(companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCategories.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAgendaCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAgendaCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCountByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAgendaCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSubcategories(companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSubcategories.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendas(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendasByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendasByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllAgendasByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendasByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendasByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllAgendasByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendasByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendasByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllAgendasByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendasMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendasMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendasSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendasSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorAgendaListForCheckIn(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorAgendaListForCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/exhibitor_agenda_list_for_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgenda(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendas(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendasByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendasByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllAgendasByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendasByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendasByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllAgendasByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendasByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendasByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllAgendasByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendasMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendasMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendasSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendasSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetAgendaCategories(companyId, agendaId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetAgendaCategories.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageSetAgendaCategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetAgendaSubcategories(companyId, agendaId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetAgendaSubcategories.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageSetAgendaSubcategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgenda(itemId, companyId, agendaUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgenda.');
      }
      if (agendaUpdateSchema === null || agendaUpdateSchema === undefined) {
        throw new Error('Required parameter agendaUpdateSchema was null or undefined when calling manageUpdateAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaByEvent(itemId, companyId, eventId, agendaUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateAgendaByEvent.');
      }
      if (agendaUpdateSchema === null || agendaUpdateSchema === undefined) {
        throw new Error('Required parameter agendaUpdateSchema was null or undefined when calling manageUpdateAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaDocument(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaDocumentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaDocumentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaDocumentBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaDocumentBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaDocumentBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaDocumentBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaDocumentBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaDocumentByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaDocumentByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaIcon(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaIcon.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaIconBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaIconBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaIconBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaIconBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaIconBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaIconBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaIconBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaIconBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaIconBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaIconByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaIconByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaIconByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaIconByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaIconByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaMapImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaMapImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaMapImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaMapImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaMapImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaMapImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaMapImageBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaMapImageBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaMapImageBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaMapImageBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaMapImageBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaMapImageByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaMapImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaMapImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaMapImageByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaMapImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaPrimaryImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaPrimaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaPrimaryImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaPrimaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaPrimaryImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaPrimaryImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaPrimaryImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaPrimaryImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaPrimaryImageBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaPrimaryImageBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaPrimaryImageBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaPrimaryImageBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaPrimaryImageBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaPrimaryImageByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaPrimaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaPrimaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaPrimaryImageByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaPrimaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaSecondaryImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaSecondaryImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaSecondaryImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaSecondaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaSecondaryImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaSecondaryImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaSecondaryImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaSecondaryImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaSecondaryImageBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaSecondaryImageBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaSecondaryImageBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaSecondaryImageBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadAgendaSecondaryImageBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadAgendaSecondaryImageByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadAgendaSecondaryImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadAgendaSecondaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadAgendaSecondaryImageByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadAgendaSecondaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaCRUDService,
        factory: AgendaCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaCategoryCRUDService = /*#__PURE__*/(() => {
  class AgendaCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgendaCategory(companyId, agendaCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaCategory.');
      }
      if (agendaCategoryCreateSchema === null || agendaCategoryCreateSchema === undefined) {
        throw new Error('Required parameter agendaCategoryCreateSchema was null or undefined when calling manageCreateAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaCategory(itemId, companyId, agendaCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaCategory.');
      }
      if (agendaCategoryUpdateSchema === null || agendaCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter agendaCategoryUpdateSchema was null or undefined when calling manageUpdateAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaCategoryCRUDService,
        factory: AgendaCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaFlowCRUDService = /*#__PURE__*/(() => {
  class AgendaFlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgendaFlowByAgenda(companyId, agendaId, agendaFlowCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateAgendaFlowByAgenda.');
      }
      if (agendaFlowCreateSchema === null || agendaFlowCreateSchema === undefined) {
        throw new Error('Required parameter agendaFlowCreateSchema was null or undefined when calling manageCreateAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaFlowCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaFlowByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaFlowByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaFlowByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaFlowByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaFlowCountByAgenda(companyId, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaFlowCountByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaFlowCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaFlowsByAgenda(companyId, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaFlowsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllAgendaFlowsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaFlowByAgenda(itemId, companyId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaFlowByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaFlowsByAgenda(companyId, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaFlowsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllAgendaFlowsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaFlowByAgenda(itemId, companyId, agendaId, agendaFlowUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaFlowByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateAgendaFlowByAgenda.');
      }
      if (agendaFlowUpdateSchema === null || agendaFlowUpdateSchema === undefined) {
        throw new Error('Required parameter agendaFlowUpdateSchema was null or undefined when calling manageUpdateAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaFlowUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaFlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaFlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaFlowCRUDService,
        factory: AgendaFlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaFlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaSpeakerCRUDService = /*#__PURE__*/(() => {
  class AgendaSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgendaSpeaker(companyId, agendaSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaSpeaker.');
      }
      if (agendaSpeakerCreateSchema === null || agendaSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerCreateSchema was null or undefined when calling manageCreateAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAgendaSpeakerByAgenda(companyId, agendaId, agendaSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateAgendaSpeakerByAgenda.');
      }
      if (agendaSpeakerCreateSchema === null || agendaSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerCreateSchema was null or undefined when calling manageCreateAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAgendaSpeakerBySpeaker(companyId, speakerId, agendaSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageCreateAgendaSpeakerBySpeaker.');
      }
      if (agendaSpeakerCreateSchema === null || agendaSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerCreateSchema was null or undefined when calling manageCreateAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSpeakerByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSpeakerByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageDeleteAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeakerByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaSpeakerByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeakerCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeakerCountByAgenda(companyId, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeakerCountByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaSpeakerCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSpeakerCountBySpeaker(companyId, speakerId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSpeakerCountBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetAgendaSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaSpeakers(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaSpeakersByAgenda(companyId, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaSpeakersByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllAgendaSpeakersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaSpeakersBySpeaker(companyId, speakerId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetAllAgendaSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaSpeaker(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaSpeakerByAgenda(itemId, companyId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaSpeakerByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaSpeakerBySpeaker(itemId, companyId, speakerId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaSpeakers(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaSpeakersByAgenda(companyId, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaSpeakersByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllAgendaSpeakersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaSpeakersBySpeaker(companyId, speakerId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderAllAgendaSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetAgendaSpeakers(companyId, agendaId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetAgendaSpeakers.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageSetAgendaSpeakers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetAgendaSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetSpeakerAgendas(companyId, speakerId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetSpeakerAgendas.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageSetSpeakerAgendas.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetSpeakerAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaSpeaker(itemId, companyId, agendaSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaSpeaker.');
      }
      if (agendaSpeakerUpdateSchema === null || agendaSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerUpdateSchema was null or undefined when calling manageUpdateAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaSpeakerByAgenda(itemId, companyId, agendaId, agendaSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaSpeakerByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateAgendaSpeakerByAgenda.');
      }
      if (agendaSpeakerUpdateSchema === null || agendaSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerUpdateSchema was null or undefined when calling manageUpdateAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaSpeakerBySpeaker(itemId, companyId, speakerId, agendaSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageUpdateAgendaSpeakerBySpeaker.');
      }
      if (agendaSpeakerUpdateSchema === null || agendaSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter agendaSpeakerUpdateSchema was null or undefined when calling manageUpdateAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaSpeakerCRUDService,
        factory: AgendaSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaSubcategoryCRUDService = /*#__PURE__*/(() => {
  class AgendaSubcategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgendaSubcategory(companyId, agendaSubcategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaSubcategory.');
      }
      if (agendaSubcategoryCreateSchema === null || agendaSubcategoryCreateSchema === undefined) {
        throw new Error('Required parameter agendaSubcategoryCreateSchema was null or undefined when calling manageCreateAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSubcategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaSubcategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSubcategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaSubcategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaSubcategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaSubcategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaSubcategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaSubcategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaSubcategory(itemId, companyId, agendaSubcategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaSubcategory.');
      }
      if (agendaSubcategoryUpdateSchema === null || agendaSubcategoryUpdateSchema === undefined) {
        throw new Error('Required parameter agendaSubcategoryUpdateSchema was null or undefined when calling manageUpdateAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaSubcategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaSubcategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaSubcategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaSubcategoryCRUDService,
        factory: AgendaSubcategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaSubcategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AgendaUserCRUDService = /*#__PURE__*/(() => {
  class AgendaUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateAgendaUser(companyId, agendaUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaUser.');
      }
      if (agendaUserCreateSchema === null || agendaUserCreateSchema === undefined) {
        throw new Error('Required parameter agendaUserCreateSchema was null or undefined when calling manageCreateAgendaUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAgendaUserByAgenda(companyId, agendaId, agendaUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaUserByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateAgendaUserByAgenda.');
      }
      if (agendaUserCreateSchema === null || agendaUserCreateSchema === undefined) {
        throw new Error('Required parameter agendaUserCreateSchema was null or undefined when calling manageCreateAgendaUserByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateAgendaUserByUser(companyId, userId, agendaUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateAgendaUserByUser.');
      }
      if (agendaUserCreateSchema === null || agendaUserCreateSchema === undefined) {
        throw new Error('Required parameter agendaUserCreateSchema was null or undefined when calling manageCreateAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaUserByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaUserByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaUserByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteAgendaUserByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAgendaUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteAgendaUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUserByAgenda(itemId, companyId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaUserByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUserByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaUserByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetAgendaUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUserCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUserCountByAgenda(companyId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUserCountByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAgendaUserCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAgendaUserCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAgendaUserCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAgendaUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaUsers(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaUsersByAgenda(companyId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaUsersByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllAgendaUsersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllAgendaUsersByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllAgendaUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllAgendaUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaUserByAgenda(itemId, companyId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaUserByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaUserByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAgendaUserByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAgendaUserByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderAgendaUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaUsers(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaUsersByAgenda(companyId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaUsersByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllAgendaUsersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllAgendaUsersByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllAgendaUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllAgendaUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetAgendaUsers(companyId, agendaId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetAgendaUsers.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageSetAgendaUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetAgendaUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserAgendas(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserAgendas.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserAgendas.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaUser(itemId, companyId, agendaUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaUser.');
      }
      if (agendaUserUpdateSchema === null || agendaUserUpdateSchema === undefined) {
        throw new Error('Required parameter agendaUserUpdateSchema was null or undefined when calling manageUpdateAgendaUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaUserByAgenda(itemId, companyId, agendaId, agendaUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaUserByAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaUserByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateAgendaUserByAgenda.');
      }
      if (agendaUserUpdateSchema === null || agendaUserUpdateSchema === undefined) {
        throw new Error('Required parameter agendaUserUpdateSchema was null or undefined when calling manageUpdateAgendaUserByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateAgendaUserByUser(itemId, companyId, userId, agendaUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateAgendaUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateAgendaUserByUser.');
      }
      if (agendaUserUpdateSchema === null || agendaUserUpdateSchema === undefined) {
        throw new Error('Required parameter agendaUserUpdateSchema was null or undefined when calling manageUpdateAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AgendaUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AgendaUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AgendaUserCRUDService,
        factory: AgendaUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AgendaUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AuthenticationService = /*#__PURE__*/(() => {
  class AuthenticationService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getGoogleMapsApiKey(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/get_google_maps_api_key`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    loginForAccessToken(username, password, grantType, scope, clientId, clientSecret, observe = 'body', reportProgress = false, options) {
      if (username === null || username === undefined) {
        throw new Error('Required parameter username was null or undefined when calling loginForAccessToken.');
      }
      if (password === null || password === undefined) {
        throw new Error('Required parameter password was null or undefined when calling loginForAccessToken.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/x-www-form-urlencoded'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (grantType !== undefined) {
        localVarFormParams = localVarFormParams.append('grant_type', grantType) || localVarFormParams;
      }
      if (username !== undefined) {
        localVarFormParams = localVarFormParams.append('username', username) || localVarFormParams;
      }
      if (password !== undefined) {
        localVarFormParams = localVarFormParams.append('password', password) || localVarFormParams;
      }
      if (scope !== undefined) {
        localVarFormParams = localVarFormParams.append('scope', scope) || localVarFormParams;
      }
      if (clientId !== undefined) {
        localVarFormParams = localVarFormParams.append('client_id', clientId) || localVarFormParams;
      }
      if (clientSecret !== undefined) {
        localVarFormParams = localVarFormParams.append('client_secret', clientSecret) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    logout(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    signZoomMeetingToken(signZoomTokenInput, observe = 'body', reportProgress = false, options) {
      if (signZoomTokenInput === null || signZoomTokenInput === undefined) {
        throw new Error('Required parameter signZoomTokenInput was null or undefined when calling signZoomMeetingToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/sign_zoom_meeting_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: signZoomTokenInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AuthenticationService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AuthenticationService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthenticationService,
        factory: AuthenticationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthenticationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let BackendHelpCRUDService = /*#__PURE__*/(() => {
  class BackendHelpCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateBackendHelp(backendHelpCreateSchema, observe = 'body', reportProgress = false, options) {
      if (backendHelpCreateSchema === null || backendHelpCreateSchema === undefined) {
        throw new Error('Required parameter backendHelpCreateSchema was null or undefined when calling manageCreateBackendHelp.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendHelpCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllBackendHelps(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteBackendHelp(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteBackendHelp.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllBackendHelps(orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllBackendHelpsMulti(idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllBackendHelpsSearch(orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendHelp(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetBackendHelp.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendHelpCount(orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendHelpCountMulti(idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendHelpCountSearch(orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllBackendHelps(orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllBackendHelpsMulti(idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllBackendHelpsSearch(orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderBackendHelp(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderBackendHelp.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateBackendHelp(itemId, backendHelpUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateBackendHelp.');
      }
      if (backendHelpUpdateSchema === null || backendHelpUpdateSchema === undefined) {
        throw new Error('Required parameter backendHelpUpdateSchema was null or undefined when calling manageUpdateBackendHelp.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_help/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendHelpUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function BackendHelpCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BackendHelpCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BackendHelpCRUDService,
        factory: BackendHelpCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BackendHelpCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let BackendTranslationCRUDService = /*#__PURE__*/(() => {
  class BackendTranslationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateBackendTranslation(backendTranslationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (backendTranslationCreateSchema === null || backendTranslationCreateSchema === undefined) {
        throw new Error('Required parameter backendTranslationCreateSchema was null or undefined when calling manageCreateBackendTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendTranslationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateBackendTranslationItem(backendTranslationItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (backendTranslationItemCreateSchema === null || backendTranslationItemCreateSchema === undefined) {
        throw new Error('Required parameter backendTranslationItemCreateSchema was null or undefined when calling manageCreateBackendTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendTranslationItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllBackendTranslationItems(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllBackendTranslations(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteBackendTranslation(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteBackendTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteBackendTranslationItem(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteBackendTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllBackendTranslationItems(orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllBackendTranslations(orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendTranslation(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetBackendTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendTranslationCount(orderBy, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendTranslationItem(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetBackendTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetBackendTranslationItemCount(orderBy, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllBackendTranslationItems(orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllBackendTranslations(orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderBackendTranslation(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderBackendTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderBackendTranslationItem(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderBackendTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateBackendTranslation(itemId, backendTranslationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateBackendTranslation.');
      }
      if (backendTranslationUpdateSchema === null || backendTranslationUpdateSchema === undefined) {
        throw new Error('Required parameter backendTranslationUpdateSchema was null or undefined when calling manageUpdateBackendTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendTranslationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateBackendTranslationItem(itemId, backendTranslationItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateBackendTranslationItem.');
      }
      if (backendTranslationItemUpdateSchema === null || backendTranslationItemUpdateSchema === undefined) {
        throw new Error('Required parameter backendTranslationItemUpdateSchema was null or undefined when calling manageUpdateBackendTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/backend_translation//backend_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: backendTranslationItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function BackendTranslationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BackendTranslationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BackendTranslationCRUDService,
        factory: BackendTranslationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BackendTranslationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CalendarService = /*#__PURE__*/(() => {
  class CalendarService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAgendaCalendar(agendaId, subdomain, observe = 'body', reportProgress = false, options) {
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAgendaCalendar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCalendar.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['text/html', 'application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/calendar/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/calendar/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventAgendaCalendar(eventId, subdomain, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventAgendaCalendar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventAgendaCalendar.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['text/html', 'application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/calendar/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/calendar/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCalendar(eventId, subdomain, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventCalendar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCalendar.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['text/html', 'application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/calendar/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/calendar/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CalendarService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CalendarService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CalendarService,
        factory: CalendarService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CalendarService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CarReservationCRUDService = /*#__PURE__*/(() => {
  class CarReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateCarReservationByEvent(companyId, eventId, carReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateCarReservationByEvent.');
      }
      if (carReservationCreateSchema === null || carReservationCreateSchema === undefined) {
        throw new Error('Required parameter carReservationCreateSchema was null or undefined when calling manageCreateCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateCarReservationByEventAndUser(companyId, eventId, userId, carReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCarReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateCarReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateCarReservationByEventAndUser.');
      }
      if (carReservationCreateSchema === null || carReservationCreateSchema === undefined) {
        throw new Error('Required parameter carReservationCreateSchema was null or undefined when calling manageCreateCarReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateCarReservationByUser(companyId, userId, carReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCarReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateCarReservationByUser.');
      }
      if (carReservationCreateSchema === null || carReservationCreateSchema === undefined) {
        throw new Error('Required parameter carReservationCreateSchema was null or undefined when calling manageCreateCarReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteCarReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteCarReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteCarReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationVoucherByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationVoucherByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationVoucherByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteCarReservationVoucherByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationVoucherByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationVoucherByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationVoucherByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteCarReservationVoucherByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteCarReservationVoucherByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCarReservationVoucherByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCarReservationVoucherByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCarReservationVoucherByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteCarReservationVoucherByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCarReservationVoucherByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadCarReservationVoucherByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCarReservationVoucherByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadCarReservationVoucherByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCarReservationVoucherByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadCarReservationVoucherByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCarReservationVoucherByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadCarReservationVoucherByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadCarReservationVoucherByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCarReservationVoucherByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadCarReservationVoucherByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCarReservationVoucherByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadCarReservationVoucherByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllCarReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllCarReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllCarReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllCarReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllCarReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllCarReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllCarReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCarReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCarReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllCarReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCarReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCarReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCarReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationCountByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationCountByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationCountByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationCountByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetCarReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCarReservationCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCarReservationCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetCarReservationCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllCarReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllCarReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllCarReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllCarReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllCarReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllCarReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllCarReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCarReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCarReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllCarReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCarReservationByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCarReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCarReservationByEventAndUser(itemId, companyId, eventId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCarReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCarReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderCarReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderCarReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCarReservationByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCarReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCarReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderCarReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCarReservationByEvent(itemId, companyId, eventId, carReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCarReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateCarReservationByEvent.');
      }
      if (carReservationUpdateSchema === null || carReservationUpdateSchema === undefined) {
        throw new Error('Required parameter carReservationUpdateSchema was null or undefined when calling manageUpdateCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCarReservationByEventAndUser(itemId, companyId, eventId, userId, carReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCarReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCarReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateCarReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateCarReservationByEventAndUser.');
      }
      if (carReservationUpdateSchema === null || carReservationUpdateSchema === undefined) {
        throw new Error('Required parameter carReservationUpdateSchema was null or undefined when calling manageUpdateCarReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCarReservationByUser(itemId, companyId, userId, carReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCarReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCarReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateCarReservationByUser.');
      }
      if (carReservationUpdateSchema === null || carReservationUpdateSchema === undefined) {
        throw new Error('Required parameter carReservationUpdateSchema was null or undefined when calling manageUpdateCarReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadCarReservationVoucherBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherBase64ByEventAndUser(itemId, companyId, eventId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadCarReservationVoucherBase64ByEventAndUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadCarReservationVoucherBase64ByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherBase64ByUser(itemId, companyId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherBase64ByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherBase64ByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadCarReservationVoucherBase64ByUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadCarReservationVoucherBase64ByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadCarReservationVoucherByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadCarReservationVoucherByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherByEventAndUser(itemId, companyId, eventId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadCarReservationVoucherByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadCarReservationVoucherByEventAndUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadCarReservationVoucherByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCarReservationVoucherByUser(itemId, companyId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCarReservationVoucherByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCarReservationVoucherByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadCarReservationVoucherByUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadCarReservationVoucherByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CarReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CarReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CarReservationCRUDService,
        factory: CarReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CarReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ChatGroupCRUDService = /*#__PURE__*/(() => {
  class ChatGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateChatGroup(companyId, chatGroupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateChatGroup.');
      }
      if (chatGroupCreateSchema === null || chatGroupCreateSchema === undefined) {
        throw new Error('Required parameter chatGroupCreateSchema was null or undefined when calling manageCreateChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatGroupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteChatGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllChatGroups(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllChatGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatGroupCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatGroupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllChatGroups(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllChatGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderChatGroup(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateChatGroup(itemId, companyId, chatGroupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateChatGroup.');
      }
      if (chatGroupUpdateSchema === null || chatGroupUpdateSchema === undefined) {
        throw new Error('Required parameter chatGroupUpdateSchema was null or undefined when calling manageUpdateChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatGroupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ChatGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChatGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChatGroupCRUDService,
        factory: ChatGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChatGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ChatRoomCRUDService = /*#__PURE__*/(() => {
  class ChatRoomCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateChatRoomByChatGroup(companyId, chatGroupId, chatRoomCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageCreateChatRoomByChatGroup.');
      }
      if (chatRoomCreateSchema === null || chatRoomCreateSchema === undefined) {
        throw new Error('Required parameter chatRoomCreateSchema was null or undefined when calling manageCreateChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteChatRoomByChatGroup(itemId, companyId, chatGroupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteChatRoomByChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageDeleteChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllChatRoomsByChatGroup(companyId, chatGroupId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllChatRoomsByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageGetAllChatRoomsByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatRoomByChatGroup(itemId, companyId, chatGroupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetChatRoomByChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageGetChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatRoomCountByChatGroup(companyId, chatGroupId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatRoomCountByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageGetChatRoomCountByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllChatRoomsByChatGroup(companyId, chatGroupId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllChatRoomsByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageRenderAllChatRoomsByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderChatRoomByChatGroup(itemId, companyId, chatGroupId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderChatRoomByChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageRenderChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateChatRoomByChatGroup(itemId, companyId, chatGroupId, chatRoomUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateChatRoomByChatGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling manageUpdateChatRoomByChatGroup.');
      }
      if (chatRoomUpdateSchema === null || chatRoomUpdateSchema === undefined) {
        throw new Error('Required parameter chatRoomUpdateSchema was null or undefined when calling manageUpdateChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ChatRoomCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChatRoomCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChatRoomCRUDService,
        factory: ChatRoomCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChatRoomCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ChatRoomMessageCRUDService = /*#__PURE__*/(() => {
  class ChatRoomMessageCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateChatRoomMessageByChatRoom(companyId, chatRoomId, chatRoomMessageCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageCreateChatRoomMessageByChatRoom.');
      }
      if (chatRoomMessageCreateSchema === null || chatRoomMessageCreateSchema === undefined) {
        throw new Error('Required parameter chatRoomMessageCreateSchema was null or undefined when calling manageCreateChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomMessageCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllChatRoomMessagesByChatRoom(companyId, chatRoomId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllChatRoomMessagesByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageDeleteAllChatRoomMessagesByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteChatRoomMessageByChatRoom(itemId, companyId, chatRoomId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteChatRoomMessageByChatRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageDeleteChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllChatRoomMessagesByChatRoom(companyId, chatRoomId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllChatRoomMessagesByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageGetAllChatRoomMessagesByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatRoomMessageByChatRoom(itemId, companyId, chatRoomId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetChatRoomMessageByChatRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageGetChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetChatRoomMessageCountByChatRoom(companyId, chatRoomId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetChatRoomMessageCountByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageGetChatRoomMessageCountByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllChatRoomMessagesByChatRoom(companyId, chatRoomId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllChatRoomMessagesByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageRenderAllChatRoomMessagesByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderChatRoomMessageByChatRoom(itemId, companyId, chatRoomId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderChatRoomMessageByChatRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageRenderChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateChatRoomMessageByChatRoom(itemId, companyId, chatRoomId, chatRoomMessageUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateChatRoomMessageByChatRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling manageUpdateChatRoomMessageByChatRoom.');
      }
      if (chatRoomMessageUpdateSchema === null || chatRoomMessageUpdateSchema === undefined) {
        throw new Error('Required parameter chatRoomMessageUpdateSchema was null or undefined when calling manageUpdateChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomMessageUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ChatRoomMessageCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ChatRoomMessageCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ChatRoomMessageCRUDService,
        factory: ChatRoomMessageCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ChatRoomMessageCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CompanyCRUDService = /*#__PURE__*/(() => {
  class CompanyCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateCompany(companyCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyCreateSchema === null || companyCreateSchema === undefined) {
        throw new Error('Required parameter companyCreateSchema was null or undefined when calling manageCreateCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompany(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCompanies(orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompany(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyCount(orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyUsedSpace(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyUsedSpace.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/used_space`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCompanies(orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCompany(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCompany(itemId, companyUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCompany.');
      }
      if (companyUpdateSchema === null || companyUpdateSchema === undefined) {
        throw new Error('Required parameter companyUpdateSchema was null or undefined when calling manageUpdateCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CompanyCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CompanyCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CompanyCRUDService,
        factory: CompanyCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CompanyCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CompanyFileCRUDService = /*#__PURE__*/(() => {
  class CompanyFileCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateCompanyFile(companyId, companyFileCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCompanyFile.');
      }
      if (companyFileCreateSchema === null || companyFileCreateSchema === undefined) {
        throw new Error('Required parameter companyFileCreateSchema was null or undefined when calling manageCreateCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyFileCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompanyFile(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompanyFile.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompanyFileAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompanyFileAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCompanyFileAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompanyFilePreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompanyFilePreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCompanyFilePreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCompanyFileAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadCompanyFileAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCompanyFileAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCompanyFilePreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadCompanyFilePreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCompanyFilePreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCompanyFiles(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCompanyFiles.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyFile(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCompanyFile.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyFileCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyFileCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCompanyFiles(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCompanyFiles.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCompanyFile(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCompanyFile.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCompanyFile(itemId, companyId, companyFileUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCompanyFile.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCompanyFile.');
      }
      if (companyFileUpdateSchema === null || companyFileUpdateSchema === undefined) {
        throw new Error('Required parameter companyFileUpdateSchema was null or undefined when calling manageUpdateCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyFileUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCompanyFileAsset(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCompanyFileAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCompanyFileAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadCompanyFileAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCompanyFileAssetBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCompanyFileAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCompanyFileAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadCompanyFileAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCompanyFilePreview(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCompanyFilePreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCompanyFilePreview.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadCompanyFilePreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadCompanyFilePreviewBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadCompanyFilePreviewBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadCompanyFilePreviewBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadCompanyFilePreviewBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CompanyFileCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CompanyFileCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CompanyFileCRUDService,
        factory: CompanyFileCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CompanyFileCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CompanyTranslationCRUDService = /*#__PURE__*/(() => {
  class CompanyTranslationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateCompanyTranslation(companyId, companyTranslationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCompanyTranslation.');
      }
      if (companyTranslationCreateSchema === null || companyTranslationCreateSchema === undefined) {
        throw new Error('Required parameter companyTranslationCreateSchema was null or undefined when calling manageCreateCompanyTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyTranslationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateCompanyTranslationItem(companyId, companyTranslationItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateCompanyTranslationItem.');
      }
      if (companyTranslationItemCreateSchema === null || companyTranslationItemCreateSchema === undefined) {
        throw new Error('Required parameter companyTranslationItemCreateSchema was null or undefined when calling manageCreateCompanyTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyTranslationItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllCompanyTranslationItems(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllCompanyTranslationItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllCompanyTranslations(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllCompanyTranslations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompanyTranslation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompanyTranslation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCompanyTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteCompanyTranslationItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteCompanyTranslationItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteCompanyTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCompanyTranslationItems(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCompanyTranslationItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllCompanyTranslations(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllCompanyTranslations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyTranslation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCompanyTranslation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyTranslationCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyTranslationCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyTranslationItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetCompanyTranslationItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyTranslationItemCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyTranslationItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCompanyTranslationItems(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCompanyTranslationItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllCompanyTranslations(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllCompanyTranslations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCompanyTranslation(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCompanyTranslation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCompanyTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderCompanyTranslationItem(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderCompanyTranslationItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderCompanyTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCompanyTranslation(itemId, companyId, companyTranslationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCompanyTranslation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCompanyTranslation.');
      }
      if (companyTranslationUpdateSchema === null || companyTranslationUpdateSchema === undefined) {
        throw new Error('Required parameter companyTranslationUpdateSchema was null or undefined when calling manageUpdateCompanyTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyTranslationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateCompanyTranslationItem(itemId, companyId, companyTranslationItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateCompanyTranslationItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateCompanyTranslationItem.');
      }
      if (companyTranslationItemUpdateSchema === null || companyTranslationItemUpdateSchema === undefined) {
        throw new Error('Required parameter companyTranslationItemUpdateSchema was null or undefined when calling manageUpdateCompanyTranslationItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_translation//company_translation_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: companyTranslationItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CompanyTranslationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CompanyTranslationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CompanyTranslationCRUDService,
        factory: CompanyTranslationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CompanyTranslationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DeviceTokenCRUDService = /*#__PURE__*/(() => {
  class DeviceTokenCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateDeviceToken(companyId, deviceTokenCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDeviceToken.');
      }
      if (deviceTokenCreateSchema === null || deviceTokenCreateSchema === undefined) {
        throw new Error('Required parameter deviceTokenCreateSchema was null or undefined when calling manageCreateDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateDeviceTokenByUser(companyId, userId, deviceTokenCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDeviceTokenByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateDeviceTokenByUser.');
      }
      if (deviceTokenCreateSchema === null || deviceTokenCreateSchema === undefined) {
        throw new Error('Required parameter deviceTokenCreateSchema was null or undefined when calling manageCreateDeviceTokenByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDeviceToken(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDeviceToken.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDeviceTokenByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDeviceTokenByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDeviceTokenByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteDeviceTokenByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDeviceTokens(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDeviceTokens.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDeviceTokensByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDeviceTokensByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllDeviceTokensByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDeviceTokensByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDeviceTokensByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllDeviceTokensByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDeviceTokensMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDeviceTokensMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceToken(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDeviceToken.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceTokenByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDeviceTokenByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceTokenByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDeviceTokenByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceTokenCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceTokenCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceTokenCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceTokenCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDeviceTokenCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceTokenCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceTokenCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDeviceTokenCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDeviceTokenCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDeviceTokenCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDeviceTokens(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDeviceTokens.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDeviceTokensByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDeviceTokensByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllDeviceTokensByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDeviceTokensByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDeviceTokensByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllDeviceTokensByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDeviceTokensMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDeviceTokensMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDeviceToken(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDeviceToken.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDeviceTokenByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDeviceTokenByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDeviceTokenByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderDeviceTokenByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDeviceToken(itemId, companyId, deviceTokenUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDeviceToken.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDeviceToken.');
      }
      if (deviceTokenUpdateSchema === null || deviceTokenUpdateSchema === undefined) {
        throw new Error('Required parameter deviceTokenUpdateSchema was null or undefined when calling manageUpdateDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDeviceTokenByUser(itemId, companyId, userId, deviceTokenUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDeviceTokenByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDeviceTokenByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateDeviceTokenByUser.');
      }
      if (deviceTokenUpdateSchema === null || deviceTokenUpdateSchema === undefined) {
        throw new Error('Required parameter deviceTokenUpdateSchema was null or undefined when calling manageUpdateDeviceTokenByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DeviceTokenCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DeviceTokenCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DeviceTokenCRUDService,
        factory: DeviceTokenCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DeviceTokenCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DocumentCRUDService = /*#__PURE__*/(() => {
  class DocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateDocument(companyId, documentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocument.');
      }
      if (documentCreateSchema === null || documentCreateSchema === undefined) {
        throw new Error('Required parameter documentCreateSchema was null or undefined when calling manageCreateDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateDocumentByEvent(companyId, eventId, documentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateDocumentByEvent.');
      }
      if (documentCreateSchema === null || documentCreateSchema === undefined) {
        throw new Error('Required parameter documentCreateSchema was null or undefined when calling manageCreateDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateDocumentByFirm(companyId, firmId, documentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageCreateDocumentByFirm.');
      }
      if (documentCreateSchema === null || documentCreateSchema === undefined) {
        throw new Error('Required parameter documentCreateSchema was null or undefined when calling manageCreateDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageDeleteDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentResource(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentResource.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentResource.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentResourceByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentResourceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentResourceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteDocumentResourceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentResourceByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentResourceByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentResourceByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageDeleteDocumentResourceByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentThumbnail(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentThumbnail.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentThumbnail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentThumbnailByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentThumbnailByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentThumbnailByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteDocumentThumbnailByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentThumbnailByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentThumbnailByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentThumbnailByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageDeleteDocumentThumbnailByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentResource(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentResource.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentResource.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentResourceByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentResourceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentResourceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadDocumentResourceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentResourceByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentResourceByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentResourceByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageDownloadDocumentResourceByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentThumbnail(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentThumbnail.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentThumbnail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentThumbnailByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentThumbnailByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentThumbnailByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadDocumentThumbnailByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentThumbnailByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadDocumentThumbnailByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentThumbnailByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageDownloadDocumentThumbnailByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocuments(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentsByFirm(companyId, firmId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageGetAllDocumentsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentByFirm(itemId, companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageGetDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetDocumentCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentCountByFirm(companyId, firmId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentCountByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageGetDocumentCountByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocuments(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentsByFirm(companyId, firmId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageRenderAllDocumentsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocument(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentByFirm(itemId, companyId, firmId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageRenderDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocument(itemId, companyId, documentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocument.');
      }
      if (documentUpdateSchema === null || documentUpdateSchema === undefined) {
        throw new Error('Required parameter documentUpdateSchema was null or undefined when calling manageUpdateDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentByEvent(itemId, companyId, eventId, documentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateDocumentByEvent.');
      }
      if (documentUpdateSchema === null || documentUpdateSchema === undefined) {
        throw new Error('Required parameter documentUpdateSchema was null or undefined when calling manageUpdateDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentByFirm(itemId, companyId, firmId, documentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageUpdateDocumentByFirm.');
      }
      if (documentUpdateSchema === null || documentUpdateSchema === undefined) {
        throw new Error('Required parameter documentUpdateSchema was null or undefined when calling manageUpdateDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResource(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResource.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResource.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentResource.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResourceBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResourceBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResourceBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentResourceBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResourceBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResourceBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResourceBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadDocumentResourceBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentResourceBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResourceBase64ByFirm(itemId, companyId, firmId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResourceBase64ByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResourceBase64ByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageUploadDocumentResourceBase64ByFirm.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentResourceBase64ByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResourceByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResourceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResourceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadDocumentResourceByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentResourceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentResourceByFirm(itemId, companyId, firmId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentResourceByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentResourceByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageUploadDocumentResourceByFirm.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentResourceByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnail(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnail.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnail.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentThumbnail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnailBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnailBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnailBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentThumbnailBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnailBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnailBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnailBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadDocumentThumbnailBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentThumbnailBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnailBase64ByFirm(itemId, companyId, firmId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnailBase64ByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnailBase64ByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageUploadDocumentThumbnailBase64ByFirm.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadDocumentThumbnailBase64ByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnailByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnailByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnailByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadDocumentThumbnailByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentThumbnailByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadDocumentThumbnailByFirm(itemId, companyId, firmId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadDocumentThumbnailByFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadDocumentThumbnailByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageUploadDocumentThumbnailByFirm.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadDocumentThumbnailByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DocumentCRUDService,
        factory: DocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DocumentDownloadCRUDService = /*#__PURE__*/(() => {
  class DocumentDownloadCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateDocumentDownloadByDocument(companyId, documentId, documentDownloadCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentDownloadByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageCreateDocumentDownloadByDocument.');
      }
      if (documentDownloadCreateSchema === null || documentDownloadCreateSchema === undefined) {
        throw new Error('Required parameter documentDownloadCreateSchema was null or undefined when calling manageCreateDocumentDownloadByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentDownloadCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateDocumentDownloadByUser(companyId, userId, documentDownloadCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentDownloadByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateDocumentDownloadByUser.');
      }
      if (documentDownloadCreateSchema === null || documentDownloadCreateSchema === undefined) {
        throw new Error('Required parameter documentDownloadCreateSchema was null or undefined when calling manageCreateDocumentDownloadByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentDownloadCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllDocumentDownloadsByDocument(companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllDocumentDownloadsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageDeleteAllDocumentDownloadsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllDocumentDownloadsByUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllDocumentDownloadsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteAllDocumentDownloadsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentDownloadByDocument(itemId, companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentDownloadByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentDownloadByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageDeleteDocumentDownloadByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentDownloadByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentDownloadByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentDownloadByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteDocumentDownloadByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentDownloadsByDocument(companyId, documentId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentDownloadsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetAllDocumentDownloadsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentDownloadsByUser(companyId, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentDownloadsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllDocumentDownloadsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentDownloadByDocument(itemId, companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentDownloadByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentDownloadByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetDocumentDownloadByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentDownloadByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentDownloadByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentDownloadByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDocumentDownloadByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentDownloadCountByDocument(companyId, documentId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentDownloadCountByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetDocumentDownloadCountByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentDownloadCountByUser(companyId, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentDownloadCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDocumentDownloadCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentDownloadsByDocument(companyId, documentId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentDownloadsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageRenderAllDocumentDownloadsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentDownloadsByUser(companyId, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentDownloadsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllDocumentDownloadsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentDownloadByDocument(itemId, companyId, documentId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentDownloadByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentDownloadByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageRenderDocumentDownloadByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentDownloadByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentDownloadByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentDownloadByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderDocumentDownloadByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentDownloadByDocument(itemId, companyId, documentId, documentDownloadUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentDownloadByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentDownloadByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageUpdateDocumentDownloadByDocument.');
      }
      if (documentDownloadUpdateSchema === null || documentDownloadUpdateSchema === undefined) {
        throw new Error('Required parameter documentDownloadUpdateSchema was null or undefined when calling manageUpdateDocumentDownloadByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentDownloadUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentDownloadByUser(itemId, companyId, userId, documentDownloadUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentDownloadByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentDownloadByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateDocumentDownloadByUser.');
      }
      if (documentDownloadUpdateSchema === null || documentDownloadUpdateSchema === undefined) {
        throw new Error('Required parameter documentDownloadUpdateSchema was null or undefined when calling manageUpdateDocumentDownloadByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_download/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentDownloadUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DocumentDownloadCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DocumentDownloadCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DocumentDownloadCRUDService,
        factory: DocumentDownloadCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DocumentDownloadCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DocumentRatingCRUDService = /*#__PURE__*/(() => {
  class DocumentRatingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateDocumentRatingByDocument(companyId, documentId, documentRatingCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentRatingByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageCreateDocumentRatingByDocument.');
      }
      if (documentRatingCreateSchema === null || documentRatingCreateSchema === undefined) {
        throw new Error('Required parameter documentRatingCreateSchema was null or undefined when calling manageCreateDocumentRatingByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateDocumentRatingByUser(companyId, userId, documentRatingCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateDocumentRatingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateDocumentRatingByUser.');
      }
      if (documentRatingCreateSchema === null || documentRatingCreateSchema === undefined) {
        throw new Error('Required parameter documentRatingCreateSchema was null or undefined when calling manageCreateDocumentRatingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllDocumentRatingsByDocument(companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllDocumentRatingsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageDeleteAllDocumentRatingsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllDocumentRatingsByUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllDocumentRatingsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteAllDocumentRatingsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentRatingByDocument(itemId, companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentRatingByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentRatingByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageDeleteDocumentRatingByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteDocumentRatingByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteDocumentRatingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteDocumentRatingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteDocumentRatingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentRatingsByDocument(companyId, documentId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentRatingsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetAllDocumentRatingsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllDocumentRatingsByUser(companyId, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllDocumentRatingsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllDocumentRatingsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentRatingByDocument(itemId, companyId, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentRatingByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentRatingByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetDocumentRatingByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentRatingByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetDocumentRatingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentRatingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDocumentRatingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentRatingCountByDocument(companyId, documentId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentRatingCountByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageGetDocumentRatingCountByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetDocumentRatingCountByUser(companyId, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetDocumentRatingCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetDocumentRatingCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentRatingsByDocument(companyId, documentId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentRatingsByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageRenderAllDocumentRatingsByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllDocumentRatingsByUser(companyId, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllDocumentRatingsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllDocumentRatingsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentRatingByDocument(itemId, companyId, documentId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentRatingByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentRatingByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageRenderDocumentRatingByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderDocumentRatingByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderDocumentRatingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderDocumentRatingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderDocumentRatingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentRatingByDocument(itemId, companyId, documentId, documentRatingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentRatingByDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentRatingByDocument.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling manageUpdateDocumentRatingByDocument.');
      }
      if (documentRatingUpdateSchema === null || documentRatingUpdateSchema === undefined) {
        throw new Error('Required parameter documentRatingUpdateSchema was null or undefined when calling manageUpdateDocumentRatingByDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateDocumentRatingByUser(itemId, companyId, userId, documentRatingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateDocumentRatingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateDocumentRatingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateDocumentRatingByUser.');
      }
      if (documentRatingUpdateSchema === null || documentRatingUpdateSchema === undefined) {
        throw new Error('Required parameter documentRatingUpdateSchema was null or undefined when calling manageUpdateDocumentRatingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DocumentRatingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DocumentRatingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DocumentRatingCRUDService,
        factory: DocumentRatingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DocumentRatingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventService = /*#__PURE__*/(() => {
  class EventService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    eventIndex(subdomain, eventSubcategoryId, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling eventIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventSubcategoryId, 'event_subcategory_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventAccess(subdomain, eventId, getEventAccessInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventAccess.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventAccess.');
      }
      if (getEventAccessInput === null || getEventAccessInput === undefined) {
        throw new Error('Required parameter getEventAccessInput was null or undefined when calling getEventAccess.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/access`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: getEventAccessInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventInfo(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventInfo.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventInfo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLegacyEvent(subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getLegacyEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getLegacyEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageAgendaIndex(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling manageAgendaIndex.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageAgendaIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/manage/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageEventIndex(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling manageEventIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/manage/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamIndex(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling manageStreamIndex.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageStreamIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/event/manage/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventService,
        factory: EventService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventAccessDomainCRUDService = /*#__PURE__*/(() => {
  class EventAccessDomainCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventAccessDomain(companyId, eventAccessDomainCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventAccessDomain.');
      }
      if (eventAccessDomainCreateSchema === null || eventAccessDomainCreateSchema === undefined) {
        throw new Error('Required parameter eventAccessDomainCreateSchema was null or undefined when calling manageCreateEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessDomainCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAccessDomain(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventAccessDomains(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventAccessDomains.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessDomain(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessDomainCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessDomainCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventAccessDomains(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventAccessDomains.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventAccessDomain(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventAccessDomain(itemId, companyId, eventAccessDomainUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventAccessDomain.');
      }
      if (eventAccessDomainUpdateSchema === null || eventAccessDomainUpdateSchema === undefined) {
        throw new Error('Required parameter eventAccessDomainUpdateSchema was null or undefined when calling manageUpdateEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessDomainUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventAccessDomainCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventAccessDomainCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventAccessDomainCRUDService,
        factory: EventAccessDomainCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventAccessDomainCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventAccessEntryCRUDService = /*#__PURE__*/(() => {
  class EventAccessEntryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventAccessEntryByEvent(companyId, eventId, eventAccessEntryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventAccessEntryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventAccessEntryByEvent.');
      }
      if (eventAccessEntryCreateSchema === null || eventAccessEntryCreateSchema === undefined) {
        throw new Error('Required parameter eventAccessEntryCreateSchema was null or undefined when calling manageCreateEventAccessEntryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessEntryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventAccessEntryByEventAndEventAccessDomain(companyId, eventId, eventAccessDomainId, eventAccessEntryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageCreateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessEntryCreateSchema === null || eventAccessEntryCreateSchema === undefined) {
        throw new Error('Required parameter eventAccessEntryCreateSchema was null or undefined when calling manageCreateEventAccessEntryByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessEntryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAccessEntryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAccessEntryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAccessEntryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventAccessEntryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAccessEntryByEventAndEventAccessDomain(itemId, companyId, eventId, eventAccessDomainId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageDeleteEventAccessEntryByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventAccessEntriesByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventAccessEntriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventAccessEntriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventAccessEntriesByEventAndEventAccessDomain(companyId, eventId, eventAccessDomainId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageGetAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessEntryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventAccessEntryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessEntryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventAccessEntryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessEntryByEventAndEventAccessDomain(itemId, companyId, eventId, eventAccessDomainId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageGetEventAccessEntryByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessEntryCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessEntryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventAccessEntryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessEntryCountByEventAndEventAccessDomain(companyId, eventId, eventAccessDomainId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessEntryCountByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventAccessEntryCountByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageGetEventAccessEntryCountByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventAccessEntriesByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventAccessEntriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventAccessEntriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventAccessEntriesByEventAndEventAccessDomain(companyId, eventId, eventAccessDomainId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageRenderAllEventAccessEntriesByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventAccessEntryByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventAccessEntryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventAccessEntryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventAccessEntryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventAccessEntryByEventAndEventAccessDomain(itemId, companyId, eventId, eventAccessDomainId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageRenderEventAccessEntryByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventAccessEntryByEvent(itemId, companyId, eventId, eventAccessEntryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventAccessEntryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventAccessEntryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventAccessEntryByEvent.');
      }
      if (eventAccessEntryUpdateSchema === null || eventAccessEntryUpdateSchema === undefined) {
        throw new Error('Required parameter eventAccessEntryUpdateSchema was null or undefined when calling manageUpdateEventAccessEntryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessEntryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventAccessEntryByEventAndEventAccessDomain(itemId, companyId, eventId, eventAccessDomainId, eventAccessEntryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessDomainId === null || eventAccessDomainId === undefined) {
        throw new Error('Required parameter eventAccessDomainId was null or undefined when calling manageUpdateEventAccessEntryByEventAndEventAccessDomain.');
      }
      if (eventAccessEntryUpdateSchema === null || eventAccessEntryUpdateSchema === undefined) {
        throw new Error('Required parameter eventAccessEntryUpdateSchema was null or undefined when calling manageUpdateEventAccessEntryByEventAndEventAccessDomain.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_domain/${this.configuration.encodeParam({
        name: 'eventAccessDomainId',
        value: eventAccessDomainId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_entry/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessEntryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventAccessEntryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventAccessEntryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventAccessEntryCRUDService,
        factory: EventAccessEntryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventAccessEntryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventAccessLevelCRUDService = /*#__PURE__*/(() => {
  class EventAccessLevelCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventAccessLevelByEvent(companyId, eventAccessLevelCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventAccessLevelByEvent.');
      }
      if (eventAccessLevelCreateSchema === null || eventAccessLevelCreateSchema === undefined) {
        throw new Error('Required parameter eventAccessLevelCreateSchema was null or undefined when calling manageCreateEventAccessLevelByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessLevelCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAccessLevelByEvent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAccessLevelByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAccessLevelByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventAccessLevelsByEvent(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventAccessLevelsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessLevelByEvent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventAccessLevelByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessLevelByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventAccessLevelCountByEvent(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventAccessLevelCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventAccessLevelsByEvent(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventAccessLevelsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventAccessLevelByEvent(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventAccessLevelByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventAccessLevelByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventAccessLevelByEvent(itemId, companyId, eventAccessLevelUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventAccessLevelByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventAccessLevelByEvent.');
      }
      if (eventAccessLevelUpdateSchema === null || eventAccessLevelUpdateSchema === undefined) {
        throw new Error('Required parameter eventAccessLevelUpdateSchema was null or undefined when calling manageUpdateEventAccessLevelByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_access_level/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventAccessLevelUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventAccessLevelCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventAccessLevelCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventAccessLevelCRUDService,
        factory: EventAccessLevelCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventAccessLevelCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventCRUDService = /*#__PURE__*/(() => {
  class EventCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCloneEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCloneEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCloneEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/clone`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEvent(companyId, eventCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEvent.');
      }
      if (eventCreateSchema === null || eventCreateSchema === undefined) {
        throw new Error('Required parameter eventCreateSchema was null or undefined when calling manageCreateEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEvent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAgendaBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAgendaBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAgendaBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventAgendaSecondaryBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventAgendaSecondaryBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventAgendaSecondaryBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_secondary_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCityPicture(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCityPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCityPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/city_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCmsBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCmsBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCmsBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/cms_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventDesktopBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventDesktopBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventDesktopBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/desktop_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventMobileBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventMobileBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventMobileBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mobile_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPrivacyPolicyDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPrivacyPolicyDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPrivacyPolicyDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/privacy_policy_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventAgendaBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventAgendaBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventAgendaBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventAgendaSecondaryBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventAgendaSecondaryBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventAgendaSecondaryBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_secondary_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventCityPicture(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventCityPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventCityPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/city_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventCmsBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventCmsBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventCmsBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/cms_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventDesktopBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventDesktopBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventDesktopBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/desktop_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventMobileBanner(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventMobileBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventMobileBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mobile_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventPrivacyPolicyDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventPrivacyPolicyDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventPrivacyPolicyDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/privacy_policy_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEvents(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEvent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCategories(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakers(companyId, eventId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakers.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSubcategories(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSubcategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEvents(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEvent(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventCategories(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventCategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSetEventCategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventSubcategories(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventSubcategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSetEventSubcategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEvent(itemId, companyId, eventUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEvent.');
      }
      if (eventUpdateSchema === null || eventUpdateSchema === undefined) {
        throw new Error('Required parameter eventUpdateSchema was null or undefined when calling manageUpdateEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventAgendaBanner(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventAgendaBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventAgendaBanner.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventAgendaBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventAgendaBannerBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventAgendaBannerBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventAgendaBannerBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventAgendaBannerBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_banner/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventAgendaSecondaryBanner(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventAgendaSecondaryBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventAgendaSecondaryBanner.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventAgendaSecondaryBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_secondary_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventAgendaSecondaryBannerBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventAgendaSecondaryBannerBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventAgendaSecondaryBannerBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventAgendaSecondaryBannerBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_secondary_banner/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventCityPicture(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventCityPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventCityPicture.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventCityPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/city_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventCityPictureBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventCityPictureBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventCityPictureBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventCityPictureBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/city_picture/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventCmsBanner(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventCmsBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventCmsBanner.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventCmsBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/cms_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventCmsBannerBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventCmsBannerBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventCmsBannerBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventCmsBannerBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/cms_banner/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventDesktopBanner(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventDesktopBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventDesktopBanner.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventDesktopBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/desktop_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventDesktopBannerBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventDesktopBannerBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventDesktopBannerBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventDesktopBannerBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/desktop_banner/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventMobileBanner(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventMobileBanner.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventMobileBanner.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventMobileBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mobile_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventMobileBannerBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventMobileBannerBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventMobileBannerBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventMobileBannerBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mobile_banner/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPrivacyPolicyDocument(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPrivacyPolicyDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPrivacyPolicyDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventPrivacyPolicyDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/privacy_policy_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPrivacyPolicyDocumentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPrivacyPolicyDocumentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPrivacyPolicyDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventPrivacyPolicyDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/privacy_policy_document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventCRUDService,
        factory: EventCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventCategoryCRUDService = /*#__PURE__*/(() => {
  class EventCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventCategory(companyId, eventCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventCategory.');
      }
      if (eventCategoryCreateSchema === null || eventCategoryCreateSchema === undefined) {
        throw new Error('Required parameter eventCategoryCreateSchema was null or undefined when calling manageCreateEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventCategory(itemId, companyId, eventCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventCategory.');
      }
      if (eventCategoryUpdateSchema === null || eventCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter eventCategoryUpdateSchema was null or undefined when calling manageUpdateEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventCategoryCRUDService,
        factory: EventCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventChatGroupCRUDService = /*#__PURE__*/(() => {
  class EventChatGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventChatGroupByEvent(companyId, eventId, eventChatGroupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventChatGroupByEvent.');
      }
      if (eventChatGroupCreateSchema === null || eventChatGroupCreateSchema === undefined) {
        throw new Error('Required parameter eventChatGroupCreateSchema was null or undefined when calling manageCreateEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventChatGroupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventChatGroupByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventChatGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventChatGroupsByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventChatGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventChatGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventChatGroupByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventChatGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventChatGroupCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventChatGroupCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventChatGroupCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventChatGroupsByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventChatGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventChatGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventChatGroupByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventChatGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventChatGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventChatGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventChatGroupCRUDService,
        factory: EventChatGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventChatGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventCheckInCRUDService = /*#__PURE__*/(() => {
  class EventCheckInCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventCheckIn(companyId, eventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventCheckIn.');
      }
      if (eventCheckInCreateSchema === null || eventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter eventCheckInCreateSchema was null or undefined when calling manageCreateEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventCheckInByEvent(companyId, eventId, eventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventCheckInByEvent.');
      }
      if (eventCheckInCreateSchema === null || eventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter eventCheckInCreateSchema was null or undefined when calling manageCreateEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventCheckInByEventUser(companyId, eventUserId, eventCheckInCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageCreateEventCheckInByEventUser.');
      }
      if (eventCheckInCreateSchema === null || eventCheckInCreateSchema === undefined) {
        throw new Error('Required parameter eventCheckInCreateSchema was null or undefined when calling manageCreateEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventCheckIns(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventCheckInsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventCheckInsByEventUser(companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventCheckInsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteAllEventCheckInsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCheckIn(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCheckInByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventCheckInByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventCheckInByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventCheckIns(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventCheckInsByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventCheckInsByEventUser(companyId, eventUserId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventCheckInsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllEventCheckInsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckIn(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckInByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckInByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventCheckInByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckInCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckInCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckInCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckInCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventCheckInCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventCheckInCountByEventUser(companyId, eventUserId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventCheckInCountByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetEventCheckInCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventCheckIns(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventCheckInsByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventCheckInsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventCheckInsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventCheckInsByEventUser(companyId, eventUserId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventCheckInsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllEventCheckInsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventCheckIn(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventCheckInByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventCheckInByEventUser(itemId, companyId, eventUserId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventCheckInByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventCheckIn(itemId, companyId, eventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventCheckIn.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventCheckIn.');
      }
      if (eventCheckInUpdateSchema === null || eventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter eventCheckInUpdateSchema was null or undefined when calling manageUpdateEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventCheckInByEvent(itemId, companyId, eventId, eventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventCheckInByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventCheckInByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventCheckInByEvent.');
      }
      if (eventCheckInUpdateSchema === null || eventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter eventCheckInUpdateSchema was null or undefined when calling manageUpdateEventCheckInByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventCheckInByEventUser(itemId, companyId, eventUserId, eventCheckInUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventCheckInByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageUpdateEventCheckInByEventUser.');
      }
      if (eventCheckInUpdateSchema === null || eventCheckInUpdateSchema === undefined) {
        throw new Error('Required parameter eventCheckInUpdateSchema was null or undefined when calling manageUpdateEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventCheckInUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventCheckInCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventCheckInCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventCheckInCRUDService,
        factory: EventCheckInCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventCheckInCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventFlowCRUDService = /*#__PURE__*/(() => {
  class EventFlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventFlowByEvent(companyId, eventId, eventFlowCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventFlowByEvent.');
      }
      if (eventFlowCreateSchema === null || eventFlowCreateSchema === undefined) {
        throw new Error('Required parameter eventFlowCreateSchema was null or undefined when calling manageCreateEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventFlowCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventFlowByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventFlowByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventFlowsByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventFlowsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventFlowsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventFlowByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventFlowByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventFlowCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventFlowCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventFlowCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventFlowsByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventFlowsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventFlowsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventFlowByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventFlowByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventFlowByEvent(itemId, companyId, eventId, eventFlowUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventFlowByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventFlowByEvent.');
      }
      if (eventFlowUpdateSchema === null || eventFlowUpdateSchema === undefined) {
        throw new Error('Required parameter eventFlowUpdateSchema was null or undefined when calling manageUpdateEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventFlowUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventFlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventFlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventFlowCRUDService,
        factory: EventFlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventFlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventGameCRUDService = /*#__PURE__*/(() => {
  class EventGameCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventGame(companyId, eventGameCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGame.');
      }
      if (eventGameCreateSchema === null || eventGameCreateSchema === undefined) {
        throw new Error('Required parameter eventGameCreateSchema was null or undefined when calling manageCreateEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventGameByEvent(companyId, eventId, eventGameCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventGameByEvent.');
      }
      if (eventGameCreateSchema === null || eventGameCreateSchema === undefined) {
        throw new Error('Required parameter eventGameCreateSchema was null or undefined when calling manageCreateEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGames(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGamesByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGamesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllEventGamesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGame(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGameByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGameByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGames(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGamesByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGamesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventGamesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGame(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGameByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventGameCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGames(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGamesByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGamesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventGamesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGame(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGameByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGameByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGame(itemId, companyId, eventGameUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGame.');
      }
      if (eventGameUpdateSchema === null || eventGameUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUpdateSchema was null or undefined when calling manageUpdateEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGameByEvent(itemId, companyId, eventId, eventGameUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGameByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventGameByEvent.');
      }
      if (eventGameUpdateSchema === null || eventGameUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUpdateSchema was null or undefined when calling manageUpdateEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventGameCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventGameCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventGameCRUDService,
        factory: EventGameCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventGameCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventGameUserScoreCRUDService = /*#__PURE__*/(() => {
  class EventGameUserScoreCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventGameUserScore(companyId, eventGameUserScoreCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGameUserScore.');
      }
      if (eventGameUserScoreCreateSchema === null || eventGameUserScoreCreateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreCreateSchema was null or undefined when calling manageCreateEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventGameUserScoreByEventGame(companyId, eventGameId, eventGameUserScoreCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageCreateEventGameUserScoreByEventGame.');
      }
      if (eventGameUserScoreCreateSchema === null || eventGameUserScoreCreateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreCreateSchema was null or undefined when calling manageCreateEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGameUserScores(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGameUserScores.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGameUserScoresByEventGame(companyId, eventGameId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGameUserScoresByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageDeleteAllEventGameUserScoresByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGameUserScore(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGameUserScore.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGameUserScoreByEventGame(itemId, companyId, eventGameId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGameUserScoreByEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageDeleteEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGameUserScores(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGameUserScores.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGameUserScoresByEventGame(companyId, eventGameId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGameUserScoresByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageGetAllEventGameUserScoresByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameUserScore(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGameUserScore.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameUserScoreByEventGame(itemId, companyId, eventGameId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGameUserScoreByEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageGetEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameUserScoreCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameUserScoreCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGameUserScoreCountByEventGame(companyId, eventGameId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGameUserScoreCountByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageGetEventGameUserScoreCountByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGameUserScores(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGameUserScores.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGameUserScoresByEventGame(companyId, eventGameId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGameUserScoresByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageRenderAllEventGameUserScoresByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGameUserScore(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGameUserScore.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGameUserScoreByEventGame(itemId, companyId, eventGameId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGameUserScoreByEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageRenderEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGameUserScore(itemId, companyId, eventGameUserScoreUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGameUserScore.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGameUserScore.');
      }
      if (eventGameUserScoreUpdateSchema === null || eventGameUserScoreUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreUpdateSchema was null or undefined when calling manageUpdateEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGameUserScoreByEventGame(itemId, companyId, eventGameId, eventGameUserScoreUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGameUserScoreByEventGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling manageUpdateEventGameUserScoreByEventGame.');
      }
      if (eventGameUserScoreUpdateSchema === null || eventGameUserScoreUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreUpdateSchema was null or undefined when calling manageUpdateEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventGameUserScoreCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventGameUserScoreCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventGameUserScoreCRUDService,
        factory: EventGameUserScoreCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventGameUserScoreCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventGroupCRUDService = /*#__PURE__*/(() => {
  class EventGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventGroup(companyId, eventGroupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGroup.');
      }
      if (eventGroupCreateSchema === null || eventGroupCreateSchema === undefined) {
        throw new Error('Required parameter eventGroupCreateSchema was null or undefined when calling manageCreateEventGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventGroupByEvent(companyId, eventId, eventGroupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventGroupByEvent.');
      }
      if (eventGroupCreateSchema === null || eventGroupCreateSchema === undefined) {
        throw new Error('Required parameter eventGroupCreateSchema was null or undefined when calling manageCreateEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventGroupByGroup(companyId, groupId, eventGroupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageCreateEventGroupByGroup.');
      }
      if (eventGroupCreateSchema === null || eventGroupCreateSchema === undefined) {
        throw new Error('Required parameter eventGroupCreateSchema was null or undefined when calling manageCreateEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGroupsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllEventGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventGroupsByGroup(companyId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventGroupsByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageDeleteAllEventGroupsByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGroupByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventGroupByGroup(itemId, companyId, groupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventGroupByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageDeleteEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGroups(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGroupsByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventGroupsByGroup(companyId, groupId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventGroupsByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetAllEventGroupsByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroupByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroupByGroup(itemId, companyId, groupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventGroupByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroupCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroupCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroupCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventGroupCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventGroupCountByGroup(companyId, groupId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventGroupCountByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetEventGroupCountByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGroups(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGroupsByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventGroupsByGroup(companyId, groupId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventGroupsByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageRenderAllEventGroupsByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGroup(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGroupByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventGroupByGroup(itemId, companyId, groupId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventGroupByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageRenderEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventGroups(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventGroups.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSetEventGroups.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetGroupEvents(companyId, groupId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetGroupEvents.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageSetGroupEvents.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetGroupEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGroup(itemId, companyId, eventGroupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGroup.');
      }
      if (eventGroupUpdateSchema === null || eventGroupUpdateSchema === undefined) {
        throw new Error('Required parameter eventGroupUpdateSchema was null or undefined when calling manageUpdateEventGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGroupByEvent(itemId, companyId, eventId, eventGroupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGroupByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventGroupByEvent.');
      }
      if (eventGroupUpdateSchema === null || eventGroupUpdateSchema === undefined) {
        throw new Error('Required parameter eventGroupUpdateSchema was null or undefined when calling manageUpdateEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventGroupByGroup(itemId, companyId, groupId, eventGroupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventGroupByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageUpdateEventGroupByGroup.');
      }
      if (eventGroupUpdateSchema === null || eventGroupUpdateSchema === undefined) {
        throw new Error('Required parameter eventGroupUpdateSchema was null or undefined when calling manageUpdateEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGroupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventGroupCRUDService,
        factory: EventGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventHotelRoomCRUDService = /*#__PURE__*/(() => {
  class EventHotelRoomCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventHotelRoom(companyId, eventHotelRoomCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventHotelRoom.');
      }
      if (eventHotelRoomCreateSchema === null || eventHotelRoomCreateSchema === undefined) {
        throw new Error('Required parameter eventHotelRoomCreateSchema was null or undefined when calling manageCreateEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventHotelRoomCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventHotelRoomByEvent(companyId, eventId, eventHotelRoomCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventHotelRoomByEvent.');
      }
      if (eventHotelRoomCreateSchema === null || eventHotelRoomCreateSchema === undefined) {
        throw new Error('Required parameter eventHotelRoomCreateSchema was null or undefined when calling manageCreateEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventHotelRoomCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventHotelRoom(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventHotelRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventHotelRoomByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventHotelRoomByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventHotelRooms(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventHotelRooms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventHotelRoomsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventHotelRoomsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventHotelRoomsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventHotelRoom(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventHotelRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventHotelRoomByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventHotelRoomByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventHotelRoomCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventHotelRoomCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventHotelRoomCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventHotelRoomCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventHotelRoomCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventHotelRooms(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventHotelRooms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventHotelRoomsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventHotelRoomsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventHotelRoomsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventHotelRoom(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventHotelRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventHotelRoomByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventHotelRoomByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventHotelRoom(itemId, companyId, eventHotelRoomUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventHotelRoom.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventHotelRoom.');
      }
      if (eventHotelRoomUpdateSchema === null || eventHotelRoomUpdateSchema === undefined) {
        throw new Error('Required parameter eventHotelRoomUpdateSchema was null or undefined when calling manageUpdateEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventHotelRoomUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventHotelRoomByEvent(itemId, companyId, eventId, eventHotelRoomUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventHotelRoomByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventHotelRoomByEvent.');
      }
      if (eventHotelRoomUpdateSchema === null || eventHotelRoomUpdateSchema === undefined) {
        throw new Error('Required parameter eventHotelRoomUpdateSchema was null or undefined when calling manageUpdateEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventHotelRoomUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventHotelRoomCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventHotelRoomCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventHotelRoomCRUDService,
        factory: EventHotelRoomCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventHotelRoomCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventPictureCRUDService = /*#__PURE__*/(() => {
  class EventPictureCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventPicture(companyId, eventId, eventPictureCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventPicture.');
      }
      if (eventPictureCreateSchema === null || eventPictureCreateSchema === undefined) {
        throw new Error('Required parameter eventPictureCreateSchema was null or undefined when calling manageCreateEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPictureCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventPictures(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventPictures.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllEventPictures.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPicture(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPictureAsset(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPictureAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventPictureAsset(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventPictureAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventPictures(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventPictures.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventPictures.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPicture(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPictureCount(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPictureCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventPictureCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPictureBundle(eventId, companyId, tag, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPictureBundle.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPictureBundle.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tag, 'tag');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/bundle`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventPictures(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventPictures.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventPictures.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventPicture(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventPicture(itemId, companyId, eventId, eventPictureUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventPicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventPicture.');
      }
      if (eventPictureUpdateSchema === null || eventPictureUpdateSchema === undefined) {
        throw new Error('Required parameter eventPictureUpdateSchema was null or undefined when calling manageUpdateEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPictureUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPictureAsset(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPictureAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadEventPictureAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPictureAssetBase64(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPictureAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPictureAssetBase64.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadEventPictureAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventPictureAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventPictureCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventPictureCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventPictureCRUDService,
        factory: EventPictureCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventPictureCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventPlaylistCRUDService = /*#__PURE__*/(() => {
  class EventPlaylistCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventPlaylist(companyId, eventPlaylistCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventPlaylist.');
      }
      if (eventPlaylistCreateSchema === null || eventPlaylistCreateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistCreateSchema was null or undefined when calling manageCreateEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventPlaylistByEvent(companyId, eventId, eventPlaylistCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventPlaylistByEvent.');
      }
      if (eventPlaylistCreateSchema === null || eventPlaylistCreateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistCreateSchema was null or undefined when calling manageCreateEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventPlaylistItem(companyId, eventPlaylistItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventPlaylistItem.');
      }
      if (eventPlaylistItemCreateSchema === null || eventPlaylistItemCreateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistItemCreateSchema was null or undefined when calling manageCreateEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventPlaylistItems(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventPlaylistItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventPlaylists(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventPlaylists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllEventPlaylistsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllEventPlaylistsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllEventPlaylistsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPlaylist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPlaylist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPlaylistByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPlaylistByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPlaylistItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPlaylistItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventPlaylistItemAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventPlaylistItemAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventPlaylistItemAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventPlaylistItemAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadEventPlaylistItemAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventPlaylistItemAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventPlaylistItems(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventPlaylistItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventPlaylists(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventPlaylists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventPlaylistsByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventPlaylistsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventPlaylistsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventPlaylist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylistByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventPlaylistByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylistCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylistCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylistCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylistCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventPlaylistCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylistItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventPlaylistItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventPlaylistItemCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventPlaylistItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventPlaylistItems(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventPlaylistItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventPlaylists(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventPlaylists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventPlaylistsByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventPlaylistsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventPlaylistsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventPlaylist(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventPlaylist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventPlaylistByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventPlaylistByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventPlaylistItem(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventPlaylistItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventPlaylist(itemId, companyId, eventPlaylistUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventPlaylist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventPlaylist.');
      }
      if (eventPlaylistUpdateSchema === null || eventPlaylistUpdateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistUpdateSchema was null or undefined when calling manageUpdateEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventPlaylistByEvent(itemId, companyId, eventId, eventPlaylistUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventPlaylistByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventPlaylistByEvent.');
      }
      if (eventPlaylistUpdateSchema === null || eventPlaylistUpdateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistUpdateSchema was null or undefined when calling manageUpdateEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventPlaylistItem(itemId, companyId, eventPlaylistItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventPlaylistItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventPlaylistItem.');
      }
      if (eventPlaylistItemUpdateSchema === null || eventPlaylistItemUpdateSchema === undefined) {
        throw new Error('Required parameter eventPlaylistItemUpdateSchema was null or undefined when calling manageUpdateEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPlaylistItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPlaylistItemAsset(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPlaylistItemAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPlaylistItemAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadEventPlaylistItemAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadEventPlaylistItemAssetBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadEventPlaylistItemAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadEventPlaylistItemAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadEventPlaylistItemAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist//event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventPlaylistCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventPlaylistCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventPlaylistCRUDService,
        factory: EventPlaylistCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventPlaylistCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventSpeakerCRUDService = /*#__PURE__*/(() => {
  class EventSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventSpeaker(companyId, eventSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSpeaker.');
      }
      if (eventSpeakerCreateSchema === null || eventSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerCreateSchema was null or undefined when calling manageCreateEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventSpeakerByEvent(companyId, eventId, eventSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventSpeakerByEvent.');
      }
      if (eventSpeakerCreateSchema === null || eventSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerCreateSchema was null or undefined when calling manageCreateEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventSpeakerBySpeaker(companyId, speakerId, eventSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageCreateEventSpeakerBySpeaker.');
      }
      if (eventSpeakerCreateSchema === null || eventSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerCreateSchema was null or undefined when calling manageCreateEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSpeakerByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSpeakerByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageDeleteEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSpeakers(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSpeakersByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSpeakersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventSpeakersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSpeakersBySpeaker(companyId, speakerId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetAllEventSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakerByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSpeakerByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakerCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakerCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakerCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSpeakerCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSpeakerCountBySpeaker(companyId, speakerId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSpeakerCountBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetEventSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSpeakers(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSpeakersByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSpeakersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventSpeakersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSpeakersBySpeaker(companyId, speakerId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderAllEventSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSpeaker(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSpeakerByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSpeakerByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSpeakerBySpeaker(itemId, companyId, speakerId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventSpeakers(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventSpeakers.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSetEventSpeakers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetSpeakerEvents(companyId, speakerId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetSpeakerEvents.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageSetSpeakerEvents.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetSpeakerEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSpeaker(itemId, companyId, eventSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSpeaker.');
      }
      if (eventSpeakerUpdateSchema === null || eventSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerUpdateSchema was null or undefined when calling manageUpdateEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSpeakerByEvent(itemId, companyId, eventId, eventSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSpeakerByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventSpeakerByEvent.');
      }
      if (eventSpeakerUpdateSchema === null || eventSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerUpdateSchema was null or undefined when calling manageUpdateEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSpeakerBySpeaker(itemId, companyId, speakerId, eventSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageUpdateEventSpeakerBySpeaker.');
      }
      if (eventSpeakerUpdateSchema === null || eventSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter eventSpeakerUpdateSchema was null or undefined when calling manageUpdateEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventSpeakerCRUDService,
        factory: EventSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventSponsorCRUDService = /*#__PURE__*/(() => {
  class EventSponsorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventSponsor(companyId, eventSponsorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSponsor.');
      }
      if (eventSponsorCreateSchema === null || eventSponsorCreateSchema === undefined) {
        throw new Error('Required parameter eventSponsorCreateSchema was null or undefined when calling manageCreateEventSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventSponsorByEventSupportTier(companyId, eventSponsorTierId, eventSponsorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSponsorByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageCreateEventSponsorByEventSupportTier.');
      }
      if (eventSponsorCreateSchema === null || eventSponsorCreateSchema === undefined) {
        throw new Error('Required parameter eventSponsorCreateSchema was null or undefined when calling manageCreateEventSponsorByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSponsor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSponsorByEventSupportTier(itemId, companyId, eventSponsorTierId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSponsorByEventSupportTier.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSponsorByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageDeleteEventSponsorByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSponsors(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSponsorsByEventSupportTier(companyId, eventSponsorTierId, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSponsorsByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageGetAllEventSponsorsByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsorByEventSupportTier(itemId, companyId, eventSponsorTierId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSponsorByEventSupportTier.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsorByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageGetEventSponsorByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsorCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsorCountByEventSupportTier(companyId, eventSponsorTierId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsorCountByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageGetEventSponsorCountByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSponsors(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSponsorsByEventSupportTier(companyId, eventSponsorTierId, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSponsorsByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageRenderAllEventSponsorsByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSponsor(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSponsorByEventSupportTier(itemId, companyId, eventSponsorTierId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSponsorByEventSupportTier.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSponsorByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageRenderEventSponsorByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventSponsors(companyId, eventSponsorTierId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventSponsors.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageSetEventSponsors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSponsor(itemId, companyId, eventSponsorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSponsor.');
      }
      if (eventSponsorUpdateSchema === null || eventSponsorUpdateSchema === undefined) {
        throw new Error('Required parameter eventSponsorUpdateSchema was null or undefined when calling manageUpdateEventSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSponsorByEventSupportTier(itemId, companyId, eventSponsorTierId, eventSponsorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSponsorByEventSupportTier.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSponsorByEventSupportTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling manageUpdateEventSponsorByEventSupportTier.');
      }
      if (eventSponsorUpdateSchema === null || eventSponsorUpdateSchema === undefined) {
        throw new Error('Required parameter eventSponsorUpdateSchema was null or undefined when calling manageUpdateEventSponsorByEventSupportTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventSponsorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventSponsorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventSponsorCRUDService,
        factory: EventSponsorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventSponsorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventSponsorTierCRUDService = /*#__PURE__*/(() => {
  class EventSponsorTierCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventSponsorTierByEvent(companyId, eventId, eventSponsorTierCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventSponsorTierByEvent.');
      }
      if (eventSponsorTierCreateSchema === null || eventSponsorTierCreateSchema === undefined) {
        throw new Error('Required parameter eventSponsorTierCreateSchema was null or undefined when calling manageCreateEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorTierCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSponsorTierByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSponsorTierByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSponsorTiersByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSponsorTiersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventSponsorTiersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsorTierByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSponsorTierByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSponsorTierCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSponsorTierCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventSponsorTierCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSponsorTiersByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSponsorTiersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventSponsorTiersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSponsorTierByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSponsorTierByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSponsorTierByEvent(itemId, companyId, eventId, eventSponsorTierUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSponsorTierByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventSponsorTierByEvent.');
      }
      if (eventSponsorTierUpdateSchema === null || eventSponsorTierUpdateSchema === undefined) {
        throw new Error('Required parameter eventSponsorTierUpdateSchema was null or undefined when calling manageUpdateEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSponsorTierUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSponsorTierOrder(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSponsorTierOrder.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventSponsorTierOrder.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageUpdateEventSponsorTierOrder.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier_order`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventSponsorTierCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventSponsorTierCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventSponsorTierCRUDService,
        factory: EventSponsorTierCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventSponsorTierCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventSubcategoryCRUDService = /*#__PURE__*/(() => {
  class EventSubcategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventSubcategory(companyId, eventSubcategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventSubcategory.');
      }
      if (eventSubcategoryCreateSchema === null || eventSubcategoryCreateSchema === undefined) {
        throw new Error('Required parameter eventSubcategoryCreateSchema was null or undefined when calling manageCreateEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSubcategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventSubcategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventSubcategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSubcategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventSubcategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventSubcategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventSubcategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventSubcategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventSubcategory(itemId, companyId, eventSubcategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventSubcategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventSubcategory.');
      }
      if (eventSubcategoryUpdateSchema === null || eventSubcategoryUpdateSchema === undefined) {
        throw new Error('Required parameter eventSubcategoryUpdateSchema was null or undefined when calling manageUpdateEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventSubcategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventSubcategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventSubcategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventSubcategoryCRUDService,
        factory: EventSubcategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventSubcategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventUserCRUDService = /*#__PURE__*/(() => {
  class EventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateEventUser(companyId, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUser.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling manageCreateEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventUserByEvent(companyId, eventId, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateEventUserByEvent.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling manageCreateEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventUserByUser(companyId, userId, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateEventUserByUser.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling manageCreateEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUserByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUserByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsersByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsersByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsersByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllEventUsersByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsersByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllEventUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsersByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsersByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllEventUsersByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUserByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCheckedInCount(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCheckedInCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventUserCheckedInCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_checked_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventUserCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventUserCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetEventUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetEventUserCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageLookupEventUser(companyId, eventId, key, provider, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageLookupEventUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageLookupEventUser.');
      }
      if (key === null || key === undefined) {
        throw new Error('Required parameter key was null or undefined when calling manageLookupEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, key, 'key');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, provider, 'provider');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_lookup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsersByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsersByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsersByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllEventUsersByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsersByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllEventUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsersByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsersByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllEventUsersByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUserByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUserByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUserByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserEventUsers(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserEventUsers.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserExhibitorProducts(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserExhibitorProducts.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserExhibitorProducts.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_products`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserExhibitors(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserExhibitors.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserExhibitors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitors`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserRestaurants(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserRestaurants.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserRestaurants.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserRestaurants.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurants`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUsers(companyId, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUsers.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSetEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserEvents(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserEvents.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserEvents.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUser(itemId, companyId, eventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUser.');
      }
      if (eventUserUpdateSchema === null || eventUserUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserUpdateSchema was null or undefined when calling manageUpdateEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUserByEvent(itemId, companyId, eventId, eventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUserByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateEventUserByEvent.');
      }
      if (eventUserUpdateSchema === null || eventUserUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserUpdateSchema was null or undefined when calling manageUpdateEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUserByUser(itemId, companyId, userId, eventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateEventUserByUser.');
      }
      if (eventUserUpdateSchema === null || eventUserUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserUpdateSchema was null or undefined when calling manageUpdateEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventUserCRUDService,
        factory: EventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EventUserRestaurantCRUDService = /*#__PURE__*/(() => {
  class EventUserRestaurantCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createEventUserRestaurantByEventUser(eventUserId, subdomain, eventUserRestaurantCreateSchema, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling createEventUserRestaurantByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventUserRestaurantByEventUser.');
      }
      if (eventUserRestaurantCreateSchema === null || eventUserRestaurantCreateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantCreateSchema was null or undefined when calling createEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventUserRestaurantByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteEventUserRestaurantByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUserRestaurantsByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllEventUserRestaurantsByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUserRestaurantsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUserRestaurantsByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllEventUserRestaurantsByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUserRestaurantsByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserRestaurantByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getEventUserRestaurantByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserRestaurantCountByEventUser(eventUserId, subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getEventUserRestaurantCountByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserRestaurantCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserRestaurantCountByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getEventUserRestaurantCountByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserRestaurantCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventUserRestaurant(companyId, eventUserRestaurantCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUserRestaurant.');
      }
      if (eventUserRestaurantCreateSchema === null || eventUserRestaurantCreateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantCreateSchema was null or undefined when calling manageCreateEventUserRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventUserRestaurantByEventUser(companyId, eventUserId, eventUserRestaurantCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageCreateEventUserRestaurantByEventUser.');
      }
      if (eventUserRestaurantCreateSchema === null || eventUserRestaurantCreateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantCreateSchema was null or undefined when calling manageCreateEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateEventUserRestaurantByRestaurant(companyId, restaurantId, eventUserRestaurantCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateEventUserRestaurantByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageCreateEventUserRestaurantByRestaurant.');
      }
      if (eventUserRestaurantCreateSchema === null || eventUserRestaurantCreateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantCreateSchema was null or undefined when calling manageCreateEventUserRestaurantByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUserRestaurant(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUserRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUserRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUserRestaurantByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUserRestaurantByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteEventUserRestaurantByRestaurant(itemId, companyId, restaurantId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteEventUserRestaurantByRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventUserRestaurantByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageDeleteEventUserRestaurantByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurants(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurants.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurantsByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurantsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllEventUserRestaurantsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurantsByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurantsByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllEventUserRestaurantsByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurantsByRestaurant(companyId, restaurantId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurantsByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageGetAllEventUserRestaurantsByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurantsByRestaurantMulti(companyId, restaurantId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurantsByRestaurantMulti.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageGetAllEventUserRestaurantsByRestaurantMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllEventUserRestaurantsMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllEventUserRestaurantsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurant(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUserRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUserRestaurantByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantByRestaurant(itemId, companyId, restaurantId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetEventUserRestaurantByRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageGetEventUserRestaurantByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCountByEventUser(companyId, eventUserId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCountByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetEventUserRestaurantCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCountByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCountByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetEventUserRestaurantCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCountByRestaurant(companyId, restaurantId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCountByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageGetEventUserRestaurantCountByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCountByRestaurantMulti(companyId, restaurantId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCountByRestaurantMulti.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageGetEventUserRestaurantCountByRestaurantMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventUserRestaurantCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventUserRestaurantCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurants(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurants.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurantsByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurantsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllEventUserRestaurantsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurantsByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurantsByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllEventUserRestaurantsByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurantsByRestaurant(companyId, restaurantId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurantsByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageRenderAllEventUserRestaurantsByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurantsByRestaurantMulti(companyId, restaurantId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurantsByRestaurantMulti.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageRenderAllEventUserRestaurantsByRestaurantMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllEventUserRestaurantsMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllEventUserRestaurantsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUserRestaurant(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUserRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUserRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUserRestaurantByEventUser(itemId, companyId, eventUserId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUserRestaurantByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderEventUserRestaurantByRestaurant(itemId, companyId, restaurantId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderEventUserRestaurantByRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderEventUserRestaurantByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageRenderEventUserRestaurantByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUserRestaurant(itemId, companyId, eventUserRestaurantUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUserRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUserRestaurant.');
      }
      if (eventUserRestaurantUpdateSchema === null || eventUserRestaurantUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantUpdateSchema was null or undefined when calling manageUpdateEventUserRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUserRestaurantByEventUser(itemId, companyId, eventUserId, eventUserRestaurantUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUserRestaurantByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageUpdateEventUserRestaurantByEventUser.');
      }
      if (eventUserRestaurantUpdateSchema === null || eventUserRestaurantUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantUpdateSchema was null or undefined when calling manageUpdateEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateEventUserRestaurantByRestaurant(itemId, companyId, restaurantId, eventUserRestaurantUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateEventUserRestaurantByRestaurant.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateEventUserRestaurantByRestaurant.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling manageUpdateEventUserRestaurantByRestaurant.');
      }
      if (eventUserRestaurantUpdateSchema === null || eventUserRestaurantUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantUpdateSchema was null or undefined when calling manageUpdateEventUserRestaurantByRestaurant.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUserRestaurantsByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllEventUserRestaurantsByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUserRestaurantsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUserRestaurantsByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllEventUserRestaurantsByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUserRestaurantsByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventUserRestaurantByEventUser(itemId, eventUserId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderEventUserRestaurantByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEventUserRestaurantByEventUser(itemId, eventUserId, subdomain, eventUserRestaurantUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateEventUserRestaurantByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling updateEventUserRestaurantByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateEventUserRestaurantByEventUser.');
      }
      if (eventUserRestaurantUpdateSchema === null || eventUserRestaurantUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserRestaurantUpdateSchema was null or undefined when calling updateEventUserRestaurantByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserRestaurantUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EventUserRestaurantCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EventUserRestaurantCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EventUserRestaurantCRUDService,
        factory: EventUserRestaurantCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EventUserRestaurantCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorBrandCRUDService = /*#__PURE__*/(() => {
  class ExhibitorBrandCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorBrand(companyId, exhibitorBrandCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorBrand.');
      }
      if (exhibitorBrandCreateSchema === null || exhibitorBrandCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorBrandCreateSchema was null or undefined when calling manageCreateExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorBrandCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorBrandByExhibitor(companyId, exhibitorId, exhibitorBrandCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorBrandByExhibitor.');
      }
      if (exhibitorBrandCreateSchema === null || exhibitorBrandCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorBrandCreateSchema was null or undefined when calling manageCreateExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorBrandCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorBrand(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorBrand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorBrandByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorBrandByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorBrandCompanyLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorBrandCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorBrandCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorBrandCompanyLogoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorBrandCompanyLogoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorBrandCompanyLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorBrandCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorBrandCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorBrandCompanyLogoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorBrands(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorBrands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorBrandsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorBrandsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorBrandsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorBrand(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorBrand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorBrandByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorBrandByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorBrandCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorBrandCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorBrandCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorBrandCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorBrandCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorBrands(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorBrands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorBrandsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorBrandsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorBrandsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorBrand(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorBrand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorBrandByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorBrandByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorBrand(itemId, companyId, exhibitorBrandUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorBrand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorBrand.');
      }
      if (exhibitorBrandUpdateSchema === null || exhibitorBrandUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorBrandUpdateSchema was null or undefined when calling manageUpdateExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorBrandUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorBrandByExhibitor(itemId, companyId, exhibitorId, exhibitorBrandUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorBrandByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorBrandByExhibitor.');
      }
      if (exhibitorBrandUpdateSchema === null || exhibitorBrandUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorBrandUpdateSchema was null or undefined when calling manageUpdateExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorBrandUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorBrandCompanyLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorBrandCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorBrandCompanyLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorBrandCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorBrandCompanyLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorBrandCompanyLogoBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorBrandCompanyLogoBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorBrandCompanyLogoByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorBrandCompanyLogoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorBrandCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorBrandCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorBrandCRUDService,
        factory: ExhibitorBrandCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorBrandCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorCRUDService = /*#__PURE__*/(() => {
  class ExhibitorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitor(companyId, exhibitorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitor.');
      }
      if (exhibitorCreateSchema === null || exhibitorCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorCreateSchema was null or undefined when calling manageCreateExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorByEvent(companyId, eventId, exhibitorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateExhibitorByEvent.');
      }
      if (exhibitorCreateSchema === null || exhibitorCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorCreateSchema was null or undefined when calling manageCreateExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorCompanyLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorCompanyLogoByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorCompanyLogoByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorCompanyLogoByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteExhibitorCompanyLogoByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorCompanyLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorCompanyLogoByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorCompanyLogoByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorCompanyLogoByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadExhibitorCompanyLogoByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitors(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllExhibitorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllExhibitorsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorsByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorsByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllExhibitorsByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorsMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetExhibitorCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetExhibitorCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetExhibitorCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorCountryList(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorCountryList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetExhibitorCountryList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_country_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPavilionList(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPavilionList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPavilionList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_pavilion_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStandList(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStandList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetStandList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_stand_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitors(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllExhibitorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllExhibitorsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorsByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorsByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllExhibitorsByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorsMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitor(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetExhibitorExhibitors(companyId, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetExhibitorExhibitors.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageSetExhibitorExhibitors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetExhibitorExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitors`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetExhibitorMerchandiseCategories(companyId, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetExhibitorMerchandiseCategories.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageSetExhibitorMerchandiseCategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetExhibitorMerchandiseCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_categories`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetExhibitorTradeShowSectors(companyId, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetExhibitorTradeShowSectors.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageSetExhibitorTradeShowSectors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetExhibitorTradeShowSectors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sectors`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitor(itemId, companyId, exhibitorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitor.');
      }
      if (exhibitorUpdateSchema === null || exhibitorUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorUpdateSchema was null or undefined when calling manageUpdateExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorByEvent(itemId, companyId, eventId, exhibitorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateExhibitorByEvent.');
      }
      if (exhibitorUpdateSchema === null || exhibitorUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorUpdateSchema was null or undefined when calling manageUpdateExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorCompanyLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorCompanyLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorCompanyLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorCompanyLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorCompanyLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorCompanyLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorCompanyLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorCompanyLogoBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorCompanyLogoBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorCompanyLogoBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadExhibitorCompanyLogoBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorCompanyLogoBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorCompanyLogoByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorCompanyLogoByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorCompanyLogoByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadExhibitorCompanyLogoByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorCompanyLogoByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorQrCode(companyId, eventId, exhibitorId, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling renderExhibitorQrCode.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderExhibitorQrCode.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.render_qr_code/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorCRUDService,
        factory: ExhibitorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorDocumentCRUDService = /*#__PURE__*/(() => {
  class ExhibitorDocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorDocument(companyId, exhibitorDocumentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorDocument.');
      }
      if (exhibitorDocumentCreateSchema === null || exhibitorDocumentCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorDocumentCreateSchema was null or undefined when calling manageCreateExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorDocumentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorDocumentByExhibitor(companyId, exhibitorId, exhibitorDocumentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorDocumentByExhibitor.');
      }
      if (exhibitorDocumentCreateSchema === null || exhibitorDocumentCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorDocumentCreateSchema was null or undefined when calling manageCreateExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorDocumentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocumentByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocumentDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocumentDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocumentDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocumentDocumentByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocumentDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocumentDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorDocumentDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocumentImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocumentImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocumentImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorDocumentImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorDocumentImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorDocumentImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorDocumentImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorDocumentDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorDocumentDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorDocumentDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorDocumentDocumentByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorDocumentDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorDocumentDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorDocumentDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorDocumentImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorDocumentImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorDocumentImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorDocumentImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorDocumentImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorDocumentImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorDocumentImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorDocuments(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorDocumentsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorDocumentsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorDocumentsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorDocumentByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorDocumentCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorDocumentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorDocumentCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorDocumentCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorDocumentCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorDocuments(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorDocumentsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorDocumentsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorDocumentsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorDocument(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorDocumentByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorDocument(itemId, companyId, exhibitorDocumentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorDocument.');
      }
      if (exhibitorDocumentUpdateSchema === null || exhibitorDocumentUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorDocumentUpdateSchema was null or undefined when calling manageUpdateExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorDocumentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorDocumentByExhibitor(itemId, companyId, exhibitorId, exhibitorDocumentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorDocumentByExhibitor.');
      }
      if (exhibitorDocumentUpdateSchema === null || exhibitorDocumentUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorDocumentUpdateSchema was null or undefined when calling manageUpdateExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorDocumentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentDocument(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorDocumentDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentDocumentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentDocumentBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorDocumentDocumentBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentDocumentByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentDocumentByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorDocumentDocumentByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorDocumentDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorDocumentImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorDocumentImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentImageBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentImageBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentImageBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorDocumentImageBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorDocumentImageBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorDocumentImageByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorDocumentImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorDocumentImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorDocumentImageByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorDocumentImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorDocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorDocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorDocumentCRUDService,
        factory: ExhibitorDocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorDocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorEventUserCRUDService = /*#__PURE__*/(() => {
  class ExhibitorEventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorEventUser(companyId, exhibitorEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorEventUser.');
      }
      if (exhibitorEventUserCreateSchema === null || exhibitorEventUserCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserCreateSchema was null or undefined when calling manageCreateExhibitorEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorEventUserByEventUser(companyId, eventUserId, exhibitorEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageCreateExhibitorEventUserByEventUser.');
      }
      if (exhibitorEventUserCreateSchema === null || exhibitorEventUserCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserCreateSchema was null or undefined when calling manageCreateExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorEventUserByExhibitor(companyId, exhibitorId, exhibitorEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorEventUserCreateSchema === null || exhibitorEventUserCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserCreateSchema was null or undefined when calling manageCreateExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllExhibitorEventUsers(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllExhibitorEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllExhibitorEventUsersByEventUser(companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllExhibitorEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllExhibitorEventUsersByExhibitor(companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllExhibitorEventUsersByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorEventUserByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorEventUserByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorEventUserByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsersByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsersByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsersByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllExhibitorEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsersByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsersByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsersByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsersByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorEventUsersByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorEventUserByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCountByEventUser(companyId, eventUserId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCountByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetExhibitorEventUserCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCountByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCountByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetExhibitorEventUserCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorEventUserCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCountByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCountByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorEventUserCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorEventUserCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorEventUserCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsersByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsersByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsersByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllExhibitorEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsersByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsersByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsersByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsersByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorEventUsersByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorEventUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorEventUserByEventUser(itemId, companyId, eventUserId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorEventUserByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorEventUserByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserExhibitorsEx(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserExhibitorsEx.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserExhibitorsEx.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserExhibitorsEx.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetExhibitorEventUsers(companyId, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetExhibitorEventUsers.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageSetExhibitorEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetExhibitorEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorEventUser(itemId, companyId, exhibitorEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorEventUser.');
      }
      if (exhibitorEventUserUpdateSchema === null || exhibitorEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserUpdateSchema was null or undefined when calling manageUpdateExhibitorEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorEventUserByEventUser(itemId, companyId, eventUserId, exhibitorEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageUpdateExhibitorEventUserByEventUser.');
      }
      if (exhibitorEventUserUpdateSchema === null || exhibitorEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserUpdateSchema was null or undefined when calling manageUpdateExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorEventUserByExhibitor(itemId, companyId, exhibitorId, exhibitorEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorEventUserByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorEventUserUpdateSchema === null || exhibitorEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserUpdateSchema was null or undefined when calling manageUpdateExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorEventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorEventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorEventUserCRUDService,
        factory: ExhibitorEventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorEventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorPressReleaseCRUDService = /*#__PURE__*/(() => {
  class ExhibitorPressReleaseCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorPressRelease(companyId, exhibitorPressReleaseCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorPressRelease.');
      }
      if (exhibitorPressReleaseCreateSchema === null || exhibitorPressReleaseCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorPressReleaseCreateSchema was null or undefined when calling manageCreateExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorPressReleaseCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorPressReleaseByExhibitor(companyId, exhibitorId, exhibitorPressReleaseCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorPressReleaseCreateSchema === null || exhibitorPressReleaseCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorPressReleaseCreateSchema was null or undefined when calling manageCreateExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorPressReleaseCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorPressRelease(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorPressRelease.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorPressReleaseByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorPressReleaseByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorPressReleaseImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorPressReleaseImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorPressReleaseImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorPressReleaseImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorPressReleaseImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorPressReleaseImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorPressReleaseImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorPressReleaseImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorPressReleaseImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorPressReleaseImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorPressReleaseImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorPressReleaseImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorPressReleaseImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorPressReleaseImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleases(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleases.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleasesByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleasesByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleasesByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorPressReleasesByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleasesMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleasesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorPressReleasesSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorPressReleasesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressRelease(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorPressRelease.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorPressReleaseByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCountByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCountByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorPressReleaseCountByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorPressReleaseCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorPressReleaseCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleases(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleases.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleasesByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleasesByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleasesByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorPressReleasesByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleasesMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleasesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorPressReleasesSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorPressReleasesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorPressRelease(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorPressRelease.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorPressReleaseByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorPressReleaseByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorPressRelease(itemId, companyId, exhibitorPressReleaseUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorPressRelease.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorPressRelease.');
      }
      if (exhibitorPressReleaseUpdateSchema === null || exhibitorPressReleaseUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorPressReleaseUpdateSchema was null or undefined when calling manageUpdateExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorPressReleaseUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorPressReleaseByExhibitor(itemId, companyId, exhibitorId, exhibitorPressReleaseUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorPressReleaseByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorPressReleaseUpdateSchema === null || exhibitorPressReleaseUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorPressReleaseUpdateSchema was null or undefined when calling manageUpdateExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorPressReleaseUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorPressReleaseImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorPressReleaseImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorPressReleaseImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorPressReleaseImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorPressReleaseImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorPressReleaseImageBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorPressReleaseImageBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorPressReleaseImageByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorPressReleaseImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorPressReleaseImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorPressReleaseImageByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorPressReleaseImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorPressReleaseCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorPressReleaseCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorPressReleaseCRUDService,
        factory: ExhibitorPressReleaseCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorPressReleaseCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorProductCRUDService = /*#__PURE__*/(() => {
  class ExhibitorProductCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorProduct(companyId, exhibitorProductCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorProduct.');
      }
      if (exhibitorProductCreateSchema === null || exhibitorProductCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorProductCreateSchema was null or undefined when calling manageCreateExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorProductCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorProductByExhibitor(companyId, exhibitorId, exhibitorProductCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorProductByExhibitor.');
      }
      if (exhibitorProductCreateSchema === null || exhibitorProductCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorProductCreateSchema was null or undefined when calling manageCreateExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorProductCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorProduct(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorProduct.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorProductByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorProductByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorProductProductImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorProductProductImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorProductProductImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorProductProductImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorProductProductImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorProductProductImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorProductProductImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorProductProductImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorProductProductImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorProductProductImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorProductProductImageByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorProductProductImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorProductProductImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorProductProductImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProducts(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProductsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProductsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorProductsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProductsByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProductsByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorProductsByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProductsByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProductsByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorProductsByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProductsMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProductsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorProductsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorProductsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProduct(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorProduct.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorProductByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorProductCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCountByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCountByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorProductCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCountByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCountByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorProductCountByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorProductCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorProductCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProducts(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProductsByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProductsByExhibitorMulti(companyId, exhibitorId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProductsByExhibitorSearch(companyId, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorProductsByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProductsMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProductsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorProductsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorProductsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorProduct(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorProduct.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorProductByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorProductByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorProduct(itemId, companyId, exhibitorProductUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorProduct.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorProduct.');
      }
      if (exhibitorProductUpdateSchema === null || exhibitorProductUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorProductUpdateSchema was null or undefined when calling manageUpdateExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorProductUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorProductByExhibitor(itemId, companyId, exhibitorId, exhibitorProductUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorProductByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorProductByExhibitor.');
      }
      if (exhibitorProductUpdateSchema === null || exhibitorProductUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorProductUpdateSchema was null or undefined when calling manageUpdateExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorProductUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorProductProductImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorProductProductImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorProductProductImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorProductProductImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorProductProductImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorProductProductImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorProductProductImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorProductProductImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorProductProductImageBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorProductProductImageBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorProductProductImageBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorProductProductImageBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorProductProductImageBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorProductProductImageByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorProductProductImageByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorProductProductImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorProductProductImageByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorProductProductImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorProductCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorProductCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorProductCRUDService,
        factory: ExhibitorProductCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorProductCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ExhibitorVideoCRUDService = /*#__PURE__*/(() => {
  class ExhibitorVideoCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateExhibitorVideo(companyId, exhibitorVideoCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorVideo.');
      }
      if (exhibitorVideoCreateSchema === null || exhibitorVideoCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorVideoCreateSchema was null or undefined when calling manageCreateExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorVideoCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateExhibitorVideoByExhibitor(companyId, exhibitorId, exhibitorVideoCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateExhibitorVideoByExhibitor.');
      }
      if (exhibitorVideoCreateSchema === null || exhibitorVideoCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorVideoCreateSchema was null or undefined when calling manageCreateExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorVideoCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorVideo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorVideoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorVideoVideo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorVideoVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorVideoVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteExhibitorVideoVideoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteExhibitorVideoVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteExhibitorVideoVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteExhibitorVideoVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorVideoVideo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorVideoVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorVideoVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadExhibitorVideoVideoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadExhibitorVideoVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadExhibitorVideoVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDownloadExhibitorVideoVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorVideos(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorVideos.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllExhibitorVideosByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllExhibitorVideosByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllExhibitorVideosByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorVideo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorVideoByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetExhibitorVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorVideoCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorVideoCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetExhibitorVideoCountByExhibitor(companyId, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetExhibitorVideoCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetExhibitorVideoCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorVideos(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorVideos.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllExhibitorVideosByExhibitor(companyId, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllExhibitorVideosByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllExhibitorVideosByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorVideo(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderExhibitorVideoByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderExhibitorVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorVideo(itemId, companyId, exhibitorVideoUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorVideo.');
      }
      if (exhibitorVideoUpdateSchema === null || exhibitorVideoUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorVideoUpdateSchema was null or undefined when calling manageUpdateExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorVideoUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateExhibitorVideoByExhibitor(itemId, companyId, exhibitorId, exhibitorVideoUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateExhibitorVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateExhibitorVideoByExhibitor.');
      }
      if (exhibitorVideoUpdateSchema === null || exhibitorVideoUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorVideoUpdateSchema was null or undefined when calling manageUpdateExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorVideoUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorVideoVideo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorVideoVideo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorVideoVideo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorVideoVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorVideoVideoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorVideoVideoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorVideoVideoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorVideoVideoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorVideoVideoBase64ByExhibitor(itemId, companyId, exhibitorId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorVideoVideoBase64ByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorVideoVideoBase64ByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorVideoVideoBase64ByExhibitor.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadExhibitorVideoVideoBase64ByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadExhibitorVideoVideoByExhibitor(itemId, companyId, exhibitorId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadExhibitorVideoVideoByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadExhibitorVideoVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUploadExhibitorVideoVideoByExhibitor.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadExhibitorVideoVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ExhibitorVideoCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ExhibitorVideoCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ExhibitorVideoCRUDService,
        factory: ExhibitorVideoCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ExhibitorVideoCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FeedbackCRUDService = /*#__PURE__*/(() => {
  class FeedbackCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFeedback(companyId, feedbackCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedback.');
      }
      if (feedbackCreateSchema === null || feedbackCreateSchema === undefined) {
        throw new Error('Required parameter feedbackCreateSchema was null or undefined when calling manageCreateFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFeedbackByEvent(companyId, eventId, feedbackCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateFeedbackByEvent.');
      }
      if (feedbackCreateSchema === null || feedbackCreateSchema === undefined) {
        throw new Error('Required parameter feedbackCreateSchema was null or undefined when calling manageCreateFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedback(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedbackByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedbackByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbacks(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbacks.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbacksByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbacksByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFeedbacksByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedback(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedbackByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFeedbackCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbacks(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbacks.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbacksByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbacksByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFeedbacksByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedback(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedbackByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedbackByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedback(itemId, companyId, feedbackUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedback.');
      }
      if (feedbackUpdateSchema === null || feedbackUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackUpdateSchema was null or undefined when calling manageUpdateFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedbackByEvent(itemId, companyId, eventId, feedbackUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedbackByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateFeedbackByEvent.');
      }
      if (feedbackUpdateSchema === null || feedbackUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackUpdateSchema was null or undefined when calling manageUpdateFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FeedbackCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeedbackCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FeedbackCRUDService,
        factory: FeedbackCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeedbackCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FeedbackQuestionCRUDService = /*#__PURE__*/(() => {
  class FeedbackQuestionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFeedbackQuestion(companyId, feedbackQuestionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedbackQuestion.');
      }
      if (feedbackQuestionCreateSchema === null || feedbackQuestionCreateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionCreateSchema was null or undefined when calling manageCreateFeedbackQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFeedbackQuestionByEvent(companyId, eventId, feedbackQuestionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateFeedbackQuestionByEvent.');
      }
      if (feedbackQuestionCreateSchema === null || feedbackQuestionCreateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionCreateSchema was null or undefined when calling manageCreateFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFeedbackQuestionByFeedback(companyId, feedbackId, feedbackQuestionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageCreateFeedbackQuestionByFeedback.');
      }
      if (feedbackQuestionCreateSchema === null || feedbackQuestionCreateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionCreateSchema was null or undefined when calling manageCreateFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedbackQuestion(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedbackQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedbackQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedbackQuestionByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedbackQuestionByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedbackQuestionByFeedback(itemId, companyId, feedbackId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedbackQuestionByFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageDeleteFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbackQuestions(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbackQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbackQuestionsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbackQuestionsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFeedbackQuestionsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbackQuestionsByFeedback(companyId, feedbackId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbackQuestionsByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageGetAllFeedbackQuestionsByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestion(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedbackQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestionByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedbackQuestionByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestionByFeedback(itemId, companyId, feedbackId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedbackQuestionByFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageGetFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestionCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestionCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestionCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFeedbackQuestionCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackQuestionCountByFeedback(companyId, feedbackId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackQuestionCountByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageGetFeedbackQuestionCountByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbackQuestions(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbackQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbackQuestionsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbackQuestionsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFeedbackQuestionsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbackQuestionsByFeedback(companyId, feedbackId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbackQuestionsByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageRenderAllFeedbackQuestionsByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedbackQuestion(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedbackQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedbackQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedbackQuestionByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedbackQuestionByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedbackQuestionByFeedback(itemId, companyId, feedbackId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedbackQuestionByFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageRenderFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedbackQuestion(itemId, companyId, feedbackQuestionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedbackQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedbackQuestion.');
      }
      if (feedbackQuestionUpdateSchema === null || feedbackQuestionUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionUpdateSchema was null or undefined when calling manageUpdateFeedbackQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedbackQuestionByEvent(itemId, companyId, eventId, feedbackQuestionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedbackQuestionByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateFeedbackQuestionByEvent.');
      }
      if (feedbackQuestionUpdateSchema === null || feedbackQuestionUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionUpdateSchema was null or undefined when calling manageUpdateFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedbackQuestionByFeedback(itemId, companyId, feedbackId, feedbackQuestionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedbackQuestionByFeedback.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageUpdateFeedbackQuestionByFeedback.');
      }
      if (feedbackQuestionUpdateSchema === null || feedbackQuestionUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackQuestionUpdateSchema was null or undefined when calling manageUpdateFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackQuestionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FeedbackQuestionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeedbackQuestionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FeedbackQuestionCRUDService,
        factory: FeedbackQuestionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeedbackQuestionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FeedbackVoteCRUDService = /*#__PURE__*/(() => {
  class FeedbackVoteCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFeedbackVoteByQuestion(companyId, questionId, feedbackVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageCreateFeedbackVoteByQuestion.');
      }
      if (feedbackVoteCreateSchema === null || feedbackVoteCreateSchema === undefined) {
        throw new Error('Required parameter feedbackVoteCreateSchema was null or undefined when calling manageCreateFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllFeedbackVotesByQuestion(companyId, questionId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllFeedbackVotesByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageDeleteAllFeedbackVotesByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFeedbackVoteByQuestion(itemId, companyId, questionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFeedbackVoteByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageDeleteFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVotesForEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVotesForEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/delete_for_event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVotesForFeedback(companyId, feedbackId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVotesForFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling manageDeleteVotesForFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/delete_for_feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVotesForQuestion(companyId, questionId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVotesForQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageDeleteVotesForQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/delete_for_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFeedbackVotesByQuestion(companyId, questionId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFeedbackVotesByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageGetAllFeedbackVotesByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackVoteByQuestion(itemId, companyId, questionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFeedbackVoteByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageGetFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFeedbackVoteCountByQuestion(companyId, questionId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFeedbackVoteCountByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageGetFeedbackVoteCountByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFeedbackVotesByQuestion(companyId, questionId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFeedbackVotesByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageRenderAllFeedbackVotesByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFeedbackVoteByQuestion(itemId, companyId, questionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFeedbackVoteByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageRenderFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFeedbackVoteByQuestion(itemId, companyId, questionId, feedbackVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFeedbackVoteByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling manageUpdateFeedbackVoteByQuestion.');
      }
      if (feedbackVoteUpdateSchema === null || feedbackVoteUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackVoteUpdateSchema was null or undefined when calling manageUpdateFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FeedbackVoteCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FeedbackVoteCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FeedbackVoteCRUDService,
        factory: FeedbackVoteCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FeedbackVoteCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FieldOfApplicationCRUDService = /*#__PURE__*/(() => {
  class FieldOfApplicationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFieldOfApplicationByEvent(companyId, eventId, fieldOfApplicationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateFieldOfApplicationByEvent.');
      }
      if (fieldOfApplicationCreateSchema === null || fieldOfApplicationCreateSchema === undefined) {
        throw new Error('Required parameter fieldOfApplicationCreateSchema was null or undefined when calling manageCreateFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: fieldOfApplicationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFieldOfApplicationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFieldOfApplicationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFieldOfApplicationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFieldOfApplicationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFieldOfApplicationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFieldOfApplicationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFieldOfApplicationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFieldOfApplicationCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFieldOfApplicationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFieldOfApplicationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFieldOfApplicationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFieldOfApplicationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFieldOfApplicationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFieldOfApplicationByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFieldOfApplicationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFieldOfApplicationByEvent(itemId, companyId, eventId, fieldOfApplicationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFieldOfApplicationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateFieldOfApplicationByEvent.');
      }
      if (fieldOfApplicationUpdateSchema === null || fieldOfApplicationUpdateSchema === undefined) {
        throw new Error('Required parameter fieldOfApplicationUpdateSchema was null or undefined when calling manageUpdateFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: fieldOfApplicationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FieldOfApplicationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FieldOfApplicationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FieldOfApplicationCRUDService,
        factory: FieldOfApplicationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FieldOfApplicationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirmCRUDService = /*#__PURE__*/(() => {
  class FirmCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFirm(companyId, firmCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFirm.');
      }
      if (firmCreateSchema === null || firmCreateSchema === undefined) {
        throw new Error('Required parameter firmCreateSchema was null or undefined when calling manageCreateFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firmCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFirm(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFirmLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFirmLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFirmLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFirmLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadFirmLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFirmLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFirms(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFirms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFirm(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFirmAgendas(companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFirmAgendas.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageGetFirmAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agendas`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFirmCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFirmCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFirmSpeakers(companyId, firmId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFirmSpeakers.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling manageGetFirmSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speakers`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFirms(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFirms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFirm(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFirm(itemId, companyId, firmUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFirm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFirm.');
      }
      if (firmUpdateSchema === null || firmUpdateSchema === undefined) {
        throw new Error('Required parameter firmUpdateSchema was null or undefined when calling manageUpdateFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firmUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFirmLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFirmLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFirmLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadFirmLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFirmLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFirmLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFirmLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadFirmLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirmCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirmCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirmCRUDService,
        factory: FirmCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirmCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FlightReservationCRUDService = /*#__PURE__*/(() => {
  class FlightReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFlightReservationByEvent(companyId, eventId, flightReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateFlightReservationByEvent.');
      }
      if (flightReservationCreateSchema === null || flightReservationCreateSchema === undefined) {
        throw new Error('Required parameter flightReservationCreateSchema was null or undefined when calling manageCreateFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFlightReservationByEventAndUser(companyId, eventId, userId, flightReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlightReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateFlightReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateFlightReservationByEventAndUser.');
      }
      if (flightReservationCreateSchema === null || flightReservationCreateSchema === undefined) {
        throw new Error('Required parameter flightReservationCreateSchema was null or undefined when calling manageCreateFlightReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFlightReservationByUser(companyId, userId, flightReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlightReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateFlightReservationByUser.');
      }
      if (flightReservationCreateSchema === null || flightReservationCreateSchema === undefined) {
        throw new Error('Required parameter flightReservationCreateSchema was null or undefined when calling manageCreateFlightReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFlightReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteFlightReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteFlightReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationTicketByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFlightReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationTicketByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteFlightReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteFlightReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlightReservationTicketByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlightReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlightReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteFlightReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFlightReservationTicketByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadFlightReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFlightReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadFlightReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFlightReservationTicketByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadFlightReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFlightReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadFlightReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadFlightReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFlightReservationTicketByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadFlightReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFlightReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadFlightReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFlightReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFlightReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllFlightReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFlightReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllFlightReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllFlightReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllFlightReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlightReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlightReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllFlightReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlightReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlightReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlightReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationCountByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationCountByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationCountByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationCountByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetFlightReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlightReservationCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlightReservationCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlightReservationCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFlightReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllFlightReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllFlightReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllFlightReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlightReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlightReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllFlightReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlightReservationByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlightReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlightReservationByEventAndUser(itemId, companyId, eventId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlightReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlightReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderFlightReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderFlightReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlightReservationByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlightReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlightReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderFlightReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlightReservationByEvent(itemId, companyId, eventId, flightReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlightReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateFlightReservationByEvent.');
      }
      if (flightReservationUpdateSchema === null || flightReservationUpdateSchema === undefined) {
        throw new Error('Required parameter flightReservationUpdateSchema was null or undefined when calling manageUpdateFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlightReservationByEventAndUser(itemId, companyId, eventId, userId, flightReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlightReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlightReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateFlightReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateFlightReservationByEventAndUser.');
      }
      if (flightReservationUpdateSchema === null || flightReservationUpdateSchema === undefined) {
        throw new Error('Required parameter flightReservationUpdateSchema was null or undefined when calling manageUpdateFlightReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlightReservationByUser(itemId, companyId, userId, flightReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlightReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlightReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateFlightReservationByUser.');
      }
      if (flightReservationUpdateSchema === null || flightReservationUpdateSchema === undefined) {
        throw new Error('Required parameter flightReservationUpdateSchema was null or undefined when calling manageUpdateFlightReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadFlightReservationTicketBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketBase64ByEventAndUser(itemId, companyId, eventId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadFlightReservationTicketBase64ByEventAndUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadFlightReservationTicketBase64ByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketBase64ByUser(itemId, companyId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketBase64ByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketBase64ByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadFlightReservationTicketBase64ByUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadFlightReservationTicketBase64ByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadFlightReservationTicketByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadFlightReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketByEventAndUser(itemId, companyId, eventId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadFlightReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadFlightReservationTicketByEventAndUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadFlightReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadFlightReservationTicketByUser(itemId, companyId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadFlightReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadFlightReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadFlightReservationTicketByUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadFlightReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FlightReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlightReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlightReservationCRUDService,
        factory: FlightReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlightReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FlowCRUDService = /*#__PURE__*/(() => {
  class FlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFlow(companyId, flowCreateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlow.');
      }
      if (flowCreateSchema === null || flowCreateSchema === undefined) {
        throw new Error('Required parameter flowCreateSchema was null or undefined when calling manageCreateFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlow(itemId, companyId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlows(companyId, searchTerms, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlows.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlow(itemId, companyId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowCount(companyId, searchTerms, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlows(companyId, searchTerms, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlows.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlow(itemId, companyId, searchTerms, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlow(itemId, companyId, flowUpdateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlow.');
      }
      if (flowUpdateSchema === null || flowUpdateSchema === undefined) {
        throw new Error('Required parameter flowUpdateSchema was null or undefined when calling manageUpdateFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlowCRUDService,
        factory: FlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FlowItemCRUDService = /*#__PURE__*/(() => {
  class FlowItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFlowItemByFlow(companyId, flowId, flowItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlowItemByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageCreateFlowItemByFlow.');
      }
      if (flowItemCreateSchema === null || flowItemCreateSchema === undefined) {
        throw new Error('Required parameter flowItemCreateSchema was null or undefined when calling manageCreateFlowItemByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlowItemByFlow(itemId, companyId, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlowItemByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlowItemByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageDeleteFlowItemByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlowItemsByFlow(companyId, flowId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlowItemsByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetAllFlowItemsByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemByFlow(itemId, companyId, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlowItemByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetFlowItemByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemCountByFlow(companyId, flowId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemCountByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetFlowItemCountByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlowItemsByFlow(companyId, flowId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlowItemsByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageRenderAllFlowItemsByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlowItemByFlow(itemId, companyId, flowId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlowItemByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlowItemByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageRenderFlowItemByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlowItemByFlow(itemId, companyId, flowId, flowItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlowItemByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlowItemByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageUpdateFlowItemByFlow.');
      }
      if (flowItemUpdateSchema === null || flowItemUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUpdateSchema was null or undefined when calling manageUpdateFlowItemByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlowItemOrder(companyId, flowId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlowItemOrder.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageUpdateFlowItemOrder.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageUpdateFlowItemOrder.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_order`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FlowItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlowItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlowItemCRUDService,
        factory: FlowItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlowItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FlowItemUserStatusCRUDService = /*#__PURE__*/(() => {
  class FlowItemUserStatusCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFlowItemUserStatusByFlowItem(companyId, flowItemId, flowItemUserStatusCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageCreateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemUserStatusCreateSchema === null || flowItemUserStatusCreateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusCreateSchema was null or undefined when calling manageCreateFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateFlowItemUserStatusByUser(companyId, userId, flowItemUserStatusCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateFlowItemUserStatusByUser.');
      }
      if (flowItemUserStatusCreateSchema === null || flowItemUserStatusCreateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusCreateSchema was null or undefined when calling manageCreateFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlowItemUserStatusByFlowItem(itemId, companyId, flowItemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlowItemUserStatusByFlowItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageDeleteFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlowItemUserStatusByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlowItemUserStatusByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlowItemUserStatusesByFlowItem(companyId, flowItemId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlowItemUserStatusesByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageGetAllFlowItemUserStatusesByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlowItemUserStatusesByUser(companyId, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlowItemUserStatusesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllFlowItemUserStatusesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemUserStatusByFlowItem(itemId, companyId, flowItemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlowItemUserStatusByFlowItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageGetFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemUserStatusByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlowItemUserStatusByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemUserStatusCountByFlowItem(companyId, flowItemId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemUserStatusCountByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageGetFlowItemUserStatusCountByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowItemUserStatusCountByUser(companyId, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowItemUserStatusCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetFlowItemUserStatusCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlowItemUserStatusesByFlowItem(companyId, flowItemId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlowItemUserStatusesByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageRenderAllFlowItemUserStatusesByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlowItemUserStatusesByUser(companyId, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlowItemUserStatusesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllFlowItemUserStatusesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlowItemUserStatusByFlowItem(itemId, companyId, flowItemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlowItemUserStatusByFlowItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageRenderFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlowItemUserStatusByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlowItemUserStatusByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlowItemUserStatusByFlowItem(itemId, companyId, flowItemId, flowItemUserStatusUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlowItemUserStatusByFlowItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling manageUpdateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemUserStatusUpdateSchema === null || flowItemUserStatusUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusUpdateSchema was null or undefined when calling manageUpdateFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlowItemUserStatusByUser(itemId, companyId, userId, flowItemUserStatusUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlowItemUserStatusByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateFlowItemUserStatusByUser.');
      }
      if (flowItemUserStatusUpdateSchema === null || flowItemUserStatusUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusUpdateSchema was null or undefined when calling manageUpdateFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FlowItemUserStatusCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlowItemUserStatusCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlowItemUserStatusCRUDService,
        factory: FlowItemUserStatusCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlowItemUserStatusCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FlowUserCRUDService = /*#__PURE__*/(() => {
  class FlowUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateFlowUserByFlow(companyId, flowId, flowUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateFlowUserByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageCreateFlowUserByFlow.');
      }
      if (flowUserCreateSchema === null || flowUserCreateSchema === undefined) {
        throw new Error('Required parameter flowUserCreateSchema was null or undefined when calling manageCreateFlowUserByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteFlowUserByFlow(itemId, companyId, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteFlowUserByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteFlowUserByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageDeleteFlowUserByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllFlowUsersByFlow(companyId, flowId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllFlowUsersByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetAllFlowUsersByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowUserByFlow(itemId, companyId, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetFlowUserByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowUserByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetFlowUserByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetFlowUserCountByFlow(companyId, flowId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetFlowUserCountByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageGetFlowUserCountByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllFlowUsersByFlow(companyId, flowId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllFlowUsersByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageRenderAllFlowUsersByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderFlowUserByFlow(itemId, companyId, flowId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderFlowUserByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderFlowUserByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageRenderFlowUserByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateFlowUserByFlow(itemId, companyId, flowId, flowUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateFlowUserByFlow.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateFlowUserByFlow.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling manageUpdateFlowUserByFlow.');
      }
      if (flowUserUpdateSchema === null || flowUserUpdateSchema === undefined) {
        throw new Error('Required parameter flowUserUpdateSchema was null or undefined when calling manageUpdateFlowUserByFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FlowUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlowUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlowUserCRUDService,
        factory: FlowUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlowUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let GameCRUDService = /*#__PURE__*/(() => {
  class GameCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateGame(companyId, gameCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGame.');
      }
      if (gameCreateSchema === null || gameCreateSchema === undefined) {
        throw new Error('Required parameter gameCreateSchema was null or undefined when calling manageCreateGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: gameCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllGames(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGame(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGames(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGame(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGameCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGameCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGames(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGame(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGame(itemId, companyId, gameUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGame.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGame.');
      }
      if (gameUpdateSchema === null || gameUpdateSchema === undefined) {
        throw new Error('Required parameter gameUpdateSchema was null or undefined when calling manageUpdateGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: gameUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function GameCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GameCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GameCRUDService,
        factory: GameCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GameCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let GigyaKeyDataCRUDService = /*#__PURE__*/(() => {
  class GigyaKeyDataCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateGigyaKeyData(companyId, gigyaKeyDataCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGigyaKeyData.');
      }
      if (gigyaKeyDataCreateSchema === null || gigyaKeyDataCreateSchema === undefined) {
        throw new Error('Required parameter gigyaKeyDataCreateSchema was null or undefined when calling manageCreateGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: gigyaKeyDataCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGigyaKeyData(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGigyaKeyData.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGigyaKeyData(companyId, orderBy, searchTermList, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGigyaKeyDataMulti(companyId, idList, orderBy, searchTermList, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGigyaKeyDataMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGigyaKeyDataSearch(companyId, orderBy, searchTermList, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGigyaKeyDataSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGigyaKeyData(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGigyaKeyData.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGigyaKeyDataCount(companyId, orderBy, searchTermList, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGigyaKeyDataCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGigyaKeyDataCountMulti(companyId, idList, orderBy, searchTermList, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGigyaKeyDataCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGigyaKeyDataCountSearch(companyId, orderBy, searchTermList, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGigyaKeyDataCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGigyaKeyData(companyId, orderBy, searchTermList, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGigyaKeyDataMulti(companyId, idList, orderBy, searchTermList, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGigyaKeyDataMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGigyaKeyDataSearch(companyId, orderBy, searchTermList, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGigyaKeyDataSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGigyaKeyData(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGigyaKeyData.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGigyaKeyData(itemId, companyId, gigyaKeyDataUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGigyaKeyData.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGigyaKeyData.');
      }
      if (gigyaKeyDataUpdateSchema === null || gigyaKeyDataUpdateSchema === undefined) {
        throw new Error('Required parameter gigyaKeyDataUpdateSchema was null or undefined when calling manageUpdateGigyaKeyData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/gigya_key_data/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: gigyaKeyDataUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function GigyaKeyDataCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GigyaKeyDataCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GigyaKeyDataCRUDService,
        factory: GigyaKeyDataCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GigyaKeyDataCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let GroupCRUDService = /*#__PURE__*/(() => {
  class GroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateGroup(companyId, groupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGroup.');
      }
      if (groupCreateSchema === null || groupCreateSchema === undefined) {
        throw new Error('Required parameter groupCreateSchema was null or undefined when calling manageCreateGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGroupLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGroupLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGroupLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadGroupLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadGroupLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadGroupLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGroups(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGroups(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGroup(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGroup(itemId, companyId, groupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGroup.');
      }
      if (groupUpdateSchema === null || groupUpdateSchema === undefined) {
        throw new Error('Required parameter groupUpdateSchema was null or undefined when calling manageUpdateGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadGroupLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadGroupLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadGroupLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadGroupLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadGroupLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadGroupLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadGroupLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadGroupLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function GroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GroupCRUDService,
        factory: GroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let GroupUserCRUDService = /*#__PURE__*/(() => {
  class GroupUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateGroupUser(companyId, groupUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGroupUser.');
      }
      if (groupUserCreateSchema === null || groupUserCreateSchema === undefined) {
        throw new Error('Required parameter groupUserCreateSchema was null or undefined when calling manageCreateGroupUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateGroupUserByGroup(companyId, groupId, groupUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGroupUserByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageCreateGroupUserByGroup.');
      }
      if (groupUserCreateSchema === null || groupUserCreateSchema === undefined) {
        throw new Error('Required parameter groupUserCreateSchema was null or undefined when calling manageCreateGroupUserByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateGroupUserByUser(companyId, userId, groupUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateGroupUserByUser.');
      }
      if (groupUserCreateSchema === null || groupUserCreateSchema === undefined) {
        throw new Error('Required parameter groupUserCreateSchema was null or undefined when calling manageCreateGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGroupUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGroupUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGroupUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGroupUserByGroup(itemId, companyId, groupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGroupUserByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGroupUserByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageDeleteGroupUserByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteGroupUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteGroupUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGroupUsers(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGroupUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGroupUsersByGroup(companyId, groupId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGroupUsersByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetAllGroupUsersByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllGroupUsersByUser(companyId, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllGroupUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllGroupUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGroupUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUserByGroup(itemId, companyId, groupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGroupUserByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUserByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetGroupUserByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetGroupUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUserCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUserCountByGroup(companyId, groupId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUserCountByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageGetGroupUserCountByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetGroupUserCountByUser(companyId, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetGroupUserCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetGroupUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGroupUsers(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGroupUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGroupUsersByGroup(companyId, groupId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGroupUsersByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageRenderAllGroupUsersByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllGroupUsersByUser(companyId, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllGroupUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllGroupUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGroupUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGroupUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGroupUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGroupUserByGroup(itemId, companyId, groupId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGroupUserByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGroupUserByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageRenderGroupUserByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderGroupUserByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderGroupUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetGroupUsers(companyId, groupId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetGroupUsers.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageSetGroupUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetGroupUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserGroups(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserGroups.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserGroups.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGroupUser(itemId, companyId, groupUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGroupUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGroupUser.');
      }
      if (groupUserUpdateSchema === null || groupUserUpdateSchema === undefined) {
        throw new Error('Required parameter groupUserUpdateSchema was null or undefined when calling manageUpdateGroupUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGroupUserByGroup(itemId, companyId, groupId, groupUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGroupUserByGroup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGroupUserByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling manageUpdateGroupUserByGroup.');
      }
      if (groupUserUpdateSchema === null || groupUserUpdateSchema === undefined) {
        throw new Error('Required parameter groupUserUpdateSchema was null or undefined when calling manageUpdateGroupUserByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateGroupUserByUser(itemId, companyId, userId, groupUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateGroupUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateGroupUserByUser.');
      }
      if (groupUserUpdateSchema === null || groupUserUpdateSchema === undefined) {
        throw new Error('Required parameter groupUserUpdateSchema was null or undefined when calling manageUpdateGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function GroupUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GroupUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GroupUserCRUDService,
        factory: GroupUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GroupUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let HealthService = /*#__PURE__*/(() => {
  class HealthService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getHealthHealthGet(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/health`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function HealthService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HealthService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HealthService,
        factory: HealthService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HealthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let HotelCRUDService = /*#__PURE__*/(() => {
  class HotelCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateHotel(companyId, hotelCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateHotel.');
      }
      if (hotelCreateSchema === null || hotelCreateSchema === undefined) {
        throw new Error('Required parameter hotelCreateSchema was null or undefined when calling manageCreateHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotel(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotel.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllHotels(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllHotels.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotel(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetHotel.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllHotels(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllHotels.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderHotel(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderHotel.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateHotel(itemId, companyId, hotelUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateHotel.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateHotel.');
      }
      if (hotelUpdateSchema === null || hotelUpdateSchema === undefined) {
        throw new Error('Required parameter hotelUpdateSchema was null or undefined when calling manageUpdateHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function HotelCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HotelCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HotelCRUDService,
        factory: HotelCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HotelCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let HotelReservationCRUDService = /*#__PURE__*/(() => {
  class HotelReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateHotelReservation(companyId, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateHotelReservation.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling manageCreateHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateHotelReservationByEvent(companyId, eventId, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateHotelReservationByEvent.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling manageCreateHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateHotelReservationByEventAndUser(companyId, eventId, userId, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateHotelReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateHotelReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateHotelReservationByEventAndUser.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling manageCreateHotelReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateHotelReservationByUser(companyId, userId, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateHotelReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateHotelReservationByUser.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling manageCreateHotelReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationBooking(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationBooking.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationBooking.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationBookingByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationBookingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationBookingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteHotelReservationBookingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationBookingByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationBookingByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationBookingByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteHotelReservationBookingByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteHotelReservationBookingByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationBookingByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationBookingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationBookingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteHotelReservationBookingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteHotelReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteHotelReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteHotelReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteHotelReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteHotelReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteHotelReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadHotelReservationBooking(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadHotelReservationBooking.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadHotelReservationBooking.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadHotelReservationBookingByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadHotelReservationBookingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadHotelReservationBookingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadHotelReservationBookingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadHotelReservationBookingByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadHotelReservationBookingByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadHotelReservationBookingByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadHotelReservationBookingByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadHotelReservationBookingByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadHotelReservationBookingByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadHotelReservationBookingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadHotelReservationBookingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadHotelReservationBookingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllHotelReservations(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllHotelReservations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllHotelReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllHotelReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllHotelReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllHotelReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllHotelReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllHotelReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllHotelReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllHotelReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllHotelReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllHotelReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetHotelReservation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetHotelReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetHotelReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetHotelReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetHotelReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetHotelReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetHotelReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetHotelReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationCountByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationCountByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetHotelReservationCountByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetHotelReservationCountByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetHotelReservationCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetHotelReservationCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetHotelReservationCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllHotelReservations(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllHotelReservations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllHotelReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllHotelReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllHotelReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllHotelReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllHotelReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllHotelReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllHotelReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllHotelReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllHotelReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllHotelReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderHotelReservation(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderHotelReservation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderHotelReservationByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderHotelReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderHotelReservationByEventAndUser(itemId, companyId, eventId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderHotelReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderHotelReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderHotelReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderHotelReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderHotelReservationByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderHotelReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderHotelReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderHotelReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateHotelReservation(itemId, companyId, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateHotelReservation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateHotelReservation.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling manageUpdateHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateHotelReservationByEvent(itemId, companyId, eventId, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateHotelReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateHotelReservationByEvent.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling manageUpdateHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateHotelReservationByEventAndUser(itemId, companyId, eventId, userId, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateHotelReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateHotelReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateHotelReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateHotelReservationByEventAndUser.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling manageUpdateHotelReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateHotelReservationByUser(itemId, companyId, userId, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateHotelReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateHotelReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateHotelReservationByUser.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling manageUpdateHotelReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBooking(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBooking.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBooking.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadHotelReservationBooking.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadHotelReservationBookingBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadHotelReservationBookingBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingBase64ByEventAndUser(itemId, companyId, eventId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadHotelReservationBookingBase64ByEventAndUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadHotelReservationBookingBase64ByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingBase64ByUser(itemId, companyId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingBase64ByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingBase64ByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadHotelReservationBookingBase64ByUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadHotelReservationBookingBase64ByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadHotelReservationBookingByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadHotelReservationBookingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingByEventAndUser(itemId, companyId, eventId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadHotelReservationBookingByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadHotelReservationBookingByEventAndUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadHotelReservationBookingByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadHotelReservationBookingByUser(itemId, companyId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadHotelReservationBookingByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadHotelReservationBookingByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadHotelReservationBookingByUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadHotelReservationBookingByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function HotelReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || HotelReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: HotelReservationCRUDService,
        factory: HotelReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return HotelReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LeaderboardService = /*#__PURE__*/(() => {
  class LeaderboardService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageDeleteEventLeaderboard(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteEventLeaderboard.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteEventLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserLeaderboard(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserLeaderboard.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteUserLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventLeaderboard(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventLeaderboard.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadEventLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/download_leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageEventLeaderboard(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageEventLeaderboard.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageEventLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LeaderboardService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LeaderboardService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LeaderboardService,
        factory: LeaderboardService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LeaderboardService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LiveChatCRUDService = /*#__PURE__*/(() => {
  class LiveChatCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateLiveChat(companyId, liveChatCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateLiveChat.');
      }
      if (liveChatCreateSchema === null || liveChatCreateSchema === undefined) {
        throw new Error('Required parameter liveChatCreateSchema was null or undefined when calling manageCreateLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateLiveChatByEvent(companyId, eventId, liveChatCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateLiveChatByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateLiveChatByEvent.');
      }
      if (liveChatCreateSchema === null || liveChatCreateSchema === undefined) {
        throw new Error('Required parameter liveChatCreateSchema was null or undefined when calling manageCreateLiveChatByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateLiveChatByEventAndAgenda(companyId, eventId, agendaId, liveChatCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateLiveChatByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateLiveChatByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateLiveChatByEventAndAgenda.');
      }
      if (liveChatCreateSchema === null || liveChatCreateSchema === undefined) {
        throw new Error('Required parameter liveChatCreateSchema was null or undefined when calling manageCreateLiveChatByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllLiveChatsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllLiveChatsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllLiveChatsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllLiveChatsByEventAndAgenda(companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllLiveChatsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllLiveChatsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteAllLiveChatsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteLiveChat(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteLiveChat.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteLiveChatByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteLiveChatByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteLiveChatByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteLiveChatByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteLiveChatByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteLiveChatByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteLiveChatByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteLiveChatByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteLiveChatByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllLiveChats(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllLiveChats.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllLiveChatsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllLiveChatsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllLiveChatsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllLiveChatsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllLiveChatsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllLiveChatsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllLiveChatsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChat(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetLiveChat.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChatByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetLiveChatByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChatByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetLiveChatByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChatByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetLiveChatByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChatByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetLiveChatByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetLiveChatByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChatCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChatCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChatCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChatCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetLiveChatCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetLiveChatCountByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetLiveChatCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetLiveChatCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetLiveChatCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllLiveChats(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllLiveChats.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllLiveChatsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllLiveChatsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllLiveChatsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllLiveChatsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllLiveChatsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllLiveChatsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllLiveChatsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderLiveChat(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderLiveChat.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderLiveChatByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderLiveChatByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderLiveChatByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderLiveChatByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderLiveChatByEventAndAgenda(itemId, companyId, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderLiveChatByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderLiveChatByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderLiveChatByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderLiveChatByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateLiveChat(itemId, companyId, liveChatUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateLiveChat.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateLiveChat.');
      }
      if (liveChatUpdateSchema === null || liveChatUpdateSchema === undefined) {
        throw new Error('Required parameter liveChatUpdateSchema was null or undefined when calling manageUpdateLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateLiveChatByEvent(itemId, companyId, eventId, liveChatUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateLiveChatByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateLiveChatByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateLiveChatByEvent.');
      }
      if (liveChatUpdateSchema === null || liveChatUpdateSchema === undefined) {
        throw new Error('Required parameter liveChatUpdateSchema was null or undefined when calling manageUpdateLiveChatByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateLiveChatByEventAndAgenda(itemId, companyId, eventId, agendaId, liveChatUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateLiveChatByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateLiveChatByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateLiveChatByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateLiveChatByEventAndAgenda.');
      }
      if (liveChatUpdateSchema === null || liveChatUpdateSchema === undefined) {
        throw new Error('Required parameter liveChatUpdateSchema was null or undefined when calling manageUpdateLiveChatByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LiveChatCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LiveChatCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LiveChatCRUDService,
        factory: LiveChatCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LiveChatCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LookupTablesService = /*#__PURE__*/(() => {
  class LookupTablesService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getEventCategoryLookupData(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/lookup/data/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGenderLookupData(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/lookup/data/gender`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRoleLookupData(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/lookup/data/role`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LookupTablesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LookupTablesService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LookupTablesService,
        factory: LookupTablesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LookupTablesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MailTemplateCRUDService = /*#__PURE__*/(() => {
  class MailTemplateCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCloneMailTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageCloneMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCloneMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/clone`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMailTemplate(companyId, mailTemplateCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMailTemplate.');
      }
      if (mailTemplateCreateSchema === null || mailTemplateCreateSchema === undefined) {
        throw new Error('Required parameter mailTemplateCreateSchema was null or undefined when calling manageCreateMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mailTemplateCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMailTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMailTemplateAttachment(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMailTemplateAttachment.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMailTemplateAttachment.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/attachment`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadMailTemplateAttachment(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadMailTemplateAttachment.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadMailTemplateAttachment.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/attachment`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMailTemplates(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMailTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailTemplateCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailTemplateCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMailTemplates(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMailTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMailTemplate(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMailTemplate(itemId, companyId, mailTemplateUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMailTemplate.');
      }
      if (mailTemplateUpdateSchema === null || mailTemplateUpdateSchema === undefined) {
        throw new Error('Required parameter mailTemplateUpdateSchema was null or undefined when calling manageUpdateMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mailTemplateUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMailTemplateAttachment(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMailTemplateAttachment.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMailTemplateAttachment.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadMailTemplateAttachment.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/attachment`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMailTemplateAttachmentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMailTemplateAttachmentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMailTemplateAttachmentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadMailTemplateAttachmentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/attachment/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MailTemplateCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MailTemplateCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MailTemplateCRUDService,
        factory: MailTemplateCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MailTemplateCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MailerService = /*#__PURE__*/(() => {
  class MailerService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageMailerPreview(companyId, mailerInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageMailerPreview.');
      }
      if (mailerInput === null || mailerInput === undefined) {
        throw new Error('Required parameter mailerInput was null or undefined when calling manageMailerPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mailer/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mailerInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageMailerSend(companyId, mailerInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageMailerSend.');
      }
      if (mailerInput === null || mailerInput === undefined) {
        throw new Error('Required parameter mailerInput was null or undefined when calling manageMailerSend.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/mailer/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/send`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mailerInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MailerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MailerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MailerService,
        factory: MailerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MailerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MailerCRUDService = /*#__PURE__*/(() => {
  class MailerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageGetAllMailers(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMailers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMailersByMailTemplate(companyId, mailTemplateId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMailersByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetAllMailersByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailer(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMailer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailerByMailTemplate(itemId, companyId, mailTemplateId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMailerByMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailerByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetMailerByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailerCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMailerCountByMailTemplate(companyId, mailTemplateId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMailerCountByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetMailerCountByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMailers(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMailers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMailersByMailTemplate(companyId, mailTemplateId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMailersByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageRenderAllMailersByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMailer(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMailer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMailer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMailerByMailTemplate(itemId, companyId, mailTemplateId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMailerByMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMailerByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageRenderMailerByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mailer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MailerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MailerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MailerCRUDService,
        factory: MailerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MailerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ManagementService = /*#__PURE__*/(() => {
  class ManagementService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    legacyManageAdministratorCompanyIndex(administratorId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageAdministratorCompanyIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageAdministratorIndex(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageCreateAdministrator(createAdministratorInput, observe = 'body', reportProgress = false, options) {
      if (createAdministratorInput === null || createAdministratorInput === undefined) {
        throw new Error('Required parameter createAdministratorInput was null or undefined when calling legacyManageCreateAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: createAdministratorInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageCreateAdministratorCompany(administratorId, companyId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageCreateAdministratorCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling legacyManageCreateAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageDeleteAdministrator(administratorId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageDeleteAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageDeleteAdministratorCompany(administratorId, companyId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageDeleteAdministratorCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling legacyManageDeleteAdministratorCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageGetAdministrator(administratorId, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageGetAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    legacyManageUpdateAdministrator(administratorId, updateAdministratorInput, observe = 'body', reportProgress = false, options) {
      if (administratorId === null || administratorId === undefined) {
        throw new Error('Required parameter administratorId was null or undefined when calling legacyManageUpdateAdministrator.');
      }
      if (updateAdministratorInput === null || updateAdministratorInput === undefined) {
        throw new Error('Required parameter updateAdministratorInput was null or undefined when calling legacyManageUpdateAdministrator.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/${this.configuration.encodeParam({
        name: 'administratorId',
        value: administratorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: updateAdministratorInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCompanyIndex(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    requestAdministratorPasswordReset(email, passwordResetUrl, observe = 'body', reportProgress = false, options) {
      if (email === null || email === undefined) {
        throw new Error('Required parameter email was null or undefined when calling requestAdministratorPasswordReset.');
      }
      if (passwordResetUrl === null || passwordResetUrl === undefined) {
        throw new Error('Required parameter passwordResetUrl was null or undefined when calling requestAdministratorPasswordReset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, passwordResetUrl, 'password_reset_url');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/request_password_change/${this.configuration.encodeParam({
        name: 'email',
        value: email,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    resetAdministratorPassword(token, resetAdministratorPasswordInput, observe = 'body', reportProgress = false, options) {
      if (token === null || token === undefined) {
        throw new Error('Required parameter token was null or undefined when calling resetAdministratorPassword.');
      }
      if (resetAdministratorPasswordInput === null || resetAdministratorPasswordInput === undefined) {
        throw new Error('Required parameter resetAdministratorPasswordInput was null or undefined when calling resetAdministratorPassword.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/administrator/reset_password/${this.configuration.encodeParam({
        name: 'token',
        value: token,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: resetAdministratorPasswordInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ManagementService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ManagementService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ManagementService,
        factory: ManagementService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ManagementService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MediaGalleryCRUDService = /*#__PURE__*/(() => {
  class MediaGalleryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMediaGallery(companyId, mediaGalleryCreateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMediaGallery.');
      }
      if (mediaGalleryCreateSchema === null || mediaGalleryCreateSchema === undefined) {
        throw new Error('Required parameter mediaGalleryCreateSchema was null or undefined when calling manageCreateMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mediaGalleryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMediaGallery(itemId, companyId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMediaGalleries(companyId, searchTerms, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMediaGalleries.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMediaGallery(itemId, companyId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMediaGalleryCount(companyId, searchTerms, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMediaGalleryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMediaGalleries(companyId, searchTerms, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMediaGalleries.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMediaGallery(itemId, companyId, searchTerms, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMediaGallery(itemId, companyId, mediaGalleryUpdateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMediaGallery.');
      }
      if (mediaGalleryUpdateSchema === null || mediaGalleryUpdateSchema === undefined) {
        throw new Error('Required parameter mediaGalleryUpdateSchema was null or undefined when calling manageUpdateMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mediaGalleryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MediaGalleryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MediaGalleryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MediaGalleryCRUDService,
        factory: MediaGalleryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MediaGalleryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MediaGalleryItemCRUDService = /*#__PURE__*/(() => {
  class MediaGalleryItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMediaGalleryItemByMediaGallery(companyId, mediaGalleryId, mediaGalleryItemCreateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageCreateMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryItemCreateSchema === null || mediaGalleryItemCreateSchema === undefined) {
        throw new Error('Required parameter mediaGalleryItemCreateSchema was null or undefined when calling manageCreateMediaGalleryItemByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mediaGalleryItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMediaGalleryItemByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMediaGalleryItemByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageDeleteMediaGalleryItemByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMediaGalleryItemMediaByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMediaGalleryItemMediaByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMediaGalleryItemMediaByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageDeleteMediaGalleryItemMediaByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMediaGalleryItemThumbnailByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageDeleteMediaGalleryItemThumbnailByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadMediaGalleryItemMediaByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadMediaGalleryItemMediaByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadMediaGalleryItemMediaByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageDownloadMediaGalleryItemMediaByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadMediaGalleryItemThumbnailByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageDownloadMediaGalleryItemThumbnailByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMediaGalleryItemsByMediaGallery(companyId, mediaGalleryId, searchTerms, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMediaGalleryItemsByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageGetAllMediaGalleryItemsByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMediaGalleryItemByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMediaGalleryItemByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageGetMediaGalleryItemByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMediaGalleryItemCountByMediaGallery(companyId, mediaGalleryId, searchTerms, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMediaGalleryItemCountByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageGetMediaGalleryItemCountByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageMediaGalleryItemVerify(companyId, media, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageMediaGalleryItemVerify.');
      }
      if (media === null || media === undefined) {
        throw new Error('Required parameter media was null or undefined when calling manageMediaGalleryItemVerify.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (media !== undefined) {
        localVarFormParams = localVarFormParams.append('media', media) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/verify`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMediaGalleryItemsByMediaGallery(companyId, mediaGalleryId, searchTerms, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMediaGalleryItemsByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageRenderAllMediaGalleryItemsByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMediaGalleryItemByMediaGallery(itemId, companyId, mediaGalleryId, searchTerms, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMediaGalleryItemByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageRenderMediaGalleryItemByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMediaGalleryItemByMediaGallery(itemId, companyId, mediaGalleryId, mediaGalleryItemUpdateSchema, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMediaGalleryItemByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageUpdateMediaGalleryItemByMediaGallery.');
      }
      if (mediaGalleryItemUpdateSchema === null || mediaGalleryItemUpdateSchema === undefined) {
        throw new Error('Required parameter mediaGalleryItemUpdateSchema was null or undefined when calling manageUpdateMediaGalleryItemByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: mediaGalleryItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMediaGalleryItemMediaBase64ByMediaGallery(itemId, companyId, mediaGalleryId, body, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMediaGalleryItemMediaBase64ByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMediaGalleryItemMediaBase64ByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageUploadMediaGalleryItemMediaBase64ByMediaGallery.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadMediaGalleryItemMediaBase64ByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMediaGalleryItemMediaByMediaGallery(itemId, companyId, mediaGalleryId, assetFile, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMediaGalleryItemMediaByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMediaGalleryItemMediaByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageUploadMediaGalleryItemMediaByMediaGallery.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadMediaGalleryItemMediaByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMediaGalleryItemThumbnailBase64ByMediaGallery(itemId, companyId, mediaGalleryId, body, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMediaGalleryItemThumbnailBase64ByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMediaGalleryItemThumbnailBase64ByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageUploadMediaGalleryItemThumbnailBase64ByMediaGallery.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadMediaGalleryItemThumbnailBase64ByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadMediaGalleryItemThumbnailByMediaGallery(itemId, companyId, mediaGalleryId, assetFile, searchTerms, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling manageUploadMediaGalleryItemThumbnailByMediaGallery.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadMediaGalleryItemThumbnailByMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MediaGalleryItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MediaGalleryItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MediaGalleryItemCRUDService,
        factory: MediaGalleryItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MediaGalleryItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MeetingCRUDService = /*#__PURE__*/(() => {
  class MeetingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMeeting(companyId, meetingCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeeting.');
      }
      if (meetingCreateSchema === null || meetingCreateSchema === undefined) {
        throw new Error('Required parameter meetingCreateSchema was null or undefined when calling manageCreateMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingByEvent(companyId, eventId, meetingCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateMeetingByEvent.');
      }
      if (meetingCreateSchema === null || meetingCreateSchema === undefined) {
        throw new Error('Required parameter meetingCreateSchema was null or undefined when calling manageCreateMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeeting(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetings(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllMeetingsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeeting(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMeetingCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetings(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllMeetingsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeeting(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeeting(itemId, companyId, meetingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeeting.');
      }
      if (meetingUpdateSchema === null || meetingUpdateSchema === undefined) {
        throw new Error('Required parameter meetingUpdateSchema was null or undefined when calling manageUpdateMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingByEvent(itemId, companyId, eventId, meetingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateMeetingByEvent.');
      }
      if (meetingUpdateSchema === null || meetingUpdateSchema === undefined) {
        throw new Error('Required parameter meetingUpdateSchema was null or undefined when calling manageUpdateMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MeetingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeetingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeetingCRUDService,
        factory: MeetingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeetingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MeetingEventUserCRUDService = /*#__PURE__*/(() => {
  class MeetingEventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMeetingEventUser(companyId, meetingEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingEventUser.');
      }
      if (meetingEventUserCreateSchema === null || meetingEventUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserCreateSchema was null or undefined when calling manageCreateMeetingEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingEventUserByEventUser(companyId, eventUserId, meetingEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageCreateMeetingEventUserByEventUser.');
      }
      if (meetingEventUserCreateSchema === null || meetingEventUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserCreateSchema was null or undefined when calling manageCreateMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingEventUserByMeeting(companyId, meetingId, meetingEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageCreateMeetingEventUserByMeeting.');
      }
      if (meetingEventUserCreateSchema === null || meetingEventUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserCreateSchema was null or undefined when calling manageCreateMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllMeetingEventUsersByEventUser(companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllMeetingEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllMeetingEventUsersByMeeting(companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllMeetingEventUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageDeleteAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingEventUserByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDeleteMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingEventUserByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingEventUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageDeleteMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsersByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsersByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsersByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetAllMeetingEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsersByMeetingMulti(companyId, meetingId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsersByMeetingMulti.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetAllMeetingEventUsersByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserByEventUser(itemId, companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingEventUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCountByEventUser(companyId, eventUserId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCountByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetMeetingEventUserCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCountByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCountByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageGetMeetingEventUserCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCountByMeeting(companyId, meetingId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingEventUserCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCountByMeetingMulti(companyId, meetingId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCountByMeetingMulti.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingEventUserCountByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingEventUserCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingEventUserCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsersByEventUser(companyId, eventUserId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsersByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsersByEventUserMulti(companyId, eventUserId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsersByEventUserMulti.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderAllMeetingEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsersByMeetingMulti(companyId, meetingId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsersByMeetingMulti.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderAllMeetingEventUsersByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingEventUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingEventUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingEventUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingEventUserByEventUser(itemId, companyId, eventUserId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageRenderMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingEventUserByMeeting(itemId, companyId, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingEventUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetEventUserMeetings(companyId, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetEventUserMeetings.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageSetEventUserMeetings.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetEventUserMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetMeetingEventUsers(companyId, meetingId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetMeetingEventUsers.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageSetMeetingEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetMeetingEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingEventUser(itemId, companyId, meetingEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingEventUser.');
      }
      if (meetingEventUserUpdateSchema === null || meetingEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserUpdateSchema was null or undefined when calling manageUpdateMeetingEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingEventUserByEventUser(itemId, companyId, eventUserId, meetingEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingEventUserByEventUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageUpdateMeetingEventUserByEventUser.');
      }
      if (meetingEventUserUpdateSchema === null || meetingEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserUpdateSchema was null or undefined when calling manageUpdateMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingEventUserByMeeting(itemId, companyId, meetingId, meetingEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingEventUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageUpdateMeetingEventUserByMeeting.');
      }
      if (meetingEventUserUpdateSchema === null || meetingEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserUpdateSchema was null or undefined when calling manageUpdateMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MeetingEventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeetingEventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeetingEventUserCRUDService,
        factory: MeetingEventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeetingEventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MeetingExhibitorCRUDService = /*#__PURE__*/(() => {
  class MeetingExhibitorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMeetingExhibitor(companyId, meetingExhibitorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingExhibitor.');
      }
      if (meetingExhibitorCreateSchema === null || meetingExhibitorCreateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorCreateSchema was null or undefined when calling manageCreateMeetingExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingExhibitorByExhibitor(companyId, exhibitorId, meetingExhibitorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageCreateMeetingExhibitorByExhibitor.');
      }
      if (meetingExhibitorCreateSchema === null || meetingExhibitorCreateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorCreateSchema was null or undefined when calling manageCreateMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingExhibitorByMeeting(companyId, meetingId, meetingExhibitorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageCreateMeetingExhibitorByMeeting.');
      }
      if (meetingExhibitorCreateSchema === null || meetingExhibitorCreateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorCreateSchema was null or undefined when calling manageCreateMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingExhibitor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingExhibitorByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingExhibitorByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageDeleteMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingExhibitorByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingExhibitorByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageDeleteMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingExhibitors(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingExhibitorsByExhibitor(companyId, exhibitorId, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingExhibitorsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetAllMeetingExhibitorsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingExhibitorsByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingExhibitorsByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetAllMeetingExhibitorsByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitorByExhibitor(itemId, companyId, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingExhibitorByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitorByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingExhibitorByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitorCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitorCountByExhibitor(companyId, exhibitorId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitorCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageGetMeetingExhibitorCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingExhibitorCountByMeeting(companyId, meetingId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingExhibitorCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingExhibitorCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingExhibitors(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingExhibitorsByExhibitor(companyId, exhibitorId, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingExhibitorsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderAllMeetingExhibitorsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingExhibitorsByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingExhibitorsByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderAllMeetingExhibitorsByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingExhibitor(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingExhibitorByExhibitor(itemId, companyId, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingExhibitorByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageRenderMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingExhibitorByMeeting(itemId, companyId, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingExhibitorByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetExhibitorMeetings(companyId, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetExhibitorMeetings.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageSetExhibitorMeetings.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetExhibitorMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetMeetingExhibitors(companyId, meetingId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetMeetingExhibitors.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageSetMeetingExhibitors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetMeetingExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingExhibitor(itemId, companyId, meetingExhibitorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingExhibitor.');
      }
      if (meetingExhibitorUpdateSchema === null || meetingExhibitorUpdateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorUpdateSchema was null or undefined when calling manageUpdateMeetingExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingExhibitorByExhibitor(itemId, companyId, exhibitorId, meetingExhibitorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingExhibitorByExhibitor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling manageUpdateMeetingExhibitorByExhibitor.');
      }
      if (meetingExhibitorUpdateSchema === null || meetingExhibitorUpdateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorUpdateSchema was null or undefined when calling manageUpdateMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingExhibitorByMeeting(itemId, companyId, meetingId, meetingExhibitorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingExhibitorByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageUpdateMeetingExhibitorByMeeting.');
      }
      if (meetingExhibitorUpdateSchema === null || meetingExhibitorUpdateSchema === undefined) {
        throw new Error('Required parameter meetingExhibitorUpdateSchema was null or undefined when calling manageUpdateMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingExhibitorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MeetingExhibitorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeetingExhibitorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeetingExhibitorCRUDService,
        factory: MeetingExhibitorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeetingExhibitorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MeetingSpeakerCRUDService = /*#__PURE__*/(() => {
  class MeetingSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMeetingSpeaker(companyId, meetingSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingSpeaker.');
      }
      if (meetingSpeakerCreateSchema === null || meetingSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerCreateSchema was null or undefined when calling manageCreateMeetingSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingSpeakerByMeeting(companyId, meetingId, meetingSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageCreateMeetingSpeakerByMeeting.');
      }
      if (meetingSpeakerCreateSchema === null || meetingSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerCreateSchema was null or undefined when calling manageCreateMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingSpeakerBySpeaker(companyId, speakerId, meetingSpeakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageCreateMeetingSpeakerBySpeaker.');
      }
      if (meetingSpeakerCreateSchema === null || meetingSpeakerCreateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerCreateSchema was null or undefined when calling manageCreateMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingSpeakerByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingSpeakerByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageDeleteMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageDeleteMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingSpeakers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingSpeakersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingSpeakersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetAllMeetingSpeakersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingSpeakersBySpeaker(companyId, speakerId, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetAllMeetingSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeakerByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingSpeakerByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeakerBySpeaker(itemId, companyId, speakerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeakerCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeakerCountByMeeting(companyId, meetingId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeakerCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingSpeakerCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingSpeakerCountBySpeaker(companyId, speakerId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingSpeakerCountBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageGetMeetingSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingSpeakers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingSpeakersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingSpeakersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderAllMeetingSpeakersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingSpeakersBySpeaker(companyId, speakerId, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingSpeakersBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderAllMeetingSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingSpeaker(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingSpeakerByMeeting(itemId, companyId, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingSpeakerByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingSpeakerBySpeaker(itemId, companyId, speakerId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageRenderMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetMeetingSpeakers(companyId, meetingId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetMeetingSpeakers.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageSetMeetingSpeakers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetMeetingSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetSpeakerMeetings(companyId, speakerId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetSpeakerMeetings.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageSetSpeakerMeetings.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetSpeakerMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingSpeaker(itemId, companyId, meetingSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingSpeaker.');
      }
      if (meetingSpeakerUpdateSchema === null || meetingSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerUpdateSchema was null or undefined when calling manageUpdateMeetingSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingSpeakerByMeeting(itemId, companyId, meetingId, meetingSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingSpeakerByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageUpdateMeetingSpeakerByMeeting.');
      }
      if (meetingSpeakerUpdateSchema === null || meetingSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerUpdateSchema was null or undefined when calling manageUpdateMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingSpeakerBySpeaker(itemId, companyId, speakerId, meetingSpeakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingSpeakerBySpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling manageUpdateMeetingSpeakerBySpeaker.');
      }
      if (meetingSpeakerUpdateSchema === null || meetingSpeakerUpdateSchema === undefined) {
        throw new Error('Required parameter meetingSpeakerUpdateSchema was null or undefined when calling manageUpdateMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingSpeakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MeetingSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeetingSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeetingSpeakerCRUDService,
        factory: MeetingSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeetingSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MeetingUserCRUDService = /*#__PURE__*/(() => {
  class MeetingUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMeetingUser(companyId, meetingUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingUser.');
      }
      if (meetingUserCreateSchema === null || meetingUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingUserCreateSchema was null or undefined when calling manageCreateMeetingUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingUserByMeeting(companyId, meetingId, meetingUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageCreateMeetingUserByMeeting.');
      }
      if (meetingUserCreateSchema === null || meetingUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingUserCreateSchema was null or undefined when calling manageCreateMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMeetingUserByUser(companyId, userId, meetingUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateMeetingUserByUser.');
      }
      if (meetingUserCreateSchema === null || meetingUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingUserCreateSchema was null or undefined when calling manageCreateMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingUserByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageDeleteMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMeetingUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMeetingUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingUsers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingUsersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetAllMeetingUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMeetingUsersByUser(companyId, userId, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMeetingUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllMeetingUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUserByMeeting(itemId, companyId, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUserByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMeetingUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUserCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUserCountByMeeting(companyId, meetingId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUserCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageGetMeetingUserCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMeetingUserCountByUser(companyId, userId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMeetingUserCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetMeetingUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingUsers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingUsersByMeeting(companyId, meetingId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderAllMeetingUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMeetingUsersByUser(companyId, userId, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMeetingUsersByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllMeetingUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingUserByMeeting(itemId, companyId, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageRenderMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMeetingUserByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMeetingUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetMeetingUsers(companyId, meetingId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetMeetingUsers.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageSetMeetingUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetMeetingUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserMeetings(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserMeetings.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserMeetings.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingUser(itemId, companyId, meetingUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingUser.');
      }
      if (meetingUserUpdateSchema === null || meetingUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingUserUpdateSchema was null or undefined when calling manageUpdateMeetingUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingUserByMeeting(itemId, companyId, meetingId, meetingUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingUserByMeeting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling manageUpdateMeetingUserByMeeting.');
      }
      if (meetingUserUpdateSchema === null || meetingUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingUserUpdateSchema was null or undefined when calling manageUpdateMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMeetingUserByUser(itemId, companyId, userId, meetingUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMeetingUserByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateMeetingUserByUser.');
      }
      if (meetingUserUpdateSchema === null || meetingUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingUserUpdateSchema was null or undefined when calling manageUpdateMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MeetingUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeetingUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeetingUserCRUDService,
        factory: MeetingUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeetingUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MenuVoiceCRUDService = /*#__PURE__*/(() => {
  class MenuVoiceCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMenuVoice(companyId, menuVoiceCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMenuVoice.');
      }
      if (menuVoiceCreateSchema === null || menuVoiceCreateSchema === undefined) {
        throw new Error('Required parameter menuVoiceCreateSchema was null or undefined when calling manageCreateMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: menuVoiceCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateMenuVoiceByEvent(companyId, eventId, menuVoiceCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMenuVoiceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateMenuVoiceByEvent.');
      }
      if (menuVoiceCreateSchema === null || menuVoiceCreateSchema === undefined) {
        throw new Error('Required parameter menuVoiceCreateSchema was null or undefined when calling manageCreateMenuVoiceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: menuVoiceCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMenuVoice(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMenuVoice.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMenuVoiceByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMenuVoiceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMenuVoiceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteMenuVoiceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMenuVoices(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMenuVoices.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMenuVoicesByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMenuVoicesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllMenuVoicesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMenuVoice(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMenuVoice.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMenuVoiceByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMenuVoiceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMenuVoiceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMenuVoiceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMenuVoiceCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMenuVoiceCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMenuVoiceCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMenuVoiceCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMenuVoiceCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMenuVoices(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMenuVoices.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMenuVoicesByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMenuVoicesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllMenuVoicesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMenuVoice(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMenuVoice.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMenuVoiceByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMenuVoiceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMenuVoiceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderMenuVoiceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMenuVoice(itemId, companyId, menuVoiceUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMenuVoice.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMenuVoice.');
      }
      if (menuVoiceUpdateSchema === null || menuVoiceUpdateSchema === undefined) {
        throw new Error('Required parameter menuVoiceUpdateSchema was null or undefined when calling manageUpdateMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: menuVoiceUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMenuVoiceByEvent(itemId, companyId, eventId, menuVoiceUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMenuVoiceByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMenuVoiceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateMenuVoiceByEvent.');
      }
      if (menuVoiceUpdateSchema === null || menuVoiceUpdateSchema === undefined) {
        throw new Error('Required parameter menuVoiceUpdateSchema was null or undefined when calling manageUpdateMenuVoiceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: menuVoiceUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MenuVoiceCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MenuVoiceCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MenuVoiceCRUDService,
        factory: MenuVoiceCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MenuVoiceCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MerchandiseCategoryCRUDService = /*#__PURE__*/(() => {
  class MerchandiseCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateMerchandiseCategoryByEvent(companyId, eventId, merchandiseCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateMerchandiseCategoryByEvent.');
      }
      if (merchandiseCategoryCreateSchema === null || merchandiseCategoryCreateSchema === undefined) {
        throw new Error('Required parameter merchandiseCategoryCreateSchema was null or undefined when calling manageCreateMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: merchandiseCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteMerchandiseCategoryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteMerchandiseCategoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMerchandiseCategoriesByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMerchandiseCategoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllMerchandiseCategoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMerchandiseCategoriesByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMerchandiseCategoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllMerchandiseCategoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllMerchandiseCategoriesByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllMerchandiseCategoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllMerchandiseCategoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMerchandiseCategoryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetMerchandiseCategoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMerchandiseCategoryCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMerchandiseCategoryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMerchandiseCategoryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMerchandiseCategoryCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMerchandiseCategoryCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMerchandiseCategoryCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMerchandiseCategoryCountByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMerchandiseCategoryCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetMerchandiseCategoryCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMerchandiseCategoriesByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMerchandiseCategoriesByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllMerchandiseCategoriesByEventSearch(companyId, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllMerchandiseCategoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderMerchandiseCategoryByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderMerchandiseCategoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateMerchandiseCategoryByEvent(itemId, companyId, eventId, merchandiseCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateMerchandiseCategoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateMerchandiseCategoryByEvent.');
      }
      if (merchandiseCategoryUpdateSchema === null || merchandiseCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter merchandiseCategoryUpdateSchema was null or undefined when calling manageUpdateMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: merchandiseCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MerchandiseCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MerchandiseCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MerchandiseCategoryCRUDService,
        factory: MerchandiseCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MerchandiseCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let NewsCategoryCRUDService = /*#__PURE__*/(() => {
  class NewsCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateNewsCategory(companyId, newsCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateNewsCategory.');
      }
      if (newsCategoryCreateSchema === null || newsCategoryCreateSchema === undefined) {
        throw new Error('Required parameter newsCategoryCreateSchema was null or undefined when calling manageCreateNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllNewsCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllNewsCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllNewsCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllNewsCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderNewsCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateNewsCategory(itemId, companyId, newsCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateNewsCategory.');
      }
      if (newsCategoryUpdateSchema === null || newsCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter newsCategoryUpdateSchema was null or undefined when calling manageUpdateNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function NewsCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NewsCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NewsCategoryCRUDService,
        factory: NewsCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NewsCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let NewsItemCRUDService = /*#__PURE__*/(() => {
  class NewsItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateNewsItem(companyId, newsItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateNewsItem.');
      }
      if (newsItemCreateSchema === null || newsItemCreateSchema === undefined) {
        throw new Error('Required parameter newsItemCreateSchema was null or undefined when calling manageCreateNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateNewsItemByNewsCategory(companyId, newsCategoryId, newsItemCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageCreateNewsItemByNewsCategory.');
      }
      if (newsItemCreateSchema === null || newsItemCreateSchema === undefined) {
        throw new Error('Required parameter newsItemCreateSchema was null or undefined when calling manageCreateNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsItemCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItemByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItemByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageDeleteNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItemImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItemImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItemImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItemImageByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItemImageByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItemImageByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageDeleteNewsItemImageByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItemPreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItemPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItemPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteNewsItemPreviewByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteNewsItemPreviewByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteNewsItemPreviewByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageDeleteNewsItemPreviewByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadNewsItemImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadNewsItemImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadNewsItemImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadNewsItemImageByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadNewsItemImageByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadNewsItemImageByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageDownloadNewsItemImageByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadNewsItemPreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadNewsItemPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadNewsItemPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadNewsItemPreviewByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadNewsItemPreviewByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadNewsItemPreviewByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageDownloadNewsItemPreviewByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllNewsItems(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllNewsItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllNewsItemsByNewsCategory(companyId, newsCategoryId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllNewsItemsByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageGetAllNewsItemsByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsItem(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetNewsItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsItemByNewsCategory(itemId, companyId, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetNewsItemByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageGetNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsItemCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetNewsItemCountByNewsCategory(companyId, newsCategoryId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetNewsItemCountByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageGetNewsItemCountByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllNewsItems(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllNewsItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllNewsItemsByNewsCategory(companyId, newsCategoryId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllNewsItemsByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageRenderAllNewsItemsByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderNewsItem(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderNewsItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderNewsItemByNewsCategory(itemId, companyId, newsCategoryId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderNewsItemByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageRenderNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateNewsItem(itemId, companyId, newsItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateNewsItem.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateNewsItem.');
      }
      if (newsItemUpdateSchema === null || newsItemUpdateSchema === undefined) {
        throw new Error('Required parameter newsItemUpdateSchema was null or undefined when calling manageUpdateNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateNewsItemByNewsCategory(itemId, companyId, newsCategoryId, newsItemUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateNewsItemByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageUpdateNewsItemByNewsCategory.');
      }
      if (newsItemUpdateSchema === null || newsItemUpdateSchema === undefined) {
        throw new Error('Required parameter newsItemUpdateSchema was null or undefined when calling manageUpdateNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: newsItemUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadNewsItemImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadNewsItemImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemImageBase64ByNewsCategory(itemId, companyId, newsCategoryId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemImageBase64ByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemImageBase64ByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageUploadNewsItemImageBase64ByNewsCategory.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadNewsItemImageBase64ByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemImageByNewsCategory(itemId, companyId, newsCategoryId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemImageByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemImageByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageUploadNewsItemImageByNewsCategory.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadNewsItemImageByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemPreview(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemPreview.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadNewsItemPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemPreviewBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemPreviewBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemPreviewBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadNewsItemPreviewBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemPreviewBase64ByNewsCategory(itemId, companyId, newsCategoryId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemPreviewBase64ByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemPreviewBase64ByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageUploadNewsItemPreviewBase64ByNewsCategory.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadNewsItemPreviewBase64ByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadNewsItemPreviewByNewsCategory(itemId, companyId, newsCategoryId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadNewsItemPreviewByNewsCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadNewsItemPreviewByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling manageUploadNewsItemPreviewByNewsCategory.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadNewsItemPreviewByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function NewsItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NewsItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NewsItemCRUDService,
        factory: NewsItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NewsItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OAuth2ClientService = /*#__PURE__*/(() => {
  class OAuth2ClientService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    oauth2ClientLogin(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling oauth2ClientLogin.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oauth2_client/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/login`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    oauth2ClientLogout(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling oauth2ClientLogout.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oauth2_client/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    oauth2ClientRedirect(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling oauth2ClientRedirect.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oauth2_client/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/redirect`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OAuth2ClientService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OAuth2ClientService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OAuth2ClientService,
        factory: OAuth2ClientService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OAuth2ClientService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OnlineUserService = /*#__PURE__*/(() => {
  class OnlineUserService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageGetCompanyOnlineUserCount(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyOnlineUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/online_user/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetCompanyOnlineUserCount_1(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetCompanyOnlineUserCount_1.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/online_user/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetOnlineUserCount(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/online_user/manage/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetOnlineUserCount_2(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/online_user/manage/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OnlineUserService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OnlineUserService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OnlineUserService,
        factory: OnlineUserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OnlineUserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PDFTemplateCRUDService = /*#__PURE__*/(() => {
  class PDFTemplateCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreatePdfTemplate(companyId, pDFTemplateCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePdfTemplate.');
      }
      if (pDFTemplateCreateSchema === null || pDFTemplateCreateSchema === undefined) {
        throw new Error('Required parameter pDFTemplateCreateSchema was null or undefined when calling manageCreatePdfTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pDFTemplateCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePdfTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePdfTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePdfTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePdfTemplateDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePdfTemplateDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePdfTemplateDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadPdfTemplateDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadPdfTemplateDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadPdfTemplateDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPdfTemplates(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPdfTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPdfTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPdfTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPdfTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPdfTemplateCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPdfTemplateCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPdfTemplates(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPdfTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPdfTemplate(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPdfTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPdfTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePdfTemplate(itemId, companyId, pDFTemplateUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePdfTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePdfTemplate.');
      }
      if (pDFTemplateUpdateSchema === null || pDFTemplateUpdateSchema === undefined) {
        throw new Error('Required parameter pDFTemplateUpdateSchema was null or undefined when calling manageUpdatePdfTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pDFTemplateUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadPdfTemplateDocument(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadPdfTemplateDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadPdfTemplateDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadPdfTemplateDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadPdfTemplateDocumentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadPdfTemplateDocumentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadPdfTemplateDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadPdfTemplateDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/pdf_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PDFTemplateCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PDFTemplateCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PDFTemplateCRUDService,
        factory: PDFTemplateCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PDFTemplateCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ParticipantCategoryCRUDService = /*#__PURE__*/(() => {
  class ParticipantCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateParticipantCategory(companyId, participantCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateParticipantCategory.');
      }
      if (participantCategoryCreateSchema === null || participantCategoryCreateSchema === undefined) {
        throw new Error('Required parameter participantCategoryCreateSchema was null or undefined when calling manageCreateParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: participantCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteParticipantCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteParticipantCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllParticipantCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllParticipantCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetParticipantCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetParticipantCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetParticipantCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetParticipantCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllParticipantCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllParticipantCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderParticipantCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderParticipantCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateParticipantCategory(itemId, companyId, participantCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateParticipantCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateParticipantCategory.');
      }
      if (participantCategoryUpdateSchema === null || participantCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter participantCategoryUpdateSchema was null or undefined when calling manageUpdateParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: participantCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ParticipantCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ParticipantCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ParticipantCategoryCRUDService,
        factory: ParticipantCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ParticipantCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PollCRUDService = /*#__PURE__*/(() => {
  class PollCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreatePoll(companyId, pollCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePoll.');
      }
      if (pollCreateSchema === null || pollCreateSchema === undefined) {
        throw new Error('Required parameter pollCreateSchema was null or undefined when calling manageCreatePoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreatePollByEvent(companyId, eventId, pollCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreatePollByEvent.');
      }
      if (pollCreateSchema === null || pollCreateSchema === undefined) {
        throw new Error('Required parameter pollCreateSchema was null or undefined when calling manageCreatePollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreatePollByEventAndAgenda(companyId, eventId, agendaId, pollCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreatePollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreatePollByEventAndAgenda.');
      }
      if (pollCreateSchema === null || pollCreateSchema === undefined) {
        throw new Error('Required parameter pollCreateSchema was null or undefined when calling manageCreatePollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePoll(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePollByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePollByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeletePollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePollByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePollByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeletePollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeletePollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPolls(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPolls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPollsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPollsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllPollsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPollsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPollsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllPollsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllPollsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPoll(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPollByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPollByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetPollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPollCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollCountByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetPollCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetPollCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    managePollResults(companyId, pollId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling managePollResults.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling managePollResults.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/results`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPolls(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPolls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPollsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPollsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllPollsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPollsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPollsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllPollsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllPollsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPoll(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPollByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPollByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderPollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPollByEventAndAgenda(itemId, companyId, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPollByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderPollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderPollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePoll(itemId, companyId, pollUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePoll.');
      }
      if (pollUpdateSchema === null || pollUpdateSchema === undefined) {
        throw new Error('Required parameter pollUpdateSchema was null or undefined when calling manageUpdatePoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePollByEvent(itemId, companyId, eventId, pollUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePollByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdatePollByEvent.');
      }
      if (pollUpdateSchema === null || pollUpdateSchema === undefined) {
        throw new Error('Required parameter pollUpdateSchema was null or undefined when calling manageUpdatePollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePollByEventAndAgenda(itemId, companyId, eventId, agendaId, pollUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePollByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdatePollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdatePollByEventAndAgenda.');
      }
      if (pollUpdateSchema === null || pollUpdateSchema === undefined) {
        throw new Error('Required parameter pollUpdateSchema was null or undefined when calling manageUpdatePollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PollCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PollCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PollCRUDService,
        factory: PollCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PollCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PollVoteCRUDService = /*#__PURE__*/(() => {
  class PollVoteCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreatePollVote(companyId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePollVote.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling manageCreatePollVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreatePollVoteByPoll(companyId, pollId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageCreatePollVoteByPoll.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling manageCreatePollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreatePollVoteByUser(companyId, userId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePollVoteByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreatePollVoteByUser.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling manageCreatePollVoteByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllPollVotes(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllPollVotes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllPollVotesByPoll(companyId, pollId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllPollVotesByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageDeleteAllPollVotesByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllPollVotesByUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllPollVotesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteAllPollVotesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePollVote(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePollVote.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePollVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePollVoteByPoll(itemId, companyId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePollVoteByPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageDeletePollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePollVoteByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePollVoteByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePollVoteByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeletePollVoteByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPollVotes(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPollVotes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPollVotesByPoll(companyId, pollId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPollVotesByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageGetAllPollVotesByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPollVotesByUser(companyId, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPollVotesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllPollVotesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVote(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPollVote.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVoteByPoll(itemId, companyId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPollVoteByPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageGetPollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVoteByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPollVoteByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVoteByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetPollVoteByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVoteCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVoteCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVoteCountByPoll(companyId, pollId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVoteCountByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageGetPollVoteCountByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPollVoteCountByUser(companyId, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPollVoteCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetPollVoteCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPollVotes(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPollVotes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPollVotesByPoll(companyId, pollId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPollVotesByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageRenderAllPollVotesByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPollVotesByUser(companyId, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPollVotesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllPollVotesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPollVote(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPollVote.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPollVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPollVoteByPoll(itemId, companyId, pollId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPollVoteByPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageRenderPollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPollVoteByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPollVoteByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPollVoteByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderPollVoteByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePollVote(itemId, companyId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePollVote.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePollVote.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling manageUpdatePollVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePollVoteByPoll(itemId, companyId, pollId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePollVoteByPoll.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageUpdatePollVoteByPoll.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling manageUpdatePollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePollVoteByUser(itemId, companyId, userId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePollVoteByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePollVoteByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdatePollVoteByUser.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling manageUpdatePollVoteByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PollVoteCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PollVoteCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PollVoteCRUDService,
        factory: PollVoteCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PollVoteCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PopupCRUDService = /*#__PURE__*/(() => {
  class PopupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreatePopup(companyId, popupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePopup.');
      }
      if (popupCreateSchema === null || popupCreateSchema === undefined) {
        throw new Error('Required parameter popupCreateSchema was null or undefined when calling manageCreatePopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: popupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreatePopupByStream(companyId, streamId, popupCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreatePopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageCreatePopupByStream.');
      }
      if (popupCreateSchema === null || popupCreateSchema === undefined) {
        throw new Error('Required parameter popupCreateSchema was null or undefined when calling manageCreatePopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: popupCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePopup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePopup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeletePopupByStream(itemId, companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeletePopupByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeletePopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDeletePopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPopups(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPopups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllPopupsByStream(companyId, streamId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllPopupsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetAllPopupsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPopup(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPopup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPopupByStream(itemId, companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetPopupByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetPopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPopupCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPopupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetPopupCountByStream(companyId, streamId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetPopupCountByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetPopupCountByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPopups(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPopups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllPopupsByStream(companyId, streamId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllPopupsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageRenderAllPopupsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPopup(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPopup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderPopupByStream(itemId, companyId, streamId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderPopupByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderPopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageRenderPopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePopup(itemId, companyId, popupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePopup.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePopup.');
      }
      if (popupUpdateSchema === null || popupUpdateSchema === undefined) {
        throw new Error('Required parameter popupUpdateSchema was null or undefined when calling manageUpdatePopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: popupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdatePopupByStream(itemId, companyId, streamId, popupUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdatePopupByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdatePopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageUpdatePopupByStream.');
      }
      if (popupUpdateSchema === null || popupUpdateSchema === undefined) {
        throw new Error('Required parameter popupUpdateSchema was null or undefined when calling manageUpdatePopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: popupUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PopupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PopupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PopupCRUDService,
        factory: PopupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PopupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PresenceService = /*#__PURE__*/(() => {
  class PresenceService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    postChannelActionLogoutChannelsChannelIdActionLogoutPost(channelId, observe = 'body', reportProgress = false, options) {
      if (channelId === null || channelId === undefined) {
        throw new Error('Required parameter channelId was null or undefined when calling postChannelActionLogoutChannelsChannelIdActionLogoutPost.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/channels/${this.configuration.encodeParam({
        name: 'channelId',
        value: channelId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/action/logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    postChannelActionRedirectChannelsChannelIdActionRedirectPost(channelId, redirectInput, observe = 'body', reportProgress = false, options) {
      if (channelId === null || channelId === undefined) {
        throw new Error('Required parameter channelId was null or undefined when calling postChannelActionRedirectChannelsChannelIdActionRedirectPost.');
      }
      if (redirectInput === null || redirectInput === undefined) {
        throw new Error('Required parameter redirectInput was null or undefined when calling postChannelActionRedirectChannelsChannelIdActionRedirectPost.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/channels/${this.configuration.encodeParam({
        name: 'channelId',
        value: channelId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/action/redirect`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: redirectInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    postChannelActionReloadChannelsChannelIdActionReloadPost(channelId, observe = 'body', reportProgress = false, options) {
      if (channelId === null || channelId === undefined) {
        throw new Error('Required parameter channelId was null or undefined when calling postChannelActionReloadChannelsChannelIdActionReloadPost.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/channels/${this.configuration.encodeParam({
        name: 'channelId',
        value: channelId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/action/reload`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PresenceService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PresenceService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PresenceService,
        factory: PresenceService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PresenceService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadAgendaDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaDocumentByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadAgendaDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaIcon(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaIcon.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaIconByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaIconByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaIconByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadAgendaIconByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaMapImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaMapImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaMapImageByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaMapImageByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaMapImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadAgendaMapImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaPrimaryImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaPrimaryImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaPrimaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaPrimaryImageByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaPrimaryImageByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaPrimaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadAgendaPrimaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/primary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaSecondaryImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaSecondaryImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaSecondaryImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadAgendaSecondaryImageByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadAgendaSecondaryImageByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadAgendaSecondaryImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadAgendaSecondaryImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/secondary_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgenda(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCountByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaInCollaborationList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaInCollaborationList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaInCollaborationList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_in_collaboration_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaModeratorList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaModeratorList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaModeratorList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_moderator_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaOrganizerList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaOrganizerList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaOrganizerList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_organizer_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaParticipationTypeList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaParticipationTypeList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaParticipationTypeList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_participation_type_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaRoomList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaRoomList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaRoomList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_room_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaSpeakerList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSponsorshipList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSponsorshipList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaSponsorshipList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_sponsorship_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendas(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendasByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendasByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllAgendasByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendasByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendasByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllAgendasByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendasByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendasByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllAgendasByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendasMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendasMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendasSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendasSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgenda(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAgendaByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendas(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendasByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendasByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllAgendasByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendasByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendasByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllAgendasByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendasByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendasByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllAgendasByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendasMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendasMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendasSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendasSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaCRUDService,
        factory: PublicAgendaCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAgendaCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCategoryAgendas(subdomain, agendaCategoryId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCategoryAgendas.');
      }
      if (agendaCategoryId === null || agendaCategoryId === undefined) {
        throw new Error('Required parameter agendaCategoryId was null or undefined when calling getAgendaCategoryAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'agendaCategoryId',
        value: agendaCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaCategoryCRUDService,
        factory: PublicAgendaCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaFlowCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaFlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAgendaFlowByAgenda(itemId, subdomain, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaFlowByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaFlowCountByAgenda(subdomain, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaFlowCountByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAgendaFlowCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaFlowsByAgenda(subdomain, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaFlowsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllAgendaFlowsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaFlowByAgenda(itemId, subdomain, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaFlowByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaFlowByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAgendaFlowByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaFlowsByAgenda(subdomain, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaFlowsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllAgendaFlowsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaFlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaFlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaFlowCRUDService,
        factory: PublicAgendaFlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaFlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaSpeakerCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAgendaSpeaker(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerByAgenda(itemId, agendaId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAgendaSpeakerByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerBySpeaker(itemId, speakerId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getAgendaSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerCountByAgenda(agendaId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAgendaSpeakerCountByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSpeakerCountBySpeaker(speakerId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getAgendaSpeakerCountBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaSpeakers(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaSpeakersByAgenda(agendaId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllAgendaSpeakersByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaSpeakersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getAllAgendaSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaSpeaker(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaSpeakerByAgenda(itemId, agendaId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaSpeakerByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAgendaSpeakerByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaSpeakerByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaSpeakerBySpeaker(itemId, speakerId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderAgendaSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaSpeakers(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaSpeakersByAgenda(agendaId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllAgendaSpeakersByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaSpeakersByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderAllAgendaSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaSpeakerCRUDService,
        factory: PublicAgendaSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaSubcategoryCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaSubcategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAgendaSubcategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaSubcategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSubcategoryAgendas(subdomain, agendaSubcategoryId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSubcategoryAgendas.');
      }
      if (agendaSubcategoryId === null || agendaSubcategoryId === undefined) {
        throw new Error('Required parameter agendaSubcategoryId was null or undefined when calling getAgendaSubcategoryAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'agendaSubcategoryId',
        value: agendaSubcategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaSubcategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaSubcategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaSubcategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaSubcategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaSubcategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaSubcategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/agenda_subcategory/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaSubcategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaSubcategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaSubcategoryCRUDService,
        factory: PublicAgendaSubcategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaSubcategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicAgendaUserCRUDService = /*#__PURE__*/(() => {
  class PublicAgendaUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createAgendaUserByEvent(subdomain, eventId, agendaUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createAgendaUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createAgendaUserByEvent.');
      }
      if (agendaUserCreateSchema === null || agendaUserCreateSchema === undefined) {
        throw new Error('Required parameter agendaUserCreateSchema was null or undefined when calling createAgendaUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createAgendaUserByUser(userId, subdomain, agendaUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling createAgendaUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createAgendaUserByUser.');
      }
      if (agendaUserCreateSchema === null || agendaUserCreateSchema === undefined) {
        throw new Error('Required parameter agendaUserCreateSchema was null or undefined when calling createAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: agendaUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAgendaUserByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteAgendaUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAgendaUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteAgendaUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAgendaUserByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteAgendaUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllAgendaUsersByEvent(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllAgendaUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteAllAgendaUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllAgendaUsersByUser(userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteAllAgendaUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllAgendaUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaUserByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaUserByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAgendaUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaUserCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaUserCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAgendaUserCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAgendaUserCountByUser(userId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAgendaUserCountByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAgendaUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaUsersByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllAgendaUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllAgendaUsersByUser(userId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAllAgendaUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllAgendaUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicSetUserAgendas(subdomain, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicSetUserAgendas.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicSetUserAgendas.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling publicSetUserAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaUserByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAgendaUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAgendaUserByUser(itemId, userId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderAgendaUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAgendaUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAgendaUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaUsersByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllAgendaUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllAgendaUsersByUser(userId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAllAgendaUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllAgendaUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicAgendaUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicAgendaUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicAgendaUserCRUDService,
        factory: PublicAgendaUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicAgendaUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicCarReservationCRUDService = /*#__PURE__*/(() => {
  class PublicCarReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createCarReservationByEvent(subdomain, eventId, carReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createCarReservationByEvent.');
      }
      if (carReservationCreateSchema === null || carReservationCreateSchema === undefined) {
        throw new Error('Required parameter carReservationCreateSchema was null or undefined when calling createCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCarReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteCarReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadCarReservationVoucherByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadCarReservationVoucherByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadCarReservationVoucherByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadCarReservationVoucherByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/voucher`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllCarReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllCarReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllCarReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllCarReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllCarReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllCarReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCarReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getCarReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCarReservationCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCarReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getCarReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCarReservationCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCarReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getCarReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllCarReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllCarReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllCarReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllCarReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllCarReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllCarReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderCarReservationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderCarReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateCarReservationByEvent(itemId, subdomain, eventId, carReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateCarReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateCarReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateCarReservationByEvent.');
      }
      if (carReservationUpdateSchema === null || carReservationUpdateSchema === undefined) {
        throw new Error('Required parameter carReservationUpdateSchema was null or undefined when calling updateCarReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/car_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: carReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicCarReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicCarReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicCarReservationCRUDService,
        factory: PublicCarReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicCarReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicChatGroupCRUDService = /*#__PURE__*/(() => {
  class PublicChatGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllChatGroups(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllChatGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatGroup(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getChatGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatGroupCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatGroupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllChatGroups(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllChatGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderChatGroup(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderChatGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicChatGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicChatGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicChatGroupCRUDService,
        factory: PublicChatGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicChatGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicChatRoomCRUDService = /*#__PURE__*/(() => {
  class PublicChatRoomCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createChatRoomByChatGroup(subdomain, chatGroupId, chatRoomCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling createChatRoomByChatGroup.');
      }
      if (chatRoomCreateSchema === null || chatRoomCreateSchema === undefined) {
        throw new Error('Required parameter chatRoomCreateSchema was null or undefined when calling createChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllChatRoomsByChatGroup(subdomain, chatGroupId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllChatRoomsByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling getAllChatRoomsByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatRoomByChatGroup(itemId, subdomain, chatGroupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getChatRoomByChatGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling getChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatRoomCountByChatGroup(subdomain, chatGroupId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatRoomCountByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling getChatRoomCountByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllChatRoomsByChatGroup(subdomain, chatGroupId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllChatRoomsByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling renderAllChatRoomsByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderChatRoomByChatGroup(itemId, subdomain, chatGroupId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderChatRoomByChatGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderChatRoomByChatGroup.');
      }
      if (chatGroupId === null || chatGroupId === undefined) {
        throw new Error('Required parameter chatGroupId was null or undefined when calling renderChatRoomByChatGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_group/${this.configuration.encodeParam({
        name: 'chatGroupId',
        value: chatGroupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicChatRoomCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicChatRoomCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicChatRoomCRUDService,
        factory: PublicChatRoomCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicChatRoomCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicChatRoomMessageCRUDService = /*#__PURE__*/(() => {
  class PublicChatRoomMessageCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createChatRoomMessageByChatRoom(subdomain, chatRoomId, chatRoomMessageCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling createChatRoomMessageByChatRoom.');
      }
      if (chatRoomMessageCreateSchema === null || chatRoomMessageCreateSchema === undefined) {
        throw new Error('Required parameter chatRoomMessageCreateSchema was null or undefined when calling createChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomMessageCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllChatRoomMessagesByChatRoom(subdomain, chatRoomId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllChatRoomMessagesByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling getAllChatRoomMessagesByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatRoomMessageByChatRoom(itemId, subdomain, chatRoomId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getChatRoomMessageByChatRoom.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling getChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getChatRoomMessageCountByChatRoom(subdomain, chatRoomId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getChatRoomMessageCountByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling getChatRoomMessageCountByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllChatRoomMessagesByChatRoom(subdomain, chatRoomId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllChatRoomMessagesByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling renderAllChatRoomMessagesByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderChatRoomMessageByChatRoom(itemId, subdomain, chatRoomId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderChatRoomMessageByChatRoom.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling renderChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateChatRoomMessageByChatRoom(itemId, subdomain, chatRoomId, chatRoomMessageUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateChatRoomMessageByChatRoom.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateChatRoomMessageByChatRoom.');
      }
      if (chatRoomId === null || chatRoomId === undefined) {
        throw new Error('Required parameter chatRoomId was null or undefined when calling updateChatRoomMessageByChatRoom.');
      }
      if (chatRoomMessageUpdateSchema === null || chatRoomMessageUpdateSchema === undefined) {
        throw new Error('Required parameter chatRoomMessageUpdateSchema was null or undefined when calling updateChatRoomMessageByChatRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/chat_room/${this.configuration.encodeParam({
        name: 'chatRoomId',
        value: chatRoomId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/chat_room_message/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: chatRoomMessageUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicChatRoomMessageCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicChatRoomMessageCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicChatRoomMessageCRUDService,
        factory: PublicChatRoomMessageCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicChatRoomMessageCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicCompanyFileCRUDService = /*#__PURE__*/(() => {
  class PublicCompanyFileCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadCompanyFileAsset(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadCompanyFileAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadCompanyFileAsset.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadCompanyFilePreview(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadCompanyFilePreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadCompanyFilePreview.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllCompanyFiles(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllCompanyFiles.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCompanyFile(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getCompanyFile.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCompanyFile.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCompanyFileCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCompanyFileCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCompanyFilePublicData(filename, fileKey, observe = 'body', reportProgress = false, options) {
      if (filename === null || filename === undefined) {
        throw new Error('Required parameter filename was null or undefined when calling getCompanyFilePublicData.');
      }
      if (fileKey === null || fileKey === undefined) {
        throw new Error('Required parameter fileKey was null or undefined when calling getCompanyFilePublicData.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/file/public/${this.configuration.encodeParam({
        name: 'fileKey',
        value: fileKey,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'filename',
        value: filename,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllCompanyFiles(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllCompanyFiles.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderCompanyFile(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderCompanyFile.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderCompanyFile.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_file/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicCompanyFileCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicCompanyFileCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicCompanyFileCRUDService,
        factory: PublicCompanyFileCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicCompanyFileCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicDeviceTokenCRUDService = /*#__PURE__*/(() => {
  class PublicDeviceTokenCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createDeviceToken(subdomain, deviceTokenCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createDeviceToken.');
      }
      if (deviceTokenCreateSchema === null || deviceTokenCreateSchema === undefined) {
        throw new Error('Required parameter deviceTokenCreateSchema was null or undefined when calling createDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteDeviceToken(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteDeviceToken.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDeviceTokens(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDeviceTokens.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDeviceTokensMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDeviceTokensMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDeviceToken(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getDeviceToken.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDeviceTokenCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDeviceTokenCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDeviceTokenCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDeviceTokenCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDeviceTokens(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDeviceTokens.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDeviceTokensMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDeviceTokensMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderDeviceToken(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderDeviceToken.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateDeviceToken(itemId, subdomain, deviceTokenUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateDeviceToken.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateDeviceToken.');
      }
      if (deviceTokenUpdateSchema === null || deviceTokenUpdateSchema === undefined) {
        throw new Error('Required parameter deviceTokenUpdateSchema was null or undefined when calling updateDeviceToken.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//device_token/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: deviceTokenUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicDeviceTokenCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicDeviceTokenCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicDeviceTokenCRUDService,
        factory: PublicDeviceTokenCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicDeviceTokenCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicDocumentCRUDService = /*#__PURE__*/(() => {
  class PublicDocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadDocumentResource(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentResource.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentResource.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadDocumentResourceByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentResourceByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentResourceByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadDocumentResourceByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadDocumentResourceByFirm(itemId, subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentResourceByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentResourceByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling downloadDocumentResourceByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/resource`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadDocumentThumbnail(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentThumbnail.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentThumbnail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadDocumentThumbnailByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentThumbnailByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentThumbnailByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadDocumentThumbnailByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadDocumentThumbnailByFirm(itemId, subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadDocumentThumbnailByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadDocumentThumbnailByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling downloadDocumentThumbnailByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDocuments(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDocumentsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDocumentsByFirm(subdomain, firmId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDocumentsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getAllDocumentsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentByFirm(itemId, subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getDocumentByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getDocumentCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentCountByFirm(subdomain, firmId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentCountByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getDocumentCountByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDocuments(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDocumentsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDocumentsByFirm(subdomain, firmId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDocumentsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling renderAllDocumentsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderDocument(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderDocumentByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderDocumentByFirm(itemId, subdomain, firmId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderDocumentByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderDocumentByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling renderDocumentByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicDocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicDocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicDocumentCRUDService,
        factory: PublicDocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicDocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicDocumentRatingCRUDService = /*#__PURE__*/(() => {
  class PublicDocumentRatingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createDocumentRating(subdomain, documentId, documentRatingCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createDocumentRating.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling createDocumentRating.');
      }
      if (documentRatingCreateSchema === null || documentRatingCreateSchema === undefined) {
        throw new Error('Required parameter documentRatingCreateSchema was null or undefined when calling createDocumentRating.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteDocumentRating(itemId, subdomain, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteDocumentRating.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteDocumentRating.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling deleteDocumentRating.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllDocumentRatings(subdomain, documentId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllDocumentRatings.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling getAllDocumentRatings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentRating(itemId, subdomain, documentId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getDocumentRating.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentRating.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling getDocumentRating.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDocumentRatingCount(subdomain, documentId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getDocumentRatingCount.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling getDocumentRatingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllDocumentRatings(subdomain, documentId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllDocumentRatings.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling renderAllDocumentRatings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderDocumentRating(itemId, subdomain, documentId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderDocumentRating.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderDocumentRating.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling renderDocumentRating.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateDocumentRating(itemId, subdomain, documentId, documentRatingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateDocumentRating.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateDocumentRating.');
      }
      if (documentId === null || documentId === undefined) {
        throw new Error('Required parameter documentId was null or undefined when calling updateDocumentRating.');
      }
      if (documentRatingUpdateSchema === null || documentRatingUpdateSchema === undefined) {
        throw new Error('Required parameter documentRatingUpdateSchema was null or undefined when calling updateDocumentRating.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/document/${this.configuration.encodeParam({
        name: 'documentId',
        value: documentId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document_rating/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: documentRatingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicDocumentRatingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicDocumentRatingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicDocumentRatingCRUDService,
        factory: PublicDocumentRatingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicDocumentRatingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventCRUDService = /*#__PURE__*/(() => {
  class PublicEventCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadEventAgendaBanner(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventAgendaBanner.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventAgendaBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventAgendaSecondaryBanner(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventAgendaSecondaryBanner.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventAgendaSecondaryBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda_secondary_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventCityPicture(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventCityPicture.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventCityPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/city_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventCmsBanner(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventCmsBanner.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventCmsBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/cms_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventDesktopBanner(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventDesktopBanner.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventDesktopBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/desktop_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventMobileBanner(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventMobileBanner.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventMobileBanner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mobile_banner`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventPrivacyPolicyDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventPrivacyPolicyDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventPrivacyPolicyDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/privacy_policy_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEvents(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEvent(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCategories(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakers(subdomain, eventId, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakers.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSubcategories(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSubcategories.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEvents(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEvent(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventCRUDService,
        factory: PublicEventCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicEventCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCategoryEvents(subdomain, eventCategoryId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCategoryEvents.');
      }
      if (eventCategoryId === null || eventCategoryId === undefined) {
        throw new Error('Required parameter eventCategoryId was null or undefined when calling getEventCategoryEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'eventCategoryId',
        value: eventCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventCategoryCRUDService,
        factory: PublicEventCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventChatGroupCRUDService = /*#__PURE__*/(() => {
  class PublicEventChatGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventChatGroupsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventChatGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventChatGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventChatGroupByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventChatGroupByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventChatGroupCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventChatGroupCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventChatGroupCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventChatGroupsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventChatGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventChatGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventChatGroupByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventChatGroupByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventChatGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventChatGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_chat_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventChatGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventChatGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventChatGroupCRUDService,
        factory: PublicEventChatGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventChatGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventCheckInCRUDService = /*#__PURE__*/(() => {
  class PublicEventCheckInCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    deleteEventCheckIn(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventCheckIn.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventCheckIn.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventCheckInByEventUser(itemId, subdomain, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventCheckInByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteEventCheckInByEventUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventCheckIns(subdomain, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventCheckInsByEventUser(subdomain, eventUserId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventCheckInsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllEventCheckInsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCheckIn(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventCheckIn.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCheckIn.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCheckInByEventUser(itemId, subdomain, eventUserId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventCheckInByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getEventCheckInByEventUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCheckInCount(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCheckInCount.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCheckInCountByEventUser(subdomain, eventUserId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCheckInCountByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getEventCheckInCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventCheckIns(subdomain, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventCheckIns.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventCheckInsByEventUser(subdomain, eventUserId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventCheckInsByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllEventCheckInsByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventCheckIn(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventCheckIn.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventCheckInByEventUser(itemId, subdomain, eventUserId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventCheckInByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventCheckInByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderEventCheckInByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_check_in/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventCheckInCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventCheckInCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventCheckInCRUDService,
        factory: PublicEventCheckInCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventCheckInCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventFlowCRUDService = /*#__PURE__*/(() => {
  class PublicEventFlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventFlowsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventFlowsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventFlowsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventFlowByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventFlowByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventFlowCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventFlowCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventFlowCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventFlowsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventFlowsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventFlowsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventFlowByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventFlowByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventFlowByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventFlowByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventFlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventFlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventFlowCRUDService,
        factory: PublicEventFlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventFlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventGameCRUDService = /*#__PURE__*/(() => {
  class PublicEventGameCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventGames(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventGamesByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGamesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventGamesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGame(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGameByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventGameCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGames(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGamesByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGamesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventGamesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGame(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGameByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGameByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGameByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventGameByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventGameCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventGameCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventGameCRUDService,
        factory: PublicEventGameCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventGameCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventGameUserScoreCRUDService = /*#__PURE__*/(() => {
  class PublicEventGameUserScoreCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createEventGameUserScore(subdomain, eventGameUserScoreCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventGameUserScore.');
      }
      if (eventGameUserScoreCreateSchema === null || eventGameUserScoreCreateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreCreateSchema was null or undefined when calling createEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createEventGameUserScoreByEventGame(subdomain, eventGameId, eventGameUserScoreCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling createEventGameUserScoreByEventGame.');
      }
      if (eventGameUserScoreCreateSchema === null || eventGameUserScoreCreateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreCreateSchema was null or undefined when calling createEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventGameUserScore(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventGameUserScore.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventGameUserScoreByEventGame(itemId, subdomain, eventGameId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventGameUserScoreByEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling deleteEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventGameUserScores(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGameUserScores.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventGameUserScoresByEventGame(subdomain, eventGameId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGameUserScoresByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling getAllEventGameUserScoresByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameUserScore(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGameUserScore.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameUserScoreByEventGame(itemId, subdomain, eventGameId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGameUserScoreByEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling getEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameUserScoreCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameUserScoreCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGameUserScoreCountByEventGame(subdomain, eventGameId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGameUserScoreCountByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling getEventGameUserScoreCountByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGameUserScores(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGameUserScores.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGameUserScoresByEventGame(subdomain, eventGameId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGameUserScoresByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling renderAllEventGameUserScoresByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGameUserScore(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGameUserScore.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGameUserScoreByEventGame(itemId, subdomain, eventGameId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGameUserScoreByEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling renderEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEventGameUserScore(itemId, subdomain, eventGameUserScoreUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateEventGameUserScore.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateEventGameUserScore.');
      }
      if (eventGameUserScoreUpdateSchema === null || eventGameUserScoreUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreUpdateSchema was null or undefined when calling updateEventGameUserScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEventGameUserScoreByEventGame(itemId, subdomain, eventGameId, eventGameUserScoreUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateEventGameUserScoreByEventGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateEventGameUserScoreByEventGame.');
      }
      if (eventGameId === null || eventGameId === undefined) {
        throw new Error('Required parameter eventGameId was null or undefined when calling updateEventGameUserScoreByEventGame.');
      }
      if (eventGameUserScoreUpdateSchema === null || eventGameUserScoreUpdateSchema === undefined) {
        throw new Error('Required parameter eventGameUserScoreUpdateSchema was null or undefined when calling updateEventGameUserScoreByEventGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_game/${this.configuration.encodeParam({
        name: 'eventGameId',
        value: eventGameId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_game_user_score/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventGameUserScoreUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventGameUserScoreCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventGameUserScoreCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventGameUserScoreCRUDService,
        factory: PublicEventGameUserScoreCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventGameUserScoreCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventGroupCRUDService = /*#__PURE__*/(() => {
  class PublicEventGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventGroupsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventGroupsByGroup(subdomain, groupId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventGroupsByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling getAllEventGroupsByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGroupByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGroupByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGroupByGroup(itemId, subdomain, groupId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventGroupByGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling getEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGroupCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGroupCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventGroupCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventGroupCountByGroup(subdomain, groupId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventGroupCountByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling getEventGroupCountByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGroupsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGroupsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventGroupsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventGroupsByGroup(subdomain, groupId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventGroupsByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling renderAllEventGroupsByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGroupByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGroupByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGroupByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventGroupByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventGroupByGroup(itemId, subdomain, groupId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventGroupByGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventGroupByGroup.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling renderEventGroupByGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventGroupCRUDService,
        factory: PublicEventGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventHotelRoomCRUDService = /*#__PURE__*/(() => {
  class PublicEventHotelRoomCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventHotelRooms(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventHotelRooms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventHotelRoomsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventHotelRoomsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventHotelRoomsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventHotelRoom(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventHotelRoom.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventHotelRoom.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventHotelRoomByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventHotelRoomByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventHotelRoomByEvent.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventHotelRoomCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventHotelRoomCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_hotel_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventHotelRoomCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventHotelRoomCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventHotelRoomCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventHotelRooms(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventHotelRooms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_hotel_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventHotelRoomsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventHotelRoomsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventHotelRoomsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventHotelRoom(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventHotelRoom.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventHotelRoom.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventHotelRoomByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventHotelRoomByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventHotelRoomByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventHotelRoomByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_hotel_room/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventHotelRoomCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventHotelRoomCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventHotelRoomCRUDService,
        factory: PublicEventHotelRoomCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventHotelRoomCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventPictureCRUDService = /*#__PURE__*/(() => {
  class PublicEventPictureCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createEventPicture(subdomain, eventId, eventPictureCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createEventPicture.');
      }
      if (eventPictureCreateSchema === null || eventPictureCreateSchema === undefined) {
        throw new Error('Required parameter eventPictureCreateSchema was null or undefined when calling createEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventPictureCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventPictureAsset(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventPictureAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventPictureAsset(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventPictureAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventPictures(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventPictures.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventPictures.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPicture(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventPicture.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPictureCount(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPictureCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventPictureCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventPictures(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventPictures.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventPictures.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventPicture(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventPicture.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventPicture.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventPicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadEventPictureAsset(itemId, subdomain, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadEventPictureAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadEventPictureAsset.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadEventPictureAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadEventPictureAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadEventPictureAssetBase64(itemId, subdomain, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadEventPictureAssetBase64.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadEventPictureAssetBase64.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadEventPictureAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadEventPictureAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_picture/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventPictureCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventPictureCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventPictureCRUDService,
        factory: PublicEventPictureCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventPictureCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventPlaylistCRUDService = /*#__PURE__*/(() => {
  class PublicEventPlaylistCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadEventPlaylistItemAsset(itemId, subdomain, eventPlaylistId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadEventPlaylistItemAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventPlaylistItemAsset.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling downloadEventPlaylistItemAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventPlaylistItems(subdomain, eventPlaylistId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventPlaylistItems.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling getAllEventPlaylistItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventPlaylists(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventPlaylists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventPlaylistsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventPlaylistsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventPlaylistsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylist(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventPlaylist.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylistByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventPlaylistByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylistCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylistCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylistCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylistCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventPlaylistCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylistItem(itemId, subdomain, eventPlaylistId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventPlaylistItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylistItem.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling getEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventPlaylistItemCount(subdomain, eventPlaylistId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventPlaylistItemCount.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling getEventPlaylistItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventPlaylistItems(subdomain, eventPlaylistId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventPlaylistItems.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling renderAllEventPlaylistItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventPlaylists(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventPlaylists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventPlaylistsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventPlaylistsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventPlaylistsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventPlaylist(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventPlaylist.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventPlaylist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventPlaylistByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventPlaylistByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventPlaylistByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventPlaylistByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventPlaylistItem(itemId, subdomain, eventPlaylistId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventPlaylistItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventPlaylistItem.');
      }
      if (eventPlaylistId === null || eventPlaylistId === undefined) {
        throw new Error('Required parameter eventPlaylistId was null or undefined when calling renderEventPlaylistItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_playlist/${this.configuration.encodeParam({
        name: 'eventPlaylistId',
        value: eventPlaylistId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_playlist_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventPlaylistCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventPlaylistCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventPlaylistCRUDService,
        factory: PublicEventPlaylistCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventPlaylistCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventSpeakerCRUDService = /*#__PURE__*/(() => {
  class PublicEventSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventSpeakers(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventSpeakersByEvent(eventId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventSpeakersByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSpeakersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getAllEventSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeaker(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakerByEvent(itemId, eventId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSpeakerByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakerBySpeaker(itemId, speakerId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getEventSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakerCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakerCountByEvent(eventId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSpeakerCountByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakerCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSpeakerCountBySpeaker(speakerId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getEventSpeakerCountBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSpeakers(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSpeakersByEvent(eventId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventSpeakersByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSpeakersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderAllEventSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSpeaker(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSpeakerByEvent(itemId, eventId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSpeakerByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventSpeakerByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSpeakerByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSpeakerBySpeaker(itemId, speakerId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderEventSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventSpeakerCRUDService,
        factory: PublicEventSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventSponsorCRUDService = /*#__PURE__*/(() => {
  class PublicEventSponsorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventSponsorsByEventSponsorTier(subdomain, eventSponsorTierId, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSponsorsByEventSponsorTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling getAllEventSponsorsByEventSponsorTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSponsorByEventSponsorTier(itemId, subdomain, eventSponsorTierId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSponsorByEventSponsorTier.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSponsorByEventSponsorTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling getEventSponsorByEventSponsorTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSponsorCountByEventSponsorTier(subdomain, eventSponsorTierId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSponsorCountByEventSponsorTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling getEventSponsorCountByEventSponsorTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSponsorsByEventSponsorTier(subdomain, eventSponsorTierId, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSponsorsByEventSponsorTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling renderAllEventSponsorsByEventSponsorTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSponsorByEventSponsorTier(itemId, subdomain, eventSponsorTierId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSponsorByEventSponsorTier.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSponsorByEventSponsorTier.');
      }
      if (eventSponsorTierId === null || eventSponsorTierId === undefined) {
        throw new Error('Required parameter eventSponsorTierId was null or undefined when calling renderEventSponsorByEventSponsorTier.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'eventSponsorTierId',
        value: eventSponsorTierId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventSponsorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventSponsorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventSponsorCRUDService,
        factory: PublicEventSponsorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventSponsorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventSponsorTierCRUDService = /*#__PURE__*/(() => {
  class PublicEventSponsorTierCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventSponsorTiersByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSponsorTiersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventSponsorTiersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSponsorTierByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSponsorTierByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSponsorTierCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSponsorTierCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventSponsorTierCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSponsorTiersByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSponsorTiersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventSponsorTiersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSponsorTierByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSponsorTierByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSponsorTierByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventSponsorTierByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_sponsor_tier/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventSponsorTierCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventSponsorTierCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventSponsorTierCRUDService,
        factory: PublicEventSponsorTierCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventSponsorTierCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventSubcategoryCRUDService = /*#__PURE__*/(() => {
  class PublicEventSubcategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllEventSubcategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSubcategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventSubcategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSubcategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSubcategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventSubcategoryEvents(subdomain, eventSubcategoryId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventSubcategoryEvents.');
      }
      if (eventSubcategoryId === null || eventSubcategoryId === undefined) {
        throw new Error('Required parameter eventSubcategoryId was null or undefined when calling getEventSubcategoryEvents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'eventSubcategoryId',
        value: eventSubcategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventSubcategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventSubcategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventSubcategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventSubcategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventSubcategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_subcategory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventSubcategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventSubcategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventSubcategoryCRUDService,
        factory: PublicEventSubcategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventSubcategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventUserCRUDService = /*#__PURE__*/(() => {
  class PublicEventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createEventUserByEvent(subdomain, eventId, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createEventUserByEvent.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling createEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createEventUserByUser(subdomain, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventUserByUser.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling createEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllEventUsersByEvent(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllEventUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteAllEventUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllEventUsersByUser(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllEventUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventUserByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEventUserByUser(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteEventUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUsersByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUsersByEventMulti(subdomain, eventId, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUsersByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventUsersByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUsersByUser(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUsersByUserMulti(subdomain, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUsersByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserByUser(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserCountByEventMulti(subdomain, eventId, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserCountByUser(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserCountByUserMulti(subdomain, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    lookupEventUser(subdomain, eventId, key, provider, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling lookupEventUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling lookupEventUser.');
      }
      if (key === null || key === undefined) {
        throw new Error('Required parameter key was null or undefined when calling lookupEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, key, 'key');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, provider, 'provider');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_lookup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUsersByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUsersByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventUsersByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUsersByEventMulti(subdomain, eventId, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUsersByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventUsersByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUsersByUser(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUsersByUserMulti(subdomain, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUsersByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventUserByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventUserByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventUserByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventUserByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventUserByUser(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserEventUsers(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserEventUsers.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserExhibitorProducts(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserExhibitorProducts.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserExhibitorProducts.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_products`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserExhibitors(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserExhibitors.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserExhibitors.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitors`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserRestaurants(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserRestaurants.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserRestaurants.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserRestaurants.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurants`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventUserCRUDService,
        factory: PublicEventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicEventUserDirectoryService = /*#__PURE__*/(() => {
  class PublicEventUserDirectoryService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createEventUserDirectoryByEvent(subdomain, eventId, eventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createEventUserDirectoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createEventUserDirectoryByEvent.');
      }
      if (eventUserCreateSchema === null || eventUserCreateSchema === undefined) {
        throw new Error('Required parameter eventUserCreateSchema was null or undefined when calling createEventUserDirectoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUserDirectoriesByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUserDirectoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventUserDirectoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUserDirectoriesByEventMulti(subdomain, eventId, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUserDirectoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventUserDirectoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllEventUserDirectoriesByEventSearch(subdomain, eventId, orderBy, searchTermList, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllEventUserDirectoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllEventUserDirectoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserDirectoryByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getEventUserDirectoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserDirectoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserDirectoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserDirectoryCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserDirectoryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserDirectoryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserDirectoryCountByEventMulti(subdomain, eventId, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserDirectoryCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserDirectoryCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventUserDirectoryCountByEventSearch(subdomain, eventId, orderBy, searchTermList, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventUserDirectoryCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventUserDirectoryCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUserDirectoriesByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUserDirectoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventUserDirectoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUserDirectoriesByEventMulti(subdomain, eventId, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUserDirectoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventUserDirectoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllEventUserDirectoriesByEventSearch(subdomain, eventId, orderBy, searchTermList, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllEventUserDirectoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllEventUserDirectoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderEventUserDirectoryByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderEventUserDirectoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderEventUserDirectoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderEventUserDirectoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEventUserDirectoryByEvent(itemId, subdomain, eventId, eventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateEventUserDirectoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateEventUserDirectoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateEventUserDirectoryByEvent.');
      }
      if (eventUserUpdateSchema === null || eventUserUpdateSchema === undefined) {
        throw new Error('Required parameter eventUserUpdateSchema was null or undefined when calling updateEventUserDirectoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user_directory/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: eventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicEventUserDirectoryService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicEventUserDirectoryService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicEventUserDirectoryService,
        factory: PublicEventUserDirectoryService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicEventUserDirectoryService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorBrandCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorBrandCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorBrandCompanyLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorBrandCompanyLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorBrandCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorBrandCompanyLogoByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorBrandCompanyLogoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorBrands(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorBrands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorBrandsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorBrandsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorBrandsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorBrand(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorBrand.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorBrandByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorBrandByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorBrandCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorBrandCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorBrandCountByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorBrandCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorBrandCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorBrands(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorBrands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorBrandsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorBrandsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorBrandsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorBrand(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorBrand.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorBrand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorBrandByExhibitor(itemId, subdomain, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorBrandByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorBrandByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorBrandByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_brand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorBrandCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorBrandCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorBrandCRUDService,
        factory: PublicExhibitorBrandCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorBrandCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorCompanyLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorCompanyLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorCompanyLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorCompanyLogoByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorCompanyLogoByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorCompanyLogoByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadExhibitorCompanyLogoByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitors(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllExhibitorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllExhibitorsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorsByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorsByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllExhibitorsByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitor(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getExhibitorCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getExhibitorCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getExhibitorCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorCountryList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorCountryList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getExhibitorCountryList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_country_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPavilionList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPavilionList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPavilionList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_pavilion_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStandList(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStandList.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getStandList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_stand_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitors(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllExhibitorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllExhibitorsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorsByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorsByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllExhibitorsByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitor(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderExhibitorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorCRUDService,
        factory: PublicExhibitorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorDocumentCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorDocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorDocumentDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorDocumentDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorDocumentDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorDocumentDocumentByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorDocumentDocumentByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorDocumentDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorDocumentDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorDocumentImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorDocumentImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorDocumentImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorDocumentImageByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorDocumentImageByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorDocumentImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorDocumentImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorDocuments(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorDocumentsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorDocumentsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorDocumentsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorDocumentByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorDocumentByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorDocumentCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorDocumentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorDocumentCountByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorDocumentCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorDocumentCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorDocuments(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorDocumentsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorDocumentsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorDocumentsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorDocument(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorDocumentByExhibitor(itemId, subdomain, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorDocumentByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorDocumentByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorDocumentByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorDocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorDocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorDocumentCRUDService,
        factory: PublicExhibitorDocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorDocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorEventUserCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorEventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createExhibitorEventUserByEventUser(eventUserId, subdomain, exhibitorEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling createExhibitorEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createExhibitorEventUserByEventUser.');
      }
      if (exhibitorEventUserCreateSchema === null || exhibitorEventUserCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserCreateSchema was null or undefined when calling createExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createExhibitorEventUserByExhibitor(exhibitorId, subdomain, exhibitorEventUserCreateSchema, userId, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling createExhibitorEventUserByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createExhibitorEventUserByExhibitor.');
      }
      if (exhibitorEventUserCreateSchema === null || exhibitorEventUserCreateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserCreateSchema was null or undefined when calling createExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllExhibitorEventUsersByEventUser(eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteAllExhibitorEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllExhibitorEventUsersByExhibitor(exhibitorId, subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling deleteAllExhibitorEventUsersByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteExhibitorEventUserByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteExhibitorEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteExhibitorEventUserByExhibitor(itemId, exhibitorId, subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling deleteExhibitorEventUserByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorEventUsersByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllExhibitorEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorEventUsersByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllExhibitorEventUsersByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorEventUsersByExhibitor(exhibitorId, subdomain, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorEventUsersByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorEventUsersByExhibitorMulti(exhibitorId, subdomain, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorEventUsersByExhibitorMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorEventUsersByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getExhibitorEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserByExhibitor(itemId, exhibitorId, subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorEventUserByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserCountByEventUser(eventUserId, subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getExhibitorEventUserCountByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserCountByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getExhibitorEventUserCountByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserCountByExhibitor(exhibitorId, subdomain, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorEventUserCountByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorEventUserCountByExhibitorMulti(exhibitorId, subdomain, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorEventUserCountByExhibitorMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorEventUserCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorEventUsersByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllExhibitorEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorEventUsersByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllExhibitorEventUsersByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorEventUsersByExhibitor(exhibitorId, subdomain, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorEventUsersByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorEventUsersByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorEventUsersByExhibitorMulti(exhibitorId, subdomain, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorEventUsersByExhibitorMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorEventUsersByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorEventUserByEventUser(itemId, eventUserId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderExhibitorEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorEventUserByExhibitor(itemId, exhibitorId, subdomain, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorEventUserByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserExhibitorsEx(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserExhibitorsEx.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserExhibitorsEx.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserExhibitorsEx.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setExhibitorEventUsersEx(subdomain, exhibitorId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setExhibitorEventUsersEx.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling setExhibitorEventUsersEx.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setExhibitorEventUsersEx.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateExhibitorEventUserByEventUser(itemId, eventUserId, subdomain, exhibitorEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateExhibitorEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling updateExhibitorEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateExhibitorEventUserByEventUser.');
      }
      if (exhibitorEventUserUpdateSchema === null || exhibitorEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserUpdateSchema was null or undefined when calling updateExhibitorEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateExhibitorEventUserByExhibitor(itemId, exhibitorId, subdomain, exhibitorEventUserUpdateSchema, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling updateExhibitorEventUserByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateExhibitorEventUserByExhibitor.');
      }
      if (exhibitorEventUserUpdateSchema === null || exhibitorEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter exhibitorEventUserUpdateSchema was null or undefined when calling updateExhibitorEventUserByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: exhibitorEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorEventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorEventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorEventUserCRUDService,
        factory: PublicExhibitorEventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorEventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorPressReleaseCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorPressReleaseCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorPressReleaseImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorPressReleaseImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorPressReleaseImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorPressReleaseImageByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorPressReleaseImageByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorPressReleaseImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorPressReleaseImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleases(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleases.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleasesByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleasesByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorPressReleasesByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleasesByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleasesByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorPressReleasesByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleasesByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleasesByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorPressReleasesByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleasesMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleasesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorPressReleasesSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorPressReleasesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressRelease(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorPressRelease.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorPressReleaseByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCountByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorPressReleaseCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCountByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCountByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorPressReleaseCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCountByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCountByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorPressReleaseCountByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorPressReleaseCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorPressReleaseCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleases(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleases.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleasesByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleasesByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorPressReleasesByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleasesByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleasesByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorPressReleasesByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleasesByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleasesByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorPressReleasesByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleasesMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleasesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorPressReleasesSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorPressReleasesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorPressRelease(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorPressRelease.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorPressRelease.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorPressReleaseByExhibitor(itemId, subdomain, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorPressReleaseByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorPressReleaseByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorPressReleaseByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_press_release/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorPressReleaseCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorPressReleaseCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorPressReleaseCRUDService,
        factory: PublicExhibitorPressReleaseCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorPressReleaseCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorProductCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorProductCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorProductProductImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorProductProductImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorProductProductImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorProductProductImageByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorProductProductImageByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorProductProductImageByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorProductProductImageByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/product_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProducts(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProductsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProductsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorProductsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProductsByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProductsByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorProductsByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProductsByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProductsByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorProductsByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProductsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProductsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorProductsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorProductsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProduct(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorProduct.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorProductByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCountByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorProductCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCountByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCountByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorProductCountByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCountByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCountByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorProductCountByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorProductCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorProductCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProducts(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProducts.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProductsByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProductsByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorProductsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProductsByExhibitorMulti(subdomain, exhibitorId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProductsByExhibitorMulti.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorProductsByExhibitorMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProductsByExhibitorSearch(subdomain, exhibitorId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProductsByExhibitorSearch.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorProductsByExhibitorSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProductsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProductsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorProductsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorProductsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorProduct(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorProduct.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorProduct.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorProductByExhibitor(itemId, subdomain, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorProductByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorProductByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorProductByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_product/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorProductCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorProductCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorProductCRUDService,
        factory: PublicExhibitorProductCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorProductCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicExhibitorVideoCRUDService = /*#__PURE__*/(() => {
  class PublicExhibitorVideoCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorVideoVideo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorVideoVideo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorVideoVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadExhibitorVideoVideoByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadExhibitorVideoVideoByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorVideoVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling downloadExhibitorVideoVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorVideos(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorVideos.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllExhibitorVideosByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllExhibitorVideosByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllExhibitorVideosByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorVideo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorVideo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorVideoByExhibitor(itemId, subdomain, exhibitorId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getExhibitorVideoByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorVideoCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorVideoCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getExhibitorVideoCountByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getExhibitorVideoCountByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getExhibitorVideoCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorVideos(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorVideos.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllExhibitorVideosByExhibitor(subdomain, exhibitorId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllExhibitorVideosByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllExhibitorVideosByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorVideo(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorVideo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorVideo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderExhibitorVideoByExhibitor(itemId, subdomain, exhibitorId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderExhibitorVideoByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderExhibitorVideoByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderExhibitorVideoByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/exhibitor_video/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicExhibitorVideoCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicExhibitorVideoCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicExhibitorVideoCRUDService,
        factory: PublicExhibitorVideoCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicExhibitorVideoCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFeedbackCRUDService = /*#__PURE__*/(() => {
  class PublicFeedbackCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllFeedbacks(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFeedbacks.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFeedbacksByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFeedbacksByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllFeedbacksByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedback(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFeedback.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFeedbackByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFeedbackCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFeedbacks(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFeedbacks.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFeedbacksByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFeedbacksByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllFeedbacksByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFeedback(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFeedback.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFeedbackByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFeedbackByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFeedbackByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderFeedbackByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFeedbackCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFeedbackCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFeedbackCRUDService,
        factory: PublicFeedbackCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFeedbackCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFeedbackQuestionCRUDService = /*#__PURE__*/(() => {
  class PublicFeedbackQuestionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllFeedbackQuestionsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFeedbackQuestionsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllFeedbackQuestionsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFeedbackQuestionsByFeedback(subdomain, feedbackId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFeedbackQuestionsByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling getAllFeedbackQuestionsByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVisibleFeedbackQuestionsByEvent(eventId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllVisibleFeedbackQuestionsByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVisibleFeedbackQuestionsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/visible_feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVisibleFeedbackQuestionsByFeedback(feedbackId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling getAllVisibleFeedbackQuestionsByFeedback.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVisibleFeedbackQuestionsByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/visible_feedback_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackQuestionByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFeedbackQuestionByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackQuestionByFeedback(itemId, subdomain, feedbackId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFeedbackQuestionByFeedback.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling getFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackQuestionCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackQuestionCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFeedbackQuestionCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackQuestionCountByFeedback(subdomain, feedbackId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackQuestionCountByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling getFeedbackQuestionCountByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFeedbackQuestionsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFeedbackQuestionsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllFeedbackQuestionsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFeedbackQuestionsByFeedback(subdomain, feedbackId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFeedbackQuestionsByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling renderAllFeedbackQuestionsByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFeedbackQuestionByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFeedbackQuestionByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFeedbackQuestionByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderFeedbackQuestionByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFeedbackQuestionByFeedback(itemId, subdomain, feedbackId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFeedbackQuestionByFeedback.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFeedbackQuestionByFeedback.');
      }
      if (feedbackId === null || feedbackId === undefined) {
        throw new Error('Required parameter feedbackId was null or undefined when calling renderFeedbackQuestionByFeedback.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback/${this.configuration.encodeParam({
        name: 'feedbackId',
        value: feedbackId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFeedbackQuestionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFeedbackQuestionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFeedbackQuestionCRUDService,
        factory: PublicFeedbackQuestionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFeedbackQuestionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFeedbackVoteCRUDService = /*#__PURE__*/(() => {
  class PublicFeedbackVoteCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createFeedbackVoteByQuestion(subdomain, questionId, feedbackVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling createFeedbackVoteByQuestion.');
      }
      if (feedbackVoteCreateSchema === null || feedbackVoteCreateSchema === undefined) {
        throw new Error('Required parameter feedbackVoteCreateSchema was null or undefined when calling createFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFeedbackVoteByQuestion(itemId, subdomain, questionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteFeedbackVoteByQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling deleteFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFeedbackVotesByQuestion(subdomain, questionId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFeedbackVotesByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling getAllFeedbackVotesByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackVoteByQuestion(itemId, subdomain, questionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFeedbackVoteByQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling getFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFeedbackVoteCountByQuestion(subdomain, questionId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFeedbackVoteCountByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling getFeedbackVoteCountByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFeedbackVotesByQuestion(subdomain, questionId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFeedbackVotesByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling renderAllFeedbackVotesByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFeedbackVoteByQuestion(itemId, subdomain, questionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFeedbackVoteByQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling renderFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFeedbackVoteByQuestion(itemId, subdomain, questionId, feedbackVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateFeedbackVoteByQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateFeedbackVoteByQuestion.');
      }
      if (questionId === null || questionId === undefined) {
        throw new Error('Required parameter questionId was null or undefined when calling updateFeedbackVoteByQuestion.');
      }
      if (feedbackVoteUpdateSchema === null || feedbackVoteUpdateSchema === undefined) {
        throw new Error('Required parameter feedbackVoteUpdateSchema was null or undefined when calling updateFeedbackVoteByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/feedback_question/${this.configuration.encodeParam({
        name: 'questionId',
        value: questionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: feedbackVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFeedbackVoteCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFeedbackVoteCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFeedbackVoteCRUDService,
        factory: PublicFeedbackVoteCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFeedbackVoteCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFieldOfApplicationCRUDService = /*#__PURE__*/(() => {
  class PublicFieldOfApplicationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllFieldOfApplicationsByEvent(subdomain, eventId, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFieldOfApplicationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllFieldOfApplicationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFieldOfApplicationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFieldOfApplicationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFieldOfApplicationCountByEvent(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFieldOfApplicationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFieldOfApplicationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFieldOfApplicationsByEvent(subdomain, eventId, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFieldOfApplicationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllFieldOfApplicationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFieldOfApplicationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFieldOfApplicationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFieldOfApplicationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderFieldOfApplicationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/field_of_application/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFieldOfApplicationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFieldOfApplicationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFieldOfApplicationCRUDService,
        factory: PublicFieldOfApplicationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFieldOfApplicationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFirmCRUDService = /*#__PURE__*/(() => {
  class PublicFirmCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    deleteFirm(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteFirm.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadFirmLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadFirmLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadFirmLogo.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFirms(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFirms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirm(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFirm.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirmAgendas(subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFirmAgendas.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getFirmAgendas.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agendas`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirmCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFirmCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirmSpeakers(subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFirmSpeakers.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getFirmSpeakers.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speakers`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFirms(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFirms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFirm(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFirmCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFirmCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFirmCRUDService,
        factory: PublicFirmCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFirmCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFlightReservationCRUDService = /*#__PURE__*/(() => {
  class PublicFlightReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createFlightReservationByEvent(subdomain, eventId, flightReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createFlightReservationByEvent.');
      }
      if (flightReservationCreateSchema === null || flightReservationCreateSchema === undefined) {
        throw new Error('Required parameter flightReservationCreateSchema was null or undefined when calling createFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFlightReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteFlightReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadFlightReservationTicketByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadFlightReservationTicketByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadFlightReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadFlightReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFlightReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlightReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllFlightReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFlightReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlightReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllFlightReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlightReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFlightReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlightReservationCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlightReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFlightReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlightReservationCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlightReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getFlightReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlightReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlightReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllFlightReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlightReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlightReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllFlightReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFlightReservationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFlightReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFlightReservationByEvent(itemId, subdomain, eventId, flightReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateFlightReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateFlightReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateFlightReservationByEvent.');
      }
      if (flightReservationUpdateSchema === null || flightReservationUpdateSchema === undefined) {
        throw new Error('Required parameter flightReservationUpdateSchema was null or undefined when calling updateFlightReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flight_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flightReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFlightReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFlightReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFlightReservationCRUDService,
        factory: PublicFlightReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFlightReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFlowCRUDService = /*#__PURE__*/(() => {
  class PublicFlowCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllFlows(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlows.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlow(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFlow.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlows(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlows.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFlow(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFlow.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFlow.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFlowCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFlowCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFlowCRUDService,
        factory: PublicFlowCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFlowCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFlowItemCRUDService = /*#__PURE__*/(() => {
  class PublicFlowItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllFlowItems(subdomain, flowId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlowItems.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getAllFlowItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowItem(itemId, subdomain, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowItem.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowItemCount(subdomain, flowId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowItemCount.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getFlowItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlowItems(subdomain, flowId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlowItems.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling renderAllFlowItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFlowItem(itemId, subdomain, flowId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFlowItem.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling renderFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFlowItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFlowItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFlowItemCRUDService,
        factory: PublicFlowItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFlowItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFlowItemUserStatusCRUDService = /*#__PURE__*/(() => {
  class PublicFlowItemUserStatusCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createFlowItemUserStatus(subdomain, flowItemUserStatusCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createFlowItemUserStatus.');
      }
      if (flowItemUserStatusCreateSchema === null || flowItemUserStatusCreateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusCreateSchema was null or undefined when calling createFlowItemUserStatus.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllFlowItemUserStatuses(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllFlowItemUserStatuses.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFlowItemUserStatus(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteFlowItemUserStatus.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteFlowItemUserStatus.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFlowItemUserStatuses(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlowItemUserStatuses.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowItemUserStatus(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFlowItemUserStatus.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowItemUserStatus.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowItemUserStatusCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowItemUserStatusCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicCreateFlowItemUserStatusByFlowItem(subdomain, flowItemId, flowItemUserStatusCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicCreateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicCreateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemUserStatusCreateSchema === null || flowItemUserStatusCreateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusCreateSchema was null or undefined when calling publicCreateFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicCreateFlowItemUserStatusByUser(subdomain, userId, flowItemUserStatusCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicCreateFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicCreateFlowItemUserStatusByUser.');
      }
      if (flowItemUserStatusCreateSchema === null || flowItemUserStatusCreateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusCreateSchema was null or undefined when calling publicCreateFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicDeleteAllFlowItemUserStatusesByFlowItem(subdomain, flowItemId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicDeleteAllFlowItemUserStatusesByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicDeleteAllFlowItemUserStatusesByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicDeleteAllFlowItemUserStatusesByUser(subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicDeleteAllFlowItemUserStatusesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicDeleteAllFlowItemUserStatusesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicDeleteFlowItemUserStatusByFlowItem(itemId, subdomain, flowItemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicDeleteFlowItemUserStatusByFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicDeleteFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicDeleteFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicDeleteFlowItemUserStatusByUser(itemId, subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicDeleteFlowItemUserStatusByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicDeleteFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicDeleteFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetAllFlowItemUserStatusesByFlowItem(subdomain, flowItemId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetAllFlowItemUserStatusesByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicGetAllFlowItemUserStatusesByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetAllFlowItemUserStatusesByUser(subdomain, userId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetAllFlowItemUserStatusesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicGetAllFlowItemUserStatusesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetFlowItemUserStatusByFlowItem(itemId, subdomain, flowItemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicGetFlowItemUserStatusByFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicGetFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetFlowItemUserStatusByUser(itemId, subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicGetFlowItemUserStatusByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicGetFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetFlowItemUserStatusCountByFlowItem(subdomain, flowItemId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetFlowItemUserStatusCountByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicGetFlowItemUserStatusCountByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicGetFlowItemUserStatusCountByUser(subdomain, userId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicGetFlowItemUserStatusCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicGetFlowItemUserStatusCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicRenderAllFlowItemUserStatusesByFlowItem(subdomain, flowItemId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicRenderAllFlowItemUserStatusesByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicRenderAllFlowItemUserStatusesByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicRenderAllFlowItemUserStatusesByUser(subdomain, userId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicRenderAllFlowItemUserStatusesByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicRenderAllFlowItemUserStatusesByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicRenderFlowItemUserStatusByFlowItem(itemId, subdomain, flowItemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicRenderFlowItemUserStatusByFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicRenderFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicRenderFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicRenderFlowItemUserStatusByUser(itemId, subdomain, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicRenderFlowItemUserStatusByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicRenderFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicRenderFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicUpdateFlowItemUserStatusByFlowItem(itemId, subdomain, flowItemId, flowItemUserStatusUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicUpdateFlowItemUserStatusByFlowItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicUpdateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemId === null || flowItemId === undefined) {
        throw new Error('Required parameter flowItemId was null or undefined when calling publicUpdateFlowItemUserStatusByFlowItem.');
      }
      if (flowItemUserStatusUpdateSchema === null || flowItemUserStatusUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusUpdateSchema was null or undefined when calling publicUpdateFlowItemUserStatusByFlowItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item/${this.configuration.encodeParam({
        name: 'flowItemId',
        value: flowItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    publicUpdateFlowItemUserStatusByUser(itemId, subdomain, userId, flowItemUserStatusUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling publicUpdateFlowItemUserStatusByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling publicUpdateFlowItemUserStatusByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling publicUpdateFlowItemUserStatusByUser.');
      }
      if (flowItemUserStatusUpdateSchema === null || flowItemUserStatusUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusUpdateSchema was null or undefined when calling publicUpdateFlowItemUserStatusByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlowItemUserStatuses(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlowItemUserStatuses.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFlowItemUserStatus(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFlowItemUserStatus.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFlowItemUserStatus.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFlowItemUserStatus(itemId, subdomain, flowItemUserStatusUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateFlowItemUserStatus.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateFlowItemUserStatus.');
      }
      if (flowItemUserStatusUpdateSchema === null || flowItemUserStatusUpdateSchema === undefined) {
        throw new Error('Required parameter flowItemUserStatusUpdateSchema was null or undefined when calling updateFlowItemUserStatus.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow_item_user_status/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowItemUserStatusUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFlowItemUserStatusCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFlowItemUserStatusCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFlowItemUserStatusCRUDService,
        factory: PublicFlowItemUserStatusCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFlowItemUserStatusCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicFlowUserCRUDService = /*#__PURE__*/(() => {
  class PublicFlowUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createFlowUser(subdomain, flowId, flowUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createFlowUser.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling createFlowUser.');
      }
      if (flowUserCreateSchema === null || flowUserCreateSchema === undefined) {
        throw new Error('Required parameter flowUserCreateSchema was null or undefined when calling createFlowUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: flowUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFlowUser(itemId, subdomain, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteFlowUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteFlowUser.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling deleteFlowUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllFlowUsers(subdomain, flowId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllFlowUsers.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getAllFlowUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowUser(itemId, subdomain, flowId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getFlowUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowUser.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getFlowUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFlowUserCount(subdomain, flowId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getFlowUserCount.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling getFlowUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllFlowUsers(subdomain, flowId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllFlowUsers.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling renderAllFlowUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderFlowUser(itemId, subdomain, flowId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderFlowUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderFlowUser.');
      }
      if (flowId === null || flowId === undefined) {
        throw new Error('Required parameter flowId was null or undefined when calling renderFlowUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/flow/${this.configuration.encodeParam({
        name: 'flowId',
        value: flowId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/flow_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicFlowUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicFlowUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicFlowUserCRUDService,
        factory: PublicFlowUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicFlowUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicGameCRUDService = /*#__PURE__*/(() => {
  class PublicGameCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllGames(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/game`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGame(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGameCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGameCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/game_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllGames(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllGames.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/game/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderGame(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderGame.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderGame.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/game/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicGameCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicGameCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicGameCRUDService,
        factory: PublicGameCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicGameCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicGroupCRUDService = /*#__PURE__*/(() => {
  class PublicGroupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadGroupLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadGroupLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadGroupLogo.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllGroups(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGroup(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGroup.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGroupCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGroupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllGroups(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllGroups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderGroup(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderGroup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderGroup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicGroupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicGroupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicGroupCRUDService,
        factory: PublicGroupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicGroupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicGroupUserCRUDService = /*#__PURE__*/(() => {
  class PublicGroupUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createGroupUserByUser(userId, subdomain, groupUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling createGroupUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createGroupUserByUser.');
      }
      if (groupUserCreateSchema === null || groupUserCreateSchema === undefined) {
        throw new Error('Required parameter groupUserCreateSchema was null or undefined when calling createGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: groupUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllGroupUsersByUser(userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteAllGroupUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllGroupUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteGroupUserByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteGroupUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllGroupUsersByUser(userId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAllGroupUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllGroupUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGroupUserByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getGroupUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGroupUserCountByUser(userId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getGroupUserCountByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGroupUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllGroupUsersByUser(userId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAllGroupUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllGroupUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderGroupUserByUser(itemId, userId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderGroupUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderGroupUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderGroupUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicGroupUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicGroupUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicGroupUserCRUDService,
        factory: PublicGroupUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicGroupUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicHotelCRUDService = /*#__PURE__*/(() => {
  class PublicHotelCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllHotels(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllHotels.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotel(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getHotel.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotel.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotelCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotelCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllHotels(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllHotels.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderHotel(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderHotel.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderHotel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicHotelCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicHotelCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicHotelCRUDService,
        factory: PublicHotelCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicHotelCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicHotelReservationCRUDService = /*#__PURE__*/(() => {
  class PublicHotelReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createHotelReservation(subdomain, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createHotelReservation.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling createHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createHotelReservationByEvent(subdomain, eventId, hotelReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createHotelReservationByEvent.');
      }
      if (hotelReservationCreateSchema === null || hotelReservationCreateSchema === undefined) {
        throw new Error('Required parameter hotelReservationCreateSchema was null or undefined when calling createHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteHotelReservation(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteHotelReservation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteHotelReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteHotelReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadHotelReservationBooking(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadHotelReservationBooking.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadHotelReservationBooking.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadHotelReservationBookingByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadHotelReservationBookingByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadHotelReservationBookingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadHotelReservationBookingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/booking`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllHotelReservations(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllHotelReservations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllHotelReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllHotelReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllHotelReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotelReservation(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getHotelReservation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotelReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getHotelReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotelReservationCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotelReservationCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getHotelReservationCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getHotelReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getHotelReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllHotelReservations(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllHotelReservations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllHotelReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllHotelReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllHotelReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderHotelReservation(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderHotelReservation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderHotelReservationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderHotelReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateHotelReservation(itemId, subdomain, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateHotelReservation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateHotelReservation.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling updateHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateHotelReservationByEvent(itemId, subdomain, eventId, hotelReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateHotelReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateHotelReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateHotelReservationByEvent.');
      }
      if (hotelReservationUpdateSchema === null || hotelReservationUpdateSchema === undefined) {
        throw new Error('Required parameter hotelReservationUpdateSchema was null or undefined when calling updateHotelReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/hotel_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: hotelReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicHotelReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicHotelReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicHotelReservationCRUDService,
        factory: PublicHotelReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicHotelReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicLeaderboardService = /*#__PURE__*/(() => {
  class PublicLeaderboardService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadEventLeaderboard(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventLeaderboard.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadEventLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/download_leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    eventLeaderboard(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling eventLeaderboard.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling eventLeaderboard.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/leaderboard`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicLeaderboardService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicLeaderboardService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicLeaderboardService,
        factory: PublicLeaderboardService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicLeaderboardService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicLiveChatCRUDService = /*#__PURE__*/(() => {
  class PublicLiveChatCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createLiveChat(subdomain, eventId, liveChatCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createLiveChat.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createLiveChat.');
      }
      if (liveChatCreateSchema === null || liveChatCreateSchema === undefined) {
        throw new Error('Required parameter liveChatCreateSchema was null or undefined when calling createLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createLiveChatByAgenda(subdomain, eventId, agendaId, liveChatCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createLiveChatByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createLiveChatByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling createLiveChatByAgenda.');
      }
      if (liveChatCreateSchema === null || liveChatCreateSchema === undefined) {
        throw new Error('Required parameter liveChatCreateSchema was null or undefined when calling createLiveChatByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: liveChatCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllLiveChats(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllLiveChats.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllLiveChats.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllLiveChatsByAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllLiveChatsByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllLiveChatsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllLiveChatsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLiveChat(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getLiveChat.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getLiveChat.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLiveChatByAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getLiveChatByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getLiveChatByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getLiveChatByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getLiveChatByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLiveChatCount(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getLiveChatCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getLiveChatCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLiveChatCountByAgenda(subdomain, eventId, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getLiveChatCountByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getLiveChatCountByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getLiveChatCountByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllLiveChats(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllLiveChats.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllLiveChats.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllLiveChatsByAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllLiveChatsByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllLiveChatsByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllLiveChatsByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderLiveChat(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderLiveChat.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderLiveChat.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderLiveChat.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderLiveChatByAgenda(itemId, subdomain, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderLiveChatByAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderLiveChatByAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderLiveChatByAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderLiveChatByAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/live_chat/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicLiveChatCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicLiveChatCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicLiveChatCRUDService,
        factory: PublicLiveChatCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicLiveChatCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMediaGalleryCRUDService = /*#__PURE__*/(() => {
  class PublicMediaGalleryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMediaGalleries(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMediaGalleries.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMediaGallery(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMediaGallery.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMediaGalleryCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMediaGalleryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMediaGalleries(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMediaGalleries.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMediaGallery(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMediaGallery.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMediaGallery.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMediaGalleryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMediaGalleryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMediaGalleryCRUDService,
        factory: PublicMediaGalleryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMediaGalleryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMediaGalleryItemCRUDService = /*#__PURE__*/(() => {
  class PublicMediaGalleryItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadMediaGalleryItemMedia(itemId, subdomain, mediaGalleryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadMediaGalleryItemMedia.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadMediaGalleryItemMedia.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling downloadMediaGalleryItemMedia.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadMediaGalleryItemReadOnlySchemaMediaWatermark(subdomain, mediaGalleryId, mediaGalleryItemId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadMediaGalleryItemReadOnlySchemaMediaWatermark.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling downloadMediaGalleryItemReadOnlySchemaMediaWatermark.');
      }
      if (mediaGalleryItemId === null || mediaGalleryItemId === undefined) {
        throw new Error('Required parameter mediaGalleryItemId was null or undefined when calling downloadMediaGalleryItemReadOnlySchemaMediaWatermark.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'mediaGalleryItemId',
        value: mediaGalleryItemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media/watermark`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadMediaGalleryItemThumbnail(itemId, subdomain, mediaGalleryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadMediaGalleryItemThumbnail.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadMediaGalleryItemThumbnail.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling downloadMediaGalleryItemThumbnail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/thumbnail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMediaGalleryItems(subdomain, mediaGalleryId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMediaGalleryItems.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling getAllMediaGalleryItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMediaGalleryItem(itemId, subdomain, mediaGalleryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMediaGalleryItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMediaGalleryItem.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling getMediaGalleryItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMediaGalleryItemCount(subdomain, mediaGalleryId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMediaGalleryItemCount.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling getMediaGalleryItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMediaGalleryItems(subdomain, mediaGalleryId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMediaGalleryItems.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling renderAllMediaGalleryItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMediaGalleryItem(itemId, subdomain, mediaGalleryId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMediaGalleryItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMediaGalleryItem.');
      }
      if (mediaGalleryId === null || mediaGalleryId === undefined) {
        throw new Error('Required parameter mediaGalleryId was null or undefined when calling renderMediaGalleryItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/media_gallery/${this.configuration.encodeParam({
        name: 'mediaGalleryId',
        value: mediaGalleryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/media_gallery_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMediaGalleryItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMediaGalleryItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMediaGalleryItemCRUDService,
        factory: PublicMediaGalleryItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMediaGalleryItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMeetingCRUDService = /*#__PURE__*/(() => {
  class PublicMeetingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMeetings(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllMeetingsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingsByFirm(subdomain, firmId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getAllMeetingsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeeting(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingByFirm(itemId, subdomain, firmId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getMeetingByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMeetingCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingCountByFirm(subdomain, firmId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingCountByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling getMeetingCountByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetings(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllMeetingsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingsByFirm(subdomain, firmId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingsByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling renderAllMeetingsByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeeting(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderMeetingByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingByFirm(itemId, subdomain, firmId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingByFirm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingByFirm.');
      }
      if (firmId === null || firmId === undefined) {
        throw new Error('Required parameter firmId was null or undefined when calling renderMeetingByFirm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/firm/${this.configuration.encodeParam({
        name: 'firmId',
        value: firmId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMeetingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMeetingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMeetingCRUDService,
        factory: PublicMeetingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMeetingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMeetingEventUserCRUDService = /*#__PURE__*/(() => {
  class PublicMeetingEventUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createMeetingEventUserByEventUser(eventUserId, subdomain, meetingEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling createMeetingEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createMeetingEventUserByEventUser.');
      }
      if (meetingEventUserCreateSchema === null || meetingEventUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserCreateSchema was null or undefined when calling createMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createMeetingEventUserByMeeting(meetingId, subdomain, meetingEventUserCreateSchema, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling createMeetingEventUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createMeetingEventUserByMeeting.');
      }
      if (meetingEventUserCreateSchema === null || meetingEventUserCreateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserCreateSchema was null or undefined when calling createMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllMeetingEventUsersByEventUser(eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteAllMeetingEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllMeetingEventUsersByMeeting(meetingId, subdomain, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling deleteAllMeetingEventUsersByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMeetingEventUserByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling deleteMeetingEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMeetingEventUserByMeeting(itemId, meetingId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling deleteMeetingEventUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingEventUsersByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllMeetingEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingEventUsersByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getAllMeetingEventUsersByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingEventUsersByMeeting(meetingId, subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getAllMeetingEventUsersByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingEventUsersByMeetingMulti(meetingId, subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getAllMeetingEventUsersByMeetingMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingEventUsersByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserByEventUser(itemId, eventUserId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getMeetingEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserByMeeting(itemId, meetingId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingEventUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserCountByEventUser(eventUserId, subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getMeetingEventUserCountByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserCountByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserCountByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling getMeetingEventUserCountByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserCountByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserCountByMeeting(meetingId, subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingEventUserCountByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingEventUserCountByMeetingMulti(meetingId, subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingEventUserCountByMeetingMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingEventUserCountByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingEventUsersByEventUser(eventUserId, subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllMeetingEventUsersByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingEventUsersByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingEventUsersByEventUserMulti(eventUserId, subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderAllMeetingEventUsersByEventUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingEventUsersByEventUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingEventUsersByMeeting(meetingId, subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderAllMeetingEventUsersByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingEventUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingEventUsersByMeetingMulti(meetingId, subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderAllMeetingEventUsersByMeetingMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingEventUsersByMeetingMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingEventUserByEventUser(itemId, eventUserId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling renderMeetingEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingEventUserByMeeting(itemId, meetingId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderMeetingEventUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventUserMeetings(subdomain, eventUserId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventUserMeetings.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling setEventUserMeetings.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventUserMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setMeetingEventUsers(subdomain, meetingId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setMeetingEventUsers.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling setMeetingEventUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setMeetingEventUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMeetingEventUserByEventUser(itemId, eventUserId, subdomain, meetingEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateMeetingEventUserByEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling updateMeetingEventUserByEventUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateMeetingEventUserByEventUser.');
      }
      if (meetingEventUserUpdateSchema === null || meetingEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserUpdateSchema was null or undefined when calling updateMeetingEventUserByEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMeetingEventUserByMeeting(itemId, meetingId, subdomain, meetingEventUserUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateMeetingEventUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling updateMeetingEventUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateMeetingEventUserByMeeting.');
      }
      if (meetingEventUserUpdateSchema === null || meetingEventUserUpdateSchema === undefined) {
        throw new Error('Required parameter meetingEventUserUpdateSchema was null or undefined when calling updateMeetingEventUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_event_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: meetingEventUserUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMeetingEventUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMeetingEventUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMeetingEventUserCRUDService,
        factory: PublicMeetingEventUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMeetingEventUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMeetingExhibitorCRUDService = /*#__PURE__*/(() => {
  class PublicMeetingExhibitorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMeetingExhibitorsByExhibitor(exhibitorId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getAllMeetingExhibitorsByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingExhibitorsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingExhibitorsByMeeting(subdomain, meetingId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingExhibitorsByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getAllMeetingExhibitorsByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingExhibitorByExhibitor(itemId, exhibitorId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getMeetingExhibitorByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingExhibitorByExhibitor.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingExhibitorByMeeting(itemId, subdomain, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingExhibitorByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingExhibitorByMeeting.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingExhibitorCountByExhibitor(exhibitorId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling getMeetingExhibitorCountByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingExhibitorCountByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingExhibitorCountByMeeting(subdomain, meetingId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingExhibitorCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingExhibitorCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingExhibitorsByExhibitor(exhibitorId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderAllMeetingExhibitorsByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingExhibitorsByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingExhibitorsByMeeting(subdomain, meetingId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingExhibitorsByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderAllMeetingExhibitorsByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingExhibitorByExhibitor(itemId, exhibitorId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingExhibitorByExhibitor.');
      }
      if (exhibitorId === null || exhibitorId === undefined) {
        throw new Error('Required parameter exhibitorId was null or undefined when calling renderMeetingExhibitorByExhibitor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingExhibitorByExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/exhibitor/${this.configuration.encodeParam({
        name: 'exhibitorId',
        value: exhibitorId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingExhibitorByMeeting(itemId, subdomain, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingExhibitorByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingExhibitorByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderMeetingExhibitorByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_exhibitor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMeetingExhibitorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMeetingExhibitorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMeetingExhibitorCRUDService,
        factory: PublicMeetingExhibitorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMeetingExhibitorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMeetingSpeakerCRUDService = /*#__PURE__*/(() => {
  class PublicMeetingSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMeetingSpeakersByMeeting(meetingId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getAllMeetingSpeakersByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingSpeakersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getAllMeetingSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingSpeakerByMeeting(itemId, meetingId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingSpeakerByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingSpeakerByMeeting.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingSpeakerBySpeaker(itemId, speakerId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getMeetingSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingSpeakerBySpeaker.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingSpeakerCountByMeeting(meetingId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingSpeakerCountByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingSpeakerCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingSpeakerCountBySpeaker(speakerId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling getMeetingSpeakerCountBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingSpeakerCountBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingSpeakersByMeeting(meetingId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderAllMeetingSpeakersByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingSpeakersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingSpeakersBySpeaker(speakerId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderAllMeetingSpeakersBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingSpeakersBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingSpeakerByMeeting(itemId, meetingId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingSpeakerByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderMeetingSpeakerByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingSpeakerByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingSpeakerBySpeaker(itemId, speakerId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingSpeakerBySpeaker.');
      }
      if (speakerId === null || speakerId === undefined) {
        throw new Error('Required parameter speakerId was null or undefined when calling renderMeetingSpeakerBySpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingSpeakerBySpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'speakerId',
        value: speakerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMeetingSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMeetingSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMeetingSpeakerCRUDService,
        factory: PublicMeetingSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMeetingSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMeetingUserCRUDService = /*#__PURE__*/(() => {
  class PublicMeetingUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMeetingUsersByMeeting(subdomain, meetingId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getAllMeetingUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMeetingUsersByUser(userId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAllMeetingUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMeetingUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingUserByMeeting(itemId, subdomain, meetingId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingUserByMeeting.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingUserByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getMeetingUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingUserCountByMeeting(subdomain, meetingId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingUserCountByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling getMeetingUserCountByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMeetingUserCountByUser(userId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getMeetingUserCountByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMeetingUserCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingUsersByMeeting(subdomain, meetingId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingUsersByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderAllMeetingUsersByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMeetingUsersByUser(userId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAllMeetingUsersByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMeetingUsersByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingUserByMeeting(itemId, subdomain, meetingId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingUserByMeeting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingUserByMeeting.');
      }
      if (meetingId === null || meetingId === undefined) {
        throw new Error('Required parameter meetingId was null or undefined when calling renderMeetingUserByMeeting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/meeting/${this.configuration.encodeParam({
        name: 'meetingId',
        value: meetingId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMeetingUserByUser(itemId, userId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMeetingUserByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderMeetingUserByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMeetingUserByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/meeting_user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMeetingUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMeetingUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMeetingUserCRUDService,
        factory: PublicMeetingUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMeetingUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMenuVoiceCRUDService = /*#__PURE__*/(() => {
  class PublicMenuVoiceCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMenuVoices(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMenuVoices.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllMenuVoices.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMenuVoice(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMenuVoice.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMenuVoice.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMenuVoiceCount(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMenuVoiceCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMenuVoiceCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMenuVoices(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMenuVoices.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllMenuVoices.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMenuVoice(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMenuVoice.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMenuVoice.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderMenuVoice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/menu_voice/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMenuVoiceCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMenuVoiceCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMenuVoiceCRUDService,
        factory: PublicMenuVoiceCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMenuVoiceCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicMerchandiseCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicMerchandiseCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllMerchandiseCategoriesByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMerchandiseCategoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllMerchandiseCategoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMerchandiseCategoriesByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMerchandiseCategoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllMerchandiseCategoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllMerchandiseCategoriesByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllMerchandiseCategoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllMerchandiseCategoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMerchandiseCategoryByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getMerchandiseCategoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMerchandiseCategoryCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMerchandiseCategoryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMerchandiseCategoryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMerchandiseCategoryCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMerchandiseCategoryCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMerchandiseCategoryCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMerchandiseCategoryCountByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getMerchandiseCategoryCountByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getMerchandiseCategoryCountByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMerchandiseCategoriesByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMerchandiseCategoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllMerchandiseCategoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMerchandiseCategoriesByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMerchandiseCategoriesByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllMerchandiseCategoriesByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllMerchandiseCategoriesByEventSearch(subdomain, eventId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllMerchandiseCategoriesByEventSearch.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllMerchandiseCategoriesByEventSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderMerchandiseCategoryByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderMerchandiseCategoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderMerchandiseCategoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderMerchandiseCategoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/merchandise_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicMerchandiseCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicMerchandiseCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicMerchandiseCategoryCRUDService,
        factory: PublicMerchandiseCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicMerchandiseCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicNewsCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicNewsCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllNewsCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllNewsCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllNewsCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllNewsCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderNewsCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicNewsCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicNewsCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicNewsCategoryCRUDService,
        factory: PublicNewsCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicNewsCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicNewsItemCRUDService = /*#__PURE__*/(() => {
  class PublicNewsItemCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadNewsItemImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadNewsItemImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadNewsItemImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadNewsItemImageByNewsCategory(itemId, subdomain, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadNewsItemImageByNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadNewsItemImageByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling downloadNewsItemImageByNewsCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadNewsItemPreview(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadNewsItemPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadNewsItemPreview.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadNewsItemPreviewByNewsCategory(itemId, subdomain, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadNewsItemPreviewByNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadNewsItemPreviewByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling downloadNewsItemPreviewByNewsCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllNewsItems(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllNewsItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllNewsItemsByNewsCategory(subdomain, newsCategoryId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllNewsItemsByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling getAllNewsItemsByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsItem(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getNewsItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsItem.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsItemByNewsCategory(itemId, subdomain, newsCategoryId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getNewsItemByNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling getNewsItemByNewsCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsItemCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsItemCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getNewsItemCountByNewsCategory(subdomain, newsCategoryId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getNewsItemCountByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling getNewsItemCountByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllNewsItems(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllNewsItems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllNewsItemsByNewsCategory(subdomain, newsCategoryId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllNewsItemsByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling renderAllNewsItemsByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderNewsItem(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderNewsItem.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderNewsItem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderNewsItemByNewsCategory(itemId, subdomain, newsCategoryId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderNewsItemByNewsCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderNewsItemByNewsCategory.');
      }
      if (newsCategoryId === null || newsCategoryId === undefined) {
        throw new Error('Required parameter newsCategoryId was null or undefined when calling renderNewsItemByNewsCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/news_category/${this.configuration.encodeParam({
        name: 'newsCategoryId',
        value: newsCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/news_item/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicNewsItemCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicNewsItemCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicNewsItemCRUDService,
        factory: PublicNewsItemCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicNewsItemCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicParticipantCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicParticipantCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllParticipantCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllParticipantCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/participant_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getParticipantCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getParticipantCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getParticipantCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getParticipantCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/participant_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllParticipantCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllParticipantCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/participant_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderParticipantCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderParticipantCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderParticipantCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/participant_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicParticipantCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicParticipantCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicParticipantCategoryCRUDService,
        factory: PublicParticipantCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicParticipantCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicPollCRUDService = /*#__PURE__*/(() => {
  class PublicPollCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllPolls(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPolls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPollsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPollsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllPollsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPollsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPollsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllPollsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllPollsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPoll(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPollByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollByEventAndAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPollByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getPollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollCountByEventAndAgenda(subdomain, eventId, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getPollCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    pollResults(subdomain, pollId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling pollResults.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling pollResults.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/results`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPolls(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPolls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPollsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPollsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllPollsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPollsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPollsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllPollsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllPollsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPoll(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPollByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPollByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPollByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderPollByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPollByEventAndAgenda(itemId, subdomain, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPollByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPollByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderPollByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderPollByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicPollCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicPollCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicPollCRUDService,
        factory: PublicPollCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicPollCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicPollVoteCRUDService = /*#__PURE__*/(() => {
  class PublicPollVoteCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createPollVoteByEventAndAgendaAndPoll(subdomain, eventId, agendaId, pollId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createPollVoteByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createPollVoteByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling createPollVoteByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling createPollVoteByEventAndAgendaAndPoll.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling createPollVoteByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createPollVoteByEventAndPoll(subdomain, eventId, pollId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createPollVoteByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createPollVoteByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling createPollVoteByEventAndPoll.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling createPollVoteByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createPollVoteByPoll(subdomain, pollId, pollVoteCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createPollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling createPollVoteByPoll.');
      }
      if (pollVoteCreateSchema === null || pollVoteCreateSchema === undefined) {
        throw new Error('Required parameter pollVoteCreateSchema was null or undefined when calling createPollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePollVoteByEventAndAgendaAndPoll(itemId, subdomain, eventId, agendaId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deletePollVoteByEventAndAgendaAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deletePollVoteByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deletePollVoteByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling deletePollVoteByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling deletePollVoteByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePollVoteByEventAndPoll(itemId, subdomain, eventId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deletePollVoteByEventAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deletePollVoteByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deletePollVoteByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling deletePollVoteByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePollVoteByPoll(itemId, subdomain, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deletePollVoteByPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deletePollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling deletePollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPollVotesByEventAndAgendaAndPoll(subdomain, eventId, agendaId, pollId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getAllPollVotesByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPollVotesByEventAndPoll(subdomain, eventId, pollId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPollVotesByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllPollVotesByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getAllPollVotesByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPollVotesByPoll(subdomain, pollId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPollVotesByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getAllPollVotesByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteByEventAndAgendaAndPoll(itemId, subdomain, eventId, agendaId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPollVoteByEventAndAgendaAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollVoteByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getPollVoteByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteByEventAndPoll(itemId, subdomain, eventId, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPollVoteByEventAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollVoteByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteByPoll(itemId, subdomain, pollId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPollVoteByPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteCountByEventAndAgendaAndPoll(subdomain, eventId, agendaId, pollId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteCountByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollVoteCountByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getPollVoteCountByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteCountByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteCountByEventAndPoll(subdomain, eventId, pollId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteCountByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getPollVoteCountByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteCountByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPollVoteCountByPoll(subdomain, pollId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPollVoteCountByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling getPollVoteCountByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPollVotesByEventAndAgendaAndPoll(subdomain, eventId, agendaId, pollId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllPollVotesByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderAllPollVotesByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPollVotesByEventAndPoll(subdomain, eventId, pollId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPollVotesByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllPollVotesByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderAllPollVotesByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPollVotesByPoll(subdomain, pollId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPollVotesByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderAllPollVotesByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPollVoteByEventAndAgendaAndPoll(itemId, subdomain, eventId, agendaId, pollId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPollVoteByEventAndAgendaAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPollVoteByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderPollVoteByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderPollVoteByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderPollVoteByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPollVoteByEventAndPoll(itemId, subdomain, eventId, pollId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPollVoteByEventAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPollVoteByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderPollVoteByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderPollVoteByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPollVoteByPoll(itemId, subdomain, pollId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPollVoteByPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling renderPollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updatePollVoteByEventAndAgendaAndPoll(itemId, subdomain, eventId, agendaId, pollId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling updatePollVoteByEventAndAgendaAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updatePollVoteByEventAndPoll(itemId, subdomain, eventId, pollId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updatePollVoteByEventAndPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updatePollVoteByEventAndPoll.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updatePollVoteByEventAndPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling updatePollVoteByEventAndPoll.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling updatePollVoteByEventAndPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updatePollVoteByPoll(itemId, subdomain, pollId, pollVoteUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updatePollVoteByPoll.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updatePollVoteByPoll.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling updatePollVoteByPoll.');
      }
      if (pollVoteUpdateSchema === null || pollVoteUpdateSchema === undefined) {
        throw new Error('Required parameter pollVoteUpdateSchema was null or undefined when calling updatePollVoteByPoll.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/poll/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pollVoteUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicPollVoteCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicPollVoteCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicPollVoteCRUDService,
        factory: PublicPollVoteCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicPollVoteCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicPopupCRUDService = /*#__PURE__*/(() => {
  class PublicPopupCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    deletePopup(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deletePopup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deletePopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deletePopupByStream(itemId, subdomain, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deletePopupByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deletePopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling deletePopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPopups(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPopups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllPopupsByStream(subdomain, streamId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllPopupsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getAllPopupsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPopup(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPopup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPopupByStream(itemId, subdomain, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getPopupByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getPopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPopupCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPopupCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPopupCountByStream(subdomain, streamId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPopupCountByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getPopupCountByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPopups(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPopups.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllPopupsByStream(subdomain, streamId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllPopupsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling renderAllPopupsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPopup(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPopup.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPopup.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderPopupByStream(itemId, subdomain, streamId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderPopupByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderPopupByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling renderPopupByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/popup/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicPopupCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicPopupCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicPopupCRUDService,
        factory: PublicPopupCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicPopupCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicQuizCRUDService = /*#__PURE__*/(() => {
  class PublicQuizCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createQuizUserAnswer(subdomain, quizId, quizQuestionId, quizAnswerId, quizUserAnswerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling createQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling createQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling createQuizUserAnswer.');
      }
      if (quizUserAnswerCreateSchema === null || quizUserAnswerCreateSchema === undefined) {
        throw new Error('Required parameter quizUserAnswerCreateSchema was null or undefined when calling createQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizUserAnswerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteQuizUserAnswer(itemId, subdomain, quizId, quizQuestionId, quizAnswerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteQuizUserAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling deleteQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling deleteQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling deleteQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadQuizAnswerAsset(itemId, subdomain, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadQuizAnswerAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadQuizAnswerAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling downloadQuizAnswerAsset.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling downloadQuizAnswerAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadQuizAnswerPreview(itemId, subdomain, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadQuizAnswerPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadQuizAnswerPreview.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling downloadQuizAnswerPreview.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling downloadQuizAnswerPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadQuizAsset(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadQuizAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadQuizAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadQuizPreview(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadQuizPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadQuizPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadQuizQuestionAsset(itemId, subdomain, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadQuizQuestionAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadQuizQuestionAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling downloadQuizQuestionAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllQuizAnswers(subdomain, quizId, quizQuestionId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllQuizAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getAllQuizAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getAllQuizAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllQuizQuestions(subdomain, quizId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllQuizQuestions.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getAllQuizQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllQuizUserAnswers(subdomain, quizId, quizQuestionId, quizAnswerId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllQuizUserAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getAllQuizUserAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getAllQuizUserAnswers.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling getAllQuizUserAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllQuizzes(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllQuizzes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuiz(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getQuiz.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizAnswer(itemId, subdomain, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getQuizAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizAnswerCount(subdomain, quizId, quizQuestionId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizAnswerCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizAnswerCount.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getQuizAnswerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizQuestion(itemId, subdomain, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getQuizQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizQuestionCount(subdomain, quizId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizQuestionCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizQuestionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizUserAnswer(itemId, subdomain, quizId, quizQuestionId, quizAnswerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getQuizUserAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling getQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQuizUserAnswerCount(subdomain, quizId, quizQuestionId, quizAnswerId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQuizUserAnswerCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling getQuizUserAnswerCount.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling getQuizUserAnswerCount.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling getQuizUserAnswerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizAllUserRank(quizId, subdomain, observe = 'body', reportProgress = false, options) {
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling quizAllUserRank.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizAllUserRank.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/all_user_rank`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizCheckComplete(quizId, subdomain, observe = 'body', reportProgress = false, options) {
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling quizCheckComplete.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizCheckComplete.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/check_complete`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizCompanyUserRank(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizCompanyUserRank.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/company_user_rank`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizGroupRank(groupId, subdomain, observe = 'body', reportProgress = false, options) {
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling quizGroupRank.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizGroupRank.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/group/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_rank`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizUnansweredInfo(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizUnansweredInfo.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling quizUnansweredInfo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/unanswered_info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    quizUserRank(quizId, subdomain, observe = 'body', reportProgress = false, options) {
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling quizUserRank.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling quizUserRank.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_rank`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllQuizAnswers(subdomain, quizId, quizQuestionId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllQuizAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderAllQuizAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling renderAllQuizAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllQuizQuestions(subdomain, quizId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllQuizQuestions.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderAllQuizQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllQuizUserAnswers(subdomain, quizId, quizQuestionId, quizAnswerId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllQuizUserAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderAllQuizUserAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling renderAllQuizUserAnswers.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling renderAllQuizUserAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllQuizzes(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllQuizzes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderQuiz(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderQuiz.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderQuizAnswer(itemId, subdomain, quizId, quizQuestionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderQuizAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling renderQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderQuizQuestion(itemId, subdomain, quizId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderQuizQuestion.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderQuizUserAnswer(itemId, subdomain, quizId, quizQuestionId, quizAnswerId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderQuizUserAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling renderQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling renderQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling renderQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateQuizUserAnswer(itemId, subdomain, quizId, quizQuestionId, quizAnswerId, quizUserAnswerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateQuizUserAnswer.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling updateQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling updateQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling updateQuizUserAnswer.');
      }
      if (quizUserAnswerUpdateSchema === null || quizUserAnswerUpdateSchema === undefined) {
        throw new Error('Required parameter quizUserAnswerUpdateSchema was null or undefined when calling updateQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizUserAnswerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicQuizCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicQuizCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicQuizCRUDService,
        factory: PublicQuizCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicQuizCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicRegistrationFormCRUDService = /*#__PURE__*/(() => {
  class PublicRegistrationFormCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllRegistrationForms(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllRegistrationForms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllRegistrationFormsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllRegistrationFormsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllRegistrationFormsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllRegistrationFormsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRegistrationForm(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getRegistrationForm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRegistrationForm.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRegistrationFormCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRegistrationFormCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRegistrationFormCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRegistrationFormCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRegistrationFormCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRegistrationFormCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllRegistrationForms(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllRegistrationForms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllRegistrationFormsMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllRegistrationFormsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllRegistrationFormsSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllRegistrationFormsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderRegistrationForm(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderRegistrationForm.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicRegistrationFormCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicRegistrationFormCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicRegistrationFormCRUDService,
        factory: PublicRegistrationFormCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicRegistrationFormCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicRestaurantCRUDService = /*#__PURE__*/(() => {
  class PublicRestaurantCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllRestaurantsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllRestaurantsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllRestaurantsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllRestaurantsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllRestaurantsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllRestaurantsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRestaurantByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getRestaurantByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRestaurantCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRestaurantCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getRestaurantCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRestaurantCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getRestaurantCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getRestaurantCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllRestaurantsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllRestaurantsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllRestaurantsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllRestaurantsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllRestaurantsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllRestaurantsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderRestaurantByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderRestaurantByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicRestaurantCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicRestaurantCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicRestaurantCRUDService,
        factory: PublicRestaurantCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicRestaurantCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicSettingCRUDService = /*#__PURE__*/(() => {
  class PublicSettingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadSettingBackground(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadSettingBackground.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadSettingBackground.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/background`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadSettingFavicon(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadSettingFavicon.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadSettingFavicon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/favicon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadSettingLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadSettingLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadSettingLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllSettings(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSetting(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getSetting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSettingCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSettingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllSettings(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderSetting(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderSetting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicSettingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicSettingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicSettingCRUDService,
        factory: PublicSettingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicSettingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicShuttleScheduleCRUDService = /*#__PURE__*/(() => {
  class PublicShuttleScheduleCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllShuttleSchedulesByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllShuttleSchedulesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllShuttleSchedulesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getShuttleScheduleByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getShuttleScheduleByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getShuttleScheduleCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getShuttleScheduleCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getShuttleScheduleCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllShuttleSchedulesByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllShuttleSchedulesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllShuttleSchedulesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderShuttleScheduleByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderShuttleScheduleByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicShuttleScheduleCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicShuttleScheduleCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicShuttleScheduleCRUDService,
        factory: PublicShuttleScheduleCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicShuttleScheduleCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicSpeakerCRUDService = /*#__PURE__*/(() => {
  class PublicSpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadSpeakerPhoto(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadSpeakerPhoto.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadSpeakerPhoto.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/photo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllSpeakers(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSpeaker(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSpeakerCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllSpeakers(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderSpeaker(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderSpeaker.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicSpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicSpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicSpeakerCRUDService,
        factory: PublicSpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicSpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicSponsorCRUDService = /*#__PURE__*/(() => {
  class PublicSponsorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadSponsorLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadSponsorLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadSponsorLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllSponsors(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSponsor(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getSponsor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSponsorCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSponsorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllSponsors(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderSponsor(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderSponsor.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicSponsorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicSponsorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicSponsorCRUDService,
        factory: PublicSponsorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicSponsorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicStandCRUDService = /*#__PURE__*/(() => {
  class PublicStandCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadStandMapImage(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadStandMapImage.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadStandMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllStands(subdomain, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllStands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStand(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getStand.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStandCount(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStandCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllStands(subdomain, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllStands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderStand(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderStand.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicStandCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicStandCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicStandCRUDService,
        factory: PublicStandCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicStandCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicStreamCRUDService = /*#__PURE__*/(() => {
  class PublicStreamCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllStreams(subdomain, orderBy, speakerId, searchTerms, tags, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllStreams.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllStreamsByEvent(subdomain, eventId, orderBy, speakerId, searchTerms, tags, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllStreamsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllStreamsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllStreamsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, speakerId, searchTerms, tags, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllStreamsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllStreamsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllStreamsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStream(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getStreamByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamByEventAndAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getStreamByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamCount(subdomain, orderBy, speakerId, searchTerms, tags, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamCountByEvent(subdomain, eventId, orderBy, speakerId, searchTerms, tags, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getStreamCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamCountByEventAndAgenda(subdomain, eventId, agendaId, orderBy, speakerId, searchTerms, tags, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getStreamCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getStreamCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllStreams(subdomain, orderBy, speakerId, searchTerms, tags, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllStreams.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllStreamsByEvent(subdomain, eventId, orderBy, speakerId, searchTerms, tags, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllStreamsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllStreamsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllStreamsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, speakerId, searchTerms, tags, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllStreamsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllStreamsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllStreamsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, speakerId, 'speaker_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tags, 'tags');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderStream(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderStreamByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderStreamByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderStreamByEventAndAgenda(itemId, subdomain, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderStreamByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicStreamCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicStreamCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicStreamCRUDService,
        factory: PublicStreamCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicStreamCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicStreamReactionCRUDService = /*#__PURE__*/(() => {
  class PublicStreamReactionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createStreamReactionByStream(subdomain, streamId, streamReactionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling createStreamReactionByStream.');
      }
      if (streamReactionCreateSchema === null || streamReactionCreateSchema === undefined) {
        throw new Error('Required parameter streamReactionCreateSchema was null or undefined when calling createStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamReactionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteStreamReactionByStream(itemId, subdomain, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteStreamReactionByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling deleteStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllStreamReactionsByStream(subdomain, streamId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllStreamReactionsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getAllStreamReactionsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamReactionByStream(itemId, subdomain, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getStreamReactionByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamReactionCountByStream(subdomain, streamId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamReactionCountByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getStreamReactionCountByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getStreamReactionData(subdomain, streamId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getStreamReactionData.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling getStreamReactionData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/data`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllStreamReactionsByStream(subdomain, streamId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllStreamReactionsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling renderAllStreamReactionsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderStreamReactionByStream(itemId, subdomain, streamId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderStreamReactionByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling renderStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateStreamReactionByStream(itemId, subdomain, streamId, streamReactionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateStreamReactionByStream.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling updateStreamReactionByStream.');
      }
      if (streamReactionUpdateSchema === null || streamReactionUpdateSchema === undefined) {
        throw new Error('Required parameter streamReactionUpdateSchema was null or undefined when calling updateStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamReactionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicStreamReactionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicStreamReactionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicStreamReactionCRUDService,
        factory: PublicStreamReactionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicStreamReactionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicSubscriptionCRUDService = /*#__PURE__*/(() => {
  class PublicSubscriptionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllSubscriptions(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSubscription(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getSubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSubscriptionCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getSubscriptionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllSubscriptions(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderSubscription(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderSubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicSubscriptionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicSubscriptionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicSubscriptionCRUDService,
        factory: PublicSubscriptionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicSubscriptionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicThemeSettingCRUDService = /*#__PURE__*/(() => {
  class PublicThemeSettingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllThemeSettings(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllThemeSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/theme_setting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getThemeSetting(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getThemeSetting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getThemeSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/theme_setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getThemeSettingCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getThemeSettingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/theme_setting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllThemeSettings(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllThemeSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/theme_setting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderThemeSetting(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderThemeSetting.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderThemeSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/theme_setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicThemeSettingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicThemeSettingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicThemeSettingCRUDService,
        factory: PublicThemeSettingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicThemeSettingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicTradeShowSectorCRUDService = /*#__PURE__*/(() => {
  class PublicTradeShowSectorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadTradeShowSectorCompanyLogoByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadTradeShowSectorCompanyLogoByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadTradeShowSectorCompanyLogoByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadTradeShowSectorCompanyLogoByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/company_logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllTradeShowSectorsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllTradeShowSectorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllTradeShowSectorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTradeShowSectorByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getTradeShowSectorByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTradeShowSectorCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getTradeShowSectorCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getTradeShowSectorCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllTradeShowSectorsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllTradeShowSectorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllTradeShowSectorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderTradeShowSectorByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderTradeShowSectorByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicTradeShowSectorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicTradeShowSectorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicTradeShowSectorCRUDService,
        factory: PublicTradeShowSectorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicTradeShowSectorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicTrainReservationCRUDService = /*#__PURE__*/(() => {
  class PublicTrainReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createTrainReservationByEvent(subdomain, eventId, trainReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createTrainReservationByEvent.');
      }
      if (trainReservationCreateSchema === null || trainReservationCreateSchema === undefined) {
        throw new Error('Required parameter trainReservationCreateSchema was null or undefined when calling createTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteTrainReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteTrainReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadTrainReservationTicketByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadTrainReservationTicketByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadTrainReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadTrainReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllTrainReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllTrainReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllTrainReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllTrainReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllTrainReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllTrainReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTrainReservationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getTrainReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTrainReservationCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getTrainReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getTrainReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTrainReservationCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getTrainReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getTrainReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllTrainReservationsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllTrainReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllTrainReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllTrainReservationsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllTrainReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllTrainReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderTrainReservationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderTrainReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateTrainReservationByEvent(itemId, subdomain, eventId, trainReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateTrainReservationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateTrainReservationByEvent.');
      }
      if (trainReservationUpdateSchema === null || trainReservationUpdateSchema === undefined) {
        throw new Error('Required parameter trainReservationUpdateSchema was null or undefined when calling updateTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicTrainReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicTrainReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicTrainReservationCRUDService,
        factory: PublicTrainReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicTrainReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserCRUDService = /*#__PURE__*/(() => {
  class PublicUserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    deleteUserAvatar(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserAvatar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserIdentityDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserIdentityDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserAvatar(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserAvatar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserIdentityDocument(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserIdentityDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUsers(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUsersMulti(subdomain, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getPublicUserAvatar(subdomain, userId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getPublicUserAvatar.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getPublicUserAvatar.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/public_avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUser(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserCountMulti(subdomain, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUsers(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUsersMulti(subdomain, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUser(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setUserGuestUsers(subdomain, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setUserGuestUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling setUserGuestUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setUserGuestUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/guest_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setUserHostUsers(subdomain, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setUserHostUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling setUserHostUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setUserHostUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/host_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setUserUsers(subdomain, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setUserUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling setUserUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setUserUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUser(itemId, subdomain, userUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUser.');
      }
      if (userUpdateSchema === null || userUpdateSchema === undefined) {
        throw new Error('Required parameter userUpdateSchema was null or undefined when calling updateUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserAvatar(itemId, subdomain, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserAvatar.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserAvatar.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserAvatarBase64(itemId, subdomain, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserAvatarBase64.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserAvatarBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserAvatarBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserIdentityDocument(itemId, subdomain, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserIdentityDocument.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserIdentityDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserIdentityDocumentBase64(itemId, subdomain, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserIdentityDocumentBase64.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserIdentityDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserIdentityDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserCRUDService,
        factory: PublicUserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicUserCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllUserCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserCategoryUsers(subdomain, userCategoryId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserCategoryUsers.');
      }
      if (userCategoryId === null || userCategoryId === undefined) {
        throw new Error('Required parameter userCategoryId was null or undefined when calling getUserCategoryUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'userCategoryId',
        value: userCategoryId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserCategoryCRUDService,
        factory: PublicUserCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserDocumentCRUDService = /*#__PURE__*/(() => {
  class PublicUserDocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadUserDocumentDocumentByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserDocumentDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserDocumentDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadUserDocumentDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserDocumentsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserDocumentsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserDocumentsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserDocumentsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserDocumentByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserDocumentCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserDocumentCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserDocumentCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserDocumentCountByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserDocumentCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserDocumentCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserDocumentsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserDocumentsByEventMulti(subdomain, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserDocumentsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserDocumentsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserDocumentByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserDocumentByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserDocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserDocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserDocumentCRUDService,
        factory: PublicUserDocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserDocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserGeneratedContentBinCRUDService = /*#__PURE__*/(() => {
  class PublicUserGeneratedContentBinCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllUserGeneratedContentBins(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserGeneratedContentBins.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserGeneratedContentBinsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserGeneratedContentBinsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserGeneratedContentBinsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserGeneratedContentBinsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBin(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserGeneratedContentBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBinByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserGeneratedContentBinByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBinByEventAndAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserGeneratedContentBinByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBinCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBinCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBinCountByEvent(subdomain, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBinCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserGeneratedContentBinCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentBinCountByEventAndAgenda(subdomain, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentBinCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserGeneratedContentBinCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getUserGeneratedContentBinCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserGeneratedContentBins(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserGeneratedContentBins.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserGeneratedContentBinsByEvent(subdomain, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserGeneratedContentBinsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserGeneratedContentBinsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserGeneratedContentBinsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserGeneratedContentBin(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserGeneratedContentBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserGeneratedContentBinByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserGeneratedContentBinByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserGeneratedContentBinByEventAndAgenda(itemId, subdomain, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserGeneratedContentBinCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserGeneratedContentBinCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserGeneratedContentBinCRUDService,
        factory: PublicUserGeneratedContentBinCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserGeneratedContentBinCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserGeneratedContentCRUDService = /*#__PURE__*/(() => {
  class PublicUserGeneratedContentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createUserGeneratedContent(subdomain, userGeneratedContentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserGeneratedContent.');
      }
      if (userGeneratedContentCreateSchema === null || userGeneratedContentCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentCreateSchema was null or undefined when calling createUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createUserGeneratedContentByBin(subdomain, userGeneratedContentBinId, userGeneratedContentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling createUserGeneratedContentByBin.');
      }
      if (userGeneratedContentCreateSchema === null || userGeneratedContentCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentCreateSchema was null or undefined when calling createUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContent(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContentAsset(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContentAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContentAssetByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContentAssetByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling deleteUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContentByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContentByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling deleteUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContentPreview(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContentPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserGeneratedContentPreviewByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserGeneratedContentPreviewByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling deleteUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserGeneratedContentAsset(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserGeneratedContentAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserGeneratedContentAssetByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserGeneratedContentAssetByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling downloadUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserGeneratedContentPreview(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserGeneratedContentPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadUserGeneratedContentPreviewByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadUserGeneratedContentPreviewByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling downloadUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserGeneratedContents(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserGeneratedContents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserGeneratedContentsByBin(subdomain, userGeneratedContentBinId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserGeneratedContentsByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling getAllUserGeneratedContentsByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContent(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserGeneratedContent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentByBin(itemId, subdomain, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserGeneratedContentByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling getUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserGeneratedContentCountByBin(subdomain, userGeneratedContentBinId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserGeneratedContentCountByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling getUserGeneratedContentCountByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserGeneratedContents(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserGeneratedContents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserGeneratedContentsByBin(subdomain, userGeneratedContentBinId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserGeneratedContentsByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling renderAllUserGeneratedContentsByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserGeneratedContent(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserGeneratedContent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserGeneratedContentByBin(itemId, subdomain, userGeneratedContentBinId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserGeneratedContentByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling renderUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserGeneratedContent(itemId, subdomain, userGeneratedContentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserGeneratedContent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserGeneratedContent.');
      }
      if (userGeneratedContentUpdateSchema === null || userGeneratedContentUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentUpdateSchema was null or undefined when calling updateUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserGeneratedContentByBin(itemId, subdomain, userGeneratedContentBinId, userGeneratedContentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserGeneratedContentByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling updateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentUpdateSchema === null || userGeneratedContentUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentUpdateSchema was null or undefined when calling updateUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentAsset(itemId, subdomain, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentAsset.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentAssetBase64(itemId, subdomain, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentAssetBase64.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserGeneratedContentAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentAssetBase64ByBin(itemId, subdomain, userGeneratedContentBinId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling uploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserGeneratedContentAssetBase64ByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentAssetByBin(itemId, subdomain, userGeneratedContentBinId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentAssetByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling uploadUserGeneratedContentAssetByBin.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentPreview(itemId, subdomain, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentPreview.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentPreview.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentPreviewBase64(itemId, subdomain, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentPreviewBase64.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentPreviewBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserGeneratedContentPreviewBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentPreviewBase64ByBin(itemId, subdomain, userGeneratedContentBinId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling uploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadUserGeneratedContentPreviewBase64ByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadUserGeneratedContentPreviewByBin(itemId, subdomain, userGeneratedContentBinId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadUserGeneratedContentPreviewByBin.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling uploadUserGeneratedContentPreviewByBin.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserGeneratedContentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserGeneratedContentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserGeneratedContentCRUDService,
        factory: PublicUserGeneratedContentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserGeneratedContentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserLocationCRUDService = /*#__PURE__*/(() => {
  class PublicUserLocationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createUserLocation(subdomain, userLocationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserLocation.');
      }
      if (userLocationCreateSchema === null || userLocationCreateSchema === undefined) {
        throw new Error('Required parameter userLocationCreateSchema was null or undefined when calling createUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createUserLocationByEvent(subdomain, eventId, userLocationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserLocationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createUserLocationByEvent.');
      }
      if (userLocationCreateSchema === null || userLocationCreateSchema === undefined) {
        throw new Error('Required parameter userLocationCreateSchema was null or undefined when calling createUserLocationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createUserLocationHistory(subdomain, userLocationHistoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserLocationHistory.');
      }
      if (userLocationHistoryCreateSchema === null || userLocationHistoryCreateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryCreateSchema was null or undefined when calling createUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserLocation(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserLocation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserLocationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserLocationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserLocationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteUserLocationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserLocationHistory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserLocationHistory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserLocationHistories(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserLocationHistories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserLocations(subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserLocations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserLocationsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserLocationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserLocationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocation(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserLocation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserLocationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserLocationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserLocationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationHistory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserLocationHistory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationHistoryCount(subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationHistoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserLocationHistories(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserLocationHistories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserLocations(subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserLocations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserLocationsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserLocationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserLocationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserLocation(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserLocation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserLocationByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserLocationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserLocationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderUserLocationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserLocationHistory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserLocationHistory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserLocation(itemId, subdomain, userLocationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserLocation.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserLocation.');
      }
      if (userLocationUpdateSchema === null || userLocationUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationUpdateSchema was null or undefined when calling updateUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserLocationByEvent(itemId, subdomain, eventId, userLocationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserLocationByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserLocationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateUserLocationByEvent.');
      }
      if (userLocationUpdateSchema === null || userLocationUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationUpdateSchema was null or undefined when calling updateUserLocationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserLocationHistory(itemId, subdomain, userLocationHistoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserLocationHistory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserLocationHistory.');
      }
      if (userLocationHistoryUpdateSchema === null || userLocationHistoryUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryUpdateSchema was null or undefined when calling updateUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserLocationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserLocationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserLocationCRUDService,
        factory: PublicUserLocationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserLocationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserLocationHistoryCRUDService = /*#__PURE__*/(() => {
  class PublicUserLocationHistoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createUserLocationHistoryByEvent(subdomain, eventId, userLocationHistoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createUserLocationHistoryByEvent.');
      }
      if (userLocationHistoryCreateSchema === null || userLocationHistoryCreateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryCreateSchema was null or undefined when calling createUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserLocationHistoryByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserLocationHistoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserLocationHistoriesByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserLocationHistoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllUserLocationHistoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationHistoryByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserLocationHistoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserLocationHistoryCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserLocationHistoryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getUserLocationHistoryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserLocationHistoriesByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserLocationHistoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllUserLocationHistoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserLocationHistoryByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserLocationHistoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateUserLocationHistoryByEvent(itemId, subdomain, eventId, userLocationHistoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateUserLocationHistoryByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateUserLocationHistoryByEvent.');
      }
      if (userLocationHistoryUpdateSchema === null || userLocationHistoryUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryUpdateSchema was null or undefined when calling updateUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserLocationHistoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserLocationHistoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserLocationHistoryCRUDService,
        factory: PublicUserLocationHistoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserLocationHistoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicUserSubscriptionCRUDService = /*#__PURE__*/(() => {
  class PublicUserSubscriptionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createUserSubscriptionBySubscription(subscriptionId, subdomain, userSubscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling createUserSubscriptionBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserSubscriptionBySubscription.');
      }
      if (userSubscriptionCreateSchema === null || userSubscriptionCreateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionCreateSchema was null or undefined when calling createUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createUserSubscriptionByUser(userId, subdomain, userSubscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling createUserSubscriptionByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createUserSubscriptionByUser.');
      }
      if (userSubscriptionCreateSchema === null || userSubscriptionCreateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionCreateSchema was null or undefined when calling createUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllUserSubscriptionsBySubscription(subscriptionId, subdomain, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling deleteAllUserSubscriptionsBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllUserSubscriptionsBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllUserSubscriptionsByUser(userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteAllUserSubscriptionsByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllUserSubscriptionsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserSubscriptionBySubscription(itemId, subscriptionId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling deleteUserSubscriptionBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteUserSubscriptionByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling deleteUserSubscriptionByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserSubscriptionsBySubscription(subscriptionId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling getAllUserSubscriptionsBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserSubscriptionsBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserSubscriptionsBySubscriptionMulti(subscriptionId, subdomain, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling getAllUserSubscriptionsBySubscriptionMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserSubscriptionsBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserSubscriptionsByUser(userId, subdomain, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAllUserSubscriptionsByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserSubscriptionsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllUserSubscriptionsByUserMulti(userId, subdomain, idList, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getAllUserSubscriptionsByUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllUserSubscriptionsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionBySubscription(itemId, subscriptionId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling getUserSubscriptionBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionByUser(itemId, userId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getUserSubscriptionByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionCountBySubscription(subscriptionId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling getUserSubscriptionCountBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionCountBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionCountBySubscriptionMulti(subscriptionId, subdomain, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling getUserSubscriptionCountBySubscriptionMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionCountBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionCountByUser(userId, subdomain, orderBy, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getUserSubscriptionCountByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserSubscriptionCountByUserMulti(userId, subdomain, idList, orderBy, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling getUserSubscriptionCountByUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserSubscriptionCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserSubscriptionsBySubscription(subscriptionId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling renderAllUserSubscriptionsBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserSubscriptionsBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserSubscriptionsBySubscriptionMulti(subscriptionId, subdomain, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling renderAllUserSubscriptionsBySubscriptionMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserSubscriptionsBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserSubscriptionsByUser(userId, subdomain, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAllUserSubscriptionsByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserSubscriptionsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllUserSubscriptionsByUserMulti(userId, subdomain, idList, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderAllUserSubscriptionsByUserMulti.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllUserSubscriptionsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserSubscriptionBySubscription(itemId, subscriptionId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling renderUserSubscriptionBySubscription.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderUserSubscriptionByUser(itemId, userId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling renderUserSubscriptionByUser.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setUserSubscriptions(subdomain, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setUserSubscriptions.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setUserSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/user//user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicUserSubscriptionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicUserSubscriptionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicUserSubscriptionCRUDService,
        factory: PublicUserSubscriptionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicUserSubscriptionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicVenueCRUDService = /*#__PURE__*/(() => {
  class PublicVenueCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadVenueLogo(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadVenueLogo.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadVenueLogo.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadVenuePicture(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadVenuePicture.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadVenuePicture.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVenues(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVenues.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVenuesMulti(subdomain, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVenuesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVenuesSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVenuesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenue(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getVenue.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenue.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCountMulti(subdomain, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCountSearch(subdomain, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllVenues(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllVenues.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllVenuesMulti(subdomain, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllVenuesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllVenuesSearch(subdomain, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllVenuesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderVenue(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderVenue.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicVenueCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicVenueCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicVenueCRUDService,
        factory: PublicVenueCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicVenueCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicVenueCategoryCRUDService = /*#__PURE__*/(() => {
  class PublicVenueCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    getAllVenueCategories(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVenueCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllVenueCategoriesSearch(subdomain, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllVenueCategoriesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCategory(itemId, subdomain, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getVenueCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCategoryCount(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getVenueCategoryCountSearch(subdomain, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getVenueCategoryCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllVenueCategories(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllVenueCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllVenueCategoriesSearch(subdomain, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllVenueCategoriesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderVenueCategory(itemId, subdomain, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderVenueCategory.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicVenueCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicVenueCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicVenueCategoryCRUDService,
        factory: PublicVenueCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicVenueCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicWallCRUDService = /*#__PURE__*/(() => {
  class PublicWallCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createWallByEvent(subdomain, eventId, wallCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createWallByEvent.');
      }
      if (wallCreateSchema === null || wallCreateSchema === undefined) {
        throw new Error('Required parameter wallCreateSchema was null or undefined when calling createWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createWallByEventAndAgenda(subdomain, eventId, agendaId, wallCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling createWallByEventAndAgenda.');
      }
      if (wallCreateSchema === null || wallCreateSchema === undefined) {
        throw new Error('Required parameter wallCreateSchema was null or undefined when calling createWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadWallImageByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadWallImageByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadWallImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadWallImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadWallImageByEventAndAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling downloadWallImageByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadWallImageByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadWallImageByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling downloadWallImageByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllWallsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllWallsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllWallsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllWallsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllWallsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllWallsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getAllWallsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWallByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getWallByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWallByEventAndAgenda(itemId, subdomain, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getWallByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWallCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWallCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWallCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWallCountByEventAndAgenda(subdomain, eventId, agendaId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWallCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWallCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling getWallCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllWallsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllWallsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllWallsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllWallsByEventAndAgenda(subdomain, eventId, agendaId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllWallsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllWallsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderAllWallsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderWallByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderWallByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderWallByEventAndAgenda(itemId, subdomain, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderWallByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling renderWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadWallImageBase64ByEvent(itemId, subdomain, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadWallImageBase64ByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadWallImageBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadWallImageBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadWallImageBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadWallImageBase64ByEventAndAgenda(itemId, subdomain, eventId, agendaId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadWallImageBase64ByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadWallImageBase64ByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadWallImageBase64ByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling uploadWallImageBase64ByEventAndAgenda.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling uploadWallImageBase64ByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadWallImageByEvent(itemId, subdomain, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadWallImageByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadWallImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadWallImageByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadWallImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadWallImageByEventAndAgenda(itemId, subdomain, eventId, agendaId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling uploadWallImageByEventAndAgenda.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling uploadWallImageByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling uploadWallImageByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling uploadWallImageByEventAndAgenda.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling uploadWallImageByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicWallCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicWallCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicWallCRUDService,
        factory: PublicWallCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicWallCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PublicWordsCloudCRUDService = /*#__PURE__*/(() => {
  class PublicWordsCloudCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createWordscloudByEvent(subdomain, eventId, wordsCloudCreateSchema, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling createWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling createWordscloudByEvent.');
      }
      if (wordsCloudCreateSchema === null || wordsCloudCreateSchema === undefined) {
        throw new Error('Required parameter wordsCloudCreateSchema was null or undefined when calling createWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAllWordscloudsByEvent(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteWordscloudByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteWordscloudByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling deleteWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling deleteWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllWordscloudsByEvent(subdomain, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWordscloudByEvent(itemId, subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getWordscloudByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWordscloudCountByEvent(subdomain, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWordscloudCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWordscloudCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWordscloudFrequencies(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getWordscloudFrequencies.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getWordscloudFrequencies.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_frequencies`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllWordscloudsByEvent(subdomain, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderWordscloudByEvent(itemId, subdomain, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderWordscloudByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateWordscloudByEvent(itemId, subdomain, eventId, wordsCloudUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateWordscloudByEvent.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling updateWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling updateWordscloudByEvent.');
      }
      if (wordsCloudUpdateSchema === null || wordsCloudUpdateSchema === undefined) {
        throw new Error('Required parameter wordsCloudUpdateSchema was null or undefined when calling updateWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/crud/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PublicWordsCloudCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PublicWordsCloudCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PublicWordsCloudCRUDService,
        factory: PublicWordsCloudCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PublicWordsCloudCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let PushNotificationsService = /*#__PURE__*/(() => {
  class PushNotificationsService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    managePushPreview(companyId, pushInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling managePushPreview.');
      }
      if (pushInput === null || pushInput === undefined) {
        throw new Error('Required parameter pushInput was null or undefined when calling managePushPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/push/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pushInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    managePushSend(companyId, pushInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling managePushSend.');
      }
      if (pushInput === null || pushInput === undefined) {
        throw new Error('Required parameter pushInput was null or undefined when calling managePushSend.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/push/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/send`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: pushInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function PushNotificationsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PushNotificationsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PushNotificationsService,
        factory: PushNotificationsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PushNotificationsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let QuizCRUDService = /*#__PURE__*/(() => {
  class QuizCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateQuiz(companyId, quizCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateQuiz.');
      }
      if (quizCreateSchema === null || quizCreateSchema === undefined) {
        throw new Error('Required parameter quizCreateSchema was null or undefined when calling manageCreateQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateQuizAnswer(companyId, quizId, quizQuestionId, quizAnswerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageCreateQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageCreateQuizAnswer.');
      }
      if (quizAnswerCreateSchema === null || quizAnswerCreateSchema === undefined) {
        throw new Error('Required parameter quizAnswerCreateSchema was null or undefined when calling manageCreateQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizAnswerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateQuizQuestion(companyId, quizId, quizQuestionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageCreateQuizQuestion.');
      }
      if (quizQuestionCreateSchema === null || quizQuestionCreateSchema === undefined) {
        throw new Error('Required parameter quizQuestionCreateSchema was null or undefined when calling manageCreateQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizQuestionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateQuizUserAnswer(companyId, quizId, quizQuestionId, quizAnswerId, quizUserAnswerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageCreateQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageCreateQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageCreateQuizUserAnswer.');
      }
      if (quizUserAnswerCreateSchema === null || quizUserAnswerCreateSchema === undefined) {
        throw new Error('Required parameter quizUserAnswerCreateSchema was null or undefined when calling manageCreateQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizUserAnswerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizAnswers(companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteAllQuizAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteAllQuizAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizQuestions(companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizQuestions.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteAllQuizQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizUserAnswers(companyId, quizId, quizQuestionId, quizAnswerId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizUserAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteAllQuizUserAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteAllQuizUserAnswers.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageDeleteAllQuizUserAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizUserAnswersByCompany(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizUserAnswersByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizUserAnswersByQuestion(companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizUserAnswersByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteAllQuizUserAnswersByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteAllQuizUserAnswersByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizUserAnswersByQuiz(companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizUserAnswersByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteAllQuizUserAnswersByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllQuizzes(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllQuizzes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuiz(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizAnswer(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizAnswerAsset(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizAnswerAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizAnswerAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizAnswerAsset.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteQuizAnswerAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizAnswerPreview(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizAnswerPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizAnswerPreview.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizAnswerPreview.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteQuizAnswerPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizQuestion(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizQuestionAsset(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizQuestionAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizQuestionAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizQuestionAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizUserAnswer(itemId, companyId, quizId, quizQuestionId, quizAnswerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizUserAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageDeleteQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizUserAnswerByCompany(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizUserAnswerByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizUserAnswerByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizUserAnswerByQuestion(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizUserAnswerByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizUserAnswerByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizUserAnswerByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDeleteQuizUserAnswerByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteQuizUserAnswerByQuiz(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteQuizUserAnswerByQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteQuizUserAnswerByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDeleteQuizUserAnswerByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizAnswerAsset(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadQuizAnswerAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizAnswerAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizAnswerAsset.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDownloadQuizAnswerAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizAnswerPreview(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadQuizAnswerPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizAnswerPreview.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizAnswerPreview.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageDownloadQuizAnswerPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizQuestionAsset(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadQuizQuestionAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizQuestionAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizQuestionAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizAnswers(companyId, quizId, quizQuestionId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetAllQuizAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetAllQuizAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizQuestions(companyId, quizId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizQuestions.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetAllQuizQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizUserAnswers(companyId, quizId, quizQuestionId, quizAnswerId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizUserAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetAllQuizUserAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetAllQuizUserAnswers.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageGetAllQuizUserAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizUserAnswersByCompany(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizUserAnswersByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizUserAnswersByQuestion(companyId, quizId, quizQuestionId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizUserAnswersByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetAllQuizUserAnswersByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetAllQuizUserAnswersByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizUserAnswersByQuiz(companyId, quizId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizUserAnswersByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetAllQuizUserAnswersByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllQuizzes(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllQuizzes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuiz(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizAnswer(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizAnswerCount(companyId, quizId, quizQuestionId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizAnswerCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizAnswerCount.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizAnswerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizQuestion(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizQuestionCount(companyId, quizId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizQuestionCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizQuestionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswer(itemId, companyId, quizId, quizQuestionId, quizAnswerId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizUserAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageGetQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerByCompany(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizUserAnswerByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerByQuestion(itemId, companyId, quizId, quizQuestionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizUserAnswerByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswerByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizUserAnswerByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerByQuiz(itemId, companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetQuizUserAnswerByQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswerByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerCount(companyId, quizId, quizQuestionId, quizAnswerId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerCount.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswerCount.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizUserAnswerCount.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageGetQuizUserAnswerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerCountByCompany(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerCountByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerCountByQuestion(companyId, quizId, quizQuestionId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerCountByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswerCountByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageGetQuizUserAnswerCountByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetQuizUserAnswerCountByQuiz(companyId, quizId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetQuizUserAnswerCountByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageGetQuizUserAnswerCountByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizAnswers(companyId, quizId, quizQuestionId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderAllQuizAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderAllQuizAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizQuestions(companyId, quizId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizQuestions.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderAllQuizQuestions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizUserAnswers(companyId, quizId, quizQuestionId, quizAnswerId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizUserAnswers.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderAllQuizUserAnswers.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderAllQuizUserAnswers.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageRenderAllQuizUserAnswers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizUserAnswersByCompany(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizUserAnswersByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizUserAnswersByQuestion(companyId, quizId, quizQuestionId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizUserAnswersByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderAllQuizUserAnswersByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderAllQuizUserAnswersByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizUserAnswersByQuiz(companyId, quizId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizUserAnswersByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderAllQuizUserAnswersByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllQuizzes(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllQuizzes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuiz(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizAnswer(itemId, companyId, quizId, quizQuestionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizQuestion(itemId, companyId, quizId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizUserAnswer(itemId, companyId, quizId, quizQuestionId, quizAnswerId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizUserAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageRenderQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizUserAnswerByCompany(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizUserAnswerByCompany.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizUserAnswerByCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizUserAnswerByQuestion(itemId, companyId, quizId, quizQuestionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizUserAnswerByQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizUserAnswerByQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderQuizUserAnswerByQuestion.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageRenderQuizUserAnswerByQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderQuizUserAnswerByQuiz(itemId, companyId, quizId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderQuizUserAnswerByQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderQuizUserAnswerByQuiz.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageRenderQuizUserAnswerByQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateQuiz(itemId, companyId, quizUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateQuiz.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateQuiz.');
      }
      if (quizUpdateSchema === null || quizUpdateSchema === undefined) {
        throw new Error('Required parameter quizUpdateSchema was null or undefined when calling manageUpdateQuiz.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateQuizAnswer(itemId, companyId, quizId, quizQuestionId, quizAnswerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateQuizAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateQuizAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUpdateQuizAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUpdateQuizAnswer.');
      }
      if (quizAnswerUpdateSchema === null || quizAnswerUpdateSchema === undefined) {
        throw new Error('Required parameter quizAnswerUpdateSchema was null or undefined when calling manageUpdateQuizAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizAnswerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateQuizQuestion(itemId, companyId, quizId, quizQuestionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateQuizQuestion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateQuizQuestion.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUpdateQuizQuestion.');
      }
      if (quizQuestionUpdateSchema === null || quizQuestionUpdateSchema === undefined) {
        throw new Error('Required parameter quizQuestionUpdateSchema was null or undefined when calling manageUpdateQuizQuestion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizQuestionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateQuizUserAnswer(itemId, companyId, quizId, quizQuestionId, quizAnswerId, quizUserAnswerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      if (quizAnswerId === null || quizAnswerId === undefined) {
        throw new Error('Required parameter quizAnswerId was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      if (quizUserAnswerUpdateSchema === null || quizUserAnswerUpdateSchema === undefined) {
        throw new Error('Required parameter quizUserAnswerUpdateSchema was null or undefined when calling manageUpdateQuizUserAnswer.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'quizAnswerId',
        value: quizAnswerId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_user_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: quizUserAnswerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizAnswerAsset(itemId, companyId, quizId, quizQuestionId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizAnswerAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizAnswerAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizAnswerAsset.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUploadQuizAnswerAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadQuizAnswerAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizAnswerAssetBase64(itemId, companyId, quizId, quizQuestionId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizAnswerAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizAnswerAssetBase64.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizAnswerAssetBase64.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUploadQuizAnswerAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadQuizAnswerAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizAnswerPreview(itemId, companyId, quizId, quizQuestionId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizAnswerPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizAnswerPreview.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizAnswerPreview.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUploadQuizAnswerPreview.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadQuizAnswerPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizAnswerPreviewBase64(itemId, companyId, quizId, quizQuestionId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizAnswerPreviewBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizAnswerPreviewBase64.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizAnswerPreviewBase64.');
      }
      if (quizQuestionId === null || quizQuestionId === undefined) {
        throw new Error('Required parameter quizQuestionId was null or undefined when calling manageUploadQuizAnswerPreviewBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadQuizAnswerPreviewBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'quizQuestionId',
        value: quizQuestionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_answer/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizQuestionAsset(itemId, companyId, quizId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizQuestionAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizQuestionAsset.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizQuestionAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadQuizQuestionAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadQuizQuestionAssetBase64(itemId, companyId, quizId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadQuizQuestionAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadQuizQuestionAssetBase64.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageUploadQuizQuestionAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadQuizQuestionAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/quiz_question/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function QuizCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QuizCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: QuizCRUDService,
        factory: QuizCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return QuizCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let RegistrationFormCRUDService = /*#__PURE__*/(() => {
  class RegistrationFormCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCloneRegistrationForm(companyId, registrationFormId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCloneRegistrationForm.');
      }
      if (registrationFormId === null || registrationFormId === undefined) {
        throw new Error('Required parameter registrationFormId was null or undefined when calling manageCloneRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'registrationFormId',
        value: registrationFormId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/clone`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateRegistrationForm(companyId, registrationFormCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateRegistrationForm.');
      }
      if (registrationFormCreateSchema === null || registrationFormCreateSchema === undefined) {
        throw new Error('Required parameter registrationFormCreateSchema was null or undefined when calling manageCreateRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registrationFormCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteRegistrationForm(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteRegistrationForm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllRegistrationForms(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllRegistrationForms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllRegistrationFormsMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllRegistrationFormsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllRegistrationFormsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllRegistrationFormsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRegistrationForm(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetRegistrationForm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRegistrationFormCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRegistrationFormCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRegistrationFormCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRegistrationFormCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRegistrationFormCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRegistrationFormCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllRegistrationForms(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllRegistrationForms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllRegistrationFormsMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllRegistrationFormsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllRegistrationFormsSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllRegistrationFormsSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderRegistrationForm(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderRegistrationForm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateRegistrationForm(itemId, companyId, registrationFormUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateRegistrationForm.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateRegistrationForm.');
      }
      if (registrationFormUpdateSchema === null || registrationFormUpdateSchema === undefined) {
        throw new Error('Required parameter registrationFormUpdateSchema was null or undefined when calling manageUpdateRegistrationForm.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/registration_form/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registrationFormUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function RegistrationFormCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RegistrationFormCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RegistrationFormCRUDService,
        factory: RegistrationFormCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RegistrationFormCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ReportService = /*#__PURE__*/(() => {
  class ReportService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    downloadExhibitorReport(subdomain, eventId, fileName, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadExhibitorReport.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadExhibitorReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, fileName, 'file_name');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/exhibitor/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAgendaReport(companyId, eventId, agendaId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAgendaReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, agendaId, 'agenda_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/agenda/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadAllPollVoteReport(companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadAllPollVoteReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, agendaId, 'agenda_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/poll_vote/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCarReservationReport(companyId, eventId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCarReservationReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/car_reservation/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadCompleteFlowsReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadCompleteFlowsReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/complete_flows/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentDownloadReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentDownloadReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/document-download/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadDocumentRatingReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadDocumentRatingReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/document-rating/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadElapsedReport(companyId, streamId, rangeIn, rangeOut, scope, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadElapsedReport.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDownloadElapsedReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, scope, 'scope');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/elapsed/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventPictureReport(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventPictureReport.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadEventPictureReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/event_picture/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFlightReservationReport(companyId, eventId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFlightReservationReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/flight_reservation/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadFlowReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadFlowReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/flows/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadHotelReservationReport(companyId, eventId, groupId, hotelId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadHotelReservationReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, hotelId, 'hotel_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/hotel_reservation/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadLivechatAgendaReport(eventId, agendaId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadLivechatAgendaReport.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDownloadLivechatAgendaReport.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadLivechatAgendaReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/livechat/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadLivechatEventReport(eventId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadLivechatEventReport.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadLivechatEventReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/livechat/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadMarketingReport(companyId, groupIdString, category, subcategory, searchTerms, orderBy, columnList, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadMarketingReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupIdString, 'group_id_string');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, category, 'category');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, subcategory, 'subcategory');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columnList, 'column_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/marketing/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadPollVoteReport(companyId, pollId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadPollVoteReport.');
      }
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling manageDownloadPollVoteReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/poll_vote/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadPresenceReport(companyId, streamId, rangeIn, rangeOut, scope, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadPresenceReport.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDownloadPresenceReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, scope, 'scope');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/presence/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizAnswerReport(companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizAnswerReport.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizAnswerReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/quiz_score/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/answer`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizGroupScoreReport(companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizGroupScoreReport.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizGroupScoreReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/quiz_score/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/group`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadQuizUserScoreReport(companyId, quizId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadQuizUserScoreReport.');
      }
      if (quizId === null || quizId === undefined) {
        throw new Error('Required parameter quizId was null or undefined when calling manageDownloadQuizUserScoreReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/quiz_score/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'quizId',
        value: quizId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadReactionsReport(companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadReactionsReport.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDownloadReactionsReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/reactions/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadRestaurantReport(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadRestaurantReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/restaurant/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadTrainReservationReport(companyId, eventId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadTrainReservationReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/train_reservation/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadTravelReport(companyId, eventId, groupId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadTravelReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupId, 'group_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/travel/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserExportReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserExportReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/user_export/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserImportTemplate(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/user_import:template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadViewersJsonReport(companyId, streamId, rangeIn, rangeOut, scope, interval, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadViewersJsonReport.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDownloadViewersJsonReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, scope, 'scope');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, interval, 'interval');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/viewers/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/json/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadViewersReport(companyId, streamId, rangeIn, rangeOut, scope, interval, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadViewersReport.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDownloadViewersReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, scope, 'scope');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, interval, 'interval');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/viewers/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadWallAgendaReport(eventId, agendaId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadWallAgendaReport.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDownloadWallAgendaReport.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadWallAgendaReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/wall/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadWallEventReport(eventId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadWallEventReport.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadWallEventReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/wall/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetMarketingData(companyId, groupIdString, category, subcategory, searchTerms, orderBy, columnList, limit, skip, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetMarketingData.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupIdString, 'group_id_string');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, category, 'category');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, subcategory, 'subcategory');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columnList, 'column_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/marketing/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendAllGaerAgendaReports(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendAllGaerAgendaReports.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/gaer-agenda/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/all`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendAllGaerExhibitorReports(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendAllGaerExhibitorReports.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/gaer-exhibitor/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/all`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendFeedbackEventReport(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendFeedbackEventReport.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageSendFeedbackEventReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/feedback/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendGaerAgendaReport(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendGaerAgendaReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/gaer-agenda/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendGaerExhibitorReport(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendGaerExhibitorReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/gaer-exhibitor/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendSentMailReport(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendSentMailReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/sent_mail/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/report`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSendUserReport(companyId, eventIdList, agendaIdList, groupIdList, columnList, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSendUserReport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventIdList, 'event_id_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, agendaIdList, 'agenda_id_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, groupIdList, 'group_id_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columnList, 'column_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/user/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportAgenda(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportAgenda.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/agenda_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportCarReservation(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportCarReservation.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportCarReservation.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportCarReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/car_reservation_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportCarReservationAttachments(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportCarReservationAttachments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportCarReservationAttachments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportCarReservationAttachments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/car_reservation_attachments_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportEventDocuments(companyId, eventId, dataFile, importThumbnails, categoryLabel, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportEventDocuments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportEventDocuments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportEventDocuments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, importThumbnails, 'import_thumbnails');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, categoryLabel, 'category_label');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/event_documents_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportExhibitor(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportExhibitor.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportExhibitor.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportExhibitor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/exhibitor_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportFlightReservation(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportFlightReservation.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportFlightReservation.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportFlightReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/flight_reservation_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportFlightReservationAttachments(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportFlightReservationAttachments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportFlightReservationAttachments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportFlightReservationAttachments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/flight_reservation_attachments_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportHotelReservation(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportHotelReservation.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportHotelReservation.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportHotelReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/hotel_reservation_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportHotelReservationAttachments(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportHotelReservationAttachments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportHotelReservationAttachments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportHotelReservationAttachments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/hotel_reservation_attachments_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportTrainReservation(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportTrainReservation.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportTrainReservation.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportTrainReservation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/train_reservation_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportTrainReservationAttachments(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportTrainReservationAttachments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportTrainReservationAttachments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportTrainReservationAttachments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/train_reservation_attachments_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportTravel(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportTravel.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportTravel.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportTravel.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/travel_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadImportTravelAttachments(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadImportTravelAttachments.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadImportTravelAttachments.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadImportTravelAttachments.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/travel_attachments_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUnicreditImportAgendas(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUnicreditImportAgendas.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadUnicreditImportAgendas.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadUnicreditImportAgendas.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/import/unicredit/agenda/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUnicreditImportFirms(companyId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUnicreditImportFirms.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadUnicreditImportFirms.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/import/unicredit/firm/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUnicreditImportMeetings(companyId, eventId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUnicreditImportMeetings.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadUnicreditImportMeetings.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadUnicreditImportMeetings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/import/unicredit/meeting/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserImport(companyId, dataFile, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserImport.');
      }
      if (dataFile === null || dataFile === undefined) {
        throw new Error('Required parameter dataFile was null or undefined when calling manageUploadUserImport.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (dataFile !== undefined) {
        localVarFormParams = localVarFormParams.append('data_file', dataFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/reports/manage/user_import/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ReportService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ReportService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ReportService,
        factory: ReportService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ReportService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let RestaurantCRUDService = /*#__PURE__*/(() => {
  class RestaurantCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateRestaurantByEvent(companyId, eventId, restaurantCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateRestaurantByEvent.');
      }
      if (restaurantCreateSchema === null || restaurantCreateSchema === undefined) {
        throw new Error('Required parameter restaurantCreateSchema was null or undefined when calling manageCreateRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: restaurantCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteRestaurantByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteRestaurantByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllRestaurantsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllRestaurantsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllRestaurantsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllRestaurantsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllRestaurantsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllRestaurantsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRestaurantByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetRestaurantByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRestaurantCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRestaurantCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetRestaurantCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetRestaurantCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetRestaurantCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetRestaurantCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllRestaurantsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllRestaurantsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllRestaurantsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllRestaurantsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllRestaurantsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllRestaurantsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderRestaurantByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderRestaurantByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateRestaurantByEvent(itemId, companyId, eventId, restaurantUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateRestaurantByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateRestaurantByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateRestaurantByEvent.');
      }
      if (restaurantUpdateSchema === null || restaurantUpdateSchema === undefined) {
        throw new Error('Required parameter restaurantUpdateSchema was null or undefined when calling manageUpdateRestaurantByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: restaurantUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderRestaurantQrCode(companyId, eventId, restaurantId, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling renderRestaurantQrCode.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling renderRestaurantQrCode.');
      }
      if (restaurantId === null || restaurantId === undefined) {
        throw new Error('Required parameter restaurantId was null or undefined when calling renderRestaurantQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/restaurant.render_qr_code/${this.configuration.encodeParam({
        name: 'restaurantId',
        value: restaurantId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function RestaurantCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RestaurantCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RestaurantCRUDService,
        factory: RestaurantCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RestaurantCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SelfService = /*#__PURE__*/(() => {
  class SelfService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    calendarAgendaList(rangeIn, rangeOut, eventId, companyId, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, companyId, 'company_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/agenda_calendar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    calendarEventList(rangeIn, rangeOut, companyId, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, companyId, 'company_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/event_calendar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    companyEventIndex(companyId, details, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling companyEventIndex.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, details, 'details');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    companyIndex(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/company`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    eventList(rangeIn, rangeOut, companyId, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, companyId, 'company_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/event`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCompanyEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling getCompanyEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getCompanyEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    info(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetApiKeyInfo(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/manage/api_key_info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStats(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/stats`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStripoToken(observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/manage/stripo_token`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    streamList(rangeIn, rangeOut, observe = 'body', reportProgress = false, options) {
      if (rangeIn === null || rangeIn === undefined) {
        throw new Error('Required parameter rangeIn was null or undefined when calling streamList.');
      }
      if (rangeOut === null || rangeOut === undefined) {
        throw new Error('Required parameter rangeOut was null or undefined when calling streamList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeIn, 'range_in');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, rangeOut, 'range_out');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/self/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SelfService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SelfService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SelfService,
        factory: SelfService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SelfService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SentMailCRUDService = /*#__PURE__*/(() => {
  class SentMailCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageGetAllSentMails(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSentMails.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSentMailsByMailTemplate(companyId, mailTemplateId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSentMailsByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetAllSentMailsByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentMail(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSentMail.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentMail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentMailByMailTemplate(itemId, companyId, mailTemplateId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSentMailByMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentMailByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetSentMailByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentMailCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentMailCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentMailCountByMailTemplate(companyId, mailTemplateId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentMailCountByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageGetSentMailCountByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSentMails(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSentMails.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSentMailsByMailTemplate(companyId, mailTemplateId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSentMailsByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageRenderAllSentMailsByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSentMail(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSentMail.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSentMail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSentMailByMailTemplate(itemId, companyId, mailTemplateId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSentMailByMailTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSentMailByMailTemplate.');
      }
      if (mailTemplateId === null || mailTemplateId === undefined) {
        throw new Error('Required parameter mailTemplateId was null or undefined when calling manageRenderSentMailByMailTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/mail_template/${this.configuration.encodeParam({
        name: 'mailTemplateId',
        value: mailTemplateId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_mail/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SentMailCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SentMailCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SentMailCRUDService,
        factory: SentMailCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SentMailCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SentPushCRUDService = /*#__PURE__*/(() => {
  class SentPushCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageGetAllSentPushes(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSentPushes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_push`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentPush(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSentPush.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentPush.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_push/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSentPushCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSentPushCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_push_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSentPushes(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSentPushes.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_push/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSentPush(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSentPush.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSentPush.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sent_push/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SentPushCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SentPushCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SentPushCRUDService,
        factory: SentPushCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SentPushCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SettingCRUDService = /*#__PURE__*/(() => {
  class SettingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageDeleteSettingBackground(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSettingBackground.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSettingBackground.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/background`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSettingFavicon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSettingFavicon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSettingFavicon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/favicon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSettingLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSettingLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSettingLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSettingWebAppIcon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSettingWebAppIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSettingWebAppIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/web_app_icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSettingBackground(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSettingBackground.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSettingBackground.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/background`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSettingFavicon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSettingFavicon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSettingFavicon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/favicon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSettingLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSettingLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSettingLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSettingWebAppIcon(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSettingWebAppIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSettingWebAppIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/web_app_icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSettings(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSetting(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSettingCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSettingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSettings(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSetting(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateSetting(itemId, companyId, settingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateSetting.');
      }
      if (settingUpdateSchema === null || settingUpdateSchema === undefined) {
        throw new Error('Required parameter settingUpdateSchema was null or undefined when calling manageUpdateSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: settingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingBackground(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingBackground.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingBackground.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSettingBackground.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/background`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingBackgroundBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingBackgroundBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingBackgroundBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSettingBackgroundBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/background/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingFavicon(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingFavicon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingFavicon.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSettingFavicon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/favicon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingFaviconBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingFaviconBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingFaviconBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSettingFaviconBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/favicon/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSettingLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSettingLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingWebAppIcon(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingWebAppIcon.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingWebAppIcon.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSettingWebAppIcon.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/web_app_icon`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSettingWebAppIconBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSettingWebAppIconBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSettingWebAppIconBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSettingWebAppIconBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/web_app_icon/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SettingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SettingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SettingCRUDService,
        factory: SettingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SettingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ShuttleScheduleCRUDService = /*#__PURE__*/(() => {
  class ShuttleScheduleCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateShuttleScheduleByEvent(companyId, eventId, shuttleScheduleCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateShuttleScheduleByEvent.');
      }
      if (shuttleScheduleCreateSchema === null || shuttleScheduleCreateSchema === undefined) {
        throw new Error('Required parameter shuttleScheduleCreateSchema was null or undefined when calling manageCreateShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: shuttleScheduleCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteShuttleScheduleByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteShuttleScheduleByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllShuttleSchedulesByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllShuttleSchedulesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllShuttleSchedulesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetShuttleScheduleByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetShuttleScheduleByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetShuttleScheduleCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetShuttleScheduleCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetShuttleScheduleCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllShuttleSchedulesByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllShuttleSchedulesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllShuttleSchedulesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderShuttleScheduleByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderShuttleScheduleByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateShuttleScheduleByEvent(itemId, companyId, eventId, shuttleScheduleUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateShuttleScheduleByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateShuttleScheduleByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateShuttleScheduleByEvent.');
      }
      if (shuttleScheduleUpdateSchema === null || shuttleScheduleUpdateSchema === undefined) {
        throw new Error('Required parameter shuttleScheduleUpdateSchema was null or undefined when calling manageUpdateShuttleScheduleByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/shuttle_schedule/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: shuttleScheduleUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ShuttleScheduleCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ShuttleScheduleCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ShuttleScheduleCRUDService,
        factory: ShuttleScheduleCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ShuttleScheduleCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SiteService = /*#__PURE__*/(() => {
  class SiteService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    checkSubdomainAvailability(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling checkSubdomainAvailability.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/site/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/available`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createCompany(createCompanyInput, observe = 'body', reportProgress = false, options) {
      if (createCompanyInput === null || createCompanyInput === undefined) {
        throw new Error('Required parameter createCompanyInput was null or undefined when calling createCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/site/create`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: createCompanyInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createSitePayment(createSitePaymentInput, observe = 'body', reportProgress = false, options) {
      if (createSitePaymentInput === null || createSitePaymentInput === undefined) {
        throw new Error('Required parameter createSitePaymentInput was null or undefined when calling createSitePayment.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/site/payment/create`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: createSitePaymentInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCompanySiteInfo(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCompanySiteInfo.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/site/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SiteService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SiteService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SiteService,
        factory: SiteService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SiteService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SmsTemplateCRUDService = /*#__PURE__*/(() => {
  class SmsTemplateCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateSmsTemplate(companyId, sMSTemplateCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateSmsTemplate.');
      }
      if (sMSTemplateCreateSchema === null || sMSTemplateCreateSchema === undefined) {
        throw new Error('Required parameter sMSTemplateCreateSchema was null or undefined when calling manageCreateSmsTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sMSTemplateCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSmsTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSmsTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSmsTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSmsTemplates(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSmsTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSmsTemplate(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSmsTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSmsTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSmsTemplateCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSmsTemplateCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSmsTemplates(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSmsTemplates.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSmsTemplate(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSmsTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSmsTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateSmsTemplate(itemId, companyId, sMSTemplateUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateSmsTemplate.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateSmsTemplate.');
      }
      if (sMSTemplateUpdateSchema === null || sMSTemplateUpdateSchema === undefined) {
        throw new Error('Required parameter sMSTemplateUpdateSchema was null or undefined when calling manageUpdateSmsTemplate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sms_template/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sMSTemplateUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SmsTemplateCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SmsTemplateCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SmsTemplateCRUDService,
        factory: SmsTemplateCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SmsTemplateCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SpeakerCRUDService = /*#__PURE__*/(() => {
  class SpeakerCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateSpeaker(companyId, speakerCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateSpeaker.');
      }
      if (speakerCreateSchema === null || speakerCreateSchema === undefined) {
        throw new Error('Required parameter speakerCreateSchema was null or undefined when calling manageCreateSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: speakerCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSpeakerPhoto(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSpeakerPhoto.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSpeakerPhoto.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/photo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSpeakerPhoto(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSpeakerPhoto.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSpeakerPhoto.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/photo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSpeakers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSpeaker(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSpeakerCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSpeakerCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSpeakers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSpeakers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSpeaker(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateSpeaker(itemId, companyId, speakerUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateSpeaker.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateSpeaker.');
      }
      if (speakerUpdateSchema === null || speakerUpdateSchema === undefined) {
        throw new Error('Required parameter speakerUpdateSchema was null or undefined when calling manageUpdateSpeaker.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: speakerUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSpeakerPhoto(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSpeakerPhoto.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSpeakerPhoto.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSpeakerPhoto.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/photo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSpeakerPhotoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSpeakerPhotoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSpeakerPhotoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSpeakerPhotoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/speaker/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/photo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SpeakerCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SpeakerCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SpeakerCRUDService,
        factory: SpeakerCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SpeakerCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SponsorCRUDService = /*#__PURE__*/(() => {
  class SponsorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateSponsor(companyId, sponsorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateSponsor.');
      }
      if (sponsorCreateSchema === null || sponsorCreateSchema === undefined) {
        throw new Error('Required parameter sponsorCreateSchema was null or undefined when calling manageCreateSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sponsorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSponsor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSponsorLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSponsorLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSponsorLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadSponsorLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadSponsorLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadSponsorLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSponsors(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSponsor(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSponsorCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSponsorCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSponsors(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSponsors.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSponsor(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateSponsor(itemId, companyId, sponsorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateSponsor.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateSponsor.');
      }
      if (sponsorUpdateSchema === null || sponsorUpdateSchema === undefined) {
        throw new Error('Required parameter sponsorUpdateSchema was null or undefined when calling manageUpdateSponsor.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sponsorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSponsorLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSponsorLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSponsorLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadSponsorLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadSponsorLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadSponsorLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadSponsorLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadSponsorLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/sponsor/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SponsorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SponsorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SponsorCRUDService,
        factory: SponsorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SponsorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let StandCRUDService = /*#__PURE__*/(() => {
  class StandCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateStand(companyId, standCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateStand.');
      }
      if (standCreateSchema === null || standCreateSchema === undefined) {
        throw new Error('Required parameter standCreateSchema was null or undefined when calling manageCreateStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: standCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStand(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStandMapImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStandMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStandMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadStandMapImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadStandMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadStandMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllStands(companyId, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllStands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStand(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetStand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStandCount(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStandCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllStands(companyId, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllStands.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderStand(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderStand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateStand(itemId, companyId, standUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateStand.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateStand.');
      }
      if (standUpdateSchema === null || standUpdateSchema === undefined) {
        throw new Error('Required parameter standUpdateSchema was null or undefined when calling manageUpdateStand.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: standUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadStandMapImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadStandMapImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadStandMapImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadStandMapImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadStandMapImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadStandMapImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadStandMapImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadStandMapImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stand/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/map_image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function StandCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StandCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StandCRUDService,
        factory: StandCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StandCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let StreamCRUDService = /*#__PURE__*/(() => {
  class StreamCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateStream(companyId, streamCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateStream.');
      }
      if (streamCreateSchema === null || streamCreateSchema === undefined) {
        throw new Error('Required parameter streamCreateSchema was null or undefined when calling manageCreateStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateStreamByEvent(companyId, eventId, streamCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateStreamByEvent.');
      }
      if (streamCreateSchema === null || streamCreateSchema === undefined) {
        throw new Error('Required parameter streamCreateSchema was null or undefined when calling manageCreateStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateStreamByEventAndAgenda(companyId, eventId, agendaId, streamCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateStreamByEventAndAgenda.');
      }
      if (streamCreateSchema === null || streamCreateSchema === undefined) {
        throw new Error('Required parameter streamCreateSchema was null or undefined when calling manageCreateStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStream(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStreamByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStreamByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStreamByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStreamByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllStreams(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllStreams.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllStreamsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllStreamsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllStreamsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllStreamsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllStreamsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllStreamsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllStreamsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetEventOnlineUserCount(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetEventOnlineUserCount.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetEventOnlineUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStream(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetStreamByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetStreamByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetStreamCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamCountByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetStreamCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetStreamCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamOnlineUser(companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamOnlineUser.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetStreamOnlineUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/online_user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamOnlineUserCount(companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamOnlineUserCount.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetStreamOnlineUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/online_user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllStreams(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllStreams.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllStreamsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllStreamsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllStreamsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllStreamsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllStreamsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllStreamsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllStreamsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderStream(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderStreamByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderStreamByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderStreamByEventAndAgenda(itemId, companyId, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderStreamByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamLogoutCompany(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamLogoutCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamLogoutCompanyUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamLogoutCompanyUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageStreamLogoutCompanyUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_logout/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamLogoutEvent(eventId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageStreamLogoutEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamLogoutEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamLogoutStream(streamId, companyId, observe = 'body', reportProgress = false, options) {
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageStreamLogoutStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamLogoutStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logout`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamRedirectCompany(companyId, redirectInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamRedirectCompany.');
      }
      if (redirectInput === null || redirectInput === undefined) {
        throw new Error('Required parameter redirectInput was null or undefined when calling manageStreamRedirectCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_redirect`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: redirectInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamRedirectCompanyUser(companyId, userId, redirectInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamRedirectCompanyUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageStreamRedirectCompanyUser.');
      }
      if (redirectInput === null || redirectInput === undefined) {
        throw new Error('Required parameter redirectInput was null or undefined when calling manageStreamRedirectCompanyUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_redirect/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: redirectInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamRedirectEvent(eventId, companyId, redirectInput, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageStreamRedirectEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamRedirectEvent.');
      }
      if (redirectInput === null || redirectInput === undefined) {
        throw new Error('Required parameter redirectInput was null or undefined when calling manageStreamRedirectEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/redirect`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: redirectInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamRedirectStream(streamId, companyId, redirectInput, observe = 'body', reportProgress = false, options) {
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageStreamRedirectStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamRedirectStream.');
      }
      if (redirectInput === null || redirectInput === undefined) {
        throw new Error('Required parameter redirectInput was null or undefined when calling manageStreamRedirectStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/redirect`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: redirectInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamReloadCompany(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamReloadCompany.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reload`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamReloadCompanyUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamReloadCompanyUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageStreamReloadCompanyUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_reload/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamReloadEvent(eventId, companyId, observe = 'body', reportProgress = false, options) {
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageStreamReloadEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamReloadEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/reload`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageStreamReloadStream(streamId, companyId, observe = 'body', reportProgress = false, options) {
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageStreamReloadStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageStreamReloadStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/reload`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateStream(itemId, companyId, streamUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateStream.');
      }
      if (streamUpdateSchema === null || streamUpdateSchema === undefined) {
        throw new Error('Required parameter streamUpdateSchema was null or undefined when calling manageUpdateStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateStreamByEvent(itemId, companyId, eventId, streamUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateStreamByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateStreamByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateStreamByEvent.');
      }
      if (streamUpdateSchema === null || streamUpdateSchema === undefined) {
        throw new Error('Required parameter streamUpdateSchema was null or undefined when calling manageUpdateStreamByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateStreamByEventAndAgenda(itemId, companyId, eventId, agendaId, streamUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateStreamByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateStreamByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateStreamByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateStreamByEventAndAgenda.');
      }
      if (streamUpdateSchema === null || streamUpdateSchema === undefined) {
        throw new Error('Required parameter streamUpdateSchema was null or undefined when calling manageUpdateStreamByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function StreamCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StreamCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StreamCRUDService,
        factory: StreamCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StreamCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let StreamReactionCRUDService = /*#__PURE__*/(() => {
  class StreamReactionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateStreamReactionByStream(companyId, streamId, streamReactionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageCreateStreamReactionByStream.');
      }
      if (streamReactionCreateSchema === null || streamReactionCreateSchema === undefined) {
        throw new Error('Required parameter streamReactionCreateSchema was null or undefined when calling manageCreateStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamReactionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllStreamReactionsByStream(companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllStreamReactionsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDeleteAllStreamReactionsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteStreamReactionByStream(itemId, companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteStreamReactionByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageDeleteStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllStreamReactionsByStream(companyId, streamId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllStreamReactionsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetAllStreamReactionsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamReactionByStream(itemId, companyId, streamId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetStreamReactionByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetStreamReactionCountByStream(companyId, streamId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetStreamReactionCountByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageGetStreamReactionCountByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllStreamReactionsByStream(companyId, streamId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllStreamReactionsByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageRenderAllStreamReactionsByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderStreamReactionByStream(itemId, companyId, streamId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderStreamReactionByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageRenderStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateStreamReactionByStream(itemId, companyId, streamId, streamReactionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateStreamReactionByStream.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateStreamReactionByStream.');
      }
      if (streamId === null || streamId === undefined) {
        throw new Error('Required parameter streamId was null or undefined when calling manageUpdateStreamReactionByStream.');
      }
      if (streamReactionUpdateSchema === null || streamReactionUpdateSchema === undefined) {
        throw new Error('Required parameter streamReactionUpdateSchema was null or undefined when calling manageUpdateStreamReactionByStream.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream/${this.configuration.encodeParam({
        name: 'streamId',
        value: streamId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/stream_reaction/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: streamReactionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function StreamReactionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StreamReactionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StreamReactionCRUDService,
        factory: StreamReactionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StreamReactionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SubscriptionCRUDService = /*#__PURE__*/(() => {
  class SubscriptionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateSubscription(companyId, subscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateSubscription.');
      }
      if (subscriptionCreateSchema === null || subscriptionCreateSchema === undefined) {
        throw new Error('Required parameter subscriptionCreateSchema was null or undefined when calling manageCreateSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: subscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteSubscription(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllSubscriptions(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSubscription(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetSubscriptionCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetSubscriptionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllSubscriptions(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderSubscription(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateSubscription(itemId, companyId, subscriptionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateSubscription.');
      }
      if (subscriptionUpdateSchema === null || subscriptionUpdateSchema === undefined) {
        throw new Error('Required parameter subscriptionUpdateSchema was null or undefined when calling manageUpdateSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: subscriptionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SubscriptionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SubscriptionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SubscriptionCRUDService,
        factory: SubscriptionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SubscriptionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ThemeSettingCRUDService = /*#__PURE__*/(() => {
  class ThemeSettingCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageGetAllThemeSettings(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllThemeSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetThemeSetting(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetThemeSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetThemeSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetThemeSettingCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetThemeSettingCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllThemeSettings(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllThemeSettings.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderThemeSetting(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderThemeSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderThemeSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateThemeSetting(itemId, companyId, themeSettingUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateThemeSetting.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateThemeSetting.');
      }
      if (themeSettingUpdateSchema === null || themeSettingUpdateSchema === undefined) {
        throw new Error('Required parameter themeSettingUpdateSchema was null or undefined when calling manageUpdateThemeSetting.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/theme_setting/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: themeSettingUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ThemeSettingCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ThemeSettingCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ThemeSettingCRUDService,
        factory: ThemeSettingCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ThemeSettingCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let TradeShowSectorCRUDService = /*#__PURE__*/(() => {
  class TradeShowSectorCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateTradeShowSectorByEvent(companyId, eventId, tradeShowSectorCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateTradeShowSectorByEvent.');
      }
      if (tradeShowSectorCreateSchema === null || tradeShowSectorCreateSchema === undefined) {
        throw new Error('Required parameter tradeShowSectorCreateSchema was null or undefined when calling manageCreateTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: tradeShowSectorCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTradeShowSectorByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTradeShowSectorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTradeShowSectorsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTradeShowSectorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllTradeShowSectorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTradeShowSectorByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetTradeShowSectorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTradeShowSectorCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTradeShowSectorCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTradeShowSectorCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTradeShowSectorsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTradeShowSectorsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllTradeShowSectorsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderTradeShowSectorByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderTradeShowSectorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateTradeShowSectorByEvent(itemId, companyId, eventId, tradeShowSectorUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateTradeShowSectorByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateTradeShowSectorByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateTradeShowSectorByEvent.');
      }
      if (tradeShowSectorUpdateSchema === null || tradeShowSectorUpdateSchema === undefined) {
        throw new Error('Required parameter tradeShowSectorUpdateSchema was null or undefined when calling manageUpdateTradeShowSectorByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/trade_show_sector/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: tradeShowSectorUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function TradeShowSectorCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TradeShowSectorCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TradeShowSectorCRUDService,
        factory: TradeShowSectorCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TradeShowSectorCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let TrainReservationCRUDService = /*#__PURE__*/(() => {
  class TrainReservationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateTrainReservationByEvent(companyId, eventId, trainReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateTrainReservationByEvent.');
      }
      if (trainReservationCreateSchema === null || trainReservationCreateSchema === undefined) {
        throw new Error('Required parameter trainReservationCreateSchema was null or undefined when calling manageCreateTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateTrainReservationByEventAndUser(companyId, eventId, userId, trainReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateTrainReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateTrainReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateTrainReservationByEventAndUser.');
      }
      if (trainReservationCreateSchema === null || trainReservationCreateSchema === undefined) {
        throw new Error('Required parameter trainReservationCreateSchema was null or undefined when calling manageCreateTrainReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateTrainReservationByUser(companyId, userId, trainReservationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateTrainReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateTrainReservationByUser.');
      }
      if (trainReservationCreateSchema === null || trainReservationCreateSchema === undefined) {
        throw new Error('Required parameter trainReservationCreateSchema was null or undefined when calling manageCreateTrainReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteTrainReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteTrainReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteTrainReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationTicketByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteTrainReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationTicketByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteTrainReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteTrainReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteTrainReservationTicketByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteTrainReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteTrainReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteTrainReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadTrainReservationTicketByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadTrainReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadTrainReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadTrainReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadTrainReservationTicketByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadTrainReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadTrainReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadTrainReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadTrainReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadTrainReservationTicketByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadTrainReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadTrainReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadTrainReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllTrainReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllTrainReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllTrainReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllTrainReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllTrainReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllTrainReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllTrainReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllTrainReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllTrainReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllTrainReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetTrainReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationByEventAndUser(itemId, companyId, eventId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetTrainReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetTrainReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationCountByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationCountByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationCountByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationCountByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetTrainReservationCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetTrainReservationCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetTrainReservationCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetTrainReservationCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllTrainReservationsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByEventAndUser(companyId, eventId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByEventAndUserMulti(companyId, eventId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUserMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllTrainReservationsByEventAndUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllTrainReservationsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllTrainReservationsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllTrainReservationsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllTrainReservationsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllTrainReservationsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderTrainReservationByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderTrainReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderTrainReservationByEventAndUser(itemId, companyId, eventId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderTrainReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderTrainReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderTrainReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderTrainReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderTrainReservationByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderTrainReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderTrainReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderTrainReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateTrainReservationByEvent(itemId, companyId, eventId, trainReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateTrainReservationByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateTrainReservationByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateTrainReservationByEvent.');
      }
      if (trainReservationUpdateSchema === null || trainReservationUpdateSchema === undefined) {
        throw new Error('Required parameter trainReservationUpdateSchema was null or undefined when calling manageUpdateTrainReservationByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateTrainReservationByEventAndUser(itemId, companyId, eventId, userId, trainReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateTrainReservationByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateTrainReservationByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateTrainReservationByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateTrainReservationByEventAndUser.');
      }
      if (trainReservationUpdateSchema === null || trainReservationUpdateSchema === undefined) {
        throw new Error('Required parameter trainReservationUpdateSchema was null or undefined when calling manageUpdateTrainReservationByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateTrainReservationByUser(itemId, companyId, userId, trainReservationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateTrainReservationByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateTrainReservationByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateTrainReservationByUser.');
      }
      if (trainReservationUpdateSchema === null || trainReservationUpdateSchema === undefined) {
        throw new Error('Required parameter trainReservationUpdateSchema was null or undefined when calling manageUpdateTrainReservationByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: trainReservationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadTrainReservationTicketBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketBase64ByEventAndUser(itemId, companyId, eventId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadTrainReservationTicketBase64ByEventAndUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadTrainReservationTicketBase64ByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketBase64ByUser(itemId, companyId, userId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketBase64ByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketBase64ByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadTrainReservationTicketBase64ByUser.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadTrainReservationTicketBase64ByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadTrainReservationTicketByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadTrainReservationTicketByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketByEventAndUser(itemId, companyId, eventId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketByEventAndUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketByEventAndUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadTrainReservationTicketByEventAndUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadTrainReservationTicketByEventAndUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadTrainReservationTicketByEventAndUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadTrainReservationTicketByUser(itemId, companyId, userId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadTrainReservationTicketByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadTrainReservationTicketByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUploadTrainReservationTicketByUser.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadTrainReservationTicketByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/train_reservation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/ticket`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function TrainReservationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TrainReservationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TrainReservationCRUDService,
        factory: TrainReservationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TrainReservationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let TranslationCRUDService = /*#__PURE__*/(() => {
  class TranslationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    createTranslation(translationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (translationCreateSchema === null || translationCreateSchema === undefined) {
        throw new Error('Required parameter translationCreateSchema was null or undefined when calling createTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: translationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteTranslation(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling deleteTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAllTranslations(orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTranslation(itemId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling getTranslation.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTranslationCount(orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderAllTranslations(orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderTranslation(itemId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling renderTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateTranslation(itemId, translationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling updateTranslation.');
      }
      if (translationUpdateSchema === null || translationUpdateSchema === undefined) {
        throw new Error('Required parameter translationUpdateSchema was null or undefined when calling updateTranslation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/translation/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: translationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function TranslationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslationCRUDService,
        factory: TranslationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserService = /*#__PURE__*/(() => {
  class UserService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    agendaCheckIn(companyId, agendaId, qrCodeString, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling agendaCheckIn.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling agendaCheckIn.');
      }
      if (qrCodeString === null || qrCodeString === undefined) {
        throw new Error('Required parameter qrCodeString was null or undefined when calling agendaCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, qrCodeString, 'qr_code_string');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users/agenda_check_in/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    checkGroupLeadEmail(email, groupId, subdomain, observe = 'body', reportProgress = false, options) {
      if (email === null || email === undefined) {
        throw new Error('Required parameter email was null or undefined when calling checkGroupLeadEmail.');
      }
      if (groupId === null || groupId === undefined) {
        throw new Error('Required parameter groupId was null or undefined when calling checkGroupLeadEmail.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling checkGroupLeadEmail.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/groups/${this.configuration.encodeParam({
        name: 'groupId',
        value: groupId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/lead/${this.configuration.encodeParam({
        name: 'email',
        value: email,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    checkLeadUser(subdomain, checkLeadUserInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling checkLeadUser.');
      }
      if (checkLeadUserInput === null || checkLeadUserInput === undefined) {
        throw new Error('Required parameter checkLeadUserInput was null or undefined when calling checkLeadUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/check_lead`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checkLeadUserInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    checkUserEmails(subdomain, checkEmailsInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling checkUserEmails.');
      }
      if (checkEmailsInput === null || checkEmailsInput === undefined) {
        throw new Error('Required parameter checkEmailsInput was null or undefined when calling checkUserEmails.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/check_emails`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: checkEmailsInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    downloadEventBadgeForUser(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling downloadEventBadgeForUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling downloadEventBadgeForUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    eventCheckIn(companyId, eventId, qrCodeString, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling eventCheckIn.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling eventCheckIn.');
      }
      if (qrCodeString === null || qrCodeString === undefined) {
        throw new Error('Required parameter qrCodeString was null or undefined when calling eventCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, qrCodeString, 'qr_code_string');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users/event_check_in/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    exhibitorCheckIn(subdomain, qrCodeString, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling exhibitorCheckIn.');
      }
      if (qrCodeString === null || qrCodeString === undefined) {
        throw new Error('Required parameter qrCodeString was null or undefined when calling exhibitorCheckIn.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling exhibitorCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, qrCodeString, 'qr_code_string');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/exhibitor_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    generateGigyaQuerySignature(subdomain, generateQuerySignatureInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling generateGigyaQuerySignature.');
      }
      if (generateQuerySignatureInput === null || generateQuerySignatureInput === undefined) {
        throw new Error('Required parameter generateQuerySignatureInput was null or undefined when calling generateGigyaQuerySignature.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/generate_gigya_query_signature`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: generateQuerySignatureInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    generateGigyaQuerySignatureForDomain(subdomain, generateQuerySignatureForDomainInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling generateGigyaQuerySignatureForDomain.');
      }
      if (generateQuerySignatureForDomainInput === null || generateQuerySignatureForDomainInput === undefined) {
        throw new Error('Required parameter generateQuerySignatureForDomainInput was null or undefined when calling generateGigyaQuerySignatureForDomain.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/generate_gigya_query_signature_for_domain`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: generateQuerySignatureForDomainInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCurrentUserLocation(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCurrentUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustomFields(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getCustomFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/custom_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEventCustomFields(subdomain, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getEventCustomFields.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling getEventCustomFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/custom_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGigyaApiKeyForDomain(subdomain, getGigyaApiKeyForDomainInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGigyaApiKeyForDomain.');
      }
      if (getGigyaApiKeyForDomainInput === null || getGigyaApiKeyForDomainInput === undefined) {
        throw new Error('Required parameter getGigyaApiKeyForDomainInput was null or undefined when calling getGigyaApiKeyForDomain.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/get_gigya_api_key_for_domain`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: getGigyaApiKeyForDomainInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getGigyaDomains(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getGigyaDomains.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/get_gigya_domains`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getInternalFields(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getInternalFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/internal_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getQrCodeString(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getQrCodeString.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/qr_code_string`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserExhibitorList(subdomain, includeEventIdList, excludeEventIdList, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserExhibitorList.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeEventIdList, 'include_event_id_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, excludeEventIdList, 'exclude_event_id_list');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/exhibitor_list`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getUserInfo(subdomain, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling getUserInfo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/info`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    insecureSetPassword(subdomain, insecureSetPasswordInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling insecureSetPassword.');
      }
      if (insecureSetPasswordInput === null || insecureSetPasswordInput === undefined) {
        throw new Error('Required parameter insecureSetPasswordInput was null or undefined when calling insecureSetPassword.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/insecure_set_password`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: insecureSetPasswordInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    loginUser(subdomain, loginUserInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling loginUser.');
      }
      if (loginUserInput === null || loginUserInput === undefined) {
        throw new Error('Required parameter loginUserInput was null or undefined when calling loginUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/login`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: loginUserInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    lookupUserByQrCode(subdomain, qrCodeString, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling lookupUserByQrCode.');
      }
      if (qrCodeString === null || qrCodeString === undefined) {
        throw new Error('Required parameter qrCodeString was null or undefined when calling lookupUserByQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, qrCodeString, 'qr_code_string');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/lookup_by_qr_code`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageConvertAllUsersToLead(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageConvertAllUsersToLead.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users/convert_all_users_to_lead`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageConvertUsersToLead(companyId, convertUsersToLeadInput, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageConvertUsersToLead.');
      }
      if (convertUsersToLeadInput === null || convertUsersToLeadInput === undefined) {
        throw new Error('Required parameter convertUsersToLeadInput was null or undefined when calling manageConvertUsersToLead.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users/convert_to_lead`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: convertUsersToLeadInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllUsers(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users/delete_all_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadBadgeLabelTest(companyId, firstName, lastName, corporation, labelWidthMm, labelHeightMm, dpi, fontSize, spacing, textWrapWidth, fontName, backgroundColor, textColor, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (firstName === null || firstName === undefined) {
        throw new Error('Required parameter firstName was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (lastName === null || lastName === undefined) {
        throw new Error('Required parameter lastName was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (corporation === null || corporation === undefined) {
        throw new Error('Required parameter corporation was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (labelWidthMm === null || labelWidthMm === undefined) {
        throw new Error('Required parameter labelWidthMm was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (labelHeightMm === null || labelHeightMm === undefined) {
        throw new Error('Required parameter labelHeightMm was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (dpi === null || dpi === undefined) {
        throw new Error('Required parameter dpi was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (fontSize === null || fontSize === undefined) {
        throw new Error('Required parameter fontSize was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (spacing === null || spacing === undefined) {
        throw new Error('Required parameter spacing was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (textWrapWidth === null || textWrapWidth === undefined) {
        throw new Error('Required parameter textWrapWidth was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (fontName === null || fontName === undefined) {
        throw new Error('Required parameter fontName was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (backgroundColor === null || backgroundColor === undefined) {
        throw new Error('Required parameter backgroundColor was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      if (textColor === null || textColor === undefined) {
        throw new Error('Required parameter textColor was null or undefined when calling manageDownloadBadgeLabelTest.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, firstName, 'first_name');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, lastName, 'last_name');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, corporation, 'corporation');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, labelWidthMm, 'label_width_mm');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, labelHeightMm, 'label_height_mm');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, dpi, 'dpi');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, fontSize, 'font_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, spacing, 'spacing');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, textWrapWidth, 'text_wrap_width');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, fontName, 'font_name');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, backgroundColor, 'background_color');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, textColor, 'text_color');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge_label_test`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventBadgeForEventUser(companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventBadgeForEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDownloadEventBadgeForEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventBadgeForUser(companyId, userId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventBadgeForUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadEventBadgeForUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadEventBadgeForUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventBadgeLabelForEventUser(companyId, eventUserId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventBadgeLabelForEventUser.');
      }
      if (eventUserId === null || eventUserId === undefined) {
        throw new Error('Required parameter eventUserId was null or undefined when calling manageDownloadEventBadgeLabelForEventUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event_user/${this.configuration.encodeParam({
        name: 'eventUserId',
        value: eventUserId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge_label`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadEventBadgeLabelForUser(companyId, userId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadEventBadgeLabelForUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDownloadEventBadgeLabelForUser.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadEventBadgeLabelForUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/badge_label`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserAgentBrowser(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserAgentBrowser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_agent/browser`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserAgentBrowserVersion(browserName, companyId, observe = 'body', reportProgress = false, options) {
      if (browserName === null || browserName === undefined) {
        throw new Error('Required parameter browserName was null or undefined when calling manageGetUserAgentBrowserVersion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserAgentBrowserVersion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_agent/browser/${this.configuration.encodeParam({
        name: 'browserName',
        value: browserName,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/version`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserAgentPlatform(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserAgentPlatform.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_agent/platform`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserAgentPlatformVersion(platformName, companyId, observe = 'body', reportProgress = false, options) {
      if (platformName === null || platformName === undefined) {
        throw new Error('Required parameter platformName was null or undefined when calling manageGetUserAgentPlatformVersion.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserAgentPlatformVersion.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_agent/platform/${this.configuration.encodeParam({
        name: 'platformName',
        value: platformName,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/version`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    otpRequestPasswordChange(email, subdomain, mailTemplateTitle, observe = 'body', reportProgress = false, options) {
      if (email === null || email === undefined) {
        throw new Error('Required parameter email was null or undefined when calling otpRequestPasswordChange.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling otpRequestPasswordChange.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, mailTemplateTitle, 'mail_template_title');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/otp_request_password_change/${this.configuration.encodeParam({
        name: 'email',
        value: email,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    otpRequired(email, subdomain, observe = 'body', reportProgress = false, options) {
      if (email === null || email === undefined) {
        throw new Error('Required parameter email was null or undefined when calling otpRequired.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling otpRequired.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/otp_required/${this.configuration.encodeParam({
        name: 'email',
        value: email,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    otpResetPassword(subdomain, otpResetPasswordInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling otpResetPassword.');
      }
      if (otpResetPasswordInput === null || otpResetPasswordInput === undefined) {
        throw new Error('Required parameter otpResetPasswordInput was null or undefined when calling otpResetPassword.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/otp_reset_password`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: otpResetPasswordInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    registerLead(subdomain, registerLeadInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling registerLead.');
      }
      if (registerLeadInput === null || registerLeadInput === undefined) {
        throw new Error('Required parameter registerLeadInput was null or undefined when calling registerLead.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/register_lead`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registerLeadInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    registerTransientUser(subdomain, registerTransientUserInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling registerTransientUser.');
      }
      if (registerTransientUserInput === null || registerTransientUserInput === undefined) {
        throw new Error('Required parameter registerTransientUserInput was null or undefined when calling registerTransientUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/register_transient`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registerTransientUserInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    registerUser(subdomain, registerUserInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling registerUser.');
      }
      if (registerUserInput === null || registerUserInput === undefined) {
        throw new Error('Required parameter registerUserInput was null or undefined when calling registerUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/register`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: registerUserInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderCustomFieldQrCode(subdomain, key, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderCustomFieldQrCode.');
      }
      if (key === null || key === undefined) {
        throw new Error('Required parameter key was null or undefined when calling renderCustomFieldQrCode.');
      }
      if (boxSize === null || boxSize === undefined) {
        throw new Error('Required parameter boxSize was null or undefined when calling renderCustomFieldQrCode.');
      }
      if (border === null || border === undefined) {
        throw new Error('Required parameter border was null or undefined when calling renderCustomFieldQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, key, 'key');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/render_custom_field_qr_code`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderInternalFieldQrCode(subdomain, key, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderInternalFieldQrCode.');
      }
      if (key === null || key === undefined) {
        throw new Error('Required parameter key was null or undefined when calling renderInternalFieldQrCode.');
      }
      if (boxSize === null || boxSize === undefined) {
        throw new Error('Required parameter boxSize was null or undefined when calling renderInternalFieldQrCode.');
      }
      if (border === null || border === undefined) {
        throw new Error('Required parameter border was null or undefined when calling renderInternalFieldQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, key, 'key');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/render_internal_field_qr_code`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderQrCode(subdomain, value, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderQrCode.');
      }
      if (value === null || value === undefined) {
        throw new Error('Required parameter value was null or undefined when calling renderQrCode.');
      }
      if (boxSize === null || boxSize === undefined) {
        throw new Error('Required parameter boxSize was null or undefined when calling renderQrCode.');
      }
      if (border === null || border === undefined) {
        throw new Error('Required parameter border was null or undefined when calling renderQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, value, 'value');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/render_qr_code`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    renderSignedQrCode(subdomain, value, boxSize, border, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling renderSignedQrCode.');
      }
      if (value === null || value === undefined) {
        throw new Error('Required parameter value was null or undefined when calling renderSignedQrCode.');
      }
      if (boxSize === null || boxSize === undefined) {
        throw new Error('Required parameter boxSize was null or undefined when calling renderSignedQrCode.');
      }
      if (border === null || border === undefined) {
        throw new Error('Required parameter border was null or undefined when calling renderSignedQrCode.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, value, 'value');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, boxSize, 'box_size');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, border, 'border');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/render_signed_qr_code`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    requestPasswordChange(email, subdomain, mailTemplateTitle, passwordResetUrl, observe = 'body', reportProgress = false, options) {
      if (email === null || email === undefined) {
        throw new Error('Required parameter email was null or undefined when calling requestPasswordChange.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling requestPasswordChange.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, mailTemplateTitle, 'mail_template_title');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, passwordResetUrl, 'password_reset_url');
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/request_password_change/${this.configuration.encodeParam({
        name: 'email',
        value: email,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    resetPassword(token, subdomain, resetPasswordInput, observe = 'body', reportProgress = false, options) {
      if (token === null || token === undefined) {
        throw new Error('Required parameter token was null or undefined when calling resetPassword.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling resetPassword.');
      }
      if (resetPasswordInput === null || resetPasswordInput === undefined) {
        throw new Error('Required parameter resetPasswordInput was null or undefined when calling resetPassword.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/reset_password/${this.configuration.encodeParam({
        name: 'token',
        value: token,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: resetPasswordInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    restaurantCheckIn(subdomain, qrCodeString, eventId, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling restaurantCheckIn.');
      }
      if (qrCodeString === null || qrCodeString === undefined) {
        throw new Error('Required parameter qrCodeString was null or undefined when calling restaurantCheckIn.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling restaurantCheckIn.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, qrCodeString, 'qr_code_string');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, eventId, 'event_id');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/restaurant_check_in`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    sendOtp(subdomain, sendOtpInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling sendOtp.');
      }
      if (sendOtpInput === null || sendOtpInput === undefined) {
        throw new Error('Required parameter sendOtpInput was null or undefined when calling sendOtp.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/send_otp`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sendOtpInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    sendOtpMail(subdomain, sendOtpSmsInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling sendOtpMail.');
      }
      if (sendOtpSmsInput === null || sendOtpSmsInput === undefined) {
        throw new Error('Required parameter sendOtpSmsInput was null or undefined when calling sendOtpMail.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/send_otp_mail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sendOtpSmsInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    sendOtpSms(subdomain, sendOtpSmsInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling sendOtpSms.');
      }
      if (sendOtpSmsInput === null || sendOtpSmsInput === undefined) {
        throw new Error('Required parameter sendOtpSmsInput was null or undefined when calling sendOtpSms.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/send_otp_sms`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: sendOtpSmsInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    sendUserMail(subdomain, mailTemplateId, mailTemplateTitle, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling sendUserMail.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, mailTemplateId, 'mail_template_id');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, mailTemplateTitle, 'mail_template_title');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/send_mail`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setCurrentUserLocation(subdomain, setUserLocationInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setCurrentUserLocation.');
      }
      if (setUserLocationInput === null || setUserLocationInput === undefined) {
        throw new Error('Required parameter setUserLocationInput was null or undefined when calling setCurrentUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: setUserLocationInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setCustomFields(subdomain, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setCustomFields.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setCustomFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/custom_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setEventCustomFields(subdomain, eventId, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setEventCustomFields.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling setEventCustomFields.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setEventCustomFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/custom_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setInternalFields(subdomain, requestBody, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setInternalFields.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling setInternalFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/internal_fields`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    setPassword(subdomain, setPasswordInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling setPassword.');
      }
      if (setPasswordInput === null || setPasswordInput === undefined) {
        throw new Error('Required parameter setPasswordInput was null or undefined when calling setPassword.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/set_password`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: setPasswordInput,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    verifyOtp(subdomain, verifyOtpInput, observe = 'body', reportProgress = false, options) {
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling verifyOtp.');
      }
      if (verifyOtpInput === null || verifyOtpInput === undefined) {
        throw new Error('Required parameter verifyOtpInput was null or undefined when calling verifyOtp.');
      }
      let localVarHeaders = this.defaultHeaders;
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/users/verify_otp`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: verifyOtpInput,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserService,
        factory: UserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserCRUDService = /*#__PURE__*/(() => {
  class UserCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageClearUserGameScorePenalty(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageClearUserGameScorePenalty.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.clear_game_score_penalty`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUser(companyId, userCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUser.');
      }
      if (userCreateSchema === null || userCreateSchema === undefined) {
        throw new Error('Required parameter userCreateSchema was null or undefined when calling manageCreateUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserAvatar(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserAvatar.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserIdentityDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserIdentityDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserAvatar(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserAvatar.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserIdentityDocument(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserIdentityDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageExpireAllUserPasswords(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageExpireAllUserPasswords.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.expire_all_passwords`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageExpireUserPassword(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageExpireUserPassword.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageExpireUserPassword.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.expire_password/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUsers(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUser(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageLockAllUsers(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageLockAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.lock_all`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageLockUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageLockUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageLockUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.lock/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUsers(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUsersMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUsersMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUser(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenewAllPasswords(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenewAllPasswords.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.renew_all_passwords`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenewUserPassword(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenewUserPassword.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenewUserPassword.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.renew_password/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserCategories(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserCategories.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserCategories.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserGuestUsers(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserGuestUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserGuestUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserGuestUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/guest_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserHostUsers(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserHostUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserHostUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserHostUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/host_users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserUsers(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserUsers.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/users`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUnlockAllUsers(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUnlockAllUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.unlock_all`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUnlockUser(companyId, userId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUnlockUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUnlockUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.unlock/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUser(itemId, companyId, userUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUser.');
      }
      if (userUpdateSchema === null || userUpdateSchema === undefined) {
        throw new Error('Required parameter userUpdateSchema was null or undefined when calling manageUpdateUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserAvatar(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserAvatar.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserAvatar.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserAvatar.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserAvatarBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserAvatarBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserAvatarBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserAvatarBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/avatar/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserIdentityDocument(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserIdentityDocument.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserIdentityDocument.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserIdentityDocument.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserIdentityDocumentBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserIdentityDocumentBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserIdentityDocumentBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserIdentityDocumentBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/identity_document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUserCustomFieldKeys(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUserCustomFieldKeys.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.custom_field_keys`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUserReportFieldKeys(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUserReportFieldKeys.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user.report_field_keys`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserCRUDService,
        factory: UserCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserCategoryCRUDService = /*#__PURE__*/(() => {
  class UserCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserCategory(companyId, userCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserCategory.');
      }
      if (userCategoryCreateSchema === null || userCategoryCreateSchema === undefined) {
        throw new Error('Required parameter userCategoryCreateSchema was null or undefined when calling manageCreateUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserCategory(itemId, companyId, userCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserCategory.');
      }
      if (userCategoryUpdateSchema === null || userCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter userCategoryUpdateSchema was null or undefined when calling manageUpdateUserCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserCategoryCRUDService,
        factory: UserCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserDocumentCRUDService = /*#__PURE__*/(() => {
  class UserDocumentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserDocumentByEvent(companyId, eventId, userDocumentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateUserDocumentByEvent.');
      }
      if (userDocumentCreateSchema === null || userDocumentCreateSchema === undefined) {
        throw new Error('Required parameter userDocumentCreateSchema was null or undefined when calling manageCreateUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userDocumentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserDocumentDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserDocumentDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserDocumentDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteUserDocumentDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserDocumentDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserDocumentDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserDocumentDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadUserDocumentDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserDocumentsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllUserDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserDocumentsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserDocumentsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllUserDocumentsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserDocumentByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserDocumentCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserDocumentCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserDocumentCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserDocumentCountByEventMulti(companyId, eventId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserDocumentCountByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserDocumentCountByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserDocumentsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserDocumentsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllUserDocumentsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserDocumentsByEventMulti(companyId, eventId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserDocumentsByEventMulti.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllUserDocumentsByEventMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserDocumentByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserDocumentByEvent(itemId, companyId, eventId, userDocumentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateUserDocumentByEvent.');
      }
      if (userDocumentUpdateSchema === null || userDocumentUpdateSchema === undefined) {
        throw new Error('Required parameter userDocumentUpdateSchema was null or undefined when calling manageUpdateUserDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userDocumentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserDocumentDocumentBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserDocumentDocumentBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserDocumentDocumentBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadUserDocumentDocumentBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserDocumentDocumentBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserDocumentDocumentByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserDocumentDocumentByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserDocumentDocumentByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadUserDocumentDocumentByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserDocumentDocumentByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_document/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/document`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserDocumentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserDocumentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserDocumentCRUDService,
        factory: UserDocumentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserDocumentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserGeneratedContentBinCRUDService = /*#__PURE__*/(() => {
  class UserGeneratedContentBinCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserGeneratedContentBin(companyId, userGeneratedContentBinCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserGeneratedContentBin.');
      }
      if (userGeneratedContentBinCreateSchema === null || userGeneratedContentBinCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinCreateSchema was null or undefined when calling manageCreateUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserGeneratedContentBinByEvent(companyId, eventId, userGeneratedContentBinCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateUserGeneratedContentBinByEvent.');
      }
      if (userGeneratedContentBinCreateSchema === null || userGeneratedContentBinCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinCreateSchema was null or undefined when calling manageCreateUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserGeneratedContentBinByEventAndAgenda(companyId, eventId, agendaId, userGeneratedContentBinCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (userGeneratedContentBinCreateSchema === null || userGeneratedContentBinCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinCreateSchema was null or undefined when calling manageCreateUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentBin(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentBinByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentBinByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentBinByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentBinByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserGeneratedContentBins(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserGeneratedContentBins.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserGeneratedContentBinsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserGeneratedContentBinsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllUserGeneratedContentBinsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserGeneratedContentBinsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBin(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserGeneratedContentBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBinByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserGeneratedContentBinByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBinByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserGeneratedContentBinByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBinCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBinCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBinCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBinCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserGeneratedContentBinCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentBinCountByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentBinCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserGeneratedContentBinCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetUserGeneratedContentBinCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserGeneratedContentBins(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserGeneratedContentBins.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserGeneratedContentBinsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserGeneratedContentBinsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllUserGeneratedContentBinsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserGeneratedContentBinsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllUserGeneratedContentBinsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserGeneratedContentBin(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserGeneratedContentBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserGeneratedContentBinByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserGeneratedContentBinByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserGeneratedContentBinByEventAndAgenda(itemId, companyId, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserGeneratedContentBin(itemId, companyId, userGeneratedContentBinUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserGeneratedContentBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserGeneratedContentBin.');
      }
      if (userGeneratedContentBinUpdateSchema === null || userGeneratedContentBinUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinUpdateSchema was null or undefined when calling manageUpdateUserGeneratedContentBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserGeneratedContentBinByEvent(itemId, companyId, eventId, userGeneratedContentBinUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserGeneratedContentBinByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserGeneratedContentBinByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateUserGeneratedContentBinByEvent.');
      }
      if (userGeneratedContentBinUpdateSchema === null || userGeneratedContentBinUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinUpdateSchema was null or undefined when calling manageUpdateUserGeneratedContentBinByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserGeneratedContentBinByEventAndAgenda(itemId, companyId, eventId, agendaId, userGeneratedContentBinUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateUserGeneratedContentBinByEventAndAgenda.');
      }
      if (userGeneratedContentBinUpdateSchema === null || userGeneratedContentBinUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentBinUpdateSchema was null or undefined when calling manageUpdateUserGeneratedContentBinByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentBinUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserGeneratedContentBinCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserGeneratedContentBinCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserGeneratedContentBinCRUDService,
        factory: UserGeneratedContentBinCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserGeneratedContentBinCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserGeneratedContentCRUDService = /*#__PURE__*/(() => {
  class UserGeneratedContentCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageClearUserGeneratedContentScore(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageClearUserGeneratedContentScore.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content:clear_score`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserGeneratedContent(companyId, userGeneratedContentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserGeneratedContent.');
      }
      if (userGeneratedContentCreateSchema === null || userGeneratedContentCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentCreateSchema was null or undefined when calling manageCreateUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserGeneratedContentByBin(companyId, userGeneratedContentBinId, userGeneratedContentCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageCreateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentCreateSchema === null || userGeneratedContentCreateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentCreateSchema was null or undefined when calling manageCreateUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentAssetByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentAssetByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageDeleteUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageDeleteUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentPreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserGeneratedContentPreviewByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserGeneratedContentPreviewByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageDeleteUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserGeneratedContentAsset(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserGeneratedContentAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserGeneratedContentAssetByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserGeneratedContentAssetByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageDownloadUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserGeneratedContentPreview(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserGeneratedContentPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadUserGeneratedContentPreviewByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadUserGeneratedContentPreviewByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageDownloadUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserGeneratedContents(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserGeneratedContents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserGeneratedContentsByBin(companyId, userGeneratedContentBinId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserGeneratedContentsByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageGetAllUserGeneratedContentsByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContent(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserGeneratedContent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentByBin(itemId, companyId, userGeneratedContentBinId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserGeneratedContentByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageGetUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserGeneratedContentCountByBin(companyId, userGeneratedContentBinId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserGeneratedContentCountByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageGetUserGeneratedContentCountByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserGeneratedContents(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserGeneratedContents.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserGeneratedContentsByBin(companyId, userGeneratedContentBinId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserGeneratedContentsByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageRenderAllUserGeneratedContentsByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserGeneratedContent(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserGeneratedContent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserGeneratedContentByBin(itemId, companyId, userGeneratedContentBinId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserGeneratedContentByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageRenderUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserGeneratedContent(itemId, companyId, userGeneratedContentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserGeneratedContent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserGeneratedContent.');
      }
      if (userGeneratedContentUpdateSchema === null || userGeneratedContentUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentUpdateSchema was null or undefined when calling manageUpdateUserGeneratedContent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserGeneratedContentByBin(itemId, companyId, userGeneratedContentBinId, userGeneratedContentUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserGeneratedContentByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageUpdateUserGeneratedContentByBin.');
      }
      if (userGeneratedContentUpdateSchema === null || userGeneratedContentUpdateSchema === undefined) {
        throw new Error('Required parameter userGeneratedContentUpdateSchema was null or undefined when calling manageUpdateUserGeneratedContentByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userGeneratedContentUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentAsset(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentAsset.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentAsset.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserGeneratedContentAsset.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentAssetBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentAssetBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentAssetBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserGeneratedContentAssetBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentAssetBase64ByBin(itemId, companyId, userGeneratedContentBinId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageUploadUserGeneratedContentAssetBase64ByBin.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserGeneratedContentAssetBase64ByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentAssetByBin(itemId, companyId, userGeneratedContentBinId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentAssetByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentAssetByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageUploadUserGeneratedContentAssetByBin.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserGeneratedContentAssetByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/asset`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentPreview(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentPreview.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentPreview.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserGeneratedContentPreview.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentPreviewBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentPreviewBase64ByBin(itemId, companyId, userGeneratedContentBinId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64ByBin.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadUserGeneratedContentPreviewBase64ByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadUserGeneratedContentPreviewByBin(itemId, companyId, userGeneratedContentBinId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadUserGeneratedContentPreviewByBin.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadUserGeneratedContentPreviewByBin.');
      }
      if (userGeneratedContentBinId === null || userGeneratedContentBinId === undefined) {
        throw new Error('Required parameter userGeneratedContentBinId was null or undefined when calling manageUploadUserGeneratedContentPreviewByBin.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadUserGeneratedContentPreviewByBin.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content_bin/${this.configuration.encodeParam({
        name: 'userGeneratedContentBinId',
        value: userGeneratedContentBinId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_generated_content/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/preview`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserGeneratedContentCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserGeneratedContentCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserGeneratedContentCRUDService,
        factory: UserGeneratedContentCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserGeneratedContentCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserLocationCRUDService = /*#__PURE__*/(() => {
  class UserLocationCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserLocation(companyId, userLocationCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserLocation.');
      }
      if (userLocationCreateSchema === null || userLocationCreateSchema === undefined) {
        throw new Error('Required parameter userLocationCreateSchema was null or undefined when calling manageCreateUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserLocation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserLocation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserLocations(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserLocations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocation(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserLocation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocationCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocationCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserLocations(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserLocations.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserLocation(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserLocation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserLocation(itemId, companyId, userLocationUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserLocation.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserLocation.');
      }
      if (userLocationUpdateSchema === null || userLocationUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationUpdateSchema was null or undefined when calling manageUpdateUserLocation.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserLocationCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserLocationCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserLocationCRUDService,
        factory: UserLocationCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserLocationCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserLocationHistoryCRUDService = /*#__PURE__*/(() => {
  class UserLocationHistoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserLocationHistory(companyId, userLocationHistoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserLocationHistory.');
      }
      if (userLocationHistoryCreateSchema === null || userLocationHistoryCreateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryCreateSchema was null or undefined when calling manageCreateUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserLocationHistoryByEvent(companyId, eventId, userLocationHistoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateUserLocationHistoryByEvent.');
      }
      if (userLocationHistoryCreateSchema === null || userLocationHistoryCreateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryCreateSchema was null or undefined when calling manageCreateUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserLocationHistory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserLocationHistory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserLocationHistoryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserLocationHistoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserLocationHistories(companyId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserLocationHistories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserLocationHistoriesByEvent(companyId, eventId, orderBy, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserLocationHistoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllUserLocationHistoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocationHistory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserLocationHistory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocationHistoryByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserLocationHistoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocationHistoryCount(companyId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocationHistoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserLocationHistoryCountByEvent(companyId, eventId, orderBy, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserLocationHistoryCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetUserLocationHistoryCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserLocationHistories(companyId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserLocationHistories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserLocationHistoriesByEvent(companyId, eventId, orderBy, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserLocationHistoriesByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllUserLocationHistoriesByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserLocationHistory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserLocationHistory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserLocationHistoryByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserLocationHistoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserLocationHistory(itemId, companyId, userLocationHistoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserLocationHistory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserLocationHistory.');
      }
      if (userLocationHistoryUpdateSchema === null || userLocationHistoryUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryUpdateSchema was null or undefined when calling manageUpdateUserLocationHistory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserLocationHistoryByEvent(itemId, companyId, eventId, userLocationHistoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserLocationHistoryByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserLocationHistoryByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateUserLocationHistoryByEvent.');
      }
      if (userLocationHistoryUpdateSchema === null || userLocationHistoryUpdateSchema === undefined) {
        throw new Error('Required parameter userLocationHistoryUpdateSchema was null or undefined when calling manageUpdateUserLocationHistoryByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_location_history/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userLocationHistoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserLocationHistoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserLocationHistoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserLocationHistoryCRUDService,
        factory: UserLocationHistoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserLocationHistoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let UserSubscriptionCRUDService = /*#__PURE__*/(() => {
  class UserSubscriptionCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateUserSubscription(companyId, userSubscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserSubscription.');
      }
      if (userSubscriptionCreateSchema === null || userSubscriptionCreateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionCreateSchema was null or undefined when calling manageCreateUserSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserSubscriptionBySubscription(companyId, subscriptionId, userSubscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageCreateUserSubscriptionBySubscription.');
      }
      if (userSubscriptionCreateSchema === null || userSubscriptionCreateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionCreateSchema was null or undefined when calling manageCreateUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateUserSubscriptionByUser(companyId, userId, userSubscriptionCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageCreateUserSubscriptionByUser.');
      }
      if (userSubscriptionCreateSchema === null || userSubscriptionCreateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionCreateSchema was null or undefined when calling manageCreateUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserSubscription(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserSubscriptionBySubscription(itemId, companyId, subscriptionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserSubscriptionBySubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageDeleteUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteUserSubscriptionByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteUserSubscriptionByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageDeleteUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptions(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptionsBySubscription(companyId, subscriptionId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptionsBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageGetAllUserSubscriptionsBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptionsBySubscriptionMulti(companyId, subscriptionId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptionsBySubscriptionMulti.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageGetAllUserSubscriptionsBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptionsByUser(companyId, userId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptionsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllUserSubscriptionsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptionsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptionsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetAllUserSubscriptionsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllUserSubscriptionsMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllUserSubscriptionsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscription(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionBySubscription(itemId, companyId, subscriptionId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserSubscriptionBySubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageGetUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionByUser(itemId, companyId, userId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetUserSubscriptionByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCountBySubscription(companyId, subscriptionId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCountBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageGetUserSubscriptionCountBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCountBySubscriptionMulti(companyId, subscriptionId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCountBySubscriptionMulti.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageGetUserSubscriptionCountBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCountByUser(companyId, userId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCountByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetUserSubscriptionCountByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCountByUserMulti(companyId, userId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCountByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageGetUserSubscriptionCountByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetUserSubscriptionCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetUserSubscriptionCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptions(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptionsBySubscription(companyId, subscriptionId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptionsBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageRenderAllUserSubscriptionsBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptionsBySubscriptionMulti(companyId, subscriptionId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptionsBySubscriptionMulti.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageRenderAllUserSubscriptionsBySubscriptionMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptionsByUser(companyId, userId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptionsByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllUserSubscriptionsByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptionsByUserMulti(companyId, userId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptionsByUserMulti.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderAllUserSubscriptionsByUserMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllUserSubscriptionsMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllUserSubscriptionsMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserSubscription(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserSubscriptionBySubscription(itemId, companyId, subscriptionId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserSubscriptionBySubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageRenderUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderUserSubscriptionByUser(itemId, companyId, userId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderUserSubscriptionByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageRenderUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetSubscriptionUsers(companyId, subscriptionId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetSubscriptionUsers.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageSetSubscriptionUsers.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetSubscriptionUsers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageSetUserSubscriptions(companyId, userId, requestBody, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageSetUserSubscriptions.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageSetUserSubscriptions.');
      }
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling manageSetUserSubscriptions.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserSubscription(itemId, companyId, userSubscriptionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserSubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserSubscription.');
      }
      if (userSubscriptionUpdateSchema === null || userSubscriptionUpdateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionUpdateSchema was null or undefined when calling manageUpdateUserSubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserSubscriptionBySubscription(itemId, companyId, subscriptionId, userSubscriptionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserSubscriptionBySubscription.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserSubscriptionBySubscription.');
      }
      if (subscriptionId === null || subscriptionId === undefined) {
        throw new Error('Required parameter subscriptionId was null or undefined when calling manageUpdateUserSubscriptionBySubscription.');
      }
      if (userSubscriptionUpdateSchema === null || userSubscriptionUpdateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionUpdateSchema was null or undefined when calling manageUpdateUserSubscriptionBySubscription.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/subscription/${this.configuration.encodeParam({
        name: 'subscriptionId',
        value: subscriptionId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateUserSubscriptionByUser(itemId, companyId, userId, userSubscriptionUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateUserSubscriptionByUser.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateUserSubscriptionByUser.');
      }
      if (userId === null || userId === undefined) {
        throw new Error('Required parameter userId was null or undefined when calling manageUpdateUserSubscriptionByUser.');
      }
      if (userSubscriptionUpdateSchema === null || userSubscriptionUpdateSchema === undefined) {
        throw new Error('Required parameter userSubscriptionUpdateSchema was null or undefined when calling manageUpdateUserSubscriptionByUser.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user/${this.configuration.encodeParam({
        name: 'userId',
        value: userId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/user_subscription/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: userSubscriptionUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function UserSubscriptionCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UserSubscriptionCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserSubscriptionCRUDService,
        factory: UserSubscriptionCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserSubscriptionCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let VenueCRUDService = /*#__PURE__*/(() => {
  class VenueCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateVenue(companyId, venueCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateVenue.');
      }
      if (venueCreateSchema === null || venueCreateSchema === undefined) {
        throw new Error('Required parameter venueCreateSchema was null or undefined when calling manageCreateVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: venueCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVenue(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteVenue.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVenueLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteVenueLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVenueLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVenuePicture(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteVenuePicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVenuePicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadVenueLogo(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadVenueLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadVenueLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadVenuePicture(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadVenuePicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadVenuePicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllVenues(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllVenues.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllVenuesMulti(companyId, idList, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllVenuesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllVenuesSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllVenuesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenue(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetVenue.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCountMulti(companyId, idList, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCountMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_count.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCountSearch(companyId, orderBy, searchTermList, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllVenues(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllVenues.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllVenuesMulti(companyId, idList, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllVenuesMulti.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (idList) {
        idList.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'id_list');
        });
      }
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/render.multi`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllVenuesSearch(companyId, orderBy, searchTermList, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllVenuesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTermList, 'search_term_list');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderVenue(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderVenue.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateVenue(itemId, companyId, venueUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateVenue.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateVenue.');
      }
      if (venueUpdateSchema === null || venueUpdateSchema === undefined) {
        throw new Error('Required parameter venueUpdateSchema was null or undefined when calling manageUpdateVenue.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: venueUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadVenueLogo(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadVenueLogo.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadVenueLogo.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadVenueLogo.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadVenueLogoBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadVenueLogoBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadVenueLogoBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadVenueLogoBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/logo/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadVenuePicture(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadVenuePicture.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadVenuePicture.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadVenuePicture.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/picture`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadVenuePictureBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadVenuePictureBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadVenuePictureBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadVenuePictureBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/picture/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function VenueCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VenueCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VenueCRUDService,
        factory: VenueCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VenueCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let VenueCategoryCRUDService = /*#__PURE__*/(() => {
  class VenueCategoryCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateVenueCategory(companyId, venueCategoryCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateVenueCategory.');
      }
      if (venueCategoryCreateSchema === null || venueCategoryCreateSchema === undefined) {
        throw new Error('Required parameter venueCategoryCreateSchema was null or undefined when calling manageCreateVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: venueCategoryCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteVenueCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteVenueCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllVenueCategories(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllVenueCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllVenueCategoriesSearch(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllVenueCategoriesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCategory(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetVenueCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCategoryCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCategoryCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetVenueCategoryCountSearch(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetVenueCategoryCountSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category_count.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllVenueCategories(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllVenueCategories.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllVenueCategoriesSearch(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllVenueCategoriesSearch.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/render.search`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderVenueCategory(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderVenueCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateVenueCategory(itemId, companyId, venueCategoryUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateVenueCategory.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateVenueCategory.');
      }
      if (venueCategoryUpdateSchema === null || venueCategoryUpdateSchema === undefined) {
        throw new Error('Required parameter venueCategoryUpdateSchema was null or undefined when calling manageUpdateVenueCategory.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/venue_category/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: venueCategoryUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function VenueCategoryCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VenueCategoryCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VenueCategoryCRUDService,
        factory: VenueCategoryCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VenueCategoryCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let VoteService = /*#__PURE__*/(() => {
  class VoteService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    userVote(pollId, pollResponse, subdomain, observe = 'body', reportProgress = false, options) {
      if (pollId === null || pollId === undefined) {
        throw new Error('Required parameter pollId was null or undefined when calling userVote.');
      }
      if (pollResponse === null || pollResponse === undefined) {
        throw new Error('Required parameter pollResponse was null or undefined when calling userVote.');
      }
      if (subdomain === null || subdomain === undefined) {
        throw new Error('Required parameter subdomain was null or undefined when calling userVote.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/company/${this.configuration.encodeParam({
        name: 'subdomain',
        value: subdomain,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'string',
        dataFormat: undefined
      })}/vote/${this.configuration.encodeParam({
        name: 'pollId',
        value: pollId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/${this.configuration.encodeParam({
        name: 'pollResponse',
        value: pollResponse,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function VoteService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VoteService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: VoteService,
        factory: VoteService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return VoteService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WallCRUDService = /*#__PURE__*/(() => {
  class WallCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateWall(companyId, wallCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWall.');
      }
      if (wallCreateSchema === null || wallCreateSchema === undefined) {
        throw new Error('Required parameter wallCreateSchema was null or undefined when calling manageCreateWall.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateWallByEvent(companyId, eventId, wallCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateWallByEvent.');
      }
      if (wallCreateSchema === null || wallCreateSchema === undefined) {
        throw new Error('Required parameter wallCreateSchema was null or undefined when calling manageCreateWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateWallByEventAndAgenda(companyId, eventId, agendaId, wallCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageCreateWallByEventAndAgenda.');
      }
      if (wallCreateSchema === null || wallCreateSchema === undefined) {
        throw new Error('Required parameter wallCreateSchema was null or undefined when calling manageCreateWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWall(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWall.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWall.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWallByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWallByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWallByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWallByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWallImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWallImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWallImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWallImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWallImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWallImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteWallImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWallImageByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWallImageByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWallImageByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteWallImageByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDeleteWallImageByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadWallImage(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadWallImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadWallImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadWallImageByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadWallImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadWallImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadWallImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDownloadWallImageByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDownloadWallImageByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDownloadWallImageByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDownloadWallImageByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageDownloadWallImageByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWalls(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWalls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWallsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWallsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllWallsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWallsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWallsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllWallsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetAllWallsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWall(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWall.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWall.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWallByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWallByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWallByEventAndAgenda(itemId, companyId, eventId, agendaId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWallByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWallCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWallCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWallCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWallCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWallCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWallCountByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWallCountByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWallCountByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageGetWallCountByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWalls(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWalls.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWallsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWallsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllWallsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWallsByEventAndAgenda(companyId, eventId, agendaId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWallsByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllWallsByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderAllWallsByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWall(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWall.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWall.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWallByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWallByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWallByEventAndAgenda(itemId, companyId, eventId, agendaId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWallByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageRenderWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWall(itemId, companyId, wallUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWall.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWall.');
      }
      if (wallUpdateSchema === null || wallUpdateSchema === undefined) {
        throw new Error('Required parameter wallUpdateSchema was null or undefined when calling manageUpdateWall.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWallByEvent(itemId, companyId, eventId, wallUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWallByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWallByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateWallByEvent.');
      }
      if (wallUpdateSchema === null || wallUpdateSchema === undefined) {
        throw new Error('Required parameter wallUpdateSchema was null or undefined when calling manageUpdateWallByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWallByEventAndAgenda(itemId, companyId, eventId, agendaId, wallUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWallByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWallByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateWallByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUpdateWallByEventAndAgenda.');
      }
      if (wallUpdateSchema === null || wallUpdateSchema === undefined) {
        throw new Error('Required parameter wallUpdateSchema was null or undefined when calling manageUpdateWallByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wallUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImage(itemId, companyId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImage.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImage.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadWallImage.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImageBase64(itemId, companyId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImageBase64.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImageBase64.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadWallImageBase64.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImageBase64ByEvent(itemId, companyId, eventId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImageBase64ByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImageBase64ByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadWallImageBase64ByEvent.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadWallImageBase64ByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImageBase64ByEventAndAgenda(itemId, companyId, eventId, agendaId, body, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImageBase64ByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImageBase64ByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadWallImageBase64ByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUploadWallImageBase64ByEventAndAgenda.');
      }
      if (body === null || body === undefined) {
        throw new Error('Required parameter body was null or undefined when calling manageUploadWallImageBase64ByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image/base64`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: body,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImageByEvent(itemId, companyId, eventId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImageByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImageByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadWallImageByEvent.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadWallImageByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUploadWallImageByEventAndAgenda(itemId, companyId, eventId, agendaId, assetFile, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUploadWallImageByEventAndAgenda.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUploadWallImageByEventAndAgenda.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUploadWallImageByEventAndAgenda.');
      }
      if (agendaId === null || agendaId === undefined) {
        throw new Error('Required parameter agendaId was null or undefined when calling manageUploadWallImageByEventAndAgenda.');
      }
      if (assetFile === null || assetFile === undefined) {
        throw new Error('Required parameter assetFile was null or undefined when calling manageUploadWallImageByEventAndAgenda.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['multipart/form-data'];
      const canConsumeForm = this.canConsumeForm(consumes);
      let localVarFormParams;
      let localVarUseForm = false;
      let localVarConvertFormParamsToString = false;
      // use FormData to transmit files using content-type "multipart/form-data"
      // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
      localVarUseForm = canConsumeForm;
      if (localVarUseForm) {
        localVarFormParams = new FormData();
      } else {
        localVarFormParams = new HttpParams({
          encoder: this.encoder
        });
      }
      if (assetFile !== undefined) {
        localVarFormParams = localVarFormParams.append('asset_file', assetFile) || localVarFormParams;
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/agenda/${this.configuration.encodeParam({
        name: 'agendaId',
        value: agendaId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wall/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/image`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WallCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WallCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WallCRUDService,
        factory: WallCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WallCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WordsCloudBlacklistCRUDService = /*#__PURE__*/(() => {
  class WordsCloudBlacklistCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateWordscloudBlacklist(companyId, wordsCloudBlacklistCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWordscloudBlacklist.');
      }
      if (wordsCloudBlacklistCreateSchema === null || wordsCloudBlacklistCreateSchema === undefined) {
        throw new Error('Required parameter wordsCloudBlacklistCreateSchema was null or undefined when calling manageCreateWordscloudBlacklist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudBlacklistCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllWordscloudBlacklists(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllWordscloudBlacklists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWordscloudBlacklist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWordscloudBlacklist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWordscloudBlacklist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWordscloudBlacklists(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWordscloudBlacklists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudBlacklist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWordscloudBlacklist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudBlacklist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudBlacklistCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudBlacklistCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWordscloudBlacklists(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWordscloudBlacklists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWordscloudBlacklist(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWordscloudBlacklist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWordscloudBlacklist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWordscloudBlacklist(itemId, companyId, wordsCloudBlacklistUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWordscloudBlacklist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWordscloudBlacklist.');
      }
      if (wordsCloudBlacklistUpdateSchema === null || wordsCloudBlacklistUpdateSchema === undefined) {
        throw new Error('Required parameter wordsCloudBlacklistUpdateSchema was null or undefined when calling manageUpdateWordscloudBlacklist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_blacklist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudBlacklistUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WordsCloudBlacklistCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WordsCloudBlacklistCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WordsCloudBlacklistCRUDService,
        factory: WordsCloudBlacklistCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WordsCloudBlacklistCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WordsCloudCRUDService = /*#__PURE__*/(() => {
  class WordsCloudCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateWordscloud(companyId, wordsCloudCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWordscloud.');
      }
      if (wordsCloudCreateSchema === null || wordsCloudCreateSchema === undefined) {
        throw new Error('Required parameter wordsCloudCreateSchema was null or undefined when calling manageCreateWordscloud.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageCreateWordscloudByEvent(companyId, eventId, wordsCloudCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageCreateWordscloudByEvent.');
      }
      if (wordsCloudCreateSchema === null || wordsCloudCreateSchema === undefined) {
        throw new Error('Required parameter wordsCloudCreateSchema was null or undefined when calling manageCreateWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllWordsclouds(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllWordsclouds.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllWordscloudsByEvent(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWordscloud(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWordscloud.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWordscloud.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWordscloudByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWordscloudByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageDeleteWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWordsclouds(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWordsclouds.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWordscloudsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloud(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWordscloud.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloud.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudByEvent(itemId, companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWordscloudByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudCountByEvent(companyId, eventId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudCountByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWordscloudCountByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudFrequencies(companyId, eventId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudFrequencies.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageGetWordscloudFrequencies.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_frequencies`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWordsclouds(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWordsclouds.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWordscloudsByEvent(companyId, eventId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWordscloudsByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderAllWordscloudsByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWordscloud(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWordscloud.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWordscloud.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWordscloudByEvent(itemId, companyId, eventId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWordscloudByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageRenderWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWordscloud(itemId, companyId, wordsCloudUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWordscloud.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWordscloud.');
      }
      if (wordsCloudUpdateSchema === null || wordsCloudUpdateSchema === undefined) {
        throw new Error('Required parameter wordsCloudUpdateSchema was null or undefined when calling manageUpdateWordscloud.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWordscloudByEvent(itemId, companyId, eventId, wordsCloudUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWordscloudByEvent.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWordscloudByEvent.');
      }
      if (eventId === null || eventId === undefined) {
        throw new Error('Required parameter eventId was null or undefined when calling manageUpdateWordscloudByEvent.');
      }
      if (wordsCloudUpdateSchema === null || wordsCloudUpdateSchema === undefined) {
        throw new Error('Required parameter wordsCloudUpdateSchema was null or undefined when calling manageUpdateWordscloudByEvent.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/event/${this.configuration.encodeParam({
        name: 'eventId',
        value: eventId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WordsCloudCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WordsCloudCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WordsCloudCRUDService,
        factory: WordsCloudCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WordsCloudCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WordsCloudWhitelistCRUDService = /*#__PURE__*/(() => {
  class WordsCloudWhitelistCRUDService extends BaseService {
    constructor(httpClient, basePath, configuration) {
      super(basePath, configuration);
      this.httpClient = httpClient;
    }
    manageCreateWordscloudWhitelist(companyId, wordsCloudWhitelistCreateSchema, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageCreateWordscloudWhitelist.');
      }
      if (wordsCloudWhitelistCreateSchema === null || wordsCloudWhitelistCreateSchema === undefined) {
        throw new Error('Required parameter wordsCloudWhitelistCreateSchema was null or undefined when calling manageCreateWordscloudWhitelist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('post', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudWhitelistCreateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteAllWordscloudWhitelists(companyId, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteAllWordscloudWhitelists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageDeleteWordscloudWhitelist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageDeleteWordscloudWhitelist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageDeleteWordscloudWhitelist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('delete', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetAllWordscloudWhitelists(companyId, orderBy, searchTerms, skip, limit, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetAllWordscloudWhitelists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudWhitelist(itemId, companyId, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageGetWordscloudWhitelist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudWhitelist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageGetWordscloudWhitelistCount(companyId, orderBy, searchTerms, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageGetWordscloudWhitelistCount.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist_count`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderAllWordscloudWhitelists(companyId, orderBy, searchTerms, skip, limit, columns, observe = 'body', reportProgress = false, options) {
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderAllWordscloudWhitelists.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orderBy, 'order_by');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchTerms, 'search_terms');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, skip, 'skip');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, limit, 'limit');
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageRenderWordscloudWhitelist(itemId, companyId, columns, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageRenderWordscloudWhitelist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageRenderWordscloudWhitelist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, columns, 'columns');
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/render`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('get', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    manageUpdateWordscloudWhitelist(itemId, companyId, wordsCloudWhitelistUpdateSchema, observe = 'body', reportProgress = false, options) {
      if (itemId === null || itemId === undefined) {
        throw new Error('Required parameter itemId was null or undefined when calling manageUpdateWordscloudWhitelist.');
      }
      if (companyId === null || companyId === undefined) {
        throw new Error('Required parameter companyId was null or undefined when calling manageUpdateWordscloudWhitelist.');
      }
      if (wordsCloudWhitelistUpdateSchema === null || wordsCloudWhitelistUpdateSchema === undefined) {
        throw new Error('Required parameter wordsCloudWhitelistUpdateSchema was null or undefined when calling manageUpdateWordscloudWhitelist.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      let localVarHeaders = this.defaultHeaders;
      // authentication (APIKeyQuery) required
      localVarQueryParameters = this.configuration.addCredentialToQuery('APIKeyQuery', 'authentication_token', localVarQueryParameters);
      // authentication (OAuth2PasswordBearer) required
      localVarHeaders = this.configuration.addCredentialToHeaders('OAuth2PasswordBearer', 'Authorization', localVarHeaders, 'Bearer ');
      const localVarHttpHeaderAcceptSelected = options?.httpHeaderAccept ?? this.configuration.selectHeaderAccept(['application/json']);
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      const localVarHttpContext = options?.context ?? new HttpContext();
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/manage/crud/company/${this.configuration.encodeParam({
        name: 'companyId',
        value: companyId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}/wordscloud_whitelist/${this.configuration.encodeParam({
        name: 'itemId',
        value: itemId,
        in: 'path',
        style: 'simple',
        explode: false,
        dataType: 'number',
        dataFormat: undefined
      })}`;
      const {
        basePath,
        withCredentials
      } = this.configuration;
      return this.httpClient.request('put', `${basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wordsCloudWhitelistUpdateSchema,
        params: localVarQueryParameters,
        responseType: responseType_,
        ...(withCredentials ? {
          withCredentials
        } : {}),
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WordsCloudWhitelistCRUDService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WordsCloudWhitelistCRUDService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WordsCloudWhitelistCRUDService,
        factory: WordsCloudWhitelistCRUDService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WordsCloudWhitelistCRUDService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const APIS = [AdministratorService, AdministratorCRUDService, AdministratorCompanyCRUDService, AdministratorEventCheckInCRUDService, AgendaCRUDService, AgendaCategoryCRUDService, AgendaFlowCRUDService, AgendaSpeakerCRUDService, AgendaSubcategoryCRUDService, AgendaUserCRUDService, AuthenticationService, BackendHelpCRUDService, BackendTranslationCRUDService, CalendarService, CarReservationCRUDService, ChatGroupCRUDService, ChatRoomCRUDService, ChatRoomMessageCRUDService, CompanyCRUDService, CompanyFileCRUDService, CompanyTranslationCRUDService, DeviceTokenCRUDService, DocumentCRUDService, DocumentDownloadCRUDService, DocumentRatingCRUDService, EventService, EventAccessDomainCRUDService, EventAccessEntryCRUDService, EventAccessLevelCRUDService, EventCRUDService, EventCategoryCRUDService, EventChatGroupCRUDService, EventCheckInCRUDService, EventFlowCRUDService, EventGameCRUDService, EventGameUserScoreCRUDService, EventGroupCRUDService, EventHotelRoomCRUDService, EventPictureCRUDService, EventPlaylistCRUDService, EventSpeakerCRUDService, EventSponsorCRUDService, EventSponsorTierCRUDService, EventSubcategoryCRUDService, EventUserCRUDService, EventUserRestaurantCRUDService, ExhibitorBrandCRUDService, ExhibitorCRUDService, ExhibitorDocumentCRUDService, ExhibitorEventUserCRUDService, ExhibitorPressReleaseCRUDService, ExhibitorProductCRUDService, ExhibitorVideoCRUDService, FeedbackCRUDService, FeedbackQuestionCRUDService, FeedbackVoteCRUDService, FieldOfApplicationCRUDService, FirmCRUDService, FlightReservationCRUDService, FlowCRUDService, FlowItemCRUDService, FlowItemUserStatusCRUDService, FlowUserCRUDService, GameCRUDService, GigyaKeyDataCRUDService, GroupCRUDService, GroupUserCRUDService, HealthService, HotelCRUDService, HotelReservationCRUDService, LeaderboardService, LiveChatCRUDService, LookupTablesService, MailTemplateCRUDService, MailerService, MailerCRUDService, ManagementService, MediaGalleryCRUDService, MediaGalleryItemCRUDService, MeetingCRUDService, MeetingEventUserCRUDService, MeetingExhibitorCRUDService, MeetingSpeakerCRUDService, MeetingUserCRUDService, MenuVoiceCRUDService, MerchandiseCategoryCRUDService, NewsCategoryCRUDService, NewsItemCRUDService, OAuth2ClientService, OnlineUserService, PDFTemplateCRUDService, ParticipantCategoryCRUDService, PollCRUDService, PollVoteCRUDService, PopupCRUDService, PresenceService, PublicAgendaCRUDService, PublicAgendaCategoryCRUDService, PublicAgendaFlowCRUDService, PublicAgendaSpeakerCRUDService, PublicAgendaSubcategoryCRUDService, PublicAgendaUserCRUDService, PublicCarReservationCRUDService, PublicChatGroupCRUDService, PublicChatRoomCRUDService, PublicChatRoomMessageCRUDService, PublicCompanyFileCRUDService, PublicDeviceTokenCRUDService, PublicDocumentCRUDService, PublicDocumentRatingCRUDService, PublicEventCRUDService, PublicEventCategoryCRUDService, PublicEventChatGroupCRUDService, PublicEventCheckInCRUDService, PublicEventFlowCRUDService, PublicEventGameCRUDService, PublicEventGameUserScoreCRUDService, PublicEventGroupCRUDService, PublicEventHotelRoomCRUDService, PublicEventPictureCRUDService, PublicEventPlaylistCRUDService, PublicEventSpeakerCRUDService, PublicEventSponsorCRUDService, PublicEventSponsorTierCRUDService, PublicEventSubcategoryCRUDService, PublicEventUserCRUDService, PublicEventUserDirectoryService, PublicExhibitorBrandCRUDService, PublicExhibitorCRUDService, PublicExhibitorDocumentCRUDService, PublicExhibitorEventUserCRUDService, PublicExhibitorPressReleaseCRUDService, PublicExhibitorProductCRUDService, PublicExhibitorVideoCRUDService, PublicFeedbackCRUDService, PublicFeedbackQuestionCRUDService, PublicFeedbackVoteCRUDService, PublicFieldOfApplicationCRUDService, PublicFirmCRUDService, PublicFlightReservationCRUDService, PublicFlowCRUDService, PublicFlowItemCRUDService, PublicFlowItemUserStatusCRUDService, PublicFlowUserCRUDService, PublicGameCRUDService, PublicGroupCRUDService, PublicGroupUserCRUDService, PublicHotelCRUDService, PublicHotelReservationCRUDService, PublicLeaderboardService, PublicLiveChatCRUDService, PublicMediaGalleryCRUDService, PublicMediaGalleryItemCRUDService, PublicMeetingCRUDService, PublicMeetingEventUserCRUDService, PublicMeetingExhibitorCRUDService, PublicMeetingSpeakerCRUDService, PublicMeetingUserCRUDService, PublicMenuVoiceCRUDService, PublicMerchandiseCategoryCRUDService, PublicNewsCategoryCRUDService, PublicNewsItemCRUDService, PublicParticipantCategoryCRUDService, PublicPollCRUDService, PublicPollVoteCRUDService, PublicPopupCRUDService, PublicQuizCRUDService, PublicRegistrationFormCRUDService, PublicRestaurantCRUDService, PublicSettingCRUDService, PublicShuttleScheduleCRUDService, PublicSpeakerCRUDService, PublicSponsorCRUDService, PublicStandCRUDService, PublicStreamCRUDService, PublicStreamReactionCRUDService, PublicSubscriptionCRUDService, PublicThemeSettingCRUDService, PublicTradeShowSectorCRUDService, PublicTrainReservationCRUDService, PublicUserCRUDService, PublicUserCategoryCRUDService, PublicUserDocumentCRUDService, PublicUserGeneratedContentBinCRUDService, PublicUserGeneratedContentCRUDService, PublicUserLocationCRUDService, PublicUserLocationHistoryCRUDService, PublicUserSubscriptionCRUDService, PublicVenueCRUDService, PublicVenueCategoryCRUDService, PublicWallCRUDService, PublicWordsCloudCRUDService, PushNotificationsService, QuizCRUDService, RegistrationFormCRUDService, ReportService, RestaurantCRUDService, SelfService, SentMailCRUDService, SentPushCRUDService, SettingCRUDService, ShuttleScheduleCRUDService, SiteService, SmsTemplateCRUDService, SpeakerCRUDService, SponsorCRUDService, StandCRUDService, StreamCRUDService, StreamReactionCRUDService, SubscriptionCRUDService, ThemeSettingCRUDService, TradeShowSectorCRUDService, TrainReservationCRUDService, TranslationCRUDService, UserService, UserCRUDService, UserCategoryCRUDService, UserDocumentCRUDService, UserGeneratedContentBinCRUDService, UserGeneratedContentCRUDService, UserLocationCRUDService, UserLocationHistoryCRUDService, UserSubscriptionCRUDService, VenueCRUDService, VenueCategoryCRUDService, VoteService, WallCRUDService, WordsCloudBlacklistCRUDService, WordsCloudCRUDService, WordsCloudWhitelistCRUDService];

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

var JSendModelListResponseAdministratorCompanyReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseAdministratorCompanyReadOnlySchema) {
  JSendModelListResponseAdministratorCompanyReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAdministratorCompanyReadOnlySchema;
}(JSendModelListResponseAdministratorCompanyReadOnlySchema || {});
var JSendModelListResponseAdministratorReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseAdministratorReadOnlySchema) {
  JSendModelListResponseAdministratorReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAdministratorReadOnlySchema;
}(JSendModelListResponseAdministratorReadOnlySchema || {});
var JSendModelListResponseAgendaCalendarItem = /*#__PURE__*/function (JSendModelListResponseAgendaCalendarItem) {
  JSendModelListResponseAgendaCalendarItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAgendaCalendarItem;
}(JSendModelListResponseAgendaCalendarItem || {});
var JSendModelListResponseAgendaReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseAgendaReadOnlySchema) {
  JSendModelListResponseAgendaReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAgendaReadOnlySchema;
}(JSendModelListResponseAgendaReadOnlySchema || {});
var JSendModelListResponseAgendaSpeakerReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseAgendaSpeakerReadOnlySchema) {
  JSendModelListResponseAgendaSpeakerReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAgendaSpeakerReadOnlySchema;
}(JSendModelListResponseAgendaSpeakerReadOnlySchema || {});
var JSendModelListResponseAgendaUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseAgendaUserReadOnlySchema) {
  JSendModelListResponseAgendaUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseAgendaUserReadOnlySchema;
}(JSendModelListResponseAgendaUserReadOnlySchema || {});
var JSendModelListResponseCompanyReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseCompanyReadOnlySchema) {
  JSendModelListResponseCompanyReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseCompanyReadOnlySchema;
}(JSendModelListResponseCompanyReadOnlySchema || {});
var JSendModelListResponseEventCalendarEntry = /*#__PURE__*/function (JSendModelListResponseEventCalendarEntry) {
  JSendModelListResponseEventCalendarEntry.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventCalendarEntry;
}(JSendModelListResponseEventCalendarEntry || {});
var JSendModelListResponseEventCalendarItem = /*#__PURE__*/function (JSendModelListResponseEventCalendarItem) {
  JSendModelListResponseEventCalendarItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventCalendarItem;
}(JSendModelListResponseEventCalendarItem || {});
var JSendModelListResponseEventGroupReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseEventGroupReadOnlySchema) {
  JSendModelListResponseEventGroupReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventGroupReadOnlySchema;
}(JSendModelListResponseEventGroupReadOnlySchema || {});
var JSendModelListResponseEventReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseEventReadOnlySchema) {
  JSendModelListResponseEventReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventReadOnlySchema;
}(JSendModelListResponseEventReadOnlySchema || {});
var JSendModelListResponseEventSpeakerReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseEventSpeakerReadOnlySchema) {
  JSendModelListResponseEventSpeakerReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventSpeakerReadOnlySchema;
}(JSendModelListResponseEventSpeakerReadOnlySchema || {});
var JSendModelListResponseEventSponsorReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseEventSponsorReadOnlySchema) {
  JSendModelListResponseEventSponsorReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventSponsorReadOnlySchema;
}(JSendModelListResponseEventSponsorReadOnlySchema || {});
var JSendModelListResponseEventUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseEventUserReadOnlySchema) {
  JSendModelListResponseEventUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseEventUserReadOnlySchema;
}(JSendModelListResponseEventUserReadOnlySchema || {});
var JSendModelListResponseExhibitorEventUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseExhibitorEventUserReadOnlySchema) {
  JSendModelListResponseExhibitorEventUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseExhibitorEventUserReadOnlySchema;
}(JSendModelListResponseExhibitorEventUserReadOnlySchema || {});
var JSendModelListResponseExhibitorReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseExhibitorReadOnlySchema) {
  JSendModelListResponseExhibitorReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseExhibitorReadOnlySchema;
}(JSendModelListResponseExhibitorReadOnlySchema || {});
var JSendModelListResponseFeedbackQuestionReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseFeedbackQuestionReadOnlySchema) {
  JSendModelListResponseFeedbackQuestionReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseFeedbackQuestionReadOnlySchema;
}(JSendModelListResponseFeedbackQuestionReadOnlySchema || {});
var JSendModelListResponseGroupUserRankItem = /*#__PURE__*/function (JSendModelListResponseGroupUserRankItem) {
  JSendModelListResponseGroupUserRankItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseGroupUserRankItem;
}(JSendModelListResponseGroupUserRankItem || {});
var JSendModelListResponseGroupUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseGroupUserReadOnlySchema) {
  JSendModelListResponseGroupUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseGroupUserReadOnlySchema;
}(JSendModelListResponseGroupUserReadOnlySchema || {});
var JSendModelListResponseLeaderboardItem = /*#__PURE__*/function (JSendModelListResponseLeaderboardItem) {
  JSendModelListResponseLeaderboardItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseLeaderboardItem;
}(JSendModelListResponseLeaderboardItem || {});
var JSendModelListResponseMeetingEventUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseMeetingEventUserReadOnlySchema) {
  JSendModelListResponseMeetingEventUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseMeetingEventUserReadOnlySchema;
}(JSendModelListResponseMeetingEventUserReadOnlySchema || {});
var JSendModelListResponseMeetingExhibitorReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseMeetingExhibitorReadOnlySchema) {
  JSendModelListResponseMeetingExhibitorReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseMeetingExhibitorReadOnlySchema;
}(JSendModelListResponseMeetingExhibitorReadOnlySchema || {});
var JSendModelListResponseMeetingSpeakerReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseMeetingSpeakerReadOnlySchema) {
  JSendModelListResponseMeetingSpeakerReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseMeetingSpeakerReadOnlySchema;
}(JSendModelListResponseMeetingSpeakerReadOnlySchema || {});
var JSendModelListResponseMeetingUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseMeetingUserReadOnlySchema) {
  JSendModelListResponseMeetingUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseMeetingUserReadOnlySchema;
}(JSendModelListResponseMeetingUserReadOnlySchema || {});
var JSendModelListResponseOakEventCategorySchema = /*#__PURE__*/function (JSendModelListResponseOakEventCategorySchema) {
  JSendModelListResponseOakEventCategorySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseOakEventCategorySchema;
}(JSendModelListResponseOakEventCategorySchema || {});
var JSendModelListResponseOakGenderSchema = /*#__PURE__*/function (JSendModelListResponseOakGenderSchema) {
  JSendModelListResponseOakGenderSchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseOakGenderSchema;
}(JSendModelListResponseOakGenderSchema || {});
var JSendModelListResponseOakRoleSchema = /*#__PURE__*/function (JSendModelListResponseOakRoleSchema) {
  JSendModelListResponseOakRoleSchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseOakRoleSchema;
}(JSendModelListResponseOakRoleSchema || {});
var JSendModelListResponseOnlineUser = /*#__PURE__*/function (JSendModelListResponseOnlineUser) {
  JSendModelListResponseOnlineUser.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseOnlineUser;
}(JSendModelListResponseOnlineUser || {});
var JSendModelListResponsePollResultItem = /*#__PURE__*/function (JSendModelListResponsePollResultItem) {
  JSendModelListResponsePollResultItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponsePollResultItem;
}(JSendModelListResponsePollResultItem || {});
var JSendModelListResponseQuizUserRankItem = /*#__PURE__*/function (JSendModelListResponseQuizUserRankItem) {
  JSendModelListResponseQuizUserRankItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseQuizUserRankItem;
}(JSendModelListResponseQuizUserRankItem || {});
var JSendModelListResponseStreamReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseStreamReadOnlySchema) {
  JSendModelListResponseStreamReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseStreamReadOnlySchema;
}(JSendModelListResponseStreamReadOnlySchema || {});
var JSendModelListResponseUserAgentBrowserItem = /*#__PURE__*/function (JSendModelListResponseUserAgentBrowserItem) {
  JSendModelListResponseUserAgentBrowserItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserAgentBrowserItem;
}(JSendModelListResponseUserAgentBrowserItem || {});
var JSendModelListResponseUserAgentBrowserVersionItem = /*#__PURE__*/function (JSendModelListResponseUserAgentBrowserVersionItem) {
  JSendModelListResponseUserAgentBrowserVersionItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserAgentBrowserVersionItem;
}(JSendModelListResponseUserAgentBrowserVersionItem || {});
var JSendModelListResponseUserAgentPlatformItem = /*#__PURE__*/function (JSendModelListResponseUserAgentPlatformItem) {
  JSendModelListResponseUserAgentPlatformItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserAgentPlatformItem;
}(JSendModelListResponseUserAgentPlatformItem || {});
var JSendModelListResponseUserAgentPlatformVersionItem = /*#__PURE__*/function (JSendModelListResponseUserAgentPlatformVersionItem) {
  JSendModelListResponseUserAgentPlatformVersionItem.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserAgentPlatformVersionItem;
}(JSendModelListResponseUserAgentPlatformVersionItem || {});
var JSendModelListResponseUserReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseUserReadOnlySchema) {
  JSendModelListResponseUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserReadOnlySchema;
}(JSendModelListResponseUserReadOnlySchema || {});
var JSendModelListResponseUserSubscriptionReadOnlySchema = /*#__PURE__*/function (JSendModelListResponseUserSubscriptionReadOnlySchema) {
  JSendModelListResponseUserSubscriptionReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseUserSubscriptionReadOnlySchema;
}(JSendModelListResponseUserSubscriptionReadOnlySchema || {});
var JSendModelListResponseWordsCloudFrequency = /*#__PURE__*/function (JSendModelListResponseWordsCloudFrequency) {
  JSendModelListResponseWordsCloudFrequency.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelListResponseWordsCloudFrequency;
}(JSendModelListResponseWordsCloudFrequency || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendModelResponse = /*#__PURE__*/function (JSendModelResponse) {
  JSendModelResponse.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponse;
}(JSendModelResponse || {});
var JSendModelResponseAdministratorReadOnlySchema = /*#__PURE__*/function (JSendModelResponseAdministratorReadOnlySchema) {
  JSendModelResponseAdministratorReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseAdministratorReadOnlySchema;
}(JSendModelResponseAdministratorReadOnlySchema || {});
var JSendModelResponseAgendaCheckInUserOutput = /*#__PURE__*/function (JSendModelResponseAgendaCheckInUserOutput) {
  JSendModelResponseAgendaCheckInUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseAgendaCheckInUserOutput;
}(JSendModelResponseAgendaCheckInUserOutput || {});
var JSendModelResponseApiKeyInfoOutput = /*#__PURE__*/function (JSendModelResponseApiKeyInfoOutput) {
  JSendModelResponseApiKeyInfoOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseApiKeyInfoOutput;
}(JSendModelResponseApiKeyInfoOutput || {});
var JSendModelResponseCheckEmailsOutput = /*#__PURE__*/function (JSendModelResponseCheckEmailsOutput) {
  JSendModelResponseCheckEmailsOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseCheckEmailsOutput;
}(JSendModelResponseCheckEmailsOutput || {});
var JSendModelResponseCheckLeadUserOutput = /*#__PURE__*/function (JSendModelResponseCheckLeadUserOutput) {
  JSendModelResponseCheckLeadUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseCheckLeadUserOutput;
}(JSendModelResponseCheckLeadUserOutput || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendModelResponseCreateCompanyOutput = /*#__PURE__*/function (JSendModelResponseCreateCompanyOutput) {
  JSendModelResponseCreateCompanyOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseCreateCompanyOutput;
}(JSendModelResponseCreateCompanyOutput || {});
var JSendModelResponseCreateSitePaymentOutput = /*#__PURE__*/function (JSendModelResponseCreateSitePaymentOutput) {
  JSendModelResponseCreateSitePaymentOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseCreateSitePaymentOutput;
}(JSendModelResponseCreateSitePaymentOutput || {});
var JSendModelResponseDashboardStatistics = /*#__PURE__*/function (JSendModelResponseDashboardStatistics) {
  JSendModelResponseDashboardStatistics.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseDashboardStatistics;
}(JSendModelResponseDashboardStatistics || {});
var JSendModelResponseEventCalendarEntry = /*#__PURE__*/function (JSendModelResponseEventCalendarEntry) {
  JSendModelResponseEventCalendarEntry.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseEventCalendarEntry;
}(JSendModelResponseEventCalendarEntry || {});
var JSendModelResponseEventCheckInUserOutput = /*#__PURE__*/function (JSendModelResponseEventCheckInUserOutput) {
  JSendModelResponseEventCheckInUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseEventCheckInUserOutput;
}(JSendModelResponseEventCheckInUserOutput || {});
var JSendModelResponseEventInfo = /*#__PURE__*/function (JSendModelResponseEventInfo) {
  JSendModelResponseEventInfo.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseEventInfo;
}(JSendModelResponseEventInfo || {});
var JSendModelResponseEventUserReadOnlySchema = /*#__PURE__*/function (JSendModelResponseEventUserReadOnlySchema) {
  JSendModelResponseEventUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseEventUserReadOnlySchema;
}(JSendModelResponseEventUserReadOnlySchema || {});
var JSendModelResponseEventUserRestaurantReadOnlySchema = /*#__PURE__*/function (JSendModelResponseEventUserRestaurantReadOnlySchema) {
  JSendModelResponseEventUserRestaurantReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseEventUserRestaurantReadOnlySchema;
}(JSendModelResponseEventUserRestaurantReadOnlySchema || {});
var JSendModelResponseExhibitorCountryList = /*#__PURE__*/function (JSendModelResponseExhibitorCountryList) {
  JSendModelResponseExhibitorCountryList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseExhibitorCountryList;
}(JSendModelResponseExhibitorCountryList || {});
var JSendModelResponseExhibitorPavilionList = /*#__PURE__*/function (JSendModelResponseExhibitorPavilionList) {
  JSendModelResponseExhibitorPavilionList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseExhibitorPavilionList;
}(JSendModelResponseExhibitorPavilionList || {});
var JSendModelResponseExhibitorStandList = /*#__PURE__*/function (JSendModelResponseExhibitorStandList) {
  JSendModelResponseExhibitorStandList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseExhibitorStandList;
}(JSendModelResponseExhibitorStandList || {});
var JSendModelResponseGenerateQuerySignatureOutput = /*#__PURE__*/function (JSendModelResponseGenerateQuerySignatureOutput) {
  JSendModelResponseGenerateQuerySignatureOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGenerateQuerySignatureOutput;
}(JSendModelResponseGenerateQuerySignatureOutput || {});
var JSendModelResponseGetCompanySiteInfoOutput = /*#__PURE__*/function (JSendModelResponseGetCompanySiteInfoOutput) {
  JSendModelResponseGetCompanySiteInfoOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGetCompanySiteInfoOutput;
}(JSendModelResponseGetCompanySiteInfoOutput || {});
var JSendModelResponseGetGigyaApiKeyForDomainOutput = /*#__PURE__*/function (JSendModelResponseGetGigyaApiKeyForDomainOutput) {
  JSendModelResponseGetGigyaApiKeyForDomainOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGetGigyaApiKeyForDomainOutput;
}(JSendModelResponseGetGigyaApiKeyForDomainOutput || {});
var JSendModelResponseGetGigyaDomainsOutput = /*#__PURE__*/function (JSendModelResponseGetGigyaDomainsOutput) {
  JSendModelResponseGetGigyaDomainsOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGetGigyaDomainsOutput;
}(JSendModelResponseGetGigyaDomainsOutput || {});
var JSendModelResponseGetSubdomainAvailabilityOutput = /*#__PURE__*/function (JSendModelResponseGetSubdomainAvailabilityOutput) {
  JSendModelResponseGetSubdomainAvailabilityOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGetSubdomainAvailabilityOutput;
}(JSendModelResponseGetSubdomainAvailabilityOutput || {});
var JSendModelResponseGetUserLocationOutput = /*#__PURE__*/function (JSendModelResponseGetUserLocationOutput) {
  JSendModelResponseGetUserLocationOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGetUserLocationOutput;
}(JSendModelResponseGetUserLocationOutput || {});
var JSendModelResponseGoogleMapsAPIKeyOutput = /*#__PURE__*/function (JSendModelResponseGoogleMapsAPIKeyOutput) {
  JSendModelResponseGoogleMapsAPIKeyOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseGoogleMapsAPIKeyOutput;
}(JSendModelResponseGoogleMapsAPIKeyOutput || {});
var JSendModelResponseLoginUserOutput = /*#__PURE__*/function (JSendModelResponseLoginUserOutput) {
  JSendModelResponseLoginUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseLoginUserOutput;
}(JSendModelResponseLoginUserOutput || {});
var JSendModelResponseMailerOutput = /*#__PURE__*/function (JSendModelResponseMailerOutput) {
  JSendModelResponseMailerOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseMailerOutput;
}(JSendModelResponseMailerOutput || {});
var JSendModelResponsePublicAgendaInCollaborationList = /*#__PURE__*/function (JSendModelResponsePublicAgendaInCollaborationList) {
  JSendModelResponsePublicAgendaInCollaborationList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaInCollaborationList;
}(JSendModelResponsePublicAgendaInCollaborationList || {});
var JSendModelResponsePublicAgendaModeratorList = /*#__PURE__*/function (JSendModelResponsePublicAgendaModeratorList) {
  JSendModelResponsePublicAgendaModeratorList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaModeratorList;
}(JSendModelResponsePublicAgendaModeratorList || {});
var JSendModelResponsePublicAgendaOrganizerList = /*#__PURE__*/function (JSendModelResponsePublicAgendaOrganizerList) {
  JSendModelResponsePublicAgendaOrganizerList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaOrganizerList;
}(JSendModelResponsePublicAgendaOrganizerList || {});
var JSendModelResponsePublicAgendaParticipationTypeList = /*#__PURE__*/function (JSendModelResponsePublicAgendaParticipationTypeList) {
  JSendModelResponsePublicAgendaParticipationTypeList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaParticipationTypeList;
}(JSendModelResponsePublicAgendaParticipationTypeList || {});
var JSendModelResponsePublicAgendaRoomList = /*#__PURE__*/function (JSendModelResponsePublicAgendaRoomList) {
  JSendModelResponsePublicAgendaRoomList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaRoomList;
}(JSendModelResponsePublicAgendaRoomList || {});
var JSendModelResponsePublicAgendaSpeakerList = /*#__PURE__*/function (JSendModelResponsePublicAgendaSpeakerList) {
  JSendModelResponsePublicAgendaSpeakerList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaSpeakerList;
}(JSendModelResponsePublicAgendaSpeakerList || {});
var JSendModelResponsePublicAgendaSponsorshipList = /*#__PURE__*/function (JSendModelResponsePublicAgendaSponsorshipList) {
  JSendModelResponsePublicAgendaSponsorshipList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicAgendaSponsorshipList;
}(JSendModelResponsePublicAgendaSponsorshipList || {});
var JSendModelResponsePublicExhibitorCountryList = /*#__PURE__*/function (JSendModelResponsePublicExhibitorCountryList) {
  JSendModelResponsePublicExhibitorCountryList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicExhibitorCountryList;
}(JSendModelResponsePublicExhibitorCountryList || {});
var JSendModelResponsePublicExhibitorPavilionList = /*#__PURE__*/function (JSendModelResponsePublicExhibitorPavilionList) {
  JSendModelResponsePublicExhibitorPavilionList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicExhibitorPavilionList;
}(JSendModelResponsePublicExhibitorPavilionList || {});
var JSendModelResponsePublicExhibitorStandList = /*#__PURE__*/function (JSendModelResponsePublicExhibitorStandList) {
  JSendModelResponsePublicExhibitorStandList.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePublicExhibitorStandList;
}(JSendModelResponsePublicExhibitorStandList || {});
var JSendModelResponsePushOutput = /*#__PURE__*/function (JSendModelResponsePushOutput) {
  JSendModelResponsePushOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponsePushOutput;
}(JSendModelResponsePushOutput || {});
var JSendModelResponseQRCodeStringOutput = /*#__PURE__*/function (JSendModelResponseQRCodeStringOutput) {
  JSendModelResponseQRCodeStringOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseQRCodeStringOutput;
}(JSendModelResponseQRCodeStringOutput || {});
var JSendModelResponseQuizCheckCompleteOutput = /*#__PURE__*/function (JSendModelResponseQuizCheckCompleteOutput) {
  JSendModelResponseQuizCheckCompleteOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseQuizCheckCompleteOutput;
}(JSendModelResponseQuizCheckCompleteOutput || {});
var JSendModelResponseQuizUnansweredInfoOutput = /*#__PURE__*/function (JSendModelResponseQuizUnansweredInfoOutput) {
  JSendModelResponseQuizUnansweredInfoOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseQuizUnansweredInfoOutput;
}(JSendModelResponseQuizUnansweredInfoOutput || {});
var JSendModelResponseRegisterAdministratorOutput = /*#__PURE__*/function (JSendModelResponseRegisterAdministratorOutput) {
  JSendModelResponseRegisterAdministratorOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseRegisterAdministratorOutput;
}(JSendModelResponseRegisterAdministratorOutput || {});
var JSendModelResponseRegisterLeadOutput = /*#__PURE__*/function (JSendModelResponseRegisterLeadOutput) {
  JSendModelResponseRegisterLeadOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseRegisterLeadOutput;
}(JSendModelResponseRegisterLeadOutput || {});
var JSendModelResponseRegisterTransientUserOutput = /*#__PURE__*/function (JSendModelResponseRegisterTransientUserOutput) {
  JSendModelResponseRegisterTransientUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseRegisterTransientUserOutput;
}(JSendModelResponseRegisterTransientUserOutput || {});
var JSendModelResponseRegisterUserOutput = /*#__PURE__*/function (JSendModelResponseRegisterUserOutput) {
  JSendModelResponseRegisterUserOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseRegisterUserOutput;
}(JSendModelResponseRegisterUserOutput || {});
var JSendModelResponseSignZoomTokenOutput = /*#__PURE__*/function (JSendModelResponseSignZoomTokenOutput) {
  JSendModelResponseSignZoomTokenOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseSignZoomTokenOutput;
}(JSendModelResponseSignZoomTokenOutput || {});
var JSendModelResponseStripoTokenOutput = /*#__PURE__*/function (JSendModelResponseStripoTokenOutput) {
  JSendModelResponseStripoTokenOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseStripoTokenOutput;
}(JSendModelResponseStripoTokenOutput || {});
var JSendModelResponseUsedSpaceInformation = /*#__PURE__*/function (JSendModelResponseUsedSpaceInformation) {
  JSendModelResponseUsedSpaceInformation.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseUsedSpaceInformation;
}(JSendModelResponseUsedSpaceInformation || {});
var JSendModelResponseUserCustomFieldKeysOutput = /*#__PURE__*/function (JSendModelResponseUserCustomFieldKeysOutput) {
  JSendModelResponseUserCustomFieldKeysOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseUserCustomFieldKeysOutput;
}(JSendModelResponseUserCustomFieldKeysOutput || {});
var JSendModelResponseUserInformationOutput = /*#__PURE__*/function (JSendModelResponseUserInformationOutput) {
  JSendModelResponseUserInformationOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseUserInformationOutput;
}(JSendModelResponseUserInformationOutput || {});
var JSendModelResponseUserReadOnlySchema = /*#__PURE__*/function (JSendModelResponseUserReadOnlySchema) {
  JSendModelResponseUserReadOnlySchema.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseUserReadOnlySchema;
}(JSendModelResponseUserReadOnlySchema || {});
var JSendModelResponseUserReportFieldKeysOutput = /*#__PURE__*/function (JSendModelResponseUserReportFieldKeysOutput) {
  JSendModelResponseUserReportFieldKeysOutput.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseUserReportFieldKeysOutput;
}(JSendModelResponseUserReportFieldKeysOutput || {});
var JSendModelResponseWatermark = /*#__PURE__*/function (JSendModelResponseWatermark) {
  JSendModelResponseWatermark.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendModelResponseWatermark;
}(JSendModelResponseWatermark || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponse = /*#__PURE__*/function (JSendObjectResponse) {
  JSendObjectResponse.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponse;
}(JSendObjectResponse || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseBool = /*#__PURE__*/function (JSendObjectResponseBool) {
  JSendObjectResponseBool.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseBool;
}(JSendObjectResponseBool || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseDict = /*#__PURE__*/function (JSendObjectResponseDict) {
  JSendObjectResponseDict.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseDict;
}(JSendObjectResponseDict || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseDictStrAny = /*#__PURE__*/function (JSendObjectResponseDictStrAny) {
  JSendObjectResponseDictStrAny.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseDictStrAny;
}(JSendObjectResponseDictStrAny || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseDictStrInt = /*#__PURE__*/function (JSendObjectResponseDictStrInt) {
  JSendObjectResponseDictStrInt.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseDictStrInt;
}(JSendObjectResponseDictStrInt || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseInt = /*#__PURE__*/function (JSendObjectResponseInt) {
  JSendObjectResponseInt.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseInt;
}(JSendObjectResponseInt || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var JSendObjectResponseListDict = /*#__PURE__*/function (JSendObjectResponseListDict) {
  JSendObjectResponseListDict.StatusEnum = {
    Success: 'success',
    Error: 'error'
  };
  return JSendObjectResponseListDict;
}(JSendObjectResponseListDict || {});
/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const OakEventCategories = {
  NUMBER_0: 0,
  NUMBER_1: 1,
  NUMBER_2: 2,
  NUMBER_3: 3,
  NUMBER_4: 4
};

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const OakFeedbackAnswerType = {
  NUMBER_0: 0,
  NUMBER_1: 1,
  NUMBER_2: 2
};

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const OakGenders = {
  NUMBER_0: 0,
  NUMBER_1: 1,
  NUMBER_2: 2,
  NUMBER_3: 3
};

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * OAK Events API
 *
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/*
 * Public API Surface of ngx-oak-api
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AdministratorCRUDService, AdministratorCompanyCRUDService, AdministratorEventCheckInCRUDService, AdministratorService, AgendaCRUDService, AgendaCategoryCRUDService, AgendaFlowCRUDService, AgendaSpeakerCRUDService, AgendaSubcategoryCRUDService, AgendaUserCRUDService, ApiModule, AuthenticationService, BackendHelpCRUDService, BackendTranslationCRUDService, CalendarService, CarReservationCRUDService, ChatGroupCRUDService, ChatRoomCRUDService, ChatRoomMessageCRUDService, CompanyCRUDService, CompanyFileCRUDService, CompanyTranslationCRUDService, Configuration, DeviceTokenCRUDService, DocumentCRUDService, DocumentDownloadCRUDService, DocumentRatingCRUDService, EventAccessDomainCRUDService, EventAccessEntryCRUDService, EventAccessLevelCRUDService, EventCRUDService, EventCategoryCRUDService, EventChatGroupCRUDService, EventCheckInCRUDService, EventFlowCRUDService, EventGameCRUDService, EventGameUserScoreCRUDService, EventGroupCRUDService, EventHotelRoomCRUDService, EventPictureCRUDService, EventPlaylistCRUDService, EventService, EventSpeakerCRUDService, EventSponsorCRUDService, EventSponsorTierCRUDService, EventSubcategoryCRUDService, EventUserCRUDService, EventUserRestaurantCRUDService, ExhibitorBrandCRUDService, ExhibitorCRUDService, ExhibitorDocumentCRUDService, ExhibitorEventUserCRUDService, ExhibitorPressReleaseCRUDService, ExhibitorProductCRUDService, ExhibitorVideoCRUDService, FeedbackCRUDService, FeedbackQuestionCRUDService, FeedbackVoteCRUDService, FieldOfApplicationCRUDService, FirmCRUDService, FlightReservationCRUDService, FlowCRUDService, FlowItemCRUDService, FlowItemUserStatusCRUDService, FlowUserCRUDService, GameCRUDService, GigyaKeyDataCRUDService, GroupCRUDService, GroupUserCRUDService, HealthService, HotelCRUDService, HotelReservationCRUDService, JSendModelListResponseAdministratorCompanyReadOnlySchema, JSendModelListResponseAdministratorReadOnlySchema, JSendModelListResponseAgendaCalendarItem, JSendModelListResponseAgendaReadOnlySchema, JSendModelListResponseAgendaSpeakerReadOnlySchema, JSendModelListResponseAgendaUserReadOnlySchema, JSendModelListResponseCompanyReadOnlySchema, JSendModelListResponseEventCalendarEntry, JSendModelListResponseEventCalendarItem, JSendModelListResponseEventGroupReadOnlySchema, JSendModelListResponseEventReadOnlySchema, JSendModelListResponseEventSpeakerReadOnlySchema, JSendModelListResponseEventSponsorReadOnlySchema, JSendModelListResponseEventUserReadOnlySchema, JSendModelListResponseExhibitorEventUserReadOnlySchema, JSendModelListResponseExhibitorReadOnlySchema, JSendModelListResponseFeedbackQuestionReadOnlySchema, JSendModelListResponseGroupUserRankItem, JSendModelListResponseGroupUserReadOnlySchema, JSendModelListResponseLeaderboardItem, JSendModelListResponseMeetingEventUserReadOnlySchema, JSendModelListResponseMeetingExhibitorReadOnlySchema, JSendModelListResponseMeetingSpeakerReadOnlySchema, JSendModelListResponseMeetingUserReadOnlySchema, JSendModelListResponseOakEventCategorySchema, JSendModelListResponseOakGenderSchema, JSendModelListResponseOakRoleSchema, JSendModelListResponseOnlineUser, JSendModelListResponsePollResultItem, JSendModelListResponseQuizUserRankItem, JSendModelListResponseStreamReadOnlySchema, JSendModelListResponseUserAgentBrowserItem, JSendModelListResponseUserAgentBrowserVersionItem, JSendModelListResponseUserAgentPlatformItem, JSendModelListResponseUserAgentPlatformVersionItem, JSendModelListResponseUserReadOnlySchema, JSendModelListResponseUserSubscriptionReadOnlySchema, JSendModelListResponseWordsCloudFrequency, JSendModelResponse, JSendModelResponseAdministratorReadOnlySchema, JSendModelResponseAgendaCheckInUserOutput, JSendModelResponseApiKeyInfoOutput, JSendModelResponseCheckEmailsOutput, JSendModelResponseCheckLeadUserOutput, JSendModelResponseCreateCompanyOutput, JSendModelResponseCreateSitePaymentOutput, JSendModelResponseDashboardStatistics, JSendModelResponseEventCalendarEntry, JSendModelResponseEventCheckInUserOutput, JSendModelResponseEventInfo, JSendModelResponseEventUserReadOnlySchema, JSendModelResponseEventUserRestaurantReadOnlySchema, JSendModelResponseExhibitorCountryList, JSendModelResponseExhibitorPavilionList, JSendModelResponseExhibitorStandList, JSendModelResponseGenerateQuerySignatureOutput, JSendModelResponseGetCompanySiteInfoOutput, JSendModelResponseGetGigyaApiKeyForDomainOutput, JSendModelResponseGetGigyaDomainsOutput, JSendModelResponseGetSubdomainAvailabilityOutput, JSendModelResponseGetUserLocationOutput, JSendModelResponseGoogleMapsAPIKeyOutput, JSendModelResponseLoginUserOutput, JSendModelResponseMailerOutput, JSendModelResponsePublicAgendaInCollaborationList, JSendModelResponsePublicAgendaModeratorList, JSendModelResponsePublicAgendaOrganizerList, JSendModelResponsePublicAgendaParticipationTypeList, JSendModelResponsePublicAgendaRoomList, JSendModelResponsePublicAgendaSpeakerList, JSendModelResponsePublicAgendaSponsorshipList, JSendModelResponsePublicExhibitorCountryList, JSendModelResponsePublicExhibitorPavilionList, JSendModelResponsePublicExhibitorStandList, JSendModelResponsePushOutput, JSendModelResponseQRCodeStringOutput, JSendModelResponseQuizCheckCompleteOutput, JSendModelResponseQuizUnansweredInfoOutput, JSendModelResponseRegisterAdministratorOutput, JSendModelResponseRegisterLeadOutput, JSendModelResponseRegisterTransientUserOutput, JSendModelResponseRegisterUserOutput, JSendModelResponseSignZoomTokenOutput, JSendModelResponseStripoTokenOutput, JSendModelResponseUsedSpaceInformation, JSendModelResponseUserCustomFieldKeysOutput, JSendModelResponseUserInformationOutput, JSendModelResponseUserReadOnlySchema, JSendModelResponseUserReportFieldKeysOutput, JSendModelResponseWatermark, JSendObjectResponse, JSendObjectResponseBool, JSendObjectResponseDict, JSendObjectResponseDictStrAny, JSendObjectResponseDictStrInt, JSendObjectResponseInt, JSendObjectResponseListDict, LeaderboardService, LiveChatCRUDService, LookupTablesService, MailTemplateCRUDService, MailerCRUDService, MailerService, ManagementService, MediaGalleryCRUDService, MediaGalleryItemCRUDService, MeetingCRUDService, MeetingEventUserCRUDService, MeetingExhibitorCRUDService, MeetingSpeakerCRUDService, MeetingUserCRUDService, MenuVoiceCRUDService, MerchandiseCategoryCRUDService, NewsCategoryCRUDService, NewsItemCRUDService, OAuth2ClientService, OakEventCategories, OakFeedbackAnswerType, OakGenders, OnlineUserService, PDFTemplateCRUDService, ParticipantCategoryCRUDService, PollCRUDService, PollVoteCRUDService, PopupCRUDService, PresenceService, PublicAgendaCRUDService, PublicAgendaCategoryCRUDService, PublicAgendaFlowCRUDService, PublicAgendaSpeakerCRUDService, PublicAgendaSubcategoryCRUDService, PublicAgendaUserCRUDService, PublicCarReservationCRUDService, PublicChatGroupCRUDService, PublicChatRoomCRUDService, PublicChatRoomMessageCRUDService, PublicCompanyFileCRUDService, PublicDeviceTokenCRUDService, PublicDocumentCRUDService, PublicDocumentRatingCRUDService, PublicEventCRUDService, PublicEventCategoryCRUDService, PublicEventChatGroupCRUDService, PublicEventCheckInCRUDService, PublicEventFlowCRUDService, PublicEventGameCRUDService, PublicEventGameUserScoreCRUDService, PublicEventGroupCRUDService, PublicEventHotelRoomCRUDService, PublicEventPictureCRUDService, PublicEventPlaylistCRUDService, PublicEventSpeakerCRUDService, PublicEventSponsorCRUDService, PublicEventSponsorTierCRUDService, PublicEventSubcategoryCRUDService, PublicEventUserCRUDService, PublicEventUserDirectoryService, PublicExhibitorBrandCRUDService, PublicExhibitorCRUDService, PublicExhibitorDocumentCRUDService, PublicExhibitorEventUserCRUDService, PublicExhibitorPressReleaseCRUDService, PublicExhibitorProductCRUDService, PublicExhibitorVideoCRUDService, PublicFeedbackCRUDService, PublicFeedbackQuestionCRUDService, PublicFeedbackVoteCRUDService, PublicFieldOfApplicationCRUDService, PublicFirmCRUDService, PublicFlightReservationCRUDService, PublicFlowCRUDService, PublicFlowItemCRUDService, PublicFlowItemUserStatusCRUDService, PublicFlowUserCRUDService, PublicGameCRUDService, PublicGroupCRUDService, PublicGroupUserCRUDService, PublicHotelCRUDService, PublicHotelReservationCRUDService, PublicLeaderboardService, PublicLiveChatCRUDService, PublicMediaGalleryCRUDService, PublicMediaGalleryItemCRUDService, PublicMeetingCRUDService, PublicMeetingEventUserCRUDService, PublicMeetingExhibitorCRUDService, PublicMeetingSpeakerCRUDService, PublicMeetingUserCRUDService, PublicMenuVoiceCRUDService, PublicMerchandiseCategoryCRUDService, PublicNewsCategoryCRUDService, PublicNewsItemCRUDService, PublicParticipantCategoryCRUDService, PublicPollCRUDService, PublicPollVoteCRUDService, PublicPopupCRUDService, PublicQuizCRUDService, PublicRegistrationFormCRUDService, PublicRestaurantCRUDService, PublicSettingCRUDService, PublicShuttleScheduleCRUDService, PublicSpeakerCRUDService, PublicSponsorCRUDService, PublicStandCRUDService, PublicStreamCRUDService, PublicStreamReactionCRUDService, PublicSubscriptionCRUDService, PublicThemeSettingCRUDService, PublicTradeShowSectorCRUDService, PublicTrainReservationCRUDService, PublicUserCRUDService, PublicUserCategoryCRUDService, PublicUserDocumentCRUDService, PublicUserGeneratedContentBinCRUDService, PublicUserGeneratedContentCRUDService, PublicUserLocationCRUDService, PublicUserLocationHistoryCRUDService, PublicUserSubscriptionCRUDService, PublicVenueCRUDService, PublicVenueCategoryCRUDService, PublicWallCRUDService, PublicWordsCloudCRUDService, PushNotificationsService, QuizCRUDService, RegistrationFormCRUDService, ReportService, RestaurantCRUDService, SelfService, SentMailCRUDService, SentPushCRUDService, SettingCRUDService, ShuttleScheduleCRUDService, SiteService, SmsTemplateCRUDService, SpeakerCRUDService, SponsorCRUDService, StandCRUDService, StreamCRUDService, StreamReactionCRUDService, SubscriptionCRUDService, ThemeSettingCRUDService, TradeShowSectorCRUDService, TrainReservationCRUDService, TranslationCRUDService, UserCRUDService, UserCategoryCRUDService, UserDocumentCRUDService, UserGeneratedContentBinCRUDService, UserGeneratedContentCRUDService, UserLocationCRUDService, UserLocationHistoryCRUDService, UserService, UserSubscriptionCRUDService, VenueCRUDService, VenueCategoryCRUDService, VoteService, WallCRUDService, WordsCloudBlacklistCRUDService, WordsCloudCRUDService, WordsCloudWhitelistCRUDService };
